import { Events, Observer, ObserverHistory, ObserverList, History as History$1 } from '@playcanvas/observer';

/* eslint-disable no-extend-native */
if (!Array.prototype.equals) {
    Object.defineProperty(Array.prototype, 'equals', {
        enumerable: false,
        value: function (array) {
            if (!array) {
                return false;
            }
            if (this.length !== array.length) {
                return false;
            }
            for (let i = 0, l = this.length; i < l; i++) {
                if (this[i] instanceof Array && array[i] instanceof Array) {
                    if (!this[i].equals(array[i])) {
                        return false;
                    }
                }
                else if (this[i] !== array[i]) {
                    return false;
                }
            }
            return true;
        }
    });
}

/**
 * Global variables
 */
class globals {
    /**
     * Alert function called when user confirmation is needed
     * for an action. Defaults to the default browser popup but
     * can be overridden to show your custom popup instead.
     *
     * @param text - The confirm dialog text
     * @param options - Options for the popup
     * @param options.yesText - Text for 'yes' option
     * @param options.noText - Text for 'no' option
     * @param options.noDismiss - If true then user cannot dismiss the popup and will have to click yes or no
     * @returns True if the user confirmed, false otherwise
     */
    static confirmFn(text, options = {}) {
        return new Promise((resolve) => {
            // eslint-disable-next-line no-alert
            resolve(confirm(text));
        });
    }
}
/**
 * The home URL
 */
globals.homeUrl = '';
/**
 * The REST API URL
 */
globals.apiUrl = '';

/**
 * Contains various utility methods
 *
 * @category Internal
 */
class utils {
    /**
     * Deep copy an object
     *
     * @param data - The data to copy
     * @returns A copy of the data
     */
    static deepCopy(data) {
        if (data == null || typeof data !== 'object') {
            return data;
        }
        if (data instanceof Array) {
            const arr = [];
            for (let i = 0; i < data.length; i++) {
                arr[i] = this.deepCopy(data[i]);
            }
            return arr;
        }
        const obj = {};
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                obj[key] = this.deepCopy(data[key]);
            }
        }
        return obj;
    }
    static expandPath(obj, path, fn) {
        function forEachPathInData(pathParts, pathSoFar, startIndex, data, fn) {
            if (!data)
                return;
            function process(item, key) {
                let current = item;
                let p = (pathSoFar ? `${pathSoFar}.` : '') + key;
                for (let i = startIndex; i < pathParts.length; i++) {
                    // if we found another star recurse
                    if (pathParts[i] === '*') {
                        forEachPathInData(pathParts, p, i + 1, current, fn);
                        return;
                    }
                    // keep getting deeper into the object as long as each path part exists
                    if (!current.hasOwnProperty(pathParts[i])) {
                        return;
                    }
                    current = current[pathParts[i]];
                    // if we found a null entry then stop unless this is the
                    // final path part which means we ended up at the end of the path
                    // which is fine
                    if (!current && i < pathParts.length - 1) {
                        return;
                    }
                    p += `.${pathParts[i]}`;
                }
                // call callback with the path we've found so far
                fn(obj, p);
            }
            if (Array.isArray(data)) {
                // if data is an array then process all of its array items
                data.forEach((item, index) => {
                    process(item, index);
                });
            }
            else {
                // if data is a JSON object then process all of its keys
                const keys = Object.keys(data);
                keys.forEach((key) => {
                    process(data[key], key);
                });
            }
        }
        // if this is a simple path without a star
        // then just early out
        if (!path.includes('*')) {
            if (obj.has(path)) {
                fn(obj, path);
            }
            return;
        }
        // paths with stars need to be broken up
        // and processed differently. When a star is encountered
        // we have to process all of the entries under the star
        const parts = path.split('.');
        if (parts[0] === '*') {
            // handle special case when first part of path is a star
            forEachPathInData(parts, '', 1, obj.json(), fn);
            return;
        }
        // find path until first star
        let firstPartialPath = parts[0];
        let i;
        for (i = 1; i < parts.length; i++) {
            if (parts[i] === '*')
                break;
            firstPartialPath += `.${parts[i]}`;
        }
        if (!obj.has(firstPartialPath))
            return;
        // get json just before first star
        const json = obj.get(firstPartialPath);
        if (!json)
            return;
        // start breaking down each path
        forEachPathInData(parts, firstPartialPath, i + 1, json, fn);
    }
}

/**
 * Basically a very large random number (128-bit) which means the
 * probability of creating two that clash is vanishingly small.
 *
 * @category Internal
 */
class Guid {
    /**
     * Create an RFC4122 version 4 compliant GUID.
     *
     * @returns A new GUID.
     */
    static create() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = (c === 'x') ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

let ASSET_PATHS$1 = null;
const REGEX_CONTAINS_STAR$1 = /\.\*\./;
/**
 * Stores asset paths in the assets dictionary by converting the array of
 * folder ids to an array of folder names.
 *
 * @param assetIds - The asset ids
 * @param assets - The assets dictionary
 */
function storeAssetPaths(assetIds, assets) {
    if (!Array.isArray(assetIds)) {
        assetIds = [assetIds];
    }
    for (let i = 0; i < assetIds.length; i++) {
        const assetId = assetIds[i];
        if (!assetId || assets[assetId])
            continue;
        const asset = globals.assets.get(assetId);
        if (!asset)
            return;
        const parts = [];
        const path = asset.get('path');
        if (path && path.length) {
            for (let j = 0; j < path.length; j++) {
                const a = globals.assets.get(path[j]);
                if (!a)
                    continue;
                parts.push(a.get('name'));
            }
        }
        parts.push(asset.get('name'));
        assets[assetId] = {
            path: parts,
            type: asset.get('type')
        };
    }
}
/**
 * Gathers all asset dependencies for this entity
 *
 * @param entity - The entity
 * @param data - The helper data
 */
function gatherDependencies(entity, data) {
    if (!ASSET_PATHS$1) {
        // get asset paths for all components
        ASSET_PATHS$1 = [];
        globals.schema.components.list().forEach((component) => {
            const paths = globals.schema.components.getFieldsOfType(component, 'asset');
            paths.forEach((path) => {
                ASSET_PATHS$1.push(`components.${component}.${path}`);
            });
        });
    }
    // store entity json
    const resourceId = entity.get('resource_id');
    if (!data.hierarchy[resourceId]) {
        data.hierarchy[resourceId] = entity.json();
    }
    // gather all asset references from the entity
    // and store their path + name
    for (let i = 0; i < ASSET_PATHS$1.length; i++) {
        const path = ASSET_PATHS$1[i];
        // handle paths that contain a '*' as a wildcard
        if (REGEX_CONTAINS_STAR$1.test(path)) {
            const parts = path.split('.*.');
            if (!entity.has(parts[0]))
                continue;
            const obj = entity.get(parts[0]);
            if (!obj)
                continue;
            for (const key in obj) {
                const fullKey = `${parts[0]}.${key}.${parts[1]}`;
                if (!entity.has(fullKey)) {
                    continue;
                }
                const assets = entity.get(fullKey);
                if (!assets)
                    continue;
                storeAssetPaths(assets, data.assets);
            }
        }
        else if (entity.has(path)) {
            // handle path without '*'
            const assets = entity.get(path);
            if (!assets)
                continue;
            storeAssetPaths(assets, data.assets);
        }
    }
    // gather script attributes
    if (entity.has('components.script.scripts')) {
        const scripts = entity.get('components.script.scripts');
        if (scripts) {
            // legacy scripts
            if (globals.hasLegacyScripts) {
                for (let i = 0; i < scripts.length; i++) {
                    const script = scripts[i];
                    if (!script.attributes)
                        continue;
                    for (const name in script.attributes) {
                        const attr = script.attributes[name];
                        if (!attr)
                            continue;
                        if (attr.type === 'asset') {
                            if (attr.value) {
                                storeAssetPaths(attr.value, data.assets);
                            }
                            if (attr.defaultValue) {
                                storeAssetPaths(attr.defaultValue, data.assets);
                            }
                        }
                    }
                }
            }
            else {
                // scripts 2.0
                for (const key in scripts) {
                    const scriptData = scripts[key];
                    if (!scriptData || !scriptData.attributes)
                        continue;
                    const asset = globals.assets.getAssetForScript(key);
                    if (!asset)
                        continue;
                    // search for asset script attributes in script asset
                    const assetData = asset.get(`data.scripts.${key}.attributes`);
                    if (!assetData)
                        continue;
                    for (const name in assetData) {
                        const componentAttribute = scriptData.attributes[name];
                        if (!componentAttribute)
                            continue;
                        if (assetData[name].type === 'asset') {
                            storeAssetPaths(componentAttribute, data.assets);
                        }
                        else if (assetData[name].type === 'json') {
                            const schema = assetData[name].schema;
                            if (Array.isArray(schema)) {
                                for (let i = 0; i < schema.length; i++) {
                                    const field = schema[i];
                                    if (field.type === 'asset') {
                                        if (Array.isArray(componentAttribute)) {
                                            for (let j = 0; j < componentAttribute.length; j++) {
                                                if (componentAttribute[j] && componentAttribute[j][field.name]) {
                                                    storeAssetPaths(componentAttribute[j][field.name], data.assets);
                                                }
                                            }
                                        }
                                        else if (componentAttribute[field.name]) {
                                            storeAssetPaths(componentAttribute[field.name], data.assets);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    const children = entity.get('children');
    for (let i = 0; i < children.length; i++) {
        gatherDependencies(globals.entities.get(children[i]), data);
    }
}
// Sorts entities by their index in their parent's children list
function sortEntities(entities) {
    entities.sort((a, b) => {
        let parentA = a.get('parent');
        if (!parentA) {
            return -1;
        }
        parentA = globals.entities.get(parentA);
        if (!parentA) {
            return -1;
        }
        const indA = parentA.get('children').indexOf(a.get('resource_id'));
        let parentB = b.get('parent');
        if (!parentB) {
            return 1;
        }
        parentB = globals.entities.get(parentB);
        if (!parentB) {
            return -1;
        }
        const indB = parentB.get('children').indexOf(b.get('resource_id'));
        return indA - indB;
    });
}
/**
 * Copy specified entities to localStorage clipboard. Can be used
 * to paste these entities later on.
 *
 * @param entities - The entities
 */
function copyEntities(entities) {
    var _a, _b;
    const currentScene = (_b = (_a = globals.realtime) === null || _a === undefined ? undefined : _a.scenes) === null || _b === undefined ? undefined : _b.current;
    if (!currentScene)
        throw new Error('No current scene loaded');
    const data = {
        project: globals.projectId,
        scene: currentScene.uniqueId,
        branch: globals.branchId,
        legacy_scripts: globals.hasLegacyScripts,
        hierarchy: {},
        assets: {},
        type: 'entity'
    };
    // build index
    const selection = {};
    for (let i = 0; i < entities.length; i++) {
        selection[entities[i].get('resource_id')] = entities[i];
    }
    sortEntities(entities);
    for (let i = 0; i < entities.length; i++) {
        const e = entities[i];
        let p = e.parent;
        let isParentSelected = false;
        while (p) {
            if (selection[p.get('resource_id')]) {
                isParentSelected = true;
                break;
            }
            p = p.parent;
        }
        // if parent is also selected then skip child
        // and only add parent to copied entities
        if (isParentSelected) {
            // remove entity from selection
            // since its parent is selected
            delete selection[e.get('resource_id')];
            continue;
        }
        // add entity to clipboard if not already added as a child of
        // a higher level entity
        gatherDependencies(e, data);
    }
    for (const key in selection) {
        // set parent of each copied entity to null
        if (data.hierarchy[key]) {
            data.hierarchy[key].parent = null;
        }
    }
    // save to local storage
    globals.clipboard.value = data;
}

/**
 * The Entity class represents an entity in the Editor.
 */
class Entity extends Events {
    /**
     * Creates new Entity
     *
     * @category Internal
     * @param data - Optional entity data
     */
    constructor(data = {}) {
        super();
        let name = data.name;
        if (name === undefined || typeof name !== 'string') {
            name = 'New Entity';
        }
        const observerData = {
            name: name,
            tags: data.tags || [],
            enabled: data.enabled !== undefined ? !!data.enabled : true,
            resource_id: data.resource_id || Guid.create(),
            parent: typeof data.parent === 'string' ? data.parent : null,
            children: [],
            position: data.position || [0, 0, 0],
            rotation: data.rotation || [0, 0, 0],
            scale: data.scale || [1, 1, 1],
            components: {}
        };
        if (data.template_id) {
            observerData.template_id = data.template_id;
        }
        if (data.template_ent_ids) {
            observerData.template_ent_ids = data.template_ent_ids;
        }
        this._observer = new Observer(observerData);
        this._observer.addEmitter(this);
        const id = this._observer.get('resource_id');
        this._observer.latestFn = () => {
            const latest = globals.entities.get(id);
            return latest && latest._observer;
        };
        this._observer.apiEntity = this;
        if (data.components) {
            for (const component in data.components) {
                this.addComponent(component, data.components[component]);
            }
        }
        this._history = {};
    }
    _initializeHistory() {
        if (this._observer.history)
            return;
        this._history = new ObserverHistory({
            item: this._observer,
            prefix: `entity.${this.get('resource_id')}.`,
            history: globals.history
        });
        this._observer.history = this._history;
    }
    /**
     * Checks if path exists. See {@link Entity} for a list of properties.
     *
     * @param path - The path
     * @returns True if path exists
     * @example
     * ```javascript
     * console.log(entity.has('components.model'));
     * ```
     */
    has(path) {
        return this._observer.has(path);
    }
    /**
     * Gets value at path. See {@link Entity} for a list of properties.
     *
     * @param path - The path
     * @returns The value
     * @example
     * ```javascript
     * console.log(entity.get('position'));
     * ```
     */
    get(path) {
        return this._observer.get(path);
    }
    /**
     * Sets value at path. See {@link Entity} for a list of properties.
     *
     * @param path - The path
     * @param value - The value
     * @returns Whether the value was set
     * @example
     * ```javascript
     * entity.set('position', [1, 0, 0]);
     * ```
     */
    set(path, value) {
        return this._observer.set(path, value);
    }
    /**
     * Unsets value at path. See {@link Entity} for a list of properties.
     *
     * @param path - The path
     * @returns Whether the value was unset
     * @example
     * ```javascript
     * entity.unset('components.model');
     * ```
     */
    unset(path) {
        return this._observer.unset(path);
    }
    /**
     * Inserts value in array at path, at specified index. See {@link Entity} for a list of properties.
     *
     * @param path - The path
     * @param value - The value
     * @param index - The index (if undefined the value will be inserted in the end)
     * @returns Whether the value was inserted
     * @example
     * ```javascript
     * entity.insert('tags', 'a_tag');
     * ```
     */
    insert(path, value, index) {
        return this._observer.insert(path, value, index);
    }
    /**
     * Remove value from array at path. See {@link Entity} for a list of properties.
     *
     * @param path - The path
     * @param value - The value
     * @returns Whether the value was removed
     * @example
     * ```javascript
     * entity.removeValue('tags', 'a_tag');
     * ```
     */
    removeValue(path, value) {
        return this._observer.removeValue(path, value);
    }
    /**
     * Returns JSON representation of entity data
     *
     * @returns - The data
     * ```javascript
     * console.log(entity.json());
     * ```
     */
    json() {
        return this._observer.json();
    }
    /**
     * Returns a JSON representation of entity data. The children array
     * of the entity gets recursively converted to an array of entity data
     * instead of containing children resource ids.
     *
     * @returns - The data
     * @example
     * ```javascript
     * const data = entity.jsonHierarchy();
     * console.log(data.children[0].name);
     * ```
     */
    jsonHierarchy() {
        const result = this.json();
        const children = this.children;
        for (let i = 0; i < children.length; i++) {
            result.children[i] = children[i] && children[i].jsonHierarchy();
        }
        return result;
    }
    /**
     * Returns true if this entity is a descendant of the specified parent entity.
     *
     * @param parent - The parent
     * @returns True if it is
     */
    isDescendantOf(parent) {
        let p = this.parent;
        while (p) {
            if (p === parent) {
                return true;
            }
            p = p.parent;
        }
        return false;
    }
    /**
     * Finds first entity by name using depth-first search
     *
     * @param name - The name
     * @returns The entity
     * @example
     * ```javascript
     * const door = editor.entities.root.findByName('Door');
     * ```
     */
    findByName(name) {
        if (this.get('name') === name) {
            return this;
        }
        const children = this.children;
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (child) {
                const found = child.findByName(name);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }
    /**
     * Finds all entities with specified tags
     *
     * @param tags - The tags. If multiple tags are specified then entities that contain ANY of the specified
     * tags will be included. If an argument is an array of tags then entities that contain ALL of the tags in the array will be included.
     * @returns The entities
     * @example
     * ```javascript
     * // entities that have the following tag
     * const entities = editor.entities.root.listByTag('tag');
     * // entities that have any of the following tags
     * const entities = editor.entities.root.listByTag('tag', 'tag2');
     * // entities that have all of the following tags
     * const entities = editor.entities.root.listByTag(['tag', 'tag2']);
     * ```
     */
    listByTag(...tags) {
        return this.filter((entity) => {
            const t = entity.get('tags');
            for (let i = 0; i < tags.length; i++) {
                if (Array.isArray(tags[i])) {
                    let countTags = 0;
                    for (let j = 0; j < tags[i].length; j++) {
                        if (t.includes(tags[i][j])) {
                            countTags++;
                        }
                        else {
                            break;
                        }
                    }
                    if (countTags === tags[i].length) {
                        return true;
                    }
                }
                else {
                    if (t.includes(tags[i])) {
                        return true;
                    }
                }
            }
            return false;
        });
    }
    /**
     * Returns the entity and children that satisfy the function
     *
     * @param fn - A function that takes an Entity and returns whether it should be included
     * in the result
     * @returns The result
     * @example
     * ```javascript
     * const doors = editor.entities.root.filter(entity => entity.get('name').startsWith('door'));
     * ```
     */
    filter(fn) {
        let result = [];
        if (fn(this)) {
            result.push(this);
        }
        const children = this.children;
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (child) {
                result = result.concat(child.filter(fn));
            }
        }
        return result;
    }
    /**
     * Executes function for this entity and its children in depth first order.
     *
     * @param fn - A function that takes an entity as an argument
     * @example
     * ```javascript
     * // get a list of all entities in the graph in depth first order
     * const entities = [];
     * editor.entities.root.depthFirst(entity => entities.push(entity));
     * ```
     */
    depthFirst(fn) {
        fn(this);
        const children = this.children;
        children.forEach((child) => {
            if (child) {
                child.depthFirst(fn);
            }
            else {
                fn(child);
            }
        });
    }
    /**
     * Adds a component to this Entity
     *
     * @param component - The component name
     * @param data - Default component data. Defaults values will be used for any missing fields.
     * For details on component properties see {@link Entity}.
     * @example
     * ```javascript
     * editor.entities.root.addComponent('model', {
     *     type: 'box'
     * });
     * ```
     */
    addComponent(component, data = {}) {
        const defaultData = globals.schema.components.getDefaultData(component);
        const componentData = Object.assign(defaultData, data);
        this.set(`components.${component}`, componentData);
    }
    /**
     * Removes a component from this Entity
     *
     * @param component - The component name
     * @example
     * ```javascript
     * editor.entities.root.removeComponent('model');
     * ```
     */
    removeComponent(component) {
        this.unset(`components.${component}`);
    }
    /**
     * Adds entity as a child
     *
     * @category Internal
     * @param entity - The entity
     * @returns Whether the child was added
     */
    addChild(entity) {
        return this.insertChild(entity);
    }
    /**
     * Inserts entity as a child at specified index.
     *
     * @category Internal
     * @param entity - The entity
     * @param index - The index. If undefined the child will be added in the end.
     * @returns Whether the child was added
     */
    insertChild(entity, index = undefined) {
        let history = this.history.enabled;
        this.history.enabled = false;
        const result = this.insert('children', entity.get('resource_id'), index);
        this.history.enabled = history;
        // BUG TRACKING: missing children
        if (!globals.entities.get(entity.get('resource_id'))) {
            console.error(`BUG TRACKING: inserting missing child guid ${entity.get('resource_id')} to parent ${this.get('resource_id')}`);
        }
        if (result) {
            history = entity.history.enabled;
            entity.history.enabled = false;
            entity.set('parent', this.get('resource_id'));
            entity.history.enabled = history;
            return true;
        }
        console.error(`Cannot add duplicate child ${entity.get('resource_id')} under parent ${this.get('resource_id')}`);
        return false;
    }
    /**
     * Removes entity from children
     *
     * @category Internal
     * @param entity - The entity
     */
    removeChild(entity) {
        let history = entity.history.enabled;
        entity.history.enabled = false;
        try {
            entity.observer.set('parent', null, true); // silent set otherwise we run into C3 error
        }
        catch (err) {
            console.error(`Error when setting parent to null for entity ${entity.get('resource_id')}`);
            console.error(err);
        }
        entity.history.enabled = history;
        history = this.history.enabled;
        this.history.enabled = false;
        try {
            this.removeValue('children', entity.get('resource_id'));
        }
        catch (err) {
            console.error(`Error when removing ${entity.get('resource_id')} from children of entity ${this.get('resource_id')}`);
            console.error(err);
        }
        this.history.enabled = history;
    }
    /**
     * Deletes entity (and its children)
     *
     * @param options.history - Whether to record a history action. Defaults to true.
     * @returns A promise
     * @example
     * ```javascript
     * editor.entities.root.findByName('door').delete();
     * ```
     *
     */
    delete(options = {}) {
        return globals.entities.delete([this], options);
    }
    /**
     * Reparents entity under new parent
     *
     * @param parent - The new parent
     * @param index - The desired index. If undefined the entity will be added at the end of the parent's children.
     * @param options.history - Whether to record a history action. Defaults to true.
     * @param options.preserveTransform - Whether to preserve the original transform after reparenting
     * @example
     * ```javascript
     * const redHouse = editor.entities.root.findByName('red house');
     * const greenHouse = editor.entities.root.findByName('green house');
     * const door = redHouse.findByName('door');
     * door.reparent(greenHouse);
     * ```
     */
    reparent(parent, index = null, options = {}) {
        globals.entities.reparent([{
                entity: this,
                parent: parent,
                index: index
            }], options);
    }
    /**
     * Duplicates entity under the same parent
     *
     * @param options.history - Whether to record a history action. Defaults to true.
     * @param options.select - Whether to select the new entity. Defaults to false.
     * @param options.rename - Whether to rename the duplicated entity. Defaults to false.
     * @returns The new entity
     */
    duplicate() {
        return __awaiter(this, arguments, undefined, function* (options = {}) {
            const result = yield globals.entities.duplicate([this], options);
            return result[0];
        });
    }
    /**
     * Returns the latest version of the Entity from the Entities API.
     *
     * @returns The entity
     */
    latest() {
        return globals.entities.get(this._observer.get('resource_id'));
    }
    /**
     * Adds a script to the script component of this entity.
     * If a script component does not exist, this method will add the script
     * component as well.
     *
     * @param scriptName - The name of the script.
     * @param options.attributes - The values of attributes. Each key is the name
     * of the attributes and each value is the value for that attribute. Leave undefined to
     * let the Editor set default values depending on the attribute types.
     * @param options.history - Whether to add a history action. Defaults to true.
     * @param options.index - The desired index in the entity's scripts order to add this script.
     * @returns A promise
     */
    addScript(scriptName, options = {}) {
        return globals.entities.addScript([this], scriptName, options);
    }
    /**
     * Removes a script from the entity's script component.
     *
     * @param scriptName - The name of the script.
     * @param options.history - Whether to record a history action. Defaults to true.
     */
    removeScript(scriptName, options = {}) {
        globals.entities.removeScript([this], scriptName, options);
    }
    /**
     * The observer object for this entity.
     */
    get observer() {
        return this._observer;
    }
    /**
     * The parent entity.
     */
    get parent() {
        const id = this.get('parent');
        return (id ? globals.entities.get(id) : null);
    }
    /**
     * The children entities. Warning: this creates a new array every time it's called.
     */
    get children() {
        return this.get('children').map((id) => globals.entities.get(id));
    }
    /**
     * The history object for this entity.
     */
    get history() {
        return this._history;
    }
    /**
     * The entity in the 3D viewport of the Editor.
     */
    get viewportEntity() {
        return (this._observer ? this._observer.entity : null);
    }
}

/**
 * Creates new entity and adds it to the hierarchy
 *
 * @param data - Optional initial data for the entity
 * @param options.index - The child index that this entity will have under its parent.
 * @param options.history - Whether to record a history action. Defaults to true.
 * @param options.select - Whether to select new Entity. Defaults to false.
 * @returns The new entity
 */
function createEntity(data, options = {}) {
    data = data || {};
    if (options.history === undefined) {
        options.history = true;
    }
    if (!data.parent) {
        data.parent = globals.entities.root ? globals.entities.root.get('resource_id') : null;
    }
    if (data.parent instanceof Entity) {
        data.parent = data.parent.get('resource_id');
    }
    if (data.parent && !globals.entities.get(data.parent)) {
        console.error(`Cannot create entity because parent ${data.parent} was not found`);
        return null;
    }
    let entity = new Entity(data);
    globals.entities.add(entity);
    // sharedb
    if (globals.realtime && globals.realtime.scenes.current) {
        globals.realtime.scenes.current.addEntity(entity);
    }
    const parent = globals.entities.get(data.parent);
    if (parent) {
        if (options.index === undefined) {
            parent.addChild(entity);
        }
        else {
            parent.insertChild(entity, options.index);
        }
    }
    // use same resource id in redo's
    data.resource_id = entity.get('resource_id');
    // add children
    if (data.children) {
        data.children.forEach((childData) => {
            childData.parent = entity;
            const child = createEntity(childData, {
                history: false,
                select: false
            });
            // use same resource_id in subsequent redo's
            childData.resource_id = child.get('resource_id');
        });
    }
    if (data.onCreate) {
        data.onCreate(entity);
    }
    let prevSelection;
    // remember previous selection
    if (options.history && globals.history) {
        if (options.select && globals.selection) {
            prevSelection = globals.selection.items;
        }
    }
    // select new entity
    if (options.select && globals.selection) {
        globals.selection.set([entity], {
            history: false
        });
    }
    if (options.history && globals.history) {
        globals.history.add({
            name: `new entity ${entity.get('resource_id')}`,
            combine: false,
            // undo new entity
            undo: () => {
                entity = entity.latest();
                if (!entity)
                    return;
                globals.entities.delete([entity], {
                    history: false
                });
                if (prevSelection) {
                    globals.selection.set(prevSelection, {
                        history: false
                    });
                }
            },
            // redo new entity
            redo: () => {
                entity = createEntity(data, {
                    history: false,
                    select: options.select
                });
            }
        });
    }
    return entity;
}

const fieldsCache = {};
function findReferencesInComponents(entity, refType) {
    const result = {};
    function addReference(entity, path, value) {
        if (!result[value]) {
            result[value] = [];
        }
        result[value].push({
            entityId: entity.get('resource_id'),
            path: path
        });
    }
    function handleScriptAttribute(entity, path, attributeDefinition, attributeValue) {
        if (!attributeValue)
            return;
        if (attributeDefinition.array) {
            attributeValue.forEach((id, i) => {
                if (id) {
                    addReference(entity, `${path}.${i}`, id);
                }
            });
        }
        else {
            if (attributeValue) {
                addReference(entity, path, attributeValue);
            }
        }
    }
    function handleReference(entity, path) {
        const value = entity.get(path);
        if (!value)
            return;
        if (Array.isArray(value)) {
            value.forEach((id, i) => {
                if (id) {
                    addReference(entity, `${path}.${i}`, id);
                }
            });
        }
        else {
            addReference(entity, path, value);
        }
    }
    entity.depthFirst((entity) => {
        const componentNames = Object.keys(entity.get('components') || {});
        componentNames.forEach((component) => {
            if (!fieldsCache[refType]) {
                fieldsCache[refType] = {};
            }
            if (!fieldsCache[refType][component]) {
                fieldsCache[refType][component] = globals.schema.components.getFieldsOfType(component, refType);
            }
            fieldsCache[refType][component].forEach((field) => {
                // expand path will handle paths that contain '*'
                utils.expandPath(entity, `components.${component}.${field}`, (entity, path) => {
                    handleReference(entity, path);
                });
            });
            // get script attributes
            if (!globals.hasLegacyScripts && globals.assets && component === 'script') {
                const scriptComponent = entity.get('components.script');
                for (const scriptName in scriptComponent.scripts) {
                    // get script asset
                    const scriptAsset = globals.assets.getAssetForScript(scriptName);
                    if (!scriptAsset)
                        continue;
                    // go through the script component attribute values
                    for (const attributeName in scriptComponent.scripts[scriptName].attributes) {
                        const attributeValue = scriptComponent.scripts[scriptName].attributes[attributeName];
                        // early out if the value is null
                        if (!attributeValue || (Array.isArray(attributeValue) && !attributeValue.length))
                            continue;
                        const attributeDef = scriptAsset.get(`data.scripts.${scriptName}.attributes.${attributeName}`);
                        if (!attributeDef)
                            continue;
                        const componentAttributePath = `components.script.scripts.${scriptName}.attributes.${attributeName}`;
                        if (attributeDef.type === 'json') {
                            if (!Array.isArray(attributeDef.schema))
                                continue;
                            if (attributeDef.array) {
                                for (let i = 0; i < attributeValue.length; i++) {
                                    attributeDef.schema.forEach((field) => {
                                        var _a;
                                        if (field.type !== refType)
                                            return;
                                        handleScriptAttribute(entity, `${componentAttributePath}.${i}.${field.name}`, field, (_a = attributeValue[i]) === null || _a === undefined ? undefined : _a[field.name]);
                                    });
                                }
                            }
                            else {
                                attributeDef.schema.forEach((field) => {
                                    if (field.type !== refType)
                                        return;
                                    handleScriptAttribute(entity, `${componentAttributePath}.${field.name}`, field, attributeValue[field.name]);
                                });
                            }
                        }
                        else if (attributeDef.type === refType) {
                            handleScriptAttribute(entity, componentAttributePath, attributeDef, attributeValue);
                        }
                    }
                }
            }
        });
    });
    return result;
}
/**
 * Return a map of all entity reference properties in the graph. This will
 * include references of the entity and also references of its children
 *
 * @param entity - The entity
 * @returns The entity references
 */
function findEntityReferencesInComponents(entity) {
    return findReferencesInComponents(entity, 'entity');
}
/**
 * Return a map of all asset reference properties in the graph.
 *
 * @param entity - The entity
 * @returns The asset references
 */
function findAssetReferencesInComponents(entity) {
    return findReferencesInComponents(entity, 'asset');
}
/**
 * Updates references to the old value to point to the new value
 *
 * @param references - A map of references that we got
 * from findEntityReferencesInComponents or findAssetReferencesInComponents.
 * @param oldValue - The value that we want to replace
 * @param newValue - The value that we want our references to point to
 */
function updateReferences(references, oldValue, newValue) {
    const referencesToEntity = references[oldValue];
    if (!referencesToEntity)
        return;
    referencesToEntity.forEach((reference) => {
        const entity = globals.entities.get(reference.entityId);
        if (entity && entity.has(reference.path)) {
            const history = entity.history.enabled;
            entity.history.enabled = false;
            entity.set(reference.path, newValue);
            entity.history.enabled = history;
        }
    });
}

const USE_BACKEND_LIMIT$2 = 500;
let evtMessenger$3 = null;
// Gets the count of the entities and their children
function getTotalEntityCount(entities) {
    let count = 0;
    entities.forEach((entity) => {
        entity.depthFirst(() => count++);
    });
    return count;
}
// When we have a lot of entities to delete
// do it in the backend
function deleteInBackend(entities) {
    const deferred = {
        resolve: null
    };
    const promise = new Promise((resolve) => {
        deferred.resolve = resolve;
    });
    const jobId = globals.jobs.start(() => {
        deferred.resolve();
    });
    if (!evtMessenger$3) {
        evtMessenger$3 = globals.messenger.on('entity.delete', (data) => {
            const callback = globals.jobs.finish(data.job_id);
            if (callback) {
                callback();
            }
        });
    }
    globals.realtime.connection.sendMessage('pipeline', {
        name: 'entities-delete',
        data: {
            projectId: globals.projectId,
            branchId: globals.branchId,
            sceneId: globals.realtime.scenes.current.uniqueId,
            jobId: jobId,
            entities: entities.map(e => e.get('resource_id'))
        }
    });
    return promise;
}
function rememberPrevious(entities) {
    const previous = [];
    entities.forEach((entity) => {
        previous.push({
            entity: entity.jsonHierarchy(),
            index: entity.parent.get('children').indexOf(entity.get('resource_id'))
        });
    });
    // sort previous records by index so that entities
    // are added in the correct order in undo
    previous.sort((a, b) => a.index - b.index);
    return previous;
}
/**
 * Delete specified entities
 *
 * @param entities - The entities
 * @param options.history - Whether to record a history action. Defaults to true.
 * @param options.waitSubmitted - Whether to wait till ops submitted.
 */
function deleteEntities(entities_1) {
    return __awaiter(this, arguments, undefined, function* (entities, options = {}) {
        if (options.history === undefined) {
            options.history = true;
        }
        if (!Array.isArray(entities)) {
            entities = [entities];
        }
        // make sure we are not deleting root
        entities.forEach((e) => {
            if (e === globals.entities.root) {
                throw new Error(`Cannot delete root entity ${e.get('resource_id')}`);
            }
        });
        // first only gather top level entities
        const ids = new Set();
        entities.forEach(entity => ids.add(entity.get('resource_id')));
        entities = entities.filter((entity) => {
            entity = entity.latest();
            if (!entity)
                return false;
            let parent = entity.parent;
            let parentInSelection = false;
            while (parent) {
                if (ids.has(parent.get('resource_id'))) {
                    parentInSelection = true;
                    break;
                }
                parent = parent.parent;
            }
            return !parentInSelection;
        });
        if (globals.messenger &&
            globals.jobs &&
            globals.realtime &&
            globals.realtime.scenes.current &&
            getTotalEntityCount(entities) > USE_BACKEND_LIMIT$2) {
            if (options.history) {
                const ok = yield globals.confirmFn('Deleting this many entities is not undoable. Are you sure?');
                if (ok) {
                    yield deleteInBackend(entities);
                }
                return;
            }
        }
        // remember previous entities
        let previous = null;
        if (options.history && globals.history) {
            previous = rememberPrevious(entities);
        }
        // find entity references
        const entityReferences = findEntityReferencesInComponents(globals.entities.root);
        entities.forEach((entity) => {
            globals.entities.remove(entity, entityReferences);
        });
        if (previous) {
            globals.history.add({
                name: 'delete entities',
                combine: false,
                undo: () => {
                    entities = previous.map((data, i) => {
                        return globals.entities.create(data.entity, {
                            history: false,
                            index: data.index
                        });
                    });
                    entities.forEach((entity) => {
                        updateReferences(entityReferences, entity.get('resource_id'), entity.get('resource_id'));
                    });
                    if (globals.selection) {
                        globals.selection.set(entities, {
                            history: false
                        });
                    }
                    previous = null;
                },
                redo: () => {
                    entities = entities.map(e => e.latest()).filter(e => !!e);
                    previous = rememberPrevious(entities);
                    globals.entities.delete(entities, {
                        history: false
                    });
                }
            });
        }
        if (options.waitSubmitted) {
            // wait for scene operational transforms to finish:
            // sometimes we need to execute the next operation on the backend
            // just after delete - use waitSubmitted to guarantee the order of operations
            yield new Promise((resolve) => {
                if (globals.realtime.scenes.current) {
                    globals.realtime.scenes.current.whenNothingPending(resolve);
                }
                else {
                    resolve();
                }
            });
        }
    });
}

const TIME_WAIT_ENTITIES$1 = 5000;
let evtMessenger$2 = null;
const USE_BACKEND_LIMIT$1 = 500;
/**
 * When an entity that has properties that contain references to some entities
 * within its subtree is duplicated, update the references to the corresponding entities within
 * the newly created duplicate subtree.
 *
 * @param newEntity - The new (duplicated) entity
 * @param oldEntity - The old entity
 * @param duplicatedIdsMap - Map of old id -> new id
 */
function updateDuplicatedEntityReferences(newEntity, oldEntity, duplicatedIdsMap) {
    // remap template_ent_ids for template instances
    newEntity.depthFirst((entity) => {
        const templateEntIds = entity.get('template_ent_ids');
        if (templateEntIds) {
            const newTemplateEntIds = {};
            for (const oldId in templateEntIds) {
                if (duplicatedIdsMap[oldId]) {
                    newTemplateEntIds[duplicatedIdsMap[oldId]] = templateEntIds[oldId];
                }
            }
            const history = entity.history.enabled;
            entity.history.enabled = false;
            entity.set('template_ent_ids', newTemplateEntIds);
            entity.history.enabled = history;
        }
    });
    // update entity references
    const entityReferences = findEntityReferencesInComponents(newEntity);
    for (const id in entityReferences) {
        const prevEntity = globals.entities.get(id);
        // only update references to this entity if it is in the old entity's subtree
        if (!prevEntity || (prevEntity !== oldEntity && !prevEntity.isDescendantOf(oldEntity))) {
            delete entityReferences[id];
        }
    }
    if (Object.keys(entityReferences).length) {
        for (const oldId in duplicatedIdsMap) {
            updateReferences(entityReferences, oldId, duplicatedIdsMap[oldId]);
        }
    }
}
function splitEntityNameAndNumber(entityName) {
    let name = '';
    let number = 1;
    // step from end of string character by character checking to see if we have a trailing number
    // stopping when the string we are constructing is no longer a valid number
    let numberString = '';
    let foundNumber = true;
    for (let i = entityName.length - 1; i >= 0; i--) {
        const char = entityName.charAt(i);
        if (foundNumber) {
            numberString = char + numberString;
            foundNumber = /^\d+$/.test(numberString);
            if (foundNumber) {
                number = parseInt(numberString, 10);
            }
        }
        if (foundNumber === false) {
            name = char + name;
        }
    }
    return {
        name,
        number
    };
}
function isEntityNameTaken(name, entities) {
    for (let j = 0; j < entities.length; j++) {
        const entity = entities[j];
        const entityName = entities[j].get('name');
        if (entity && entityName === name) {
            return true;
        }
    }
    return false;
}
function getUniqueNameForDuplicatedEntity(entityName, entities) {
    // if entityName === '1box23' then name === '1box' and number === 23,  if entityName === '1' then name === '' and number === 1
    const { name, number } = splitEntityNameAndNumber(entityName);
    let startIndex = number + 1;
    let newUniqueName = name + startIndex;
    while (isEntityNameTaken(newUniqueName, entities)) {
        newUniqueName = name + startIndex++;
    }
    return newUniqueName;
}
/**
 * Duplicates an entity in the scene
 *
 * @param entity - The entity
 * @param parent - The parent of the new entity
 * @param ind - The index in the parent's children array where we want to insert the new entity
 * @param duplicatedIdsMap - A guid->guid map that contains references from the source resource ids to the new resource ids
 * @param useUniqueName - Controls whether duplicated entity will have a unique name
 * @returns The new entity
 */
function duplicateEntity(entity, parent, ind, duplicatedIdsMap, useUniqueName = false) {
    const originalResourceId = entity.get('resource_id');
    const data = entity.json();
    const children = data.children;
    data.children = [];
    if (useUniqueName) {
        data.name = getUniqueNameForDuplicatedEntity(data.name, parent.children);
    }
    data.resource_id = Guid.create();
    data.parent = parent.get('resource_id');
    entity = globals.entities.create(data, {
        history: false,
        select: false,
        index: ind
    });
    duplicatedIdsMap[originalResourceId] = entity.get('resource_id');
    const templateEntIds = entity.get('template_ent_ids');
    if (templateEntIds) {
        for (const key in templateEntIds) {
            if (!globals.entities.get(key)) {
                duplicatedIdsMap[key] = Guid.create();
            }
        }
    }
    // add children too
    children.forEach((childId) => {
        duplicateEntity(globals.entities.get(childId), entity, undefined, duplicatedIdsMap);
    });
    return entity;
}
function duplicateInBackend(entities, options = {}) {
    const originalEntities = entities;
    let cancelWaitForEntities;
    let deferred = {
        resolve: null,
        reject: null
    };
    const promise = new Promise((resolve, reject) => {
        deferred.resolve = resolve;
        deferred.reject = reject;
    });
    if (!evtMessenger$2) {
        evtMessenger$2 = globals.messenger.on('entity.copy', (data) => {
            const callback = globals.jobs.finish(data.job_id);
            if (!callback)
                return;
            const result = data.multTaskResults.map((d) => d.newRootId);
            callback(result);
        });
    }
    function redo() {
        const jobId = globals.jobs.start((newEntityIds) => {
            const cancel = globals.entities.waitToExist(newEntityIds, TIME_WAIT_ENTITIES$1, (newEntities) => {
                entities = newEntities;
                if (deferred) {
                    deferred.resolve(newEntities);
                    deferred = null;
                }
            });
            cancelWaitForEntities = () => {
                cancel();
                if (deferred) {
                    deferred.reject();
                    deferred = null;
                }
            };
        });
        globals.realtime.connection.sendMessage('pipeline', {
            name: 'entities-duplicate',
            data: {
                projectId: globals.projectId,
                branchId: globals.branchId,
                sceneId: globals.realtime.scenes.current.uniqueId,
                jobId: jobId,
                entities: originalEntities.map(e => e.get('resource_id'))
            }
        });
    }
    redo();
    // add history
    if (options.history && globals.history) {
        globals.history.add({
            name: 'duplicate entities',
            combine: false,
            redo: redo,
            undo: () => {
                if (cancelWaitForEntities) {
                    cancelWaitForEntities();
                    cancelWaitForEntities = null;
                }
                globals.entities.delete(entities, {
                    history: false
                });
            }
        });
    }
    return promise;
}
/**
 * Duplicates entities under the same parent
 *
 * @param entities - The entities
 * @param options - Options
 * @param options.history - Whether to record a history action. Defaults to true.
 * @param options.select - Whether to select the new entities. Defaults to false.
 * @param options.rename - Whether to rename the duplicated entities. Defaults to false.
 * @returns The duplicated entities
 */
function duplicateEntities(entities_1) {
    return __awaiter(this, arguments, undefined, function* (entities, options = {}) {
        // copy entities for safety in undo / redo
        entities = entities.slice();
        if (options.history === undefined) {
            options.history = true;
        }
        const root = globals.entities.root;
        // make sure we are not duplicating the root
        if (entities.includes(root)) {
            console.error('Cannot duplicate the root Entity');
            return;
        }
        // build index
        const records = {};
        entities.forEach((entity) => {
            const id = entity.get('resource_id');
            records[id] = {
                entity: entity,
                index: entity.parent.children.indexOf(entity)
            };
        });
        // only duplicate top level parents
        let i = entities.length;
        while (i--) {
            let parent = entities[i].parent;
            while (parent && parent !== root) {
                if (records[parent.get('resource_id')]) {
                    delete records[entities[i].get('resource_id')];
                    entities.splice(i, 1);
                    break;
                }
                parent = parent.parent;
            }
        }
        // original order is a dictionary that contains
        // resource_id -> [index_before_sort, index_after_sort]
        // for each entity. It used to return our results in the
        // original order passed by the user because when we duplicate
        // we change the order of the entities to their order within their parent
        const originalOrder = {};
        entities.forEach((e, i) => {
            originalOrder[e.get('resource_id')] = [i, null];
        });
        // sort by order within parent
        entities.sort((a, b) => {
            return records[b.get('resource_id')].index - records[a.get('resource_id')].index;
        });
        entities.forEach((e, i) => {
            originalOrder[e.get('resource_id')][1] = i;
        });
        let newEntities = [];
        // If we have a lot of entities duplicate in the backend
        if (globals.messenger && globals.jobs && entities.length > USE_BACKEND_LIMIT$1) {
            newEntities = yield duplicateInBackend(entities, options);
        }
        else {
            // remember previous selection
            let previousSelection;
            if (options.history && globals.history && globals.selection && options.select) {
                previousSelection = globals.selection.items;
            }
            // duplicate
            entities.forEach((entity) => {
                const duplicatedIdsMap = {};
                const newEntity = duplicateEntity(entity, entity.parent, records[entity.get('resource_id')].index + 1, duplicatedIdsMap, options.rename);
                updateDuplicatedEntityReferences(newEntity, entity, duplicatedIdsMap);
                newEntities.push(newEntity);
            });
            if (options.history && globals.history) {
                let previous = null;
                globals.history.add({
                    name: 'duplicate entities',
                    combine: false,
                    undo: () => {
                        // make sure we get the entities that are in the scene
                        newEntities = newEntities.map((entity) => {
                            return entity.latest();
                        });
                        // remember previous entities
                        previous = {};
                        newEntities.forEach((entity) => {
                            entity.depthFirst((e) => {
                                previous[e.get('resource_id')] = e.json();
                            });
                        });
                        globals.entities.delete(newEntities, {
                            history: false
                        });
                        if (previousSelection) {
                            globals.selection.set(previousSelection, {
                                history: false
                            });
                        }
                    },
                    redo: () => {
                        function recreateEntityData(data) {
                            data = Object.assign({}, data);
                            data.children = data.children.map(id => recreateEntityData(previous[id]));
                            return data;
                        }
                        newEntities = newEntities.map((entity, index) => {
                            const data = recreateEntityData(previous[entity.get('resource_id')]);
                            entity = globals.entities.create(data, {
                                history: false,
                                select: false,
                                index: records[entities[index].get('resource_id')].index + 1
                            });
                            return entity;
                        });
                        if (options.select && globals.selection) {
                            globals.selection.set(newEntities, { history: false });
                        }
                        previous = null;
                    }
                });
            }
        }
        // select duplicated
        if (options.select && globals.selection) {
            globals.selection.set(newEntities, {
                history: false
            });
        }
        // return duplicated entities in their original order
        const result = new Array(newEntities.length);
        for (const id in originalOrder) {
            const pair = originalOrder[id];
            result[pair[0]] = newEntities[pair[1]];
        }
        return result;
    });
}

const USE_BACKEND_LIMIT = 500;
const TIME_WAIT_ENTITIES = 5000;
const REGEX_CONTAINS_STAR = /\.\*\./;
let ASSET_PATHS;
let evtMessenger$1;
/**
 * Try to find an assetId in this project that
 * corresponds to the specified assetId that may come from
 * a different project.
 *
 * @param assetId - The asset id we are trying to remap
 * @param assetsIndex - The assets index stored in localStorage that contains paths of assets
 * @returns The asset id in this project
 */
function remapAsset(assetId, assetsIndex) {
    if (!assetId)
        return null;
    // return the old asset id if not found
    let result = parseInt(assetId, 10);
    const assetData = assetsIndex[assetId];
    if (!assetData) {
        return result;
    }
    const len = assetData.path.length;
    const name = assetData.path[len - 1];
    const type = assetData.type;
    const pathToId = [];
    const assets = globals.assets.list();
    const assetLen = assets.length;
    // change path names to folder ids
    for (let i = 0; i < len - 1; i++) {
        let folder = null;
        for (let j = 0; j < assetLen; j++) {
            const asset = assets[j];
            if (asset.get('name') === assetData.path[i] && asset.get('type') === 'folder') {
                folder = asset;
                break;
            }
        }
        if (!folder) {
            return result;
        }
        pathToId.push(parseInt(folder.get('id'), 10));
    }
    const pathToIdLen = pathToId.length;
    // search for asset of same name, type
    // and path as original
    for (let i = 0; i < assetLen; i++) {
        const asset = assets[i];
        if (asset.get('name') === name &&
            asset.get('type') === type &&
            !asset.get('source')) {
            const path = asset.get('path');
            const pathLen = path && path.length;
            if (path && pathLen === pathToIdLen) {
                let pathsEqual = true;
                for (let j = 0; j < pathLen; j++) {
                    if (path[j] !== pathToId[j]) {
                        pathsEqual = false;
                        break;
                    }
                }
                if (!pathsEqual) {
                    continue;
                }
            }
            result = parseInt(asset.get('id'), 10);
            break;
        }
    }
    return result;
}
function mapValue(value, mapping, sameProject) {
    if (sameProject) {
        return mapping[value] || value;
    }
    return mapping[value] || null;
}
function remapField(entity, path, mapping, sameProject) {
    if (REGEX_CONTAINS_STAR.test(path)) {
        const parts = path.split('.*.');
        if (!entity.has(parts[0]))
            return;
        const obj = entity.get(parts[0]);
        if (!obj)
            return;
        for (const key in obj) {
            const fullKey = `${parts[0]}.${key}.${parts[1]}`;
            if (!entity.has(fullKey))
                continue;
            const value = entity.get(fullKey);
            if (!value)
                continue;
            if (value instanceof Array) {
                for (let j = 0; j < value.length; j++) {
                    value[j] = mapValue(value[j], mapping, sameProject);
                }
                entity.set(fullKey, value);
            }
            else {
                entity.set(fullKey, mapValue(value, mapping, sameProject));
            }
        }
    }
    else if (entity.has(path)) {
        const value = entity.get(path);
        if (!value)
            return;
        if (value instanceof Array) {
            for (let j = 0; j < value.length; j++) {
                value[j] = mapValue(value[j], mapping, sameProject);
            }
            entity.set(path, value);
        }
        else {
            entity.set(path, mapValue(value, mapping, sameProject));
        }
    }
}
function remapScriptAttribute(assetAttr, componentAttr, entity, path, entityMapping, assetMapping, sameProject) {
    if (assetAttr.type === 'asset') {
        if (sameProject)
            return;
        // remap asset ids
        if (assetAttr.array) {
            for (let i = 0; i < componentAttr.length; i++) {
                entity.set(`${path}.${i}`, mapValue(componentAttr[i], assetMapping, sameProject));
            }
        }
        else {
            entity.set(path, mapValue(componentAttr, assetMapping, sameProject));
        }
    }
    else if (assetAttr.type === 'entity') {
        // try to remap entities
        if (assetAttr.array) {
            for (let i = 0; i < componentAttr.length; i++) {
                if (componentAttr[i]) {
                    entity.set(`${path}.${i}`, mapValue(componentAttr[i], entityMapping, sameProject));
                }
            }
        }
        else {
            entity.set(path, mapValue(componentAttr, entityMapping, sameProject));
        }
    }
}
/**
 * Remaps the resource ids of the entities and their entity references in localStorage
 * with new resource ids, also remaps asset ids.
 *
 * @param entity - The entity we are remapping
 * @param parent - The parent of the pasted entity
 * @param data - The data in localStorage
 * @param entityMapping - An index that maps old resource ids to new resource ids
 * @param assetMapping - An index that maps old asset ids to new asset ids
 */
function remapEntitiesAndAssets(entity, parent, data, entityMapping, assetMapping) {
    const sameProject = (data.project === globals.projectId);
    const resourceId = entity.get('resource_id');
    const newResourceId = entityMapping[resourceId];
    entity.set('resource_id', newResourceId);
    // set new resource id for parent
    const parentId = entity.get('parent');
    if (parentId) {
        entity.set('parent', entityMapping[parentId]);
    }
    else {
        entity.set('parent', parent.get('resource_id'));
    }
    // if this is a template instance remap template_ent_ids
    const templateEntIds = entity.get('template_ent_ids');
    if (templateEntIds) {
        const newTemplateEntIds = {};
        for (const oldId in templateEntIds) {
            if (entityMapping[oldId]) {
                newTemplateEntIds[entityMapping[oldId]] = templateEntIds[oldId];
            }
        }
        entity.set('template_ent_ids', newTemplateEntIds);
    }
    // set children to empty array because these
    // are going to get added later on
    entity.set('children', []);
    // remap assets and entities
    if (!sameProject) {
        if (!ASSET_PATHS) {
            // get asset paths for all components
            ASSET_PATHS = [];
            globals.schema.components.list().forEach((component) => {
                const paths = globals.schema.components.getFieldsOfType(component, 'asset');
                paths.forEach((path) => {
                    ASSET_PATHS.push(`components.${component}.${path}`);
                });
            });
        }
        for (let i = 0; i < ASSET_PATHS.length; i++) {
            const path = ASSET_PATHS[i];
            remapField(entity, path, assetMapping, sameProject);
        }
    }
    // remap script asset attributes
    if (entity.has('components.script.scripts')) {
        if (entity.has('components.script')) {
            // remove script component if legacy scripts flag is different between the two projects
            if (globals.hasLegacyScripts !== data.legacy_scripts) {
                entity.unset('components.script');
            }
            else {
                const scripts = entity.get('components.script.scripts');
                // legacy scripts
                if (globals.hasLegacyScripts) {
                    for (let i = 0; i < scripts.length; i++) {
                        const script = scripts[i];
                        if (!script.attributes)
                            continue;
                        for (const name in script.attributes) {
                            const attr = script.attributes[name];
                            if (!attr)
                                continue;
                            if (attr.type === 'asset' && data.project !== globals.projectId) {
                                if (attr.value) {
                                    if (attr.value instanceof Array) {
                                        for (let j = 0; j < attr.value.length; j++) {
                                            entity.set(`components.script.scripts.${i}.attributes.${name}.value.${j}`, mapValue(attr.value[j], assetMapping, sameProject));
                                        }
                                    }
                                    else {
                                        entity.set(`components.script.scripts.${i}.attributes.${name}.value`, mapValue(attr.value, assetMapping, sameProject));
                                    }
                                }
                                if (attr.defaultValue) {
                                    if (attr.defaultValue instanceof Array) {
                                        for (let j = 0; j < attr.defaultValue.length; j++) {
                                            entity.set(`components.script.scripts.${i}.attributes.${name}.defaultValue.${j}`, mapValue(attr.value[j], assetMapping, sameProject));
                                        }
                                    }
                                    else {
                                        entity.set(`components.script.scripts.${i}.attributes.${name}.defaultValue`, mapValue(attr.value, assetMapping, sameProject));
                                    }
                                }
                            }
                            else if (attr.type === 'entity') {
                                if (entityMapping[attr.value]) {
                                    entity.set(`components.script.scripts.${i}.attributes.${name}.value`, mapValue(attr.value, entityMapping, sameProject));
                                }
                                if (entityMapping[attr.defaultValue]) {
                                    entity.set(`components.script.scripts.${i}.attributes.${name}.defaultValue`, mapValue(attr.defaultValue, entityMapping, sameProject));
                                }
                            }
                        }
                    }
                }
                else {
                    // scripts 2.0
                    if (scripts) {
                        for (const script in scripts) {
                            const asset = globals.assets.getAssetForScript(script);
                            if (!asset)
                                continue;
                            const attrs = scripts[script].attributes;
                            if (!attrs)
                                continue;
                            for (const key in attrs) {
                                const attrData = asset.get(`data.scripts.${script}.attributes.${key}`);
                                if (attrData) {
                                    // handle json script attributes
                                    if (attrData.type === 'json' && Array.isArray(attrData.schema)) {
                                        // json attribute array
                                        if (attrData.array) {
                                            for (let j = 0; j < attrs[key].length; j++) {
                                                if (!attrs[key][j])
                                                    continue;
                                                for (let k = 0; k < attrData.schema.length; k++) {
                                                    const field = attrData.schema[k];
                                                    if (attrs[key][j][field.name]) {
                                                        remapScriptAttribute(field, attrs[key][j][field.name], entity, `components.script.scripts.${script}.attributes.${key}.${j}.${field.name}`, entityMapping, assetMapping, sameProject);
                                                    }
                                                }
                                            }
                                        }
                                        else {
                                            // regular json attribute
                                            for (let k = 0; k < attrData.schema.length; k++) {
                                                const field = attrData.schema[k];
                                                if (attrs[key][field.name]) {
                                                    remapScriptAttribute(field, attrs[key][field.name], entity, `components.script.scripts.${script}.attributes.${key}.${field.name}`, entityMapping, assetMapping, sameProject);
                                                }
                                            }
                                        }
                                    }
                                    else {
                                        // non json attribute
                                        remapScriptAttribute(attrData, attrs[key], entity, `components.script.scripts.${script}.attributes.${key}`, entityMapping, assetMapping, sameProject);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    // remap entity references in components
    const components = entity.get('components');
    Object.keys(components).forEach((componentName) => {
        const entityFields = globals.schema.components.getFieldsOfType(componentName, 'entity');
        entityFields.forEach((fieldName) => {
            const path = `components.${componentName}.${fieldName}`;
            remapField(entity, path, entityMapping, sameProject);
        });
    });
}
/**
 * Paste entities in backend
 *
 * @param data - The clipboard data
 * @param parent - The parent entity
 * @param options - The paste options
 * @returns A promise that resolves to an array of pasted entities
 */
function pasteInBackend(data, parent, options) {
    let entities;
    let cancelWaitForEntities;
    let deferred = {
        resolve: null,
        reject: null
    };
    const promise = new Promise((resolve, reject) => {
        deferred.resolve = resolve;
        deferred.reject = reject;
    });
    if (!evtMessenger$1) {
        evtMessenger$1 = globals.messenger.on('entity.copy', (data) => {
            const callback = globals.jobs.finish(data.job_id);
            if (!callback)
                return;
            const result = data.multTaskResults.map((d) => d.newRootId);
            callback(result);
        });
    }
    function redo() {
        parent = parent.latest();
        if (!parent)
            return;
        const jobId = globals.jobs.start((newEntityIds) => {
            const cancel = globals.entities.waitToExist(newEntityIds, TIME_WAIT_ENTITIES, (newEntities) => {
                entities = newEntities;
                globals.selection.set(newEntities, { history: false });
                if (deferred) {
                    deferred.resolve(newEntities);
                    deferred = null;
                }
            });
            cancelWaitForEntities = () => {
                cancel();
                if (deferred) {
                    deferred.reject();
                    deferred = null;
                }
            };
        });
        const children = parent.get('children');
        const taskData = {
            projectId: globals.projectId,
            branchId: data.branch || globals.branchId,
            parentId: parent.get('resource_id'),
            sceneId: data.scene || globals.realtime.scenes.current.uniqueId,
            jobId: jobId,
            children: children,
            childIndex: children.length,
            entities: Object.keys(data.hierarchy)
                .filter((id) => {
                return data.hierarchy[id].parent === null;
            })
                .map((id) => {
                return {
                    id: id
                };
            })
        };
        if (data.scene && data.scene !== globals.realtime.scenes.current.uniqueId) {
            taskData.newSceneId = globals.realtime.scenes.current.uniqueId;
            taskData.newBranchId = globals.branchId;
        }
        globals.realtime.connection.sendMessage('pipeline', {
            name: 'entities-copy',
            data: taskData
        });
    }
    redo();
    if (options.history && globals.history) {
        globals.history.add({
            name: 'paste entities',
            redo: redo,
            combine: false,
            undo: () => {
                if (cancelWaitForEntities) {
                    cancelWaitForEntities();
                    cancelWaitForEntities = null;
                }
                if (entities && entities.length) {
                    globals.entities.delete(entities, { history: false });
                }
                entities = null;
            }
        });
    }
    return promise;
}
/**
 * Paste entities copied into clipboard
 * under the specified parent.
 *
 * @param parent - The parent entity
 * @param options - Options
 * @param options.history - Whether to record a history action. Defaults to true.
 * @returns A promise that resolves to an array of new entities
 */
function pasteEntities(parent_1) {
    return __awaiter(this, arguments, undefined, function* (parent, options = {}) {
        if (options.history === undefined) {
            options.history = true;
        }
        // parse data from local storage
        const data = globals.clipboard.value;
        if (!data || data.type !== 'entity')
            return;
        // paste on root if no parent specified
        if (!parent) {
            parent = globals.entities.root;
        }
        if (data.project === globals.projectId &&
            (data.branch !== globals.branchId ||
                Object.keys(data.hierarchy).length > USE_BACKEND_LIMIT)) {
            // TODO support pasting in different projects
            const result = yield pasteInBackend(data, parent, options);
            return result;
        }
        // remap assets
        const remappedAssets = {};
        if (data.assets) {
            for (const key in data.assets) {
                remappedAssets[key] = remapAsset(key, data.assets);
            }
        }
        // change resource ids
        const mapping = {};
        for (const guid in data.hierarchy) {
            mapping[guid] = Guid.create();
        }
        for (const guid in data.hierarchy) {
            // create new guids for any missing entities in template_ent_ids
            if (data.hierarchy[guid].template_ent_ids) {
                for (const key in data.hierarchy[guid].template_ent_ids) {
                    if (!mapping[key]) {
                        mapping[key] = Guid.create();
                    }
                }
            }
        }
        // add all entities with different resource ids
        const selectedEntities = [];
        const newEntities = {};
        for (const resourceId in data.hierarchy) {
            // create new observer for entity
            const observer = new Observer(data.hierarchy[resourceId]);
            // select the entity if its parent is not selected
            const select = !data.hierarchy[observer.get('parent')];
            // change resource ids
            remapEntitiesAndAssets(observer, parent, data, mapping, remappedAssets);
            const json = observer.json();
            newEntities[json.resource_id] = json;
            const entity = globals.entities.create(json, {
                history: false,
                select: false
            });
            if (select) {
                selectedEntities.push(entity);
            }
        }
        if (selectedEntities.length) {
            globals.selection.set(selectedEntities, { history: false });
        }
        // add history
        if (options.history && globals.history) {
            let deletedHierarchy = null;
            let previousSelection = null;
            globals.history.add({
                name: 'paste entities',
                combine: false,
                undo: () => {
                    parent = parent.latest();
                    if (!parent)
                        return;
                    deletedHierarchy = [];
                    previousSelection = globals.selection.items;
                    // get latest data for each new entity
                    for (const id in newEntities) {
                        const e = globals.entities.get(id);
                        if (!e)
                            continue;
                        // put top level entities in deletedHierarchy
                        if (!(e.parent.get('resource_id') in newEntities)) {
                            deletedHierarchy.push(e.jsonHierarchy());
                        }
                    }
                    if (deletedHierarchy.length) {
                        globals.entities.delete(deletedHierarchy.map(data => globals.entities.get(data.resource_id)), {
                            history: false
                        });
                    }
                },
                redo: () => {
                    parent = parent.latest();
                    if (!parent)
                        return;
                    // re-insert delete hierarchy
                    for (const entity of deletedHierarchy) {
                        globals.entities.create(entity, { history: false, select: false });
                    }
                    deletedHierarchy = null;
                    // restore selection
                    previousSelection = previousSelection
                        .map(item => item.latest())
                        .filter(item => !!item);
                    globals.selection.set(previousSelection, { history: false });
                    previousSelection = null;
                }
            });
        }
        return selectedEntities;
    });
}

/**
 * Reparents entities under new parent.
 *
 * @param data - The reparenting data
 * @param options.preserveTransform - Whether to preserve the transform of the entities
 * @param options.history - Whether to record history. Defaults to true
 */
function reparentEntities(data, options = {}) {
    if (options.history === undefined) {
        options.history = true;
    }
    const records = data.map((entry) => {
        const parentOld = entry.entity.parent;
        const indexOld = parentOld.get('children').indexOf(entry.entity.get('resource_id'));
        const record = {
            entity: entry.entity,
            resourceId: entry.entity.get('resource_id'),
            parentOld: parentOld,
            indOld: indexOld,
            parent: entry.parent,
            indNew: entry.index !== undefined && entry.index !== null ? entry.index : entry.parent.get('children').length
        };
        if (options.preserveTransform) {
            record.position = record.entity.viewportEntity.getPosition().clone();
            record.rotation = record.entity.viewportEntity.getRotation().clone();
        }
        return record;
    });
    // sort records by a field
    const sortRecords = (by) => {
        records.sort((a, b) => {
            return a[by] - b[by];
        });
    };
    const isValidRecord = (entity, parentOld, parent) => {
        const resourceId = entity.get('resource_id');
        if (parentOld.get('children').indexOf(resourceId) === -1 || (parent.get('children').indexOf(resourceId) !== -1 && parent !== parentOld)) {
            return false;
        }
        // check if not reparenting to own child
        let deny = false;
        let checkParent = parent.parent;
        while (checkParent) {
            if (checkParent === entity) {
                deny = true;
                checkParent = null;
                break;
            }
            checkParent = checkParent.parent;
        }
        return !deny;
    };
    const doReparent = (entity, parent, indNew, position, rotation) => {
        const history = {
            parent: parent.history.enabled,
            entity: entity.history.enabled
        };
        parent.history.enabled = false;
        if (indNew !== -1 && indNew <= parent.get('children').length) {
            parent.insert('children', entity.get('resource_id'), indNew);
        }
        else {
            parent.insert('children', entity.get('resource_id'));
        }
        parent.history.enabled = history.parent;
        // BUG TRACKING: missing children
        if (!globals.entities.get(entity.get('resource_id'))) {
            console.error(`BUG TRACKING: reparenting missing child guid ${entity.get('resource_id')} to parent ${parent.get('resource_id')}`);
        }
        entity.history.enabled = false;
        entity.set('parent', parent.get('resource_id'));
        if (options.preserveTransform && position && entity.viewportEntity) {
            entity.viewportEntity.setPosition(position);
            entity.viewportEntity.setRotation(rotation);
            const localPosition = entity.viewportEntity.getLocalPosition();
            const localRotation = entity.viewportEntity.getLocalEulerAngles();
            entity.set('position', [localPosition.x, localPosition.y, localPosition.z]);
            entity.set('rotation', [localRotation.x, localRotation.y, localRotation.z]);
        }
        entity.history.enabled = history.entity;
    };
    const redo = () => {
        sortRecords('indNew');
        const latest = (record) => {
            const entity = record.entity.latest();
            if (!entity)
                return;
            const parent = record.parent.latest();
            const parentOld = entity.parent;
            if (!parentOld || !parent)
                return;
            return { entity, parent, parentOld };
        };
        const validRecords = [];
        records.forEach((record, i) => {
            const data = latest(record);
            if (!data)
                return;
            if (isValidRecord(data.entity, data.parentOld, data.parent)) {
                validRecords.push(record);
            }
        });
        if (!validRecords.length)
            return false;
        // remember selection
        let selection;
        let selectionHistory;
        if (globals.selection) {
            selection = globals.selection.items;
            selectionHistory = globals.selection.history.enabled;
            globals.selection.history.enabled = false;
        }
        // remove all children from old parents
        validRecords.forEach((record) => {
            const parentOld = record.entity.latest().parent;
            const history = parentOld.history.enabled;
            parentOld.history.enabled = false;
            parentOld.removeValue('children', record.resourceId);
            parentOld.history.enabled = history;
        });
        // reparent
        validRecords.forEach((record) => {
            const data = latest(record);
            doReparent(data.entity, data.parent, record.indNew, record.position, record.rotation);
        });
        // restore selection
        if (selection) {
            globals.selection.set(selection, { history: false });
            globals.selection.history.enabled = selectionHistory;
        }
        return true;
    };
    const dirty = redo();
    if (dirty && options.history && globals.history) {
        const undo = () => {
            sortRecords('indOld');
            const latest = (record) => {
                const entity = record.entity.latest();
                if (!entity)
                    return;
                const parent = entity.parent;
                if (!parent)
                    return;
                const parentOld = record.parentOld.latest();
                if (!parentOld)
                    return;
                return { entity, parent, parentOld };
            };
            const validRecords = [];
            records.forEach((record) => {
                const data = latest(record);
                if (!data)
                    return;
                if (isValidRecord(data.entity, data.parent, data.parentOld)) {
                    validRecords.push(record);
                }
            });
            if (!validRecords.length)
                return;
            // remember selection
            let selection;
            let selectionHistory;
            if (globals.selection) {
                selection = globals.selection.items;
                selectionHistory = globals.selection.history.enabled;
                globals.selection.history.enabled = false;
            }
            // remove all children from parents
            validRecords.forEach((record) => {
                const parent = record.entity.latest().parent;
                const history = parent.history.enabled;
                parent.history.enabled = false;
                parent.removeValue('children', record.resourceId);
                parent.history.enabled = history;
            });
            // reparent
            validRecords.forEach((record) => {
                const data = latest(record);
                doReparent(data.entity, data.parentOld, record.indOld, record.position, record.rotation);
            });
            // restore selection
            if (selection) {
                globals.selection.set(selection, { history: false });
                globals.selection.history.enabled = selectionHistory;
            }
        };
        globals.history.add({
            name: 'reparent entities',
            combine: false,
            undo: undo,
            redo: redo
        });
    }
}

function addScript(entities_1, scriptName_1) {
    return __awaiter(this, arguments, undefined, function* (entities, scriptName, options = {}) {
        entities = entities.filter(e => !e.has(`components.script.scripts.${scriptName}`));
        if (!entities.length)
            return;
        const addedComponentsTo = new Set();
        entities.forEach((entity) => {
            const historyEnabled = entity.history.enabled;
            entity.history.enabled = false;
            // add script component
            if (!entity.has('components.script')) {
                entity.addComponent('script');
                addedComponentsTo.add(entity.get('resource_id'));
            }
            // add script data
            entity.set(`components.script.scripts.${scriptName}`, {
                enabled: options.enabled !== undefined ? options.enabled : true,
                attributes: options.attributes !== undefined ? options.attributes : {}
            });
            // add to script order
            entity.insert('components.script.order', scriptName, options.index);
            entity.history.enabled = historyEnabled;
        });
        // wait for scene script ops to finish before starting backend
        // for default attributes values
        yield new Promise((resolve) => {
            if (globals.realtime.scenes.current) {
                globals.realtime.scenes.current.whenNothingPending(resolve);
            }
            else {
                resolve();
            }
        });
        // setup promise
        const deferred = {
            resolve: null,
            reject: null
        };
        const promise = new Promise((resolve, reject) => {
            deferred.resolve = resolve;
            deferred.reject = reject;
        });
        // start job
        const jobId = globals.jobs.start((result) => {
            if (result.status === 'success') {
                deferred.resolve();
            }
            else {
                deferred.reject();
            }
        });
        // send backend message to set script attribute defaults
        globals.realtime.connection.sendMessage('pipeline', {
            name: 'script-attributes',
            data: {
                script_task_type: 'set_entity_defaults',
                job_id: jobId,
                project_id: globals.projectId,
                branch_id: globals.branchId,
                script_added_to_ent: scriptName,
                dst_scene_id: globals.realtime.scenes.current.id,
                dst_ent_ids: entities.map(e => e.get('resource_id'))
            }
        });
        // wait for messenger response
        globals.messenger.once(`scriptAttrsFinished:${jobId}`, (msg) => {
            globals.jobs.finish(jobId)(msg);
        });
        // add history action
        if (globals.history && (options.history || options.history === undefined)) {
            // copy entities for undo / redo
            entities = entities.slice();
            globals.history.add({
                name: `entities.components.script.scripts.${scriptName}`,
                combine: false,
                redo: () => {
                    const newOptions = Object.assign({}, options);
                    newOptions.history = false;
                    entities = entities.map(e => e.latest()).filter(e => !!e);
                    globals.entities.addScript(entities, scriptName, newOptions).catch((err) => {
                        console.error(err);
                    });
                },
                undo: () => {
                    entities = entities.map(e => e.latest()).filter(e => !!e);
                    globals.entities.removeScript(entities, scriptName, {
                        history: false
                    });
                    entities.forEach((entity) => {
                        if (addedComponentsTo.has(entity.get('resource_id'))) {
                            const historyEnabled = entity.history.enabled;
                            entity.history.enabled = false;
                            entity.removeComponent('script');
                            entity.history.enabled = historyEnabled;
                        }
                    });
                }
            });
        }
        yield promise;
    });
}
function removeScript(entities, scriptName, options = {}) {
    const history = (options.history || options.history === undefined);
    let prev = {};
    if (history) {
        entities.forEach((entity) => {
            let prevIndex;
            const order = entity.get('components.script.order');
            if (order) {
                prevIndex = order.indexOf(scriptName);
            }
            const prevScript = entity.get(`components.script.scripts.${scriptName}`);
            prev[entity.get('resource_id')] = { prevIndex, prevScript };
        });
        // copy entities for undo / redo
        entities = entities.slice();
    }
    entities.forEach((entity) => {
        const historyEnabled = entity.history.enabled;
        entity.history.enabled = false;
        entity.unset(`components.script.scripts.${scriptName}`);
        entity.removeValue('components.script.order', scriptName);
        entity.history.enabled = historyEnabled;
    });
    if (globals.history && history) {
        globals.history.add({
            name: `entities.components.script.scripts.${scriptName}`,
            combine: false,
            undo: () => {
                entities = entities.map(e => e.latest()).filter(e => e && e.has('components.script') && prev[e.get('resource_id')]);
                entities.forEach((entity) => {
                    const prevData = prev[entity.get('resource_id')];
                    entity.addScript(scriptName, Object.assign({ history: false, index: prevData.prevIndex }, prevData.prevScript)).catch((err) => {
                        console.error(err);
                    });
                });
            },
            redo: () => {
                prev = {};
                entities = entities.map(e => e.latest()).filter(e => !!e);
                if (!entities.length)
                    return;
                entities.forEach((entity) => {
                    let prevIndex;
                    const order = entity.get('components.script.order');
                    if (order) {
                        prevIndex = order.indexOf(scriptName);
                    }
                    const prevScript = entity.get(`components.script.scripts.${scriptName}`);
                    prev[entity.get('resource_id')] = { prevIndex, prevScript };
                });
                const newOptions = Object.assign({}, options);
                newOptions.history = false;
                globals.entities.removeScript(entities, scriptName, newOptions);
            }
        });
    }
}

/**
 * Waits for specified entity ids to be added to the scene.
 * Once they are the callback is called with the entities as its argument.
 *
 * @param entityIds - The ids of the entities to wait for
 * @param timeoutMs - Number of ms to wait before stopping to wait
 * @param callback - The callback to call when all entities have been added.
 * The signature is (Entity[]) => void.
 * @returns Returns a cancel function which can be called to cancel calling the
 * callback when the entities are added.
 */
function wait(entityIds, timeoutMs, callback) {
    const index = {};
    let earlyOut = true;
    entityIds.forEach((id) => {
        index[id] = globals.entities.get(id);
        if (!index[id]) {
            earlyOut = false;
        }
    });
    if (earlyOut) {
        callback(entityIds.map(id => index[id]));
        return () => { };
    }
    let timeout;
    const checkAllThere = () => {
        return entityIds.filter(id => !index[id]).length === 0;
    };
    let evtAdd;
    const cancel = () => {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
        if (evtAdd) {
            evtAdd.unbind();
            evtAdd = null;
        }
    };
    const onAdd = (entity) => {
        const id = entity.get('resource_id');
        if (!index.hasOwnProperty(id))
            return;
        index[id] = entity;
        if (checkAllThere()) {
            cancel();
            callback(entityIds.map(id => index[id]));
        }
    };
    evtAdd = globals.entities.on('add', onAdd);
    timeout = setTimeout(cancel, timeoutMs);
    return cancel;
}

/**
 * The entities editor API
 */
class Entities extends Events {
    /**
     * Creates new API instance
     *
     * @category Internal
     */
    constructor() {
        super();
        this._root = null;
        this._entities = new ObserverList({
            index: 'resource_id'
        });
    }
    /**
     * Gets entity by resource id
     *
     * @param id - The entity's resource id
     * @returns The entity
     * @example
     * ```javascript
     * const entity = editor.entities.get(resourceId);
     * ```
     */
    get(id) {
        const e = this._entities.get(id);
        return e ? e.apiEntity : null;
    }
    /**
     * Returns array of all entities
     *
     * @returns The entities
     * @example
     * ```javascript
     * const entities = editor.entities.list();
     * console.log(entities.length);
     * ```
     */
    list() {
        return this._entities.array().map((e) => e.apiEntity);
    }
    /**
     * Adds entity to list
     *
     * @category Internal
     * @param entity - The entity
     */
    add(entity) {
        const id = entity.get('resource_id');
        if (this.get(id)) {
            console.error(`Cannot add duplicate Entity ${id}`);
            return;
        }
        this._entities.add(entity.observer);
        if (!entity.get('parent')) {
            if (this._root) {
                console.error(`More than one root entities in Scene. Current root is Entity "${this._root.get('name')}" [${this._root.get('resource_id')}] but Entity "${entity.get('name')}" [${id}] also has a null parent`);
            }
            else {
                this._root = entity;
            }
        }
        entity._initializeHistory();
        this.emit('add', entity, this._root === entity);
    }
    /**
     * Called when an entity is added from the server
     *
     * @category Internal
     * @param entityData - The entity data
     */
    serverAdd(entityData) {
        const entity = new Entity(entityData);
        entity.set('parent', entityData.parent);
        entity.set('children', entityData.children);
        this.add(entity);
    }
    /**
     * Removes entity from the list
     *
     * @category Internal
     * @param entity - The entity
     * @param entityReferences - A map of entity references to nullify
     * when this entity is removed
     */
    remove(entity, entityReferences = null) {
        if (entityReferences) {
            updateReferences(entityReferences, entity.get('resource_id'), null);
        }
        // remove children first
        entity.children.forEach((child) => {
            this.remove(child, entityReferences);
        });
        // remove from selection
        if (globals.selection && globals.selection.has(entity)) {
            globals.selection.remove(entity, {
                history: false
            });
        }
        // remove from parent
        if (entity.parent) {
            entity.parent.removeChild(entity);
        }
        // remove from observer list
        try {
            this._entities.remove(entity.observer);
            entity.observer.destroy();
        }
        catch (err) {
            console.error(err);
        }
        // reset root
        if (this._root === entity) {
            this._root = null;
        }
        // sharedb
        if (globals.realtime && globals.realtime.scenes.current) {
            globals.realtime.scenes.current.removeEntity(entity);
        }
        this.emit('remove', entity);
    }
    /**
     * Called when an entity is removed from the server
     *
     * @category Internal
     * @param entity - The entity
     */
    serverRemove(entity) {
        if (globals.selection && globals.selection.has(entity)) {
            globals.selection.remove(entity, {
                history: false
            });
        }
        this._entities.remove(entity.observer);
        entity.observer.destroy();
        if (this._root === entity) {
            this._root = null;
        }
        this.emit('remove', entity);
    }
    /**
     * Removes all entities from the list
     *
     * @category Internal
     */
    clear() {
        this._root = null;
        const entities = this.list();
        this._entities.clear();
        if (globals.selection) {
            if (globals.selection.items[0] instanceof Entity) {
                const history = globals.selection.history.enabled;
                globals.selection.history.enabled = false;
                globals.selection.clear();
                globals.selection.history.enabled = history;
            }
        }
        entities.forEach((entity) => {
            entity.observer.destroy();
            this.emit('remove', entity);
        });
        this.emit('clear');
    }
    /**
     * Creates new entity and adds it to the hierarchy
     *
     * @param data - Initial data for the entity
     * @param options.index - The child index that this entity will have under its parent.
     * @param options.history - Whether to record a history action. Defaults to true.
     * @param options.select - Whether to select new Entity. Defaults to false.
     * @returns The new entity
     *
     * @example
     * ```javascript
     * const root = editor.entities.create({
     *     name: 'parent',
     * });
     *
     * const child = editor.entities.create({
     *     name: 'child',
     *     parent: root,
     * });
     *```
     */
    create(data = null, options = {}) {
        return createEntity(data, options);
    }
    /**
     * Delete specified entities
     *
     * @param entities - The entities
     * @param options.history - Whether to record a history action. Defaults to true.
     *
     * @example
     * ```javascript
     * await editor.entities.delete([entity1, entity2]);
     * ```
     */
    delete(entities_1) {
        return __awaiter(this, arguments, undefined, function* (entities, options = {}) {
            yield deleteEntities(entities, options);
        });
    }
    /**
     * Reparents entities under new parent.
     *
     * @param data - The reparenting data
     * @param options.preserveTransform - Whether to preserve the transform of the entities. Defaults to false.
     * @param options.history - Whether to record history. Defaults to true
     * @example
     * ```javascript
     * const child = editor.entities.create();
     * const parent = editor.entities.create();
     * editor.entities.reparent([{
     *     entity: child,
     *     parent: parent
     * }])
     * ```
     */
    reparent(data, options = {}) {
        reparentEntities(data, options);
    }
    /**
     * Duplicates the specified entities under the same parent
     *
     * @param entities - The entities
     * @param options.history - Whether to record a history action. Defaults to true.
     * @param options.select - Whether to select the new entities. Defaults to false.
     * @param options.rename - Whether to rename the duplicated entities. Defaults to false.
     * @returns The duplicated entities
     * @example
     * const duplicated = await editor.entities.duplicate(entities);
     */
    duplicate(entities_1) {
        return __awaiter(this, arguments, undefined, function* (entities, options = {}) {
            const result = yield duplicateEntities(entities, options);
            return result;
        });
    }
    /**
     * Copy specified entities to localStorage clipboard. Can be used
     * to paste these entities later on.
     *
     * @param entities - The entities
     */
    copyToClipboard(entities) {
        copyEntities(entities);
    }
    /**
     * Paste entities copied into clipboard
     * under the specified parent.
     *
     * @param parent - The parent
     * @param options.history - Whether to record a history action. Defaults to true.
     * @returns The new entities
     */
    pasteFromClipboard(parent, options = {}) {
        return pasteEntities(parent, options);
    }
    /**
     * Waits for specified entity ids to be added to the scene.
     * Once they are the callback is called with the entities as its argument.
     *
     * @param entityIds - The ids of the entities to wait for
     * @param timeoutMs - Number of ms to wait before stopping to wait
     * @param callback - The callback to call when all entities have been added.
     * The signature is (Entity[]) => void.
     * @returns Returns a cancel function which can be called to cancel calling the
     * callback when the entities are added.
     */
    waitToExist(entityIds, timeoutMs, callback) {
        return wait(entityIds, timeoutMs, callback);
    }
    /**
     * Like {@link Entity.addScript} but works on multiple entities using
     * a single history action.
     *
     * @param entities - The entities.
     * @param scriptName - The name of the script.
     * @param options.attributes - The values of attributes. Each key is the name
     * of the attributes and each value is the value for that attribute. Leave undefined to
     * let the Editor set default values depending on the attribute types.
     * @param options.history - Whether to add a history action. Defaults to true.
     * @param options.index - The desired index in the entity's scripts order to add this script.
     * @returns A promise
     */
    addScript(entities, scriptName, options = {}) {
        return addScript(entities, scriptName, options);
    }
    /**
     * Like {@link Entity.removeScript} but works on multiple entities using
     * a single history action.
     *
     * @param entities - The entities.
     * @param scriptName - The name of the script.
     * @param options.history - Whether to record a history action. Defaults to true.
     */
    removeScript(entities, scriptName, options = {}) {
        removeScript(entities, scriptName, options);
    }
    get raw() {
        return this._entities;
    }
    /**
     * Gets the root Entity
     */
    get root() {
        return this._root;
    }
}

const REGEX_MODEL_DATA_MAPPING = /^data\.mapping\.(\d+)\.material$/;
// Replace references in components or script attributes
function replaceEntityRefs(oldAsset, newAsset) {
    const oldId = parseInt(oldAsset.get('id'), 10);
    const newId = parseInt(newAsset.get('id'), 10);
    const records = [];
    const refs = findAssetReferencesInComponents(globals.entities.root);
    if (refs[oldId]) {
        // remember previous values
        refs[oldId].forEach((ref) => {
            const entity = globals.entities.get(ref.entityId);
            records.push({
                obj: entity,
                path: ref.path,
                value: entity.get(ref.path)
            });
        });
        // update references
        updateReferences(refs, oldId, newId);
    }
    return records;
}
function replaceAssetRefs(oldAsset, newAsset) {
    const oldId = parseInt(oldAsset.get('id'), 10);
    const newId = parseInt(newAsset.get('id'), 10);
    const records = [];
    const pathsByType = {};
    globals.assets.list().forEach((asset) => {
        const type = asset.get('type');
        if (!pathsByType[type]) {
            pathsByType[type] = globals.schema.assets.getFieldsOfType(type, 'asset');
        }
        // for models we also need to update meta.userMapping
        // if any materials change
        let modelUserMapping = null;
        // get paths and add i18n as well
        const paths = pathsByType[type].concat(['i18n.*']);
        paths.forEach((path) => {
            utils.expandPath(asset, path, (asset, path) => {
                const value = asset.get(path);
                if (Array.isArray(value)) {
                    if (!value.includes(oldId)) {
                        return;
                    }
                }
                else if (value !== oldId) {
                    return;
                }
                // remember previous values
                records.push({
                    obj: asset,
                    path: path,
                    value: value
                });
                const history = asset.history.enabled;
                asset.history.enabled = false;
                if (Array.isArray(value)) {
                    asset.set(path, value.map((id) => {
                        return oldId === id ? newId : id;
                    }));
                }
                else {
                    asset.set(path, newId);
                }
                asset.history.enabled = history;
                if (type === 'model') {
                    const match = path.match(REGEX_MODEL_DATA_MAPPING);
                    if (match) {
                        modelUserMapping = modelUserMapping || {};
                        modelUserMapping[match[1]] = true;
                    }
                }
            });
        });
        if (modelUserMapping) {
            const history = asset.history.enabled;
            asset.history.enabled = false;
            if (!asset.get('meta')) {
                records.push({
                    obj: asset,
                    path: 'meta',
                    value: {}
                });
                asset.set('meta', {});
            }
            else {
                records.push({
                    obj: asset,
                    path: 'meta.userMapping',
                    value: asset.has('meta.userMapping') ? asset.get('meta.userMapping') : undefined
                });
            }
            asset.set('meta.userMapping', modelUserMapping);
            asset.history.enabled = history;
        }
    });
    return records;
}
function replaceSceneSettingsRefs(oldAsset, newAsset) {
    const oldId = parseInt(oldAsset.get('id'), 10);
    const newId = parseInt(newAsset.get('id'), 10);
    const records = [];
    if (globals.settings.scene.get('render.skybox') === oldId) {
        records.push({
            obj: globals.settings.scene,
            path: 'render.skybox',
            value: oldId
        });
        const history = globals.settings.scene.history.enabled;
        globals.settings.scene.history.enabled = false;
        globals.settings.scene.set('render.skybox', newId);
        globals.settings.scene.history.enabled = history;
    }
    return records;
}
function replace(oldAsset, newAsset, options = {}) {
    options = options || {};
    if (options.history === undefined) {
        options.history = true;
    }
    let records = [];
    if (globals.entities && globals.entities.root) {
        records = records.concat(replaceEntityRefs(oldAsset, newAsset));
    }
    records = records.concat(replaceAssetRefs(oldAsset, newAsset));
    if (globals.settings) {
        records = records.concat(replaceSceneSettingsRefs(oldAsset, newAsset));
    }
    if (globals.history && options.history) {
        globals.history.add({
            name: 'replace asset references',
            combine: false,
            undo: () => {
                if (!records)
                    return;
                records.forEach((record) => {
                    const obj = record.obj.latest ? record.obj.latest() : record.obj;
                    if (!obj || !obj.has(record.path))
                        return;
                    const history = obj.history.enabled;
                    obj.history.enabled = false;
                    if (record.value === undefined) {
                        obj.unset(record.path);
                    }
                    else {
                        obj.set(record.path, record.value);
                    }
                    obj.history.enabled = history;
                });
            },
            redo: () => {
                records = null;
                if (!oldAsset.latest())
                    return;
                if (!newAsset.latest())
                    return;
                records = replace(oldAsset, newAsset, { history: false });
            }
        });
    }
    return records;
}

/**
 * The Asset class represents an asset in Editor.
 */
class Asset extends Events {
    /**
     * Constructor
     *
     * @param data - The asset data
     */
    constructor(data = {}) {
        super();
        // allow duplicate values in data.frameKeys of sprite asset
        let options = {};
        if (data.type === 'sprite') {
            options = {
                pathsWithDuplicates: ['data.frameKeys']
            };
        }
        data = Object.assign({
            name: 'New Asset',
            tags: [],
            meta: null,
            data: null,
            file: null,
            path: []
        }, data);
        this._observer = new Observer(data, options);
        this._observer.apiAsset = this;
        this._observer.addEmitter(this);
        this._observer.latestFn = () => {
            const latest = globals.assets.get(this.get('id'));
            return latest && latest._observer;
        };
        this._resetThumbnailUrls();
        this._observer.on('has_thumbnail:set', this._resetThumbnailUrls.bind(this));
        // this can happen when the asset is created without a type because
        // the type is not yet available e.g. when listing Assets using the REST API
        // or when fields are set out of order e.g. has_thumbnail set before type
        if (!data.type) {
            this._observer.once('type:set', this._resetThumbnailUrls.bind(this));
        }
        this._suspendOnSet = false;
        this._observer.on('*:set', this._onSet.bind(this));
        this._history = {};
    }
    initializeHistory() {
        if (this._observer.history)
            return;
        this._history = new ObserverHistory({
            item: this._observer,
            prefix: `asset.${this.get('id')}.`,
            history: globals.history
        });
        this._observer.history = this._history;
    }
    _resetThumbnailUrls() {
        const type = this.get('type') || '';
        if (!type.startsWith('texture'))
            return;
        if (this.get('has_thumbnail')) {
            const id = this.get('id');
            this.set('thumbnails', {
                's': `/api/assets/${id}/thumbnail/small?branchId=${globals.branchId}`,
                'm': `/api/assets/${id}/thumbnail/medium?branchId=${globals.branchId}`,
                'l': `/api/assets/${id}/thumbnail/large?branchId=${globals.branchId}`,
                'xl': `/api/assets/${id}/thumbnail/xlarge?branchId=${globals.branchId}`
            });
        }
        else {
            this.unset('thumbnails');
        }
    }
    _onSet(path, value) {
        if (this._suspendOnSet || !path.startsWith('file') || path.endsWith('.url') || !this.get('file')) {
            return;
        }
        this._suspendOnSet = true;
        const parts = path.split('.');
        if ((parts.length === 1 || parts.length === 2) && parts[1] !== 'variants') {
            // reset file url
            this.set('file.url', Asset.getFileUrl(this.get('id'), this.get('file.filename')));
            // reset thumbnails
            this._resetThumbnailUrls();
        }
        else if (parts.length >= 3 && parts[1] === 'variants') {
            const format = parts[2];
            this.set(`file.variants.${format}.url`, Asset.getFileUrl(this.get('id'), this.get(`file.variants.${format}.filename`)));
        }
        this._suspendOnSet = false;
    }
    /**
     * Checks if path exists. See the {@link Asset} overview for a full list of properties.
     *
     * @param path - The path
     * @returns True if path exists
     */
    has(path) {
        return this._observer.has(path);
    }
    /**
     * Gets value at path. See the {@link Asset} overview for a full list of properties.
     *
     * @param path - The path
     * @returns The value
     */
    get(path) {
        return this._observer.get(path);
    }
    /**
     * Sets value at path. See the {@link Asset} overview for a full list of properties.
     *
     * @param path - The path
     * @param value - The value
     * @returns Whether the value was set
     */
    set(path, value) {
        return this._observer.set(path, value);
    }
    /**
     * Unsets value at path. See the {@link Asset} overview for a full list of properties.
     *
     * @param path - The path
     * @returns Whether the value was unset
     */
    unset(path) {
        return this._observer.unset(path);
    }
    /**
     * Inserts value in array at path, at specified index. See the {@link Asset} overview for a
     * full list of properties.
     *
     * @param path - The path
     * @param value - The value
     * @param index - The index (if undefined the value will be inserted in the end)
     * @returns Whether the value was inserted
     */
    insert(path, value, index) {
        return this._observer.insert(path, value, index);
    }
    /**
     * Remove value from array at path. See the {@link Asset} overview for a full list of properties.
     *
     * @param path - The path
     * @param value - The value
     * @returns Whether the value was removed
     */
    removeValue(path, value) {
        return this._observer.removeValue(path, value);
    }
    /**
     * Returns JSON representation of entity data
     *
     * @returns - The data
     */
    json() {
        return this._observer.json();
    }
    /**
     * Returns the latest version of the Asset from the Assets API.
     *
     * @returns The asset
     */
    latest() {
        return globals.assets.get(this._observer.get('id'));
    }
    /**
     * Loads asset from the server without subscribing to realtime changes.
     */
    load() {
        return __awaiter(this, undefined, undefined, function* () {
            const response = yield fetch(`/api/assets/${this.get('id')}?branchId=${globals.branchId}`);
            if (!response.ok) {
                throw new Error(`${response.status}: ${response.statusText}`);
            }
            const data = yield response.json();
            if (data.file) {
                data.file.url = Asset.getFileUrl(data.id, data.file.filename);
                if (data.file.variants) {
                    for (const key in data.file.variants) {
                        data.file.variants[key].url = Asset.getFileUrl(data.id, data.file.variants[key].filename);
                    }
                }
            }
            for (const field in data) {
                this.set(field, data[field]);
            }
        });
    }
    /**
     * Loads the asset's data from sharedb and subscribes to changes.
     */
    loadAndSubscribe() {
        return __awaiter(this, undefined, undefined, function* () {
            if (!globals.realtime)
                return;
            const uniqueId = this.get('uniqueId');
            const a = globals.realtime.assets.load(uniqueId);
            yield new Promise((resolve, reject) => {
                a.once('load', () => {
                    const data = a.data;
                    data.id = parseInt(data.item_id, 10);
                    data.uniqueId = uniqueId;
                    data.createdAt = this.get('createdAt');
                    // delete unnecessary fields
                    delete data.item_id;
                    delete data.branch_id;
                    if (data.file) {
                        data.file.url = Asset.getFileUrl(data.id, data.file.filename);
                        if (data.file.variants) {
                            for (const key in data.file.variants) {
                                data.file.variants[key].url = Asset.getFileUrl(data.id, data.file.variants[key].filename);
                            }
                        }
                    }
                    for (const field in a.data) {
                        this.set(field, a.data[field]);
                    }
                    resolve();
                });
                a.once('error:load', (err) => {
                    reject(err);
                });
            });
        });
    }
    /**
     * Deletes this asset.
     */
    delete() {
        return __awaiter(this, undefined, undefined, function* () {
            yield globals.assets.delete([this]);
        });
    }
    /**
     * Creates an instance of this template asset. Assumes this asset is a template asset.
     *
     * @param parent - The parent entity
     * @param options.index - The desired index under the parent to instantiate the template.
     * @param options.history - Whether to record a history action.
     * @param options.select - Whether to select the new entity.
     * @param options.extraData - Extra data passed to the backend. Used by the Editor on specific cases.
     * @returns The new entity.
     */
    instantiateTemplate(parent_1) {
        return __awaiter(this, arguments, undefined, function* (parent, options = {}) {
            const entities = yield globals.assets.instantiateTemplates([this], parent, options);
            return entities[0];
        });
    }
    /**
     * Replaces any references to this asset with references to the new asset specified.
     *
     * @param asset - The new asset.
     * @param options.history - Whether to record a history action.
     */
    replace(asset, options = {}) {
        replace(this, asset, options);
    }
    /**
     * The observer object for this asset.
     */
    get observer() {
        return this._observer;
    }
    /**
     * Gets observer history for this asset.
     */
    get history() {
        return this._history;
    }
    /**
     * Gets the file URL for an asset file.
     *
     * @param id - The asset id
     * @param filename - The desired filename
     * @returns The file URL
     */
    static getFileUrl(id, filename) {
        return `/api/assets/${id}/file/${encodeURIComponent(filename)}?branchId=${globals.branchId}`;
    }
}

const VALID_FILENAME = /^[^0-9.#<>$+%!`&='{}@\\/:*?"|\n][^#<>$+%!`&='{}@\\/:*?"|\n]*$/;
const extensionToBoilerplateMap = new Map([
    ['.js', createBoilerplate],
    ['.mjs', createEsmBoilerplate],
    ['.ts', createTsBoilerplate]
]);
const validExtensions = Array.from(extensionToBoilerplateMap.keys());
/**
 * Creates filename and script content from provided arguments. If the provide filename contains a '.mjs'
 * suffix, it will generate an ESM based class syntax.
 *
 * @param filename - The desired filename.
 * @param text - The desired contents of the script. If not provided boilerplate code will be used.
 * @returns The filename and content of the script
 */
function createScript(filename, text) {
    let className = '';
    let scriptName = '';
    // tokenize filename
    const tokens = [];
    const name = filename.slice(0, -3);
    const str = name.replace(/([^A-Z])([A-Z][^A-Z])/g, '$1 $2').replace(/([A-Z0-9]{2,})/g, ' $1');
    const parts = str.split(/([\s\-_.])/);
    // filter valid tokens
    for (let i = 0; i < parts.length; i++) {
        parts[i] = parts[i].toLowerCase().trim();
        if (parts[i] && parts[i] !== '-' && parts[i] !== '_' && parts[i] !== '.') {
            tokens.push(parts[i]);
        }
    }
    if (tokens.length) {
        scriptName = tokens[0];
        for (let i = 1; i < tokens.length; i++) {
            scriptName += tokens[i].charAt(0).toUpperCase() + tokens[i].slice(1);
        }
        for (let i = 0; i < tokens.length; i++) {
            className += tokens[i].charAt(0).toUpperCase() + tokens[i].slice(1);
        }
    }
    else {
        className = 'Script';
        scriptName = 'script';
    }
    if (!VALID_FILENAME.test(className)) {
        className = 'Script';
    }
    if (!filename) {
        filename = `${scriptName}.js`;
    }
    const hasValidExtension = validExtensions.some(ext => filename.endsWith(ext));
    if (!hasValidExtension) {
        filename += '.js';
    }
    // Extract extension from filename
    const extension = filename.slice(filename.lastIndexOf('.'));
    // Get the correct boilerplate generator based on the file extension
    const boilerPlateGenerator = extensionToBoilerplateMap.get(extension);
    const content = text || boilerPlateGenerator(className, scriptName);
    return {
        filename,
        content
    };
}
function createBoilerplate(className, scriptName) {
    return `
var ${className} = pc.createScript('${scriptName}');

// initialize code called once per entity
${className}.prototype.initialize = function() {

};

// update code called every frame
${className}.prototype.update = function(dt) {

};

// uncomment the swap method to enable hot-reloading for this script
// update the method body to copy state from the old instance
// ${className}.prototype.swap = function(old) { };

// learn more about scripting here:
// https://developer.playcanvas.com/user-manual/scripting/
`.trim();
}
function createEsmBoilerplate(className, scriptName) {
    return `
import { Script } from 'playcanvas';

/**
 * The {@link https://api.playcanvas.com/classes/Engine.Script.html | Script} class is
 * the base class for all PlayCanvas scripts. Learn more about writing scripts in the
 * {@link https://developer.playcanvas.com/user-manual/scripting/ | scripting guide}.
 */
export class ${className} extends Script {

    static scriptName = "${scriptName}"

    /**
     * Called when the script is about to run for the first time.
     */
    initialize() {
    }

    /**
     * Called for enabled (running state) scripts on each tick.
     * 
     * @param {number} dt - The delta time in seconds since the last frame.
     */
    update(dt) {
    }
}
`.trim();
}
function createTsBoilerplate(className, scriptName) {
    return `
import { Script } from 'playcanvas';

/**
 * The {@link https://api.playcanvas.com/classes/Engine.Script.html | Script} class is
 * the base class for all PlayCanvas scripts. Learn more about writing scripts in the
 * {@link https://developer.playcanvas.com/user-manual/scripting/ | scripting guide}.
 */
export class ${className} extends Script {

    static scriptName = "${scriptName}"

    /**
     * Called when the script is about to run for the first time.
     */
    initialize(): void {
    }

    /**
     * Called for enabled (running state) scripts on each tick.
     * 
     * @param {number} dt - The delta time in seconds since the last frame.
     */
    update(dt: number): void {
    }
}
`.trim();
}

function createTemplate(rootEntity) {
    const entities = {};
    const oldToNewIds = {};
    const rootId = rootEntity.get('resource_id');
    // go through each entity and generate new resource_ids
    rootEntity.depthFirst((entity) => {
        const id = entity.get('resource_id');
        const newId = Guid.create();
        oldToNewIds[id] = newId;
        const json = entity.json();
        json.resource_id = newId;
        // delete template_id and template_ent_ids
        // from root
        if (id === rootId) {
            json.parent = null;
            delete json.template_id;
            delete json.template_ent_ids;
        }
        // if we have any missing references in template_ent_ids
        // then generate new resource_ids for those
        if (json.template_ent_ids) {
            for (const key in json.template_ent_ids) {
                if (!oldToNewIds[key] && !globals.entities.get(key)) {
                    oldToNewIds[key] = Guid.create();
                }
            }
        }
        const newEntity = new Entity(json);
        newEntity.set('children', entity.get('children'));
        entities[newId] = newEntity;
    });
    // find component entity references
    const references = findEntityReferencesInComponents(rootEntity);
    for (const oldId in references) {
        const prevEntity = globals.entities.get(oldId);
        const isExternalReference = (!prevEntity || (prevEntity !== rootEntity && !prevEntity.isDescendantOf(rootEntity)));
        const referencesToEntity = references[oldId];
        referencesToEntity.forEach((reference) => {
            const entity = entities[oldToNewIds[reference.entityId]];
            if (entity) {
                let value = null;
                if (!isExternalReference && oldToNewIds[oldId]) {
                    value = oldToNewIds[oldId];
                }
                entity.set(reference.path, value);
            }
        });
    }
    // remap top level references
    for (const id in entities) {
        const parent = entities[id].get('parent');
        if (parent) {
            entities[id].set('parent', oldToNewIds[parent]);
        }
        entities[id].set('children', entities[id].get('children').map((child) => oldToNewIds[child]));
        const templateEntIds = entities[id].get('template_ent_ids');
        if (templateEntIds) {
            const newTemplateEntIds = {};
            for (const key in templateEntIds) {
                newTemplateEntIds[oldToNewIds[key]] = templateEntIds[key];
            }
            entities[id].set('template_ent_ids', newTemplateEntIds);
        }
    }
    // convert observers to json
    for (const id in entities) {
        entities[id] = entities[id].json();
    }
    return {
        entities,
        oldToNewIds
    };
}

let evtMessenger;
function instantiateTemplates(assets_1, parent_1) {
    return __awaiter(this, arguments, undefined, function* (assets, parent, options = {}) {
        parent = parent || globals.entities.root;
        if (!parent) {
            throw new Error('Invalid parent');
        }
        // setup promise
        const deferred = {
            resolve: null,
            reject: null
        };
        const promise = new Promise((resolve, reject) => {
            deferred.resolve = resolve;
            deferred.reject = reject;
        });
        // start job
        const jobId = globals.jobs.start((msg) => {
            // resolve promise
            if (msg.status === 'success') {
                const newEntityIds = msg.multTaskResults.map((d) => d.newRootId);
                globals.entities.waitToExist(newEntityIds, 5000, (entities) => {
                    deferred.resolve(entities);
                });
            }
            else {
                deferred.reject();
            }
        });
        // subscribe to messenger for backend response
        if (!evtMessenger) {
            evtMessenger = globals.messenger.on('template.instance', (msg) => {
                const callback = globals.jobs.finish(msg.job_id);
                if (callback) {
                    callback(msg);
                }
            });
        }
        // start backend job
        globals.realtime.connection.sendMessage('pipeline', {
            name: 'template-instance',
            data: {
                projectId: globals.projectId,
                branchId: globals.branchId,
                parentId: parent.get('resource_id'),
                sceneId: globals.realtime.scenes.current.uniqueId,
                jobId: jobId,
                children: parent.get('children'),
                childIndex: options.index,
                templates: assets.map((asset) => {
                    return {
                        id: parseInt(asset.get('uniqueId'), 10),
                        opts: options.extraData
                    };
                })
            }
        });
        let entities = yield promise;
        // record history action
        if (globals.history && (options.history || options.history === undefined)) {
            globals.history.add({
                name: 'instantiate templates',
                combine: false,
                undo: () => {
                    // delete entities
                    entities = entities.map((e) => e.latest()).filter((e) => !!e);
                    if (entities.length) {
                        globals.entities.delete(entities, { history: false })
                            .catch((err) => {
                            console.error(err);
                        });
                    }
                },
                redo: () => {
                    parent = parent.latest();
                    if (!parent)
                        return;
                    const newOptions = Object.assign({}, options);
                    newOptions.history = false;
                    // re-instantiate templates
                    instantiateTemplates(assets, parent, newOptions)
                        .then((newEntities) => {
                        entities = newEntities;
                    })
                        .catch((err) => {
                        console.error(err);
                    });
                }
            });
        }
        if (options.select) {
            globals.selection.set(entities, { history: false });
        }
        return entities;
    });
}

function getSetting(settings, name, defaultValue) {
    return settings && settings[name] !== undefined ? settings[name] : defaultValue;
}
function createFormData(data, settings) {
    const form = new FormData();
    form.append('branchId', globals.branchId);
    if (data.folderId) {
        form.append('parent', data.folderId.toString());
    }
    if (data.filename) {
        form.append('filename', data.filename);
    }
    if (data.file && data.file.size) {
        form.append('file', data.file, data.filename || data.name);
    }
    if (data.type === 'texture' || data.type === 'textureatlas') {
        form.append('pow2', getSetting(settings, 'pow2', true));
        form.append('searchRelatedAssets', getSetting(settings, 'searchRelatedAssets', true));
    }
    else if (data.type === 'scene') {
        form.append('searchRelatedAssets', getSetting(settings, 'searchRelatedAssets', true));
        form.append('overwriteModel', getSetting(settings, 'overwriteModel', true));
        form.append('overwriteAnimation', getSetting(settings, 'overwriteAnimation', true));
        form.append('overwriteMaterial', getSetting(settings, 'overwriteMaterial', true));
        form.append('overwriteTexture', getSetting(settings, 'overwriteTexture', true));
        form.append('pow2', getSetting(settings, 'pow2', true));
        form.append('preserveMapping', getSetting(settings, 'preserveMapping', true));
        form.append('useGlb', getSetting(settings, 'useGlb', true));
        form.append('animSampleRate', getSetting(settings, 'animSampleRate', 10));
        form.append('animCurveTolerance', getSetting(settings, 'animCurveTolerance', 0));
        form.append('animEnableCubic', getSetting(settings, 'animEnableCubic', false));
        form.append('animUseFbxFilename', getSetting(settings, 'animUseFbxFilename', false));
    }
    return form;
}
function appendCreateFields(form, data) {
    form.append('projectId', globals.projectId);
    form.append('type', data.type);
    if (data.name) {
        form.append('name', data.name);
    }
    // tags
    if (data.tags) {
        form.append('tags', data.tags.join('\n'));
    }
    // source_asset_id
    if (data.sourceAssetId) {
        form.append('source_asset_id', data.sourceAssetId);
    }
    // data
    if (data.data) {
        form.append('data', JSON.stringify(data.data));
    }
    // meta
    if (data.meta) {
        form.append('meta', JSON.stringify(data.meta));
    }
    form.append('preload', getSetting(data, 'preload', true));
    return form;
}
/**
 * Uploads an asset file in order to create a new asset
 * or update an existing asset.
 *
 * @param data - The data
 * @param settings - Import settings
 * @param onProgress - Progress function
 * @returns The JSON response from the server
 */
function uploadFile(data_1) {
    return __awaiter(this, arguments, undefined, function* (data, settings = null, onProgress = null) {
        let method;
        let url;
        const form = createFormData(data, settings);
        if (data.id) {
            method = 'PUT';
            url = `/api/assets/${data.id}`;
        }
        else {
            appendCreateFields(form, data);
            method = 'POST';
            url = '/api/assets';
        }
        const response = yield new Promise((resolve) => {
            let progress;
            function onProgressUpdate(value) {
                if (progress !== value) {
                    progress = value;
                    if (onProgress) {
                        onProgress(progress);
                    }
                }
            }
            function onError(status, err) {
                resolve({
                    ok: false,
                    status: status,
                    error: err
                });
            }
            const xhr = new XMLHttpRequest();
            xhr.withCredentials = true;
            xhr.addEventListener('load', () => {
                onProgressUpdate(1.0);
                if (xhr.status === 200 || xhr.status === 201) {
                    resolve({
                        ok: true,
                        status: xhr.status,
                        json: JSON.parse(xhr.responseText)
                    });
                }
                else {
                    try {
                        const json = JSON.parse(xhr.responseText);
                        let msg = json.message;
                        if (!msg) {
                            msg = json.error || (json.response && json.response.error);
                        }
                        if (!msg) {
                            msg = xhr.responseText;
                        }
                        onError(xhr.status, msg);
                    }
                    catch (ex) {
                        onError(xhr.status, ex);
                    }
                }
            });
            xhr.upload.addEventListener('progress', (evt) => {
                if (!evt.lengthComputable)
                    return;
                onProgressUpdate(evt.loaded / evt.total);
            });
            xhr.addEventListener('error', (evt) => {
                onError(xhr.status, evt);
            });
            xhr.addEventListener('abort', (evt) => {
                onError(xhr.status, evt);
            });
            onProgressUpdate(0);
            xhr.open(method, url, true);
            xhr.send(form);
        });
        if (!response.ok) {
            throw new Error(`${response.status}${response.error ? `: ${response.error}` : ''}`);
        }
        return response.json;
    });
}

/**
 * The Assets Editor API
 */
class Assets extends Events {
    /**
     * Constructor
     *
     * @param options.autoSubscribe - Whether to auto subscribe to asset changes when assets are loaded.
     */
    constructor(options = {}) {
        super();
        this._uniqueIdToItemId = {};
        this._assets = new ObserverList({
            index: 'id',
            sorted: (a, b) => {
                const f = +(b._data.type === 'folder') - +(a._data.type === 'folder');
                if (f !== 0) {
                    return f;
                }
                if ((a._data.name || '').toLowerCase() > (b._data.name || '').toLowerCase()) {
                    return 1;
                }
                else if ((a._data.name || '').toLowerCase() < (b._data.name || '').toLowerCase()) {
                    return -1;
                }
                return 0;
            }
        });
        this._parseScriptCallback = null;
        this._defaultUploadCompletedCallback = null;
        this._defaultUploadProgressCallback = null;
        this._defaultUploadErrorCallback = null;
        this._uploadId = 0;
        this._autoSubscribe = options.autoSubscribe || false;
        if (this._autoSubscribe && !globals.messenger) {
            throw new Error('Cannot autosubscribe to asset changes without the messenger API');
        }
        if (globals.messenger) {
            globals.messenger.on('asset.new', this._onMessengerAddAsset.bind(this));
            globals.messenger.on('asset.delete', this._onMessengerDeleteAsset.bind(this));
            globals.messenger.on('assets.delete', this._onMessengerDeleteAssets.bind(this));
        }
    }
    _onMessengerAddAsset(data) {
        if (data.asset.branchId !== globals.branchId)
            return;
        const uniqueId = parseInt(data.asset.id, 10);
        if (data.asset.source === false && data.asset.status && data.asset.status !== 'complete') {
            return;
        }
        let asset = this.getUnique(uniqueId);
        if (asset)
            return;
        asset = new Asset({
            id: uniqueId,
            uniqueId: uniqueId,
            type: data.asset.type,
            source: data.asset.source,
            source_asset_id: parseInt(data.asset.source_asset_id, 10),
            createdAt: data.asset.createdAt
        });
        if (this._autoSubscribe) {
            asset.loadAndSubscribe().then(() => {
                this.add(asset);
            });
        }
        else {
            asset.load().then(() => {
                this.add(asset);
            });
        }
    }
    _onMessengerDeleteAsset(data) {
        const asset = this.getUnique(data.asset.id);
        if (asset) {
            this.remove(asset);
        }
    }
    _onMessengerDeleteAssets(data) {
        for (let i = 0; i < data.assets.length; i++) {
            const asset = this.getUnique(parseInt(data.assets[i], 10));
            if (asset) {
                this.remove(asset);
            }
        }
    }
    /**
     * Gets asset by id
     *
     * @param id - The asset id
     * @returns The asset
     */
    get(id) {
        const a = this._assets.get(id);
        return a ? a.apiAsset : null;
    }
    /**
     * Gets asset by its unique id
     *
     * @param uniqueId - The unique id
     * @returns The asset
     */
    getUnique(uniqueId) {
        const id = this._uniqueIdToItemId[uniqueId];
        return id ? this.get(id) : null;
    }
    /**
     * Returns array of all assets
     *
     * @returns The assets
     */
    list() {
        return this._assets.array().map((a) => a.apiAsset);
    }
    /**
     * Finds all assets with specified tags
     *
     * @param tags - The tags. If multiple tags are specified then assets that contain ANY of the specified
     * tags will be included. If an argument is an array of tags then assets that contain ALL of the tags in the array will be included.
     * @returns The assets
     */
    listByTag(...tags) {
        return this.filter((asset) => {
            const t = asset.get('tags');
            for (let i = 0; i < tags.length; i++) {
                if (Array.isArray(tags[i])) {
                    let countTags = 0;
                    for (let j = 0; j < tags[i].length; j++) {
                        if (t.includes(tags[i][j])) {
                            countTags++;
                        }
                        else {
                            break;
                        }
                    }
                    if (countTags === tags[i].length) {
                        return true;
                    }
                }
                else {
                    if (t.includes(tags[i])) {
                        return true;
                    }
                }
            }
            return false;
        });
    }
    /**
     * Adds asset to the list
     *
     * @category Internal
     * @param asset - The asset
     */
    add(asset) {
        asset.initializeHistory();
        const pos = this._assets.add(asset.observer);
        if (pos === null)
            return;
        const id = asset.get('id');
        this._uniqueIdToItemId[asset.get('uniqueId')] = id;
        asset.observer.on('name:set', (name, oldName) => {
            name = (name || '').toLowerCase();
            oldName = (oldName || '').toLowerCase();
            const ind = this._assets.data.indexOf(asset.observer);
            let pos = this._assets.positionNextClosest(asset.observer, (a, b) => {
                const f = +(b._data.type === 'folder') - +(a._data.type === 'folder');
                if (f !== 0) {
                    return f;
                }
                if ((a === b ? oldName : (a._data.name || '').toLowerCase()) > name) {
                    return 1;
                }
                else if ((a === b ? oldName : (a._data.name || '').toLowerCase()) < name) {
                    return -1;
                }
                return 0;
            });
            if (pos === -1 && (ind + 1) === this._assets.data.length) {
                return;
            }
            if (ind !== -1 && (ind + 1 === pos) || (ind === pos)) {
                return;
            }
            if (ind < pos) {
                pos--;
            }
            this._assets.move(asset.observer, pos);
            this.emit('move', asset, pos);
        });
        this.emit(`add[${id}]`, asset, pos);
        this.emit('add', asset, pos);
    }
    /**
     * Removes asset from the list
     *
     * @category Internal
     * @param asset - The asset
     */
    remove(asset) {
        if (!this._assets.has(asset.observer))
            return;
        this._assets.remove(asset.observer);
        delete this._uniqueIdToItemId[asset.get('uniqueId')];
        asset.observer.destroy();
        if (globals.realtime) {
            globals.realtime.assets.unload(asset.get('uniqueId'));
        }
        this.emit('remove', asset);
        this.emit(`remove[${asset.get('id')}]`);
    }
    /**
     * Removes all assets from the list
     *
     * @category Internal
     */
    clear() {
        const assets = this.list();
        if (!assets.length)
            return;
        this._assets.clear();
        let i = assets.length;
        while (i--) {
            this.remove(assets[i]);
        }
        this.emit('clear');
    }
    /**
     * Gets assets that satisfy function
     *
     * @param fn - The function (takes an asset as an argument and returns boolean).
     * @returns The assets
     */
    filter(fn) {
        return this._assets.data
            .filter((observer) => fn(observer.apiAsset))
            .map((observer) => observer.apiAsset);
    }
    /**
     * Finds first asset that satisfies function
     *
     * @param fn - A function that takes an asset as an argument and returns boolean.
     * @returns The asset
     */
    findOne(fn) {
        const result = this._assets.data.find((observer) => fn(observer.apiAsset));
        return result ? result.apiAsset : null;
    }
    /**
     * Loads all assets in the current project / branch. Does not
     * subscribe to realtime changes.
     *
     * @category Internal
     * @param options.view - The desired view for the REST API e.g 'designer', 'shader-editor'. This might limit
     * the assets returned to a smaller subset depending on the view.
     */
    loadAll() {
        return __awaiter(this, arguments, undefined, function* (options = {}) {
            this.clear();
            this.emit('load:progress', 0.1);
            const response = yield fetch(`/api/projects/${globals.projectId}/assets?branchId=${globals.branchId}&view=${options.view || 'designer'}`);
            if (!response.ok) {
                console.error(`Could not load assets: [${response.status}] - ${response.statusText}`);
                return;
            }
            const assets = yield response.json();
            this.emit('load:progress', 0.5);
            const total = assets.length;
            if (!total) {
                this.emit('load:progress', 1);
                this.emit('load:all');
                return;
            }
            let loaded = 0;
            const onProgress = () => {
                loaded++;
                if (loaded === total) {
                    this.emit('load:progress', 1);
                    this.emit('load:all');
                }
                else {
                    this.emit('load:progress', (loaded / total) * 0.5 + 0.5);
                }
            };
            for (let i = 0; i < total; i++) {
                const asset = new Asset(assets[i]);
                asset.load().then(() => {
                    this.add(asset);
                    onProgress();
                }).catch((err) => {
                    onProgress();
                });
            }
        });
    }
    /**
     * Loads all assets in the current project / branch
     * and subscribes to changes.
     *
     * @param options.view - The desired view for the REST API e.g 'designer', 'shader-editor'. This might limit
     * the assets returned to a smaller subset depending on the view.
     * @category Internal
     */
    loadAllAndSubscribe() {
        return __awaiter(this, arguments, undefined, function* (options = {}) {
            this.clear();
            this.emit('load:progress', 0.1);
            const response = yield fetch(`/api/projects/${globals.projectId}/assets?branchId=${globals.branchId}&view=${options.view || 'designer'}`);
            if (!response.ok) {
                console.error(`Could not load assets: [${response.status}] - ${response.statusText}`);
                return;
            }
            const assets = yield response.json();
            this.emit('load:progress', 0.5);
            const total = assets.length;
            if (!total) {
                this.emit('load:progress', 1);
                this.emit('load:all');
                return;
            }
            const batchSize = 256;
            let loaded = 0;
            let startBatch = 0;
            const onProgress = () => {
                loaded++;
                if (loaded === total) {
                    this.emit('load:progress', 1);
                    this.emit('load:all');
                }
                else {
                    this.emit('load:progress', (loaded / total) * 0.5 + 0.5);
                }
            };
            while (startBatch < total) {
                globals.realtime.connection.startBulkSubscribe();
                for (let i = startBatch; i < startBatch + batchSize && i < total; i++) {
                    const asset = new Asset(assets[i]);
                    asset.loadAndSubscribe().then(() => {
                        this.add(asset);
                        onProgress();
                    }).catch((err) => {
                        onProgress();
                    });
                }
                globals.realtime.connection.endBulkSubscribe();
                startBatch += batchSize;
            }
        });
    }
    /**
     * Gets the first script asset that contains the specified script
     *
     * @param script - The script name
     * @returns The script asset
     */
    getAssetForScript(script) {
        return this.findOne((asset) => {
            return asset.get('type') === 'script' &&
                asset.has(`data.scripts.${script}`);
        });
    }
    /**
     * Creates new asset
     *
     * @param data - The asset fields
     * @param settings - Import settings
     * @param onProgress - Function to report progress
     * @returns The new asset
     */
    upload(data_1) {
        return __awaiter(this, arguments, undefined, function* (data, settings = {}, onProgress = null) {
            if (data.folder) {
                data.folderId = data.folder.get('id');
            }
            const uploadId = this._uploadId++;
            if (!onProgress && this._defaultUploadProgressCallback) {
                onProgress = (progress) => {
                    this._defaultUploadProgressCallback(uploadId, progress);
                };
            }
            try {
                const result = yield uploadFile(data, settings, onProgress || this._defaultUploadProgressCallback);
                let asset = this.get(result.id);
                if (!asset) {
                    asset = yield new Promise((resolve) => {
                        this.once(`add[${result.id}]`, (a) => {
                            resolve(a);
                        });
                    });
                }
                if (this._defaultUploadCompletedCallback) {
                    this._defaultUploadCompletedCallback(uploadId, asset);
                }
                return asset;
            }
            catch (err) {
                if (this._defaultUploadErrorCallback) {
                    this._defaultUploadErrorCallback(uploadId, err);
                }
                throw err;
            }
        });
    }
    /**
     * Creates new anim state graph asset.
     *
     * @param options.name - The asset name
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.data - The asset data. See {@link Asset} for Animstategraph data.
     * @param options.folder - The parent folder asset
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createAnimStateGraph(options = {}) {
        return this.upload({
            name: options.name || 'New Anim State Graph',
            type: 'animstategraph',
            data: options.data || globals.schema.assets.getDefaultData('animstategraph'),
            folder: options.folder,
            preload: options.preload
        }, null, options.onProgress);
    }
    /**
     * Creates new bundle asset
     *
     * @param options.name - The asset name
     * @param options.assets - The assets that the bundle will contain
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createBundle(options = {}) {
        return this.upload({
            name: options.name || 'New Bundle',
            type: 'bundle',
            folder: options.folder,
            data: {
                assets: (options.assets || []).map((a) => a.get('id'))
            },
            preload: options.preload
        }, null, options.onProgress);
    }
    /**
     * Creates new CSS asset
     *
     * @param options.name - The asset name
     * @param options.text - The CSS
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createCss(options = {}) {
        return this.upload({
            name: options.name || 'New Css',
            type: 'css',
            folder: options.folder,
            filename: 'asset.css',
            file: new Blob([options.text || '\n'], { type: 'text/css' }),
            preload: options.preload
        }, null, options.onProgress);
    }
    /**
     * Creates new cubemap asset
     *
     * @param options.name - The asset name
     * @param options.textures - The textures for each cubemap face in this order:
     * right, left, up, down, front, back
     * @param options.minFilter - Cubemap minFilter value. Defaults to pc.FILTER_LINEAR_MIPMAP_LINEAR.
     * @param options.magFilter - Cubemap magFilter value. Defaults to pc.FILTER_LINEAR.
     * @param options.anisotropy - Cubemap anisotropy value. Defaults to 1.
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createCubemap(options = {}) {
        const textures = (options.textures || new Array(6)).slice(0, 6);
        for (let i = 0; i < 6; i++) {
            textures[i] = (textures[i] ? textures[i].get('id') : null);
        }
        return this.upload({
            name: options.name || 'New Cubemap',
            type: 'cubemap',
            folder: options.folder,
            preload: options.preload,
            data: {
                name: options.name || 'New Cubemap',
                textures: textures,
                minFilter: options.minFilter !== undefined ? options.minFilter : 5, // linear mipmap linear
                magFilter: options.magFilter !== undefined ? options.magFilter : 1, // linear
                anisotropy: options.anisotropy !== undefined ? options.anisotropy : 1
            }
        }, null, options.onProgress);
    }
    /**
     * Creates a new folder asset
     *
     * @param options.name - The asset name
     * @param options.folder - The parent folder asset
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createFolder(options) {
        return this.upload({
            name: options.name || 'New Folder',
            type: 'folder',
            folder: options.folder
        }, null, options.onProgress);
    }
    /**
     * Creates new HTML asset
     *
     * @param options.name - The asset name
     * @param options.text - The HTML
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createHtml(options = {}) {
        return this.upload({
            name: options.name || 'New Html',
            type: 'html',
            folder: options.folder,
            preload: options.preload,
            filename: 'asset.html',
            file: new Blob([options.text || '\n'], { type: 'text/html' })
        }, null, options.onProgress);
    }
    /**
     * Creates new JSON asset
     *
     * @param options.name - The asset name
     * @param options.json - The JSON
     * @param options.spaces - The number of spaces used for indentation. Defaults to 0
     * (tightly packed output).
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createJson(options = {}) {
        var _a;
        const spaces = (_a = options.spaces) !== null && _a !== undefined ? _a : 0;
        const str = JSON.stringify(options.json || {}, null, spaces);
        return this.upload({
            name: options.name || 'New Json',
            type: 'json',
            folder: options.folder,
            preload: options.preload,
            filename: 'asset.json',
            file: new Blob([str], { type: 'application/json' })
        }, null, options.onProgress);
    }
    /**
     * Creates new localization JSON asset
     *
     * @param options.name - The asset name
     * @param options.localizationData - The localization data. If null then default data will be used.
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createI18n(options = {}) {
        return this.createJson({
            name: options.name,
            json: options.localizationData || {
                'header': {
                    'version': 1
                },
                'data': [{
                        'info': {
                            'locale': 'en-US'
                        },
                        'messages': {
                            'key': 'Single key translation',
                            'key plural': ['One key translation', 'Translation for {number} keys']
                        }
                    }]
            },
            folder: options.folder,
            preload: options.preload,
            spaces: 4,
            onProgress: options.onProgress
        });
    }
    /**
     * Creates new material asset
     *
     * @param options.name - The asset name
     * @param options.data - The material data. Default values will be used for missing fields. See {@link Asset} for material data.
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createMaterial(options = {}) {
        const defaultData = globals.schema.assets.getDefaultData('material');
        if (options.data) {
            for (const key in defaultData) {
                if (options.data[key]) {
                    defaultData[key] = options.data[key];
                }
            }
        }
        return this.upload({
            name: options.name || 'New Material',
            type: 'material',
            folder: options.folder,
            data: defaultData,
            preload: options.preload
        }, null, options.onProgress);
    }
    /**
     * Creates new script asset
     *
     * @param options.filename - The filename of the script. This will also be the name of the script asset. If not defined it will be generated
     * from the name of the script.
     * @param options.text - The contents of the script. If none then boilerplate code will be used.
     * @param options.data - The script data. See {@link Asset} for Script data.
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createScript() {
        return __awaiter(this, arguments, undefined, function* (options = {}) {
            if (!options.filename) {
                throw new Error('createScript: missing required filename');
            }
            const result = createScript(options.filename, options.text);
            const asset = yield this.upload({
                name: result.filename,
                type: 'script',
                folder: options.folder,
                filename: result.filename,
                file: new Blob([result.content], { type: 'text/javascript' }),
                preload: options.preload,
                data: options.data || {
                    scripts: {},
                    loading: false,
                    loadingType: 0 // load script as asset
                }
            }, null, options.onProgress);
            // wait for asset to have a file url
            if (this._parseScriptCallback) {
                if (!asset.get('file.url')) {
                    yield new Promise((resolve) => {
                        asset.once('file.url:set', resolve);
                    });
                }
                const scripts = yield this._parseScriptCallback(asset);
                // check if all scripts have been set to the asset
                // because of possible network delays.
                // if not then wait until those scripts have been set
                // before returning
                const wait = [];
                scripts.forEach((script) => {
                    if (!asset.has(`data.scripts.${script}`)) {
                        wait.push(new Promise((resolve) => {
                            asset.once(`data.scripts.${script}:set`, resolve);
                        }));
                    }
                });
                yield Promise.all(wait);
            }
            return asset;
        });
    }
    /**
     * Creates new shader asset
     *
     * @param options.name - The asset name
     * @param options.text - The GLSL
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createShader(options = {}) {
        return this.upload({
            name: options.name || 'New Shader',
            type: 'shader',
            folder: options.folder,
            preload: options.preload,
            filename: 'asset.glsl',
            file: new Blob([options.text || '\n'], { type: 'text/x-glsl' })
        }, null, options.onProgress);
    }
    /**
     * Creates new sprite asset
     *
     * @param options.name - The asset name
     * @param options.pixelsPerUnit - The sprite's pixels per unit value. Defaults to 100.
     * @param options.frameKeys - The sprite's frame keys
     * @param options.textureAtlas - The sprite's texture atlas asset
     * @param options.renderMode - The sprite's render mode. Defaults to pc.SPRITE_RENDERMODE_SIMPLE.
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createSprite(options = {}) {
        const data = {};
        data.pixelsPerUnit = options.pixelsPerUnit !== undefined ? options.pixelsPerUnit : 100;
        data.frameKeys = options.frameKeys ? options.frameKeys.map((val) => val.toString()) : [];
        data.textureAtlasAsset = options.textureAtlas ? options.textureAtlas.get('id') : null;
        data.renderMode = options.renderMode !== undefined ? options.renderMode : 0;
        return this.upload({
            name: options.name || 'New Sprite',
            type: 'sprite',
            folder: options.folder,
            preload: options.preload,
            data: data
        }, null, options.onProgress);
    }
    /**
     * Creates new text asset
     *
     * @param options.name - The asset name
     * @param options.text - The text
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createText(options = {}) {
        return this.upload({
            name: options.name || 'New Text',
            type: 'text',
            folder: options.folder,
            preload: options.preload,
            filename: 'asset.txt',
            file: new Blob([options.text || '\n'], { type: 'text/plain' })
        }, null, options.onProgress);
    }
    /**
     * Creates new template asset
     *
     * @param options.name - The asset name
     * @param options.entity - The entity to create the template from
     * @param options.folder - The parent folder asset
     * @param options.preload - Whether to preload the asset. Defaults to true.
     * @param options.onProgress - Function to report progress
     * @returns The new asset
     */
    createTemplate(options) {
        return __awaiter(this, undefined, undefined, function* () {
            const { entities, oldToNewIds } = createTemplate(options.entity);
            const asset = yield this.upload({
                name: options.name || options.entity.get('name'),
                type: 'template',
                folder: options.folder,
                preload: options.preload,
                data: { entities }
            }, null, options.onProgress);
            const history = options.entity.history.enabled;
            options.entity.history.enabled = false;
            options.entity.set('template_id', parseInt(asset.get('id'), 10));
            options.entity.set('template_ent_ids', oldToNewIds);
            options.entity.history.enabled = history;
        });
    }
    /**
     * Deletes specified assets
     *
     * @param assets - The assets
     */
    delete(assets) {
        return __awaiter(this, undefined, undefined, function* () {
            const response = yield fetch('/api/assets', {
                body: JSON.stringify({
                    assets: assets.map(a => a.get('id')),
                    branchId: globals.branchId
                }),
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error(`${response.status}: ${response.statusText}`);
            }
            assets.forEach(a => this.remove(a));
        });
    }
    /**
     * Instantiates the specified template assets under the specified
     * parent entity.
     *
     * @param assets - The template assets.
     * @param parent - The parent entity
     * @param options.index - The desired index under the parent to instantiate the templates.
     * @param options.history - Whether to record a history action.
     * @param options.select - Whether to select the new entities.
     * @param options.extraData - Extra data passed to the backend. Used by the Editor on specific cases.
     * @returns The new entities
     */
    instantiateTemplates(assets, parent, options = {}) {
        return instantiateTemplates(assets, parent, options);
    }
    get raw() {
        return this._assets;
    }
    /**
     * Sets the default callback called when on asset upload succeeds.
     * The function takes 2 arguments: the upload id, and the new asset.
     */
    set defaultUploadCompletedCallback(value) {
        this._defaultUploadCompletedCallback = value;
    }
    /**
     * Gets the default callback called when on asset upload succeeds.
     */
    get defaultUploadCompletedCallback() {
        return this._defaultUploadCompletedCallback;
    }
    /**
     * Sets the default callback called when on asset upload progress.
     * The function takes 2 arguments: the upload id and the progress.
     */
    set defaultUploadProgressCallback(value) {
        this._defaultUploadProgressCallback = value;
    }
    /**
     * Gets the default callback called when on asset upload progress.
     */
    get defaultUploadProgressCallback() {
        return this._defaultUploadProgressCallback;
    }
    /**
     * Sets the default callback called when on asset upload progress.
     * The function takes 2 arguments: the upload id, and the error.
     */
    set defaultUploadErrorCallback(value) {
        this._defaultUploadErrorCallback = value;
    }
    /**
     * Gets the default callback called when on asset upload fails.
     */
    get defaultUploadErrorCallback() {
        return this._defaultUploadErrorCallback;
    }
    /**
     * Sets the callback which parses script assets. When this
     * callback is set, new script assets will be parsed after they
     * are created. The function takes the asset as a parameter and returns
     * a promise with a list of script names when it is done parsing.
     */
    set parseScriptCallback(value) {
        this._parseScriptCallback = value;
    }
    /**
     * Gets the callback which parses script assets.
     */
    get parseScriptCallback() {
        return this._parseScriptCallback;
    }
}

/**
 * The Scene Settings API provides access to the settings of the currently loaded scene.
 */
class SceneSettings extends Events {
    /**
     * Creates new instance of the API
     */
    constructor() {
        super();
        this._initializeObserver();
        if (globals.realtime) {
            globals.realtime.on('load:scene', this._onLoadScene.bind(this));
        }
    }
    /**
     * Initialize internal observer and history.
     */
    _initializeObserver() {
        if (!this._observer) {
            this._observer = new Observer();
        }
        if (this._history) {
            this._history.destroy();
        }
        this._history = new ObserverHistory({
            item: this._observer,
            prefix: 'settings.',
            history: globals.history
        });
        this._observer.history = this._history;
    }
    /**
     * Called when a scene is loaded.
     *
     * @param scene - The loaded scene
     */
    _onLoadScene(scene) {
        this._initializeObserver();
        this._history.enabled = false;
        this._observer.patch(scene.data.settings);
        this._history.enabled = true;
        this.emit('load');
    }
    /**
     * Checks if path exists. See the {@link SceneSettings} overview for a full list of properties.
     *
     * @param path - The path
     * @returns True if path exists
     * @example
     * ```javascript
     * console.log(editor.settings.scene.has('render.fog'));
     * ```
     */
    has(path) {
        return this._observer.has(path);
    }
    /**
     * Gets value at path. See the {@link SceneSettings} overview for a full list of properties.
     *
     * @param path - The path
     * @returns The value
     * @example
     * ```javascript
     * console.log(editor.settings.scene.get('render.fog'));
     * ```
     */
    get(path) {
        return this._observer.get(path);
    }
    /**
     * Sets value at path. See the {@link SceneSettings} overview for a full list of properties.
     *
     * @param path - The path
     * @param value - The value
     * @returns Whether the value was set
     * @example
     * ```javascript
     * editor.settings.scene.set('render.fog', 'none');
     * ```
     */
    set(path, value) {
        return this._observer.set(path, value);
    }
    /**
     * Returns JSON representation of scene settings data
     *
     * @returns - The data
     * @example
     * ```javascript
     * console.log(editor.settings.scene.json());
     * ```
     */
    json() {
        return this._observer.json();
    }
    get observer() {
        return this._observer;
    }
    /**
     * Gets the history object for this entity
     */
    get history() {
        return this._history;
    }
}

/**
 * The settings for the Editor.
 */
class Settings extends Events {
    /**
     * Creates new API instance
     */
    constructor() {
        super();
        this._sceneSettings = new SceneSettings();
    }
    /**
     * Gets the settings for the currently loaded scene.
     */
    get scene() {
        return this._sceneSettings;
    }
}

/**
 * The history API responsible for undo / redo.
 */
class History extends Events {
    /**
     * Creates new instance of the API
     *
     * @category Internal
     */
    constructor() {
        super();
        this._history = new History$1();
        this._history.on('add', name => this.emit('add', name));
        this._history.on('undo', name => this.emit('undo', name));
        this._history.on('redo', name => this.emit('redo', name));
        this._history.on('canUndo', value => this.emit('canUndo', value));
        this._history.on('canRedo', value => this.emit('canRedo', value));
    }
    /**
     * Adds history action
     *
     * @param action - The action
     * @example
     * ```javascript
     * const prevSelection = editor.selection.items;
     * editor.history.add({
     *     name: 'clear selection',
     *     redo: () => { editor.selection.clear({ history: false }); },
     *     undo: () => { editor.selection.set(prevSelection, { history: false }); },
     * });
     * ```
     */
    add(action) {
        this._history.add(action);
    }
    /**
     * Adds history action and execute redo
     *
     * @param action - The action
     * @example
     * ```javascript
     * const prevSelection = editor.selection.items;
     * editor.history.addAndExecute({
     *     name: 'clear selection',
     *     redo: () => { editor.selection.clear({ history: false }); },
     *     undo: () => { editor.selection.set(prevSelection, { history: false }); },
     * });
     * ```
     */
    addAndExecute(action) {
        this._history.addAndExecute(action);
    }
    /**
     * Undo last action
     *
     * @example
     * ```javascript
     * editor.history.undo();
     * ```
     */
    undo() {
        this._history.undo();
    }
    /**
     * Redo last action
     *
     * @example
     * ```javascript
     * editor.history.redo();
     * ```
     */
    redo() {
        this._history.redo();
    }
    /**
     * Clear history
     *
     * @example
     * ```javascript
     * editor.history.clear();
     * ```
     */
    clear() {
        this._history.clear();
    }
    /**
     * Gets the current action
     */
    get currentAction() {
        return this._history.currentAction;
    }
    /**
     * Gets the last action
     */
    get lastAction() {
        return this._history.lastAction;
    }
    /**
     * Sets whether there are actions to undo.
     */
    set canUndo(value) {
        this._history.canUndo = value;
    }
    /**
     * Gets whether there are actions to undo.
     */
    get canUndo() {
        return this._history.canUndo;
    }
    /**
     * Sets whether there are actions to redo.
     */
    set canRedo(value) {
        this._history.canRedo = value;
    }
    /**
     * Gets whether there are actions to redo.
     */
    get canRedo() {
        return this._history.canRedo;
    }
}

/**
 * Enables undo / redo of selection changes
 */
class SelectionHistory {
    /**
     * Constructor
     *
     * @category Internal
     */
    constructor(selection) {
        this._selection = selection;
        this._enabled = true;
        this._executingAction = false;
    }
    /**
     * Sets enabled state of selection undo / redo.
     */
    set enabled(value) {
        this._enabled = value;
    }
    /**
     * Gets enabled state of selection undo / redo.
     */
    get enabled() {
        return this._enabled;
    }
    /**
     * Record history action after executing function.
     * The history action will restore the previous selection.
     */
    wrapAction(name, fn) {
        if (!this._enabled || !globals.history || this._executingAction) {
            fn();
            return;
        }
        this._executingAction = true;
        const previousSelection = this._selection.items;
        fn();
        const newSelection = this._selection.items;
        this._executingAction = false;
        globals.history.add({
            name: name,
            combine: false,
            undo: () => {
                // set previous selection making sure every item still exists
                this._selection.set(previousSelection.map((item) => item.latest()).filter((item) => !!item), {
                    history: false
                });
            },
            redo: () => {
                // set new selection making sure every item still exists
                this._selection.set(newSelection.map((item) => item.latest()).filter((item) => !!item), {
                    history: false
                });
            }
        });
    }
}
/**
 * Selection API. Allows selecting Entities, Assets etc.
 */
class Selection extends Events {
    /**
     * Constructor
     *
     * @category Internal
     */
    constructor() {
        super();
        this._history = new SelectionHistory(this);
        this._items = [];
        this._enabled = true;
        this._timeoutChange = null;
    }
    /**
     * Fire 'change' event in timeout
     */
    _deferChangeEvt() {
        if (this._timeoutChange)
            return;
        this._timeoutChange = setTimeout(() => {
            this._timeoutChange = null;
            this.emit('change', this.items);
        });
    }
    /**
     * Add item to selection
     *
     * @example
     * ```javascript
     * // add root entity to selection
     * editor.selection.add(editor.entities.root);
     * ```
     */
    add(item, options = {}) {
        if (!this.enabled)
            return;
        if (this.has(item))
            return;
        if (options.history === undefined) {
            options.history = true;
        }
        const history = this._history.enabled;
        if (!options.history) {
            this._history.enabled = false;
        }
        this._history.wrapAction('select', () => {
            if (this._items[0] && this._items[0].constructor !== item.constructor) {
                this.clear();
            }
            this._items.push(item);
            this.emit('add', item);
            this._deferChangeEvt();
        });
        this._history.enabled = history;
    }
    /**
     * Remove item from selection
     *
     * @example
     * ```javascript
     * // remove root entity from selection
     * editor.selection.remove(editor.entities.root);
     * ```
     */
    remove(item, options = {}) {
        if (!this.enabled)
            return;
        if (options.history === undefined) {
            options.history = true;
        }
        const index = this._items.indexOf(item);
        if (index !== -1) {
            const history = this._history.enabled;
            if (!options.history) {
                this._history.enabled = false;
            }
            this._history.wrapAction('deselect', () => {
                this._items.splice(index, 1);
                this.emit('remove', item);
                this._deferChangeEvt();
            });
            this._history.enabled = history;
        }
    }
    /**
     * Toggle item selection
     *
     * @example
     * ```javascript
     * // toggle root entity selection
     * editor.selection.toggle(editor.entities.root);
     * ```
     */
    toggle(item, options = {}) {
        if (!this.enabled)
            return;
        if (options.history === undefined) {
            options.history = true;
        }
        const history = this._history.enabled;
        if (!options.history) {
            this._history.enabled = false;
        }
        this._history.wrapAction('toggle selection', () => {
            if (this._items[0] && this._items[0].constructor !== item.constructor) {
                this.clear();
            }
            if (this.has(item)) {
                this.remove(item);
            }
            else {
                this.add(item);
            }
        });
        this._history.enabled = history;
    }
    /**
     * Checks if item is in selection
     *
     * @example
     * ```javascript
     * const isRootSelected = editor.selection.has(editor.entities.root);
     * ```
     */
    has(item) {
        return this._items.includes(item);
    }
    /**
     * Clears selection
     *
     * @example
     * ```javascript
     * editor.selection.clear();
     * ```
     */
    clear(options = {}) {
        if (!this.enabled)
            return;
        const length = this._items.length;
        if (!length)
            return;
        if (options.history === undefined) {
            options.history = true;
        }
        const history = this._history.enabled;
        if (!options.history) {
            this._history.enabled = false;
        }
        this._history.wrapAction('deselect', () => {
            let i = length;
            const changed = (i > 0);
            while (i--) {
                const item = this._items[i];
                this._items.splice(i, 1);
                this.emit('remove', item);
            }
            if (changed) {
                this._deferChangeEvt();
            }
        });
        this._history.enabled = history;
    }
    /**
     * Sets current selection
     *
     * @example
     * ```javascript
     * // select root entity
     * editor.selection.set([editor.entities.root]);
     * ```
     */
    set(items, options = {}) {
        if (!this.enabled)
            return;
        if (options.history === undefined) {
            options.history = true;
        }
        const history = this._history.enabled;
        if (!options.history) {
            this._history.enabled = false;
        }
        this._history.wrapAction('modify selection', () => {
            // remove items no longer selected
            const removed = this._items.filter(item => !items.includes(item));
            removed.forEach((item) => {
                this.remove(item);
            });
            // add new items
            items.forEach(item => this.add(item));
        });
        this._history.enabled = history;
    }
    /**
     * Gets the selected items. This creates a new array every time it is called.
     *
     * @example
     * ```javascript
     * editor.selection.items.add(editor.entities.root);
     * const selectedEntities = editor.selection.items;
     * ```
     */
    get items() {
        return this._items.slice();
    }
    /**
     * Gets the first selected item. Short for this.items[0].
     */
    get item() {
        return this._items[0];
    }
    /**
     * Sets enabled state of the selection methods.
     */
    set enabled(value) {
        this._enabled = value;
    }
    /**
     * Gets enabled state of the selection methods.
     */
    get enabled() {
        return this._enabled;
    }
    /**
     * Gets the number of selected items
     */
    get count() {
        return this._items.length;
    }
    /**
     * Gets the selection history
     */
    get history() {
        return this._history;
    }
}

/**
 * Provides methods to access the Assets schema
 */
class AssetsSchema {
    /**
     * @category Internal
     * @param schema - The schema API
     */
    constructor(schema) {
        this._schemaApi = schema;
        this._schema = this._schemaApi.schema;
    }
    /**
     * Gets default data for asset type
     *
     * @param type - The asset type
     * @returns The default data
     */
    getDefaultData(type) {
        const field = `${type}Data`;
        if (!field || !this._schema[field])
            return null;
        const result = {};
        const schema = this._schema[field];
        for (const key in schema) {
            if (key.startsWith('$'))
                continue;
            const block = schema[key];
            if (block.hasOwnProperty('$default')) {
                result[key] = utils.deepCopy(block.$default);
            }
        }
        return result;
    }
    /**
     * Gets a list of fields of a particular type for an asset type
     *
     * @param assetType - The type of the asset.
     * @param type - The desired type
     * @returns A list of fields
     * @example
     * ```javascript
     * const materialAssetPaths = editor.schema.assets.getFieldsOfType('material', 'asset');
     * ```
     */
    getFieldsOfType(assetType, type) {
        const result = [];
        const recurse = (schemaField, path, prefix = '') => {
            if (!schemaField)
                return;
            if (schemaField.$editorType === type || schemaField.$editorType === `array:${type}`) {
                result.push(prefix + path);
                return;
            }
            for (const field in schemaField) {
                if (field.startsWith('$'))
                    continue;
                const p = (path ? `${path}.` : '') + field;
                const fieldType = this._schemaApi.getType(schemaField[field]);
                if (fieldType === type || fieldType === `array:${type}`) {
                    result.push(prefix + p);
                }
                else if (fieldType === 'object' && schemaField[field].$of) {
                    recurse(schemaField[field].$of, `${p}.*`, prefix);
                }
                else if (fieldType === 'array:object' && Array.isArray(schemaField[field].$type)) {
                    recurse(schemaField[field].$type[0], `${p}.*`, prefix);
                }
            }
        };
        recurse(this._schema[`${assetType}Data`], '', 'data.');
        return result;
    }
}

/**
 * Provides methods to access the components schema
 */
class ComponentSchema {
    /**
     * Creates new instance of API
     *
     * @category Internal
     * @param schema - The schema API
     */
    constructor(schema) {
        this._schemaApi = schema;
        this._schema = this._schemaApi.schema.scene.entities.$of.components;
    }
    _resolveLazyDefaults(defaults) {
        // Any functions in the default property set are used to provide
        // lazy resolution, to handle cases where the values are not known
        // at startup time.
        Object.keys(defaults).forEach((key) => {
            const value = defaults[key];
            if (typeof value === 'function') {
                defaults[key] = value();
            }
        });
    }
    /**
     * Gets default data for a component
     *
     * @param component - The component name
     * @returns The default data
     * @example
     * ```javascript
     * const modelData = editor.schema.components.getDefaultData('model');
     * ```
     */
    getDefaultData(component) {
        const result = {};
        for (const fieldName in this._schema[component]) {
            if (fieldName.startsWith('$'))
                continue;
            const field = this._schema[component][fieldName];
            if (field.hasOwnProperty('$default')) {
                result[fieldName] = utils.deepCopy(field.$default);
            }
        }
        this._resolveLazyDefaults(result);
        return result;
    }
    /**
     * Gets a list of fields of a particular type for a component
     *
     * @param componentName - The component name
     * @param type - The desired type
     * @returns A list of fields
     * @example
     * ```javascript
     * const buttonEntityFields = editor.schema.components.getFieldsOfType('button', 'entity');
     * ```
     */
    getFieldsOfType(componentName, type) {
        const result = [];
        const recurse = (schemaField, path) => {
            if (!schemaField)
                return;
            if (schemaField.$editorType === type || schemaField.$editorType === `array:${type}`) {
                result.push(path);
                return;
            }
            for (const field in schemaField) {
                if (field.startsWith('$'))
                    continue;
                const p = (path ? `${path}.` : '') + field;
                const fieldType = this._schemaApi.getType(schemaField[field]);
                if (fieldType === type || fieldType === `array:${type}`) {
                    result.push(p);
                }
                else if (fieldType === 'object' && schemaField[field].$of) {
                    recurse(schemaField[field].$of, `${p}.*`);
                }
            }
        };
        recurse(this._schema[componentName], '');
        return result;
    }
    /**
     * Gets a list of all the available components
     *
     * @returns The components
     */
    list() {
        const result = Object.keys(this._schema);
        result.sort();
        // filter out zone (which is not really supported)
        const idx = result.indexOf('zone');
        if (idx !== -1) {
            result.splice(idx, 1);
        }
        return result;
    }
}

/**
 * Provides methods to access the render schema
 */
class SceneSchema {
    /**
     * Creates new instance of API
     *
     * @category Internal
     * @param schema - The schema API
     */
    constructor(schema) {
        this._schemaApi = schema;
        this._schema = this._schemaApi.schema.scene;
    }
    _getDefaultData(obj) {
        const result = {};
        for (const key in obj) {
            if (key.startsWith('$'))
                continue;
            if (!(obj[key] instanceof Object))
                continue;
            if (obj[key].hasOwnProperty('$default')) {
                result[key] = utils.deepCopy(obj[key].$default);
                continue;
            }
            else {
                const subDefaults = this._getDefaultData(obj[key]);
                if (Object.keys(subDefaults).length) {
                    result[key] = subDefaults;
                }
            }
        }
        return result;
    }
    /**
     * Get the default physics scene settings for the project
     *
     * @returns The default physics scene settings
     * @example
     * ```javascript
     * const scenePhysicsSettings = editor.schema.scene.getDefaultPhysicsSettings();
     * ```
     */
    getDefaultPhysicsSettings() {
        return this._getDefaultData(this._schema.settings.physics);
    }
    /**
     * Get the default render scene settings for the project
     *
     * @returns The default physics scene settings
     * @example
     * ```javascript
     * const sceneRenderSettings = editor.schema.scene.getDefaultRenderSettings();
     * ```
     */
    getDefaultRenderSettings() {
        return this._getDefaultData(this._schema.settings.render);
    }
}

/**
 * Provides methods to access the settings schema
 */
class SettingsSchema {
    /**
     * Creates new instance of API
     *
     * @category Internal
     * @param schema - The schema API
     */
    constructor(schema) {
        this._schemaApi = schema;
        this._schema = this._schemaApi.schema.settings;
    }
    _getDefaultData(obj, scope) {
        const result = {};
        for (const key in obj) {
            if (key.startsWith('$'))
                continue;
            if (!(obj[key] instanceof Object))
                continue;
            if (obj[key].hasOwnProperty('$default')) {
                if (obj[key].$scope === scope) {
                    result[key] = utils.deepCopy(obj[key].$default);
                }
                continue;
            }
            else {
                const subDefaults = this._getDefaultData(obj[key], scope);
                if (Object.keys(subDefaults).length) {
                    result[key] = subDefaults;
                }
            }
        }
        return result;
    }
    /**
     * Get the default settings for the project
     *
     * @returns The default settings for the project
     * @example
     * ```javascript
     * const projectSettings = editor.schema.settings.getDefaultProjectSettings();
     * ```
     */
    getDefaultProjectSettings() {
        return this._getDefaultData(this._schema, 'project');
    }
    /**
     * Get the default settings for the user
     *
     * @returns The default settings for the user
     * @example
     * ```javascript
     * const userSettings = editor.schema.settings.getDefaultUserSettings();
     * ```
     */
    getDefaultUserSettings() {
        return this._getDefaultData(this._schema, 'user');
    }
    /**
     * Get the default settings for the user in the project
     *
     * @returns The default settings for the user in the project
     * @example
     * ```javascript
     * const projectUserSettings = editor.schema.settings.getDefaultProjectUserSettings();
     * ```
     */
    getDefaultProjectUserSettings() {
        return this._getDefaultData(this._schema, 'projectUser');
    }
}

/**
 * Provides methods to access the Editor schema.
 */
class Schema {
    /**
     * Creates new instance of API
     */
    constructor(schema) {
        this._schema = schema;
        this._assetsSchema = new AssetsSchema(this);
        this._componentSchema = new ComponentSchema(this);
        this._sceneSchema = new SceneSchema(this);
        this._settingsSchema = new SettingsSchema(this);
    }
    /**
     * Gets the schema
     */
    get schema() {
        return this._schema;
    }
    /**
     * Gets the assets schema
     */
    get assets() {
        return this._assetsSchema;
    }
    /**
     * Gets the component schema
     */
    get components() {
        return this._componentSchema;
    }
    /**
     * Gets the scene schema
     */
    get scene() {
        return this._sceneSchema;
    }
    /**
     * Gets the settings schema
     */
    get settings() {
        return this._settingsSchema;
    }
    /**
     * Converts the specified schema field to a type recursively.
     *
     * @category Internal
     */
    getType(field, fixedLength = 0) {
        if (typeof field === 'string') {
            if (field === 'map' || field === 'mixed') {
                field = 'object';
            }
            return field.toLowerCase();
        }
        if (field.$editorType) {
            return field.$editorType;
        }
        if (Array.isArray(field)) {
            if (field[0] === 'number' && fixedLength) {
                if (fixedLength === 2) {
                    return 'vec2';
                }
                else if (fixedLength === 3) {
                    return 'vec3';
                }
                else if (fixedLength === 4) {
                    return 'vec4';
                }
            }
            return `array:${this.getType(field[0])}`;
        }
        if (field.$type) {
            return this.getType(field.$type, field.$length);
        }
        return 'object';
    }
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getAugmentedNamespace(n) {
  if (n.__esModule) return n;
  var f = n.default;
	if (typeof f == "function") {
		var a = function a () {
			if (this instanceof a) {
        return Reflect.construct(f, arguments, this.constructor);
			}
			return f.apply(this, arguments);
		};
		a.prototype = f.prototype;
  } else a = {};
  Object.defineProperty(a, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

var text$2 = {};

/* Text OT!
 *
 * This is an OT implementation for text. It is the standard implementation of
 * text used by ShareJS.
 *
 * This type is composable but non-invertable. Its similar to ShareJS's old
 * text-composable type, but its not invertable and its very similar to the
 * text-tp2 implementation but it doesn't support tombstones or purging.
 *
 * Ops are lists of components which iterate over the document.
 * Components are either:
 *   A number N: Skip N characters in the original document
 *   "str"     : Insert "str" at the current position in the document
 *   {d:N}     : Delete N characters at the current position in the document
 *
 * Eg: [3, 'hi', 5, {d:8}]
 *
 * The operation does not have to skip the last characters in the document.
 *
 * Snapshots are strings.
 *
 * Cursors are either a single number (which is the cursor position) or a pair of
 * [anchor, focus] (aka [start, end]). Be aware that end can be before start.
 */

/** @module text */

text$2.name = 'text';
text$2.uri = 'http://sharejs.org/types/textv1';

/** Create a new text snapshot.
 *
 * @param {string} initial - initial snapshot data. Optional. Defaults to ''.
 */
text$2.create = (initial) => {
  if ((initial != null) && typeof initial !== 'string') {
    throw Error('Initial data must be a string')
  }
  return initial || ''
};

/** Check the operation is valid. Throws if not valid. */
const checkOp = function(op) {
  if (!Array.isArray(op)) throw Error('Op must be an array of components');

  let last = null;
  for (let i = 0; i < op.length; i++) {
    const c = op[i];
    switch (typeof c) {
      case 'object':
        // The only valid objects are {d:X} for +ive values of X.
        if (!(typeof c.d === 'number' && c.d > 0)) throw Error('Object components must be deletes of size > 0')
        break
      case 'string':
        // Strings are inserts.
        if (!(c.length > 0)) throw Error('Inserts cannot be empty')
        break
      case 'number':
        // Numbers must be skips. They have to be +ive numbers.
        if (!(c > 0)) throw Error('Skip components must be >0')
        if (typeof last === 'number') throw Error('Adjacent skip components should be combined')
        break
    }
    last = c;
  }

  if (typeof last === 'number') throw Error('Op has a trailing skip')
};

/** Make a function that appends to the given operation. */
const makeAppend = op => component => {
  if (!component || component.d === 0) ; else if (op.length === 0) {
    op.push(component);

  } else if (typeof component === typeof op[op.length - 1]) {
    if (typeof component === 'object') {
      op[op.length - 1].d += component.d;
    } else {
      op[op.length - 1] += component;
    }
  } else {
    op.push(component);
  }
};

/** Makes and returns utility functions take and peek. */
const makeTake = function(op) {
  // The index of the next component to take
  let idx = 0;
  // The offset into the component
  let offset = 0;

  // Take up to length n from the front of op. If n is -1, take the entire next
  // op component. If indivisableField == 'd', delete components won't be separated.
  // If indivisableField == 'i', insert components won't be separated.
  const take = (n, indivisableField) => {
    // We're at the end of the operation. The op has skips, forever. Infinity
    // might make more sense than null here.
    if (idx === op.length)
      return n === -1 ? null : n

    const c = op[idx];
    let part;
    if (typeof c === 'number') {
      // Skip
      if (n === -1 || c - offset <= n) {
        part = c - offset;
        ++idx;
        offset = 0;
        return part
      } else {
        offset += n;
        return n
      }
    } else if (typeof c === 'string') {
      // Insert
      if (n === -1 || indivisableField === 'i' || c.length - offset <= n) {
        part = c.slice(offset);
        ++idx;
        offset = 0;
        return part
      } else {
        part = c.slice(offset, offset + n);
        offset += n;
        return part
      }
    } else {
      // Delete
      if (n === -1 || indivisableField === 'd' || c.d - offset <= n) {
        part = {d: c.d - offset};
        ++idx;
        offset = 0;
        return part
      } else {
        offset += n;
        return {d: n}
      }
    }
  };

  // Peek at the next op that will be returned.
  const peekType = () => op[idx];

  return [take, peekType]
};

/** Get the length of a component */
const componentLength = c => typeof c === 'number' ? c : (c.length || c.d);

/** Trim any excess skips from the end of an operation.
 *
 * There should only be at most one, because the operation was made with append.
 */
const trim = op => {
  if (op.length > 0 && typeof op[op.length - 1] === 'number') {
    op.pop();
  }
  return op
};

text$2.normalize = function(op) {
  const newOp = [];
  const append = makeAppend(newOp);
  for (let i = 0; i < op.length; i++) append(op[i]);
  return trim(newOp)
};

/** Apply an operation to a document snapshot */
text$2.apply = function(str, op) {
  if (typeof str !== 'string') {
    throw Error('Snapshot should be a string')
  }
  checkOp(op);

  // We'll gather the new document here and join at the end.
  const newDoc = [];

  for (let i = 0; i < op.length; i++) {
    const component = op[i];
    switch (typeof component) {
      case 'number':
        if (component > str.length) throw Error('The op is too long for this document')

        newDoc.push(str.slice(0, component));
        // This might be slow for big strings. Consider storing the offset in
        // str instead of rewriting it each time.
        str = str.slice(component);
        break
      case 'string':
        newDoc.push(component);
        break
      case 'object':
        str = str.slice(component.d);
        break
    }
  }

  return newDoc.join('') + str
};

/** Transform op by otherOp.
 *
 * @param op - The operation to transform
 * @param otherOp - Operation to transform it by
 * @param side - Either 'left' or 'right'
 */
text$2.transform = function(op, otherOp, side) {
  if (side !== 'left' && side !== 'right') {
    throw Error("side (" + side + ") must be 'left' or 'right'")
  }

  checkOp(op);
  checkOp(otherOp);

  const newOp = [];

  const append = makeAppend(newOp);
  const [take, peek] = makeTake(op);

  for (let i = 0; i < otherOp.length; i++) {
    const component = otherOp[i];

    let length, chunk;
    switch (typeof component) {
      case 'number': // Skip
        length = component;
        while (length > 0) {
          chunk = take(length, 'i');
          append(chunk);
          if (typeof chunk !== 'string') {
            length -= componentLength(chunk);
          }
        }
        break

      case 'string': // Insert
        if (side === 'left') {
          // The left insert should go first.
          if (typeof peek() === 'string') {
            append(take(-1));
          }
        }

        // Otherwise skip the inserted text.
        append(component.length);
        break

      case 'object': // Delete
        length = component.d;
        while (length > 0) {
          chunk = take(length, 'i');
          switch (typeof chunk) {
            case 'number':
              length -= chunk;
              break
            case 'string':
              append(chunk);
              break
            case 'object':
              // The delete is unnecessary now - the text has already been deleted.
              length -= chunk.d;
          }
        }
        break
    }
  }
  
  // Append any extra data in op1.
  let c;
  while ((c = take(-1))) append(c);
  
  return trim(newOp)
};

/** Compose op1 and op2 together and return the result */
text$2.compose = function(op1, op2) {
  checkOp(op1);
  checkOp(op2);

  const result = [];
  const append = makeAppend(result);
  const take = makeTake(op1)[0];

  for (let i = 0; i < op2.length; i++) {
    const component = op2[i];
    let length, chunk;
    switch (typeof component) {
      case 'number': // Skip
        length = component;
        while (length > 0) {
          chunk = take(length, 'd');
          append(chunk);
          if (typeof chunk !== 'object') {
            length -= componentLength(chunk);
          }
        }
        break

      case 'string': // Insert
        append(component);
        break

      case 'object': // Delete
        length = component.d;

        while (length > 0) {
          chunk = take(length, 'd');

          switch (typeof chunk) {
            case 'number':
              append({d: chunk});
              length -= chunk;
              break
            case 'string':
              length -= chunk.length;
              break
            case 'object':
              append(chunk);
          }
        }
        break
    }
  }

  let c;
  while ((c = take(-1))) append(c);

  return trim(result)
};


const transformPosition$1 = (cursor, op) => {
  let pos = 0;
  for (let i = 0; i < op.length; i++) {
    const c = op[i];
    if (cursor <= pos) break

    // I could actually use the op_iter stuff above - but I think its simpler
    // like this.
    switch (typeof c) {
      case 'number':
        if (cursor <= pos + c) return cursor
        pos += c;
        break

      case 'string':
        pos += c.length;
        cursor += c.length;
        break

      case 'object':
        cursor -= Math.min(c.d, cursor - pos);
        break
    }
  }
  return cursor
};

text$2.transformSelection = function(selection, op, isOwnOp) {
  let pos = 0;
  if (isOwnOp) {
    // Just track the position. We'll teleport the cursor to the end anyway.
    // This works because text ops don't have any trailing skips at the end - so the last
    // component is the last thing.
    for (let i = 0; i < op.length; i++) {
      const c = op[i];
      switch (typeof c) {
        case 'number':
          pos += c;
          break
        case 'string':
          pos += c.length;
          break
        // Just eat deletes.
      }
    }
    return pos
  } else {
    return typeof selection === 'number'
      ? transformPosition$1(selection, op)
      : [transformPosition$1(selection[0], op), transformPosition$1(selection[1], op)]
  }
};

text$2.selectionEq = function(c1, c2) {
  if (c1[0] != null && c1[0] === c1[1]) c1 = c1[0];
  if (c2[0] != null && c2[0] === c2[1]) c2 = c2[0];
  return c1 === c2 || (c1[0] != null && c2[0] != null && c1[0] === c2[0] && c1[1] == c2[1])
};

// Calculate the cursor position after the given operation
text$2.applyToCursor = function (op) {
  var pos = 0;
  for (var i = 0; i < op.length; i++) {
      var c = op[i];
      switch (typeof c) {
          case 'number':
              pos += c;
              break;
          case 'string':
              pos += c.length;
              break;
      }
  }
  return pos;
};

// Generate an operation that semantically inverts the given operation
// when applied to the provided snapshot.
// It needs a snapshot of the document before the operation
// was applied to invert delete operations.
text$2.semanticInvert = function (str, op) {
  if (typeof str !== 'string') {
      throw Error('Snapshot should be a string');
  }

  // eslint-disable-next-line no-undef
  checkOp(op);

  // Save copy
  var originalOp = op.slice();

  // Shallow copy
  op = op.slice();

  var len = op.length;
  var cursor, prevOps, tmpStr;
  for (var i = 0; i < len; i++) {
      var c = op[i];
      switch (typeof c) {
          case 'number':
              // In case we have cursor movement we do nothing
              break;
          case 'string':
              // In case we have string insertion we generate a string deletion
              op[i] = { d: c.length };
              break;
          case 'object':
              // In case of a deletion we need to reinsert the deleted string
              prevOps = originalOp.slice(0, i);
              cursor = this.applyToCursor(prevOps);
              // eslint-disable-next-line no-undef
              tmpStr = this.apply(str, trim(prevOps));
              op[i] = tmpStr.substring(cursor, cursor + c.d);
              break;
      }
  }

  return this.normalize(op);
};

// Text document API for the 'text' type. This implements some standard API
// methods for any text-like type, so you can easily bind a textarea or
// something without being fussy about the underlying OT implementation.
//
// The API is desigend as a set of functions to be mixed in to some context
// object as part of its lifecycle. It expects that object to have getSnapshot
// and submitOp methods, and call _onOp when an operation is received.
//
// This API defines:
//
// - getLength() returns the length of the document in characters
// - getText() returns a string of the document
// - insert(pos, text, [callback]) inserts text at position pos in the document
// - remove(pos, length, [callback]) removes length characters at position pos
//
// A user can define:
// - onInsert(pos, text): Called when text is inserted.
// - onRemove(pos, length): Called when text is removed.

var api_1 = api;
function api(getSnapshot, submitOp) {
  return {
    // Returns the text content of the document
    get: getSnapshot,

    // Returns the number of characters in the string
    getLength() { return getSnapshot().length },

    // Insert the specified text at the given position in the document
    insert(pos, text, callback) {
      return submitOp([pos, text], callback)
    },

    remove(pos, length, callback) {
      return submitOp([pos, {d:length}], callback)
    },

    // When you use this API, you should implement these two methods
    // in your editing context.
    //onInsert: function(pos, text) {},
    //onRemove: function(pos, removedLength) {},

    _onOp(op) {
      var pos = 0;
      var spos = 0;
      for (var i = 0; i < op.length; i++) {
        var component = op[i];
        switch (typeof component) {
          case 'number':
            pos += component;
            spos += component;
            break
          case 'string':
            if (this.onInsert) this.onInsert(pos, component);
            pos += component.length;
            break
          case 'object':
            if (this.onRemove) this.onRemove(pos, component.d);
            spos += component.d;
        }
      }
    }
  }
}
api.provides = {text: true};

var type = text$2;
type.api = api_1;

var lib$1 = {
  type: type
};

var emitter$6 = {};

var domain;

// This constructor is used to store event handlers. Instantiating this is
// faster than explicitly calling `Object.create(null)` to get a "clean" empty
// object (tested with v8 v4.9).
function EventHandlers() {}
EventHandlers.prototype = Object.create(null);

function EventEmitter$2() {
  EventEmitter$2.init.call(this);
}

// nodejs oddity
// require('events') === require('events').EventEmitter
EventEmitter$2.EventEmitter = EventEmitter$2;

EventEmitter$2.usingDomains = false;

EventEmitter$2.prototype.domain = undefined;
EventEmitter$2.prototype._events = undefined;
EventEmitter$2.prototype._maxListeners = undefined;

// By default EventEmitters will print a warning if more than 10 listeners are
// added to it. This is a useful default which helps finding memory leaks.
EventEmitter$2.defaultMaxListeners = 10;

EventEmitter$2.init = function() {
  this.domain = null;
  if (EventEmitter$2.usingDomains) {
    // if there is an active domain, then attach to it.
    if (domain.active) ;
  }

  if (!this._events || this._events === Object.getPrototypeOf(this)._events) {
    this._events = new EventHandlers();
    this._eventsCount = 0;
  }

  this._maxListeners = this._maxListeners || undefined;
};

// Obviously not all Emitters should be limited to 10. This function allows
// that to be increased. Set to zero for unlimited.
EventEmitter$2.prototype.setMaxListeners = function setMaxListeners(n) {
  if (typeof n !== 'number' || n < 0 || isNaN(n))
    throw new TypeError('"n" argument must be a positive number');
  this._maxListeners = n;
  return this;
};

function $getMaxListeners(that) {
  if (that._maxListeners === undefined)
    return EventEmitter$2.defaultMaxListeners;
  return that._maxListeners;
}

EventEmitter$2.prototype.getMaxListeners = function getMaxListeners() {
  return $getMaxListeners(this);
};

// These standalone emit* functions are used to optimize calling of event
// handlers for fast cases because emit() itself often has a variable number of
// arguments and can be deoptimized because of that. These functions always have
// the same number of arguments and thus do not get deoptimized, so the code
// inside them can execute faster.
function emitNone(handler, isFn, self) {
  if (isFn)
    handler.call(self);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self);
  }
}
function emitOne(handler, isFn, self, arg1) {
  if (isFn)
    handler.call(self, arg1);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self, arg1);
  }
}
function emitTwo(handler, isFn, self, arg1, arg2) {
  if (isFn)
    handler.call(self, arg1, arg2);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self, arg1, arg2);
  }
}
function emitThree(handler, isFn, self, arg1, arg2, arg3) {
  if (isFn)
    handler.call(self, arg1, arg2, arg3);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self, arg1, arg2, arg3);
  }
}

function emitMany(handler, isFn, self, args) {
  if (isFn)
    handler.apply(self, args);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].apply(self, args);
  }
}

EventEmitter$2.prototype.emit = function emit(type) {
  var er, handler, len, args, i, events, domain;
  var doError = (type === 'error');

  events = this._events;
  if (events)
    doError = (doError && events.error == null);
  else if (!doError)
    return false;

  domain = this.domain;

  // If there is no 'error' event listener then throw.
  if (doError) {
    er = arguments[1];
    if (domain) {
      if (!er)
        er = new Error('Uncaught, unspecified "error" event');
      er.domainEmitter = this;
      er.domain = domain;
      er.domainThrown = false;
      domain.emit('error', er);
    } else if (er instanceof Error) {
      throw er; // Unhandled 'error' event
    } else {
      // At least give some kind of context to the user
      var err = new Error('Uncaught, unspecified "error" event. (' + er + ')');
      err.context = er;
      throw err;
    }
    return false;
  }

  handler = events[type];

  if (!handler)
    return false;

  var isFn = typeof handler === 'function';
  len = arguments.length;
  switch (len) {
    // fast cases
    case 1:
      emitNone(handler, isFn, this);
      break;
    case 2:
      emitOne(handler, isFn, this, arguments[1]);
      break;
    case 3:
      emitTwo(handler, isFn, this, arguments[1], arguments[2]);
      break;
    case 4:
      emitThree(handler, isFn, this, arguments[1], arguments[2], arguments[3]);
      break;
    // slower
    default:
      args = new Array(len - 1);
      for (i = 1; i < len; i++)
        args[i - 1] = arguments[i];
      emitMany(handler, isFn, this, args);
  }

  return true;
};

function _addListener(target, type, listener, prepend) {
  var m;
  var events;
  var existing;

  if (typeof listener !== 'function')
    throw new TypeError('"listener" argument must be a function');

  events = target._events;
  if (!events) {
    events = target._events = new EventHandlers();
    target._eventsCount = 0;
  } else {
    // To avoid recursion in the case that type === "newListener"! Before
    // adding it to the listeners, first emit "newListener".
    if (events.newListener) {
      target.emit('newListener', type,
                  listener.listener ? listener.listener : listener);

      // Re-assign `events` because a newListener handler could have caused the
      // this._events to be assigned to a new object
      events = target._events;
    }
    existing = events[type];
  }

  if (!existing) {
    // Optimize the case of one listener. Don't need the extra array object.
    existing = events[type] = listener;
    ++target._eventsCount;
  } else {
    if (typeof existing === 'function') {
      // Adding the second element, need to change to array.
      existing = events[type] = prepend ? [listener, existing] :
                                          [existing, listener];
    } else {
      // If we've already got an array, just append.
      if (prepend) {
        existing.unshift(listener);
      } else {
        existing.push(listener);
      }
    }

    // Check for listener leak
    if (!existing.warned) {
      m = $getMaxListeners(target);
      if (m && m > 0 && existing.length > m) {
        existing.warned = true;
        var w = new Error('Possible EventEmitter memory leak detected. ' +
                            existing.length + ' ' + type + ' listeners added. ' +
                            'Use emitter.setMaxListeners() to increase limit');
        w.name = 'MaxListenersExceededWarning';
        w.emitter = target;
        w.type = type;
        w.count = existing.length;
        emitWarning(w);
      }
    }
  }

  return target;
}
function emitWarning(e) {
  typeof console.warn === 'function' ? console.warn(e) : console.log(e);
}
EventEmitter$2.prototype.addListener = function addListener(type, listener) {
  return _addListener(this, type, listener, false);
};

EventEmitter$2.prototype.on = EventEmitter$2.prototype.addListener;

EventEmitter$2.prototype.prependListener =
    function prependListener(type, listener) {
      return _addListener(this, type, listener, true);
    };

function _onceWrap(target, type, listener) {
  var fired = false;
  function g() {
    target.removeListener(type, g);
    if (!fired) {
      fired = true;
      listener.apply(target, arguments);
    }
  }
  g.listener = listener;
  return g;
}

EventEmitter$2.prototype.once = function once(type, listener) {
  if (typeof listener !== 'function')
    throw new TypeError('"listener" argument must be a function');
  this.on(type, _onceWrap(this, type, listener));
  return this;
};

EventEmitter$2.prototype.prependOnceListener =
    function prependOnceListener(type, listener) {
      if (typeof listener !== 'function')
        throw new TypeError('"listener" argument must be a function');
      this.prependListener(type, _onceWrap(this, type, listener));
      return this;
    };

// emits a 'removeListener' event iff the listener was removed
EventEmitter$2.prototype.removeListener =
    function removeListener(type, listener) {
      var list, events, position, i, originalListener;

      if (typeof listener !== 'function')
        throw new TypeError('"listener" argument must be a function');

      events = this._events;
      if (!events)
        return this;

      list = events[type];
      if (!list)
        return this;

      if (list === listener || (list.listener && list.listener === listener)) {
        if (--this._eventsCount === 0)
          this._events = new EventHandlers();
        else {
          delete events[type];
          if (events.removeListener)
            this.emit('removeListener', type, list.listener || listener);
        }
      } else if (typeof list !== 'function') {
        position = -1;

        for (i = list.length; i-- > 0;) {
          if (list[i] === listener ||
              (list[i].listener && list[i].listener === listener)) {
            originalListener = list[i].listener;
            position = i;
            break;
          }
        }

        if (position < 0)
          return this;

        if (list.length === 1) {
          list[0] = undefined;
          if (--this._eventsCount === 0) {
            this._events = new EventHandlers();
            return this;
          } else {
            delete events[type];
          }
        } else {
          spliceOne(list, position);
        }

        if (events.removeListener)
          this.emit('removeListener', type, originalListener || listener);
      }

      return this;
    };
    
// Alias for removeListener added in NodeJS 10.0
// https://nodejs.org/api/events.html#events_emitter_off_eventname_listener
EventEmitter$2.prototype.off = function(type, listener){
    return this.removeListener(type, listener);
};

EventEmitter$2.prototype.removeAllListeners =
    function removeAllListeners(type) {
      var listeners, events;

      events = this._events;
      if (!events)
        return this;

      // not listening for removeListener, no need to emit
      if (!events.removeListener) {
        if (arguments.length === 0) {
          this._events = new EventHandlers();
          this._eventsCount = 0;
        } else if (events[type]) {
          if (--this._eventsCount === 0)
            this._events = new EventHandlers();
          else
            delete events[type];
        }
        return this;
      }

      // emit removeListener for all listeners on all events
      if (arguments.length === 0) {
        var keys = Object.keys(events);
        for (var i = 0, key; i < keys.length; ++i) {
          key = keys[i];
          if (key === 'removeListener') continue;
          this.removeAllListeners(key);
        }
        this.removeAllListeners('removeListener');
        this._events = new EventHandlers();
        this._eventsCount = 0;
        return this;
      }

      listeners = events[type];

      if (typeof listeners === 'function') {
        this.removeListener(type, listeners);
      } else if (listeners) {
        // LIFO order
        do {
          this.removeListener(type, listeners[listeners.length - 1]);
        } while (listeners[0]);
      }

      return this;
    };

EventEmitter$2.prototype.listeners = function listeners(type) {
  var evlistener;
  var ret;
  var events = this._events;

  if (!events)
    ret = [];
  else {
    evlistener = events[type];
    if (!evlistener)
      ret = [];
    else if (typeof evlistener === 'function')
      ret = [evlistener.listener || evlistener];
    else
      ret = unwrapListeners(evlistener);
  }

  return ret;
};

EventEmitter$2.listenerCount = function(emitter, type) {
  if (typeof emitter.listenerCount === 'function') {
    return emitter.listenerCount(type);
  } else {
    return listenerCount.call(emitter, type);
  }
};

EventEmitter$2.prototype.listenerCount = listenerCount;
function listenerCount(type) {
  var events = this._events;

  if (events) {
    var evlistener = events[type];

    if (typeof evlistener === 'function') {
      return 1;
    } else if (evlistener) {
      return evlistener.length;
    }
  }

  return 0;
}

EventEmitter$2.prototype.eventNames = function eventNames() {
  return this._eventsCount > 0 ? Reflect.ownKeys(this._events) : [];
};

// About 1.5x faster than the two-arg version of Array#splice().
function spliceOne(list, index) {
  for (var i = index, k = i + 1, n = list.length; k < n; i += 1, k += 1)
    list[i] = list[k];
  list.pop();
}

function arrayClone(arr, i) {
  var copy = new Array(i);
  while (i--)
    copy[i] = arr[i];
  return copy;
}

function unwrapListeners(arr) {
  var ret = new Array(arr.length);
  for (var i = 0; i < ret.length; ++i) {
    ret[i] = arr[i].listener || arr[i];
  }
  return ret;
}

var _polyfillNode_events = /*#__PURE__*/Object.freeze({
    __proto__: null,
    EventEmitter: EventEmitter$2,
    default: EventEmitter$2
});

var require$$1 = /*@__PURE__*/getAugmentedNamespace(_polyfillNode_events);

var EventEmitter$1 = require$$1.EventEmitter;

emitter$6.EventEmitter = EventEmitter$1;
emitter$6.mixin = mixin;

function mixin(Constructor) {
  for (var key in EventEmitter$1.prototype) {
    Constructor.prototype[key] = EventEmitter$1.prototype[key];
  }
}

var SUPPORTED_METHODS = [
  'info',
  'warn',
  'error'
];

function Logger$1() {
  var defaultMethods = {};
  SUPPORTED_METHODS.forEach(function(method) {
    // Deal with Chrome issue: https://bugs.chromium.org/p/chromium/issues/detail?id=179628
    defaultMethods[method] = console[method].bind(console);
  });
  this.setMethods(defaultMethods);
}
var logger$3 = Logger$1;

Logger$1.prototype.setMethods = function(overrides) {
  overrides = overrides || {};
  var logger = this;

  SUPPORTED_METHODS.forEach(function(method) {
    if (typeof overrides[method] === 'function') {
      logger[method] = overrides[method];
    }
  });
};

var Logger = logger$3;
var logger$2 = new Logger();
var logger_1 = logger$2;

function ShareDBError$3(code, message) {
  this.code = code;
  this.message = message || '';
  if (Error.captureStackTrace) {
    Error.captureStackTrace(this, ShareDBError$3);
  } else {
    this.stack = new Error().stack;
  }
}

ShareDBError$3.prototype = Object.create(Error.prototype);
ShareDBError$3.prototype.constructor = ShareDBError$3;
ShareDBError$3.prototype.name = 'ShareDBError';

ShareDBError$3.CODES = {
  ERR_APPLY_OP_VERSION_DOES_NOT_MATCH_SNAPSHOT: 'ERR_APPLY_OP_VERSION_DOES_NOT_MATCH_SNAPSHOT',
  ERR_APPLY_SNAPSHOT_NOT_PROVIDED: 'ERR_APPLY_SNAPSHOT_NOT_PROVIDED',
  ERR_FIXUP_IS_ONLY_VALID_ON_APPLY: 'ERR_FIXUP_IS_ONLY_VALID_ON_APPLY',
  ERR_CANNOT_FIXUP_DELETION: 'ERR_CANNOT_FIXUP_DELETION',
  ERR_CLIENT_ID_BADLY_FORMED: 'ERR_CLIENT_ID_BADLY_FORMED',
  ERR_CANNOT_PING_OFFLINE: 'ERR_CANNOT_PING_OFFLINE',
  ERR_CONNECTION_SEQ_INTEGER_OVERFLOW: 'ERR_CONNECTION_SEQ_INTEGER_OVERFLOW',
  ERR_CONNECTION_STATE_TRANSITION_INVALID: 'ERR_CONNECTION_STATE_TRANSITION_INVALID',
  ERR_DATABASE_ADAPTER_NOT_FOUND: 'ERR_DATABASE_ADAPTER_NOT_FOUND',
  ERR_DATABASE_DOES_NOT_SUPPORT_SUBSCRIBE: 'ERR_DATABASE_DOES_NOT_SUPPORT_SUBSCRIBE',
  ERR_DATABASE_METHOD_NOT_IMPLEMENTED: 'ERR_DATABASE_METHOD_NOT_IMPLEMENTED',
  ERR_DEFAULT_TYPE_MISMATCH: 'ERR_DEFAULT_TYPE_MISMATCH',
  ERR_DOC_MISSING_VERSION: 'ERR_DOC_MISSING_VERSION',
  ERR_DOC_ALREADY_CREATED: 'ERR_DOC_ALREADY_CREATED',
  ERR_DOC_DOES_NOT_EXIST: 'ERR_DOC_DOES_NOT_EXIST',
  ERR_DOC_TYPE_NOT_RECOGNIZED: 'ERR_DOC_TYPE_NOT_RECOGNIZED',
  ERR_DOC_WAS_DELETED: 'ERR_DOC_WAS_DELETED',
  ERR_INFLIGHT_OP_MISSING: 'ERR_INFLIGHT_OP_MISSING',
  ERR_INGESTED_SNAPSHOT_HAS_NO_VERSION: 'ERR_INGESTED_SNAPSHOT_HAS_NO_VERSION',
  ERR_MAX_SUBMIT_RETRIES_EXCEEDED: 'ERR_MAX_SUBMIT_RETRIES_EXCEEDED',
  ERR_MESSAGE_BADLY_FORMED: 'ERR_MESSAGE_BADLY_FORMED',
  ERR_MILESTONE_ARGUMENT_INVALID: 'ERR_MILESTONE_ARGUMENT_INVALID',
  ERR_OP_ALREADY_SUBMITTED: 'ERR_OP_ALREADY_SUBMITTED',
  ERR_OP_NOT_ALLOWED_IN_PROJECTION: 'ERR_OP_NOT_ALLOWED_IN_PROJECTION',
  ERR_OP_SUBMIT_REJECTED: 'ERR_OP_SUBMIT_REJECTED',
  ERR_OP_VERSION_MISMATCH_AFTER_TRANSFORM: 'ERR_OP_VERSION_MISMATCH_AFTER_TRANSFORM',
  ERR_OP_VERSION_MISMATCH_DURING_TRANSFORM: 'ERR_OP_VERSION_MISMATCH_DURING_TRANSFORM',
  ERR_OP_VERSION_NEWER_THAN_CURRENT_SNAPSHOT: 'ERR_OP_VERSION_NEWER_THAN_CURRENT_SNAPSHOT',
  ERR_OT_LEGACY_JSON0_OP_CANNOT_BE_NORMALIZED: 'ERR_OT_LEGACY_JSON0_OP_CANNOT_BE_NORMALIZED',
  ERR_OT_OP_BADLY_FORMED: 'ERR_OT_OP_BADLY_FORMED',
  ERR_OT_OP_NOT_APPLIED: 'ERR_OT_OP_NOT_APPLIED',
  ERR_OT_OP_NOT_PROVIDED: 'ERR_OT_OP_NOT_PROVIDED',
  ERR_PRESENCE_TRANSFORM_FAILED: 'ERR_PRESENCE_TRANSFORM_FAILED',
  ERR_PROTOCOL_VERSION_NOT_SUPPORTED: 'ERR_PROTOCOL_VERSION_NOT_SUPPORTED',
  ERR_QUERY_CHANNEL_MISSING: 'ERR_QUERY_CHANNEL_MISSING',
  ERR_QUERY_EMITTER_LISTENER_NOT_ASSIGNED: 'ERR_QUERY_EMITTER_LISTENER_NOT_ASSIGNED',
  /**
   * A special error that a "readSnapshots" middleware implementation can use to indicate that it
   * wishes for the ShareDB client to treat it as a silent rejection, not passing the error back to
   * user code.
   *
   * For subscribes, the ShareDB client will still cancel the document subscription.
   */
  ERR_SNAPSHOT_READ_SILENT_REJECTION: 'ERR_SNAPSHOT_READ_SILENT_REJECTION',
  /**
   * A "readSnapshots" middleware rejected the reads of specific snapshots.
   *
   * This error code is mostly for server use and generally will not be encountered on the client.
   * Instead, each specific doc that encountered an error will receive its specific error.
   *
   * The one exception is for queries, where a "readSnapshots" rejection of specific snapshots will
   * cause the client to receive this error for the whole query, since queries don't support
   * doc-specific errors.
   */
  ERR_SNAPSHOT_READS_REJECTED: 'ERR_SNAPSHOT_READS_REJECTED',
  ERR_SUBMIT_TRANSFORM_OPS_NOT_FOUND: 'ERR_SUBMIT_TRANSFORM_OPS_NOT_FOUND',
  ERR_TYPE_CANNOT_BE_PROJECTED: 'ERR_TYPE_CANNOT_BE_PROJECTED',
  ERR_TYPE_DOES_NOT_SUPPORT_COMPOSE: 'ERR_TYPE_DOES_NOT_SUPPORT_COMPOSE',
  ERR_TYPE_DOES_NOT_SUPPORT_PRESENCE: 'ERR_TYPE_DOES_NOT_SUPPORT_PRESENCE',
  ERR_UNKNOWN_ERROR: 'ERR_UNKNOWN_ERROR'
};

var error = ShareDBError$3;

var types$3 = {};

// These methods let you build a transform function from a transformComponent
// function for OT types like JSON0 in which operations are lists of components
// and transforming them requires N^2 work. I find it kind of nasty that I need
// this, but I'm not really sure what a better solution is. Maybe I should do
// this automatically to types that don't have a compose function defined.

// Add transform and transformX functions for an OT type which has
// transformComponent defined.  transformComponent(destination array,
// component, other component, side)
var bootstrapTransform_1 = bootstrapTransform;
function bootstrapTransform(type, transformComponent, checkValidOp, append) {
  var transformComponentX = function(left, right, destLeft, destRight) {
    transformComponent(destLeft, left, right, 'left');
    transformComponent(destRight, right, left, 'right');
  };

  var transformX = type.transformX = function(leftOp, rightOp) {
    checkValidOp(leftOp);
    checkValidOp(rightOp);
    var newRightOp = [];

    for (var i = 0; i < rightOp.length; i++) {
      var rightComponent = rightOp[i];

      // Generate newLeftOp by composing leftOp by rightComponent
      var newLeftOp = [];
      var k = 0;
      while (k < leftOp.length) {
        var nextC = [];
        transformComponentX(leftOp[k], rightComponent, newLeftOp, nextC);
        k++;

        if (nextC.length === 1) {
          rightComponent = nextC[0];
        } else if (nextC.length === 0) {
          for (var j = k; j < leftOp.length; j++) {
            append(newLeftOp, leftOp[j]);
          }
          rightComponent = null;
          break;
        } else {
          // Recurse.
          var pair = transformX(leftOp.slice(k), nextC);
          for (var l = 0; l < pair[0].length; l++) {
            append(newLeftOp, pair[0][l]);
          }
          for (var r = 0; r < pair[1].length; r++) {
            append(newRightOp, pair[1][r]);
          }
          rightComponent = null;
          break;
        }
      }

      if (rightComponent != null) {
        append(newRightOp, rightComponent);
      }
      leftOp = newLeftOp;
    }
    return [leftOp, newRightOp];
  };

  // Transforms op with specified type ('left' or 'right') by otherOp.
  type.transform = function(op, otherOp, type) {
    if (!(type === 'left' || type === 'right'))
      throw new Error("type must be 'left' or 'right'");

    if (otherOp.length === 0) return op;

    if (op.length === 1 && otherOp.length === 1)
      return transformComponent([], op[0], otherOp[0], type);

    if (type === 'left')
      return transformX(op, otherOp)[0];
    else
      return transformX(otherOp, op)[1];
  };
}

var text0 = {exports: {}};

// DEPRECATED!
//
// This type works, but is not exported. Its included here because the JSON0
// embedded string operations use this library.


// A simple text implementation
//
// Operations are lists of components. Each component either inserts or deletes
// at a specified position in the document.
//
// Components are either:
//  {i:'str', p:100}: Insert 'str' at position 100 in the document
//  {d:'str', p:100}: Delete 'str' at position 100 in the document
//
// Components in an operation are executed sequentially, so the position of components
// assumes previous components have already executed.
//
// Eg: This op:
//   [{i:'abc', p:0}]
// is equivalent to this op:
//   [{i:'a', p:0}, {i:'b', p:1}, {i:'c', p:2}]

var text$1 = text0.exports = {
  name: 'text0',
  uri: 'http://sharejs.org/types/textv0',
  create: function(initial) {
    if ((initial != null) && typeof initial !== 'string') {
      throw new Error('Initial data must be a string');
    }
    return initial || '';
  }
};

/** Insert s2 into s1 at pos. */
var strInject = function(s1, pos, s2) {
  return s1.slice(0, pos) + s2 + s1.slice(pos);
};

/** Check that an operation component is valid. Throws if its invalid. */
var checkValidComponent = function(c) {
  if (typeof c.p !== 'number')
    throw new Error('component missing position field');

  if ((typeof c.i === 'string') === (typeof c.d === 'string'))
    throw new Error('component needs an i or d field');

  if (c.p < 0)
    throw new Error('position cannot be negative');
};

/** Check that an operation is valid */
var checkValidOp = function(op) {
  for (var i = 0; i < op.length; i++) {
    checkValidComponent(op[i]);
  }
};

/** Apply op to snapshot */
text$1.apply = function(snapshot, op) {
  var deleted;

  checkValidOp(op);
  for (var i = 0; i < op.length; i++) {
    var component = op[i];
    if (component.i != null) {
      snapshot = strInject(snapshot, component.p, component.i);
    } else {
      deleted = snapshot.slice(component.p, component.p + component.d.length);
      if (component.d !== deleted)
        throw new Error("Delete component '" + component.d + "' does not match deleted text '" + deleted + "'");

      snapshot = snapshot.slice(0, component.p) + snapshot.slice(component.p + component.d.length);
    }
  }
  return snapshot;
};

/**
 * Append a component to the end of newOp. Exported for use by the random op
 * generator and the JSON0 type.
 */
var append = text$1._append = function(newOp, c) {
  if (c.i === '' || c.d === '') return;

  if (newOp.length === 0) {
    newOp.push(c);
  } else {
    var last = newOp[newOp.length - 1];

    if (last.i != null && c.i != null && last.p <= c.p && c.p <= last.p + last.i.length) {
      // Compose the insert into the previous insert
      newOp[newOp.length - 1] = {i:strInject(last.i, c.p - last.p, c.i), p:last.p};

    } else if (last.d != null && c.d != null && c.p <= last.p && last.p <= c.p + c.d.length) {
      // Compose the deletes together
      newOp[newOp.length - 1] = {d:strInject(c.d, last.p - c.p, last.d), p:c.p};

    } else {
      newOp.push(c);
    }
  }
};

/** Compose op1 and op2 together */
text$1.compose = function(op1, op2) {
  checkValidOp(op1);
  checkValidOp(op2);
  var newOp = op1.slice();
  for (var i = 0; i < op2.length; i++) {
    append(newOp, op2[i]);
  }
  return newOp;
};

/** Clean up an op */
text$1.normalize = function(op) {
  var newOp = [];

  // Normalize should allow ops which are a single (unwrapped) component:
  // {i:'asdf', p:23}.
  // There's no good way to test if something is an array:
  // http://perfectionkills.com/instanceof-considered-harmful-or-how-to-write-a-robust-isarray/
  // so this is probably the least bad solution.
  if (op.i != null || op.p != null) op = [op];

  for (var i = 0; i < op.length; i++) {
    var c = op[i];
    if (c.p == null) c.p = 0;

    append(newOp, c);
  }

  return newOp;
};

// This helper method transforms a position by an op component.
//
// If c is an insert, insertAfter specifies whether the transform
// is pushed after the insert (true) or before it (false).
//
// insertAfter is optional for deletes.
var transformPosition = function(pos, c, insertAfter) {
  // This will get collapsed into a giant ternary by uglify.
  if (c.i != null) {
    if (c.p < pos || (c.p === pos && insertAfter)) {
      return pos + c.i.length;
    } else {
      return pos;
    }
  } else {
    // I think this could also be written as: Math.min(c.p, Math.min(c.p -
    // otherC.p, otherC.d.length)) but I think its harder to read that way, and
    // it compiles using ternary operators anyway so its no slower written like
    // this.
    if (pos <= c.p) {
      return pos;
    } else if (pos <= c.p + c.d.length) {
      return c.p;
    } else {
      return pos - c.d.length;
    }
  }
};

// Helper method to transform a cursor position as a result of an op.
//
// Like transformPosition above, if c is an insert, insertAfter specifies
// whether the cursor position is pushed after an insert (true) or before it
// (false).
text$1.transformCursor = function(position, op, side) {
  var insertAfter = side === 'right';
  for (var i = 0; i < op.length; i++) {
    position = transformPosition(position, op[i], insertAfter);
  }

  return position;
};

// Transform an op component by another op component. Asymmetric.
// The result will be appended to destination.
//
// exported for use in JSON type
var transformComponent = text$1._tc = function(dest, c, otherC, side) {
  //var cIntersect, intersectEnd, intersectStart, newC, otherIntersect, s;

  checkValidComponent(c);
  checkValidComponent(otherC);

  if (c.i != null) {
    // Insert.
    append(dest, {i:c.i, p:transformPosition(c.p, otherC, side === 'right')});
  } else {
    // Delete
    if (otherC.i != null) {
      // Delete vs insert
      var s = c.d;
      if (c.p < otherC.p) {
        append(dest, {d:s.slice(0, otherC.p - c.p), p:c.p});
        s = s.slice(otherC.p - c.p);
      }
      if (s !== '')
        append(dest, {d: s, p: c.p + otherC.i.length});

    } else {
      // Delete vs delete
      if (c.p >= otherC.p + otherC.d.length)
        append(dest, {d: c.d, p: c.p - otherC.d.length});
      else if (c.p + c.d.length <= otherC.p)
        append(dest, c);
      else {
        // They overlap somewhere.
        var newC = {d: '', p: c.p};

        if (c.p < otherC.p)
          newC.d = c.d.slice(0, otherC.p - c.p);

        if (c.p + c.d.length > otherC.p + otherC.d.length)
          newC.d += c.d.slice(otherC.p + otherC.d.length - c.p);

        // This is entirely optional - I'm just checking the deleted text in
        // the two ops matches
        var intersectStart = Math.max(c.p, otherC.p);
        var intersectEnd = Math.min(c.p + c.d.length, otherC.p + otherC.d.length);
        var cIntersect = c.d.slice(intersectStart - c.p, intersectEnd - c.p);
        var otherIntersect = otherC.d.slice(intersectStart - otherC.p, intersectEnd - otherC.p);
        if (cIntersect !== otherIntersect)
          throw new Error('Delete ops delete different text in the same region of the document');

        if (newC.d !== '') {
          newC.p = transformPosition(newC.p, otherC);
          append(dest, newC);
        }
      }
    }
  }

  return dest;
};

var invertComponent = function(c) {
  return (c.i != null) ? {d:c.i, p:c.p} : {i:c.d, p:c.p};
};

// No need to use append for invert, because the components won't be able to
// cancel one another.
text$1.invert = function(op) {
  // Shallow copy & reverse that sucka.
  op = op.slice().reverse();
  for (var i = 0; i < op.length; i++) {
    op[i] = invertComponent(op[i]);
  }
  return op;
};

bootstrapTransform_1(text$1, transformComponent, checkValidOp, append);

var text0Exports = text0.exports;

/*
 This is the implementation of the JSON OT type.

 Spec is here: https://github.com/josephg/ShareJS/wiki/JSON-Operations

 Note: This is being made obsolete. It will soon be replaced by the JSON2 type.
*/

/**
 * UTILITY FUNCTIONS
 */

/**
 * Checks if the passed object is an Array instance. Can't use Array.isArray
 * yet because its not supported on IE8.
 *
 * @param obj
 * @returns {boolean}
 */
var isArray$1 = function(obj) {
  return Object.prototype.toString.call(obj) == '[object Array]';
};

/**
 * Checks if the passed object is an Object instance.
 * No function call (fast) version
 *
 * @param obj
 * @returns {boolean}
 */
var isObject = function(obj) {
  return (!!obj) && (obj.constructor === Object);
};

/**
 * Clones the passed object using JSON serialization (which is slow).
 *
 * hax, copied from test/types/json. Apparently this is still the fastest way
 * to deep clone an object, assuming we have browser support for JSON.  @see
 * http://jsperf.com/cloning-an-object/12
 */
var clone$1 = function(o) {
  return JSON.parse(JSON.stringify(o));
};

/**
 * JSON OT Type
 * @type {*}
 */
var json = {
  name: 'json0',
  uri: 'http://sharejs.org/types/JSONv0'
};

// You can register another OT type as a subtype in a JSON document using
// the following function. This allows another type to handle certain
// operations instead of the builtin JSON type.
var subtypes = {};
json.registerSubtype = function(subtype) {
  subtypes[subtype.name] = subtype;
};

json.create = function(data) {
  // Null instead of undefined if you don't pass an argument.
  return data === undefined ? null : clone$1(data);
};

json.invertComponent = function(c) {
  var c_ = {p: c.p};

  // handle subtype ops
  if (c.t && subtypes[c.t]) {
    c_.t = c.t;
    c_.o = subtypes[c.t].invert(c.o);
  }

  if (c.si !== undefined) c_.sd = c.si;
  if (c.sd !== undefined) c_.si = c.sd;
  if (c.oi !== undefined) c_.od = c.oi;
  if (c.od !== undefined) c_.oi = c.od;
  if (c.li !== undefined) c_.ld = c.li;
  if (c.ld !== undefined) c_.li = c.ld;
  if (c.na !== undefined) c_.na = -c.na;

  if (c.lm !== undefined) {
    c_.lm = c.p[c.p.length-1];
    c_.p = c.p.slice(0,c.p.length-1).concat([c.lm]);
  }

  return c_;
};

json.invert = function(op) {
  var op_ = op.slice().reverse();
  var iop = [];
  for (var i = 0; i < op_.length; i++) {
    iop.push(json.invertComponent(op_[i]));
  }
  return iop;
};

json.checkValidOp = function(op) {
  for (var i = 0; i < op.length; i++) {
    if (!isArray$1(op[i].p)) throw new Error('Missing path');
  }
};

json.checkList = function(elem) {
  if (!isArray$1(elem))
    throw new Error('Referenced element not a list');
};

json.checkObj = function(elem) {
  if (!isObject(elem)) {
    throw new Error("Referenced element not an object (it was " + JSON.stringify(elem) + ")");
  }
};

// helper functions to convert old string ops to and from subtype ops
function convertFromText(c) {
  c.t = 'text0';
  var o = {p: c.p.pop()};
  if (c.si != null) o.i = c.si;
  if (c.sd != null) o.d = c.sd;
  c.o = [o];
}

function convertToText(c) {
  c.p.push(c.o[0].p);
  if (c.o[0].i != null) c.si = c.o[0].i;
  if (c.o[0].d != null) c.sd = c.o[0].d;
  delete c.t;
  delete c.o;
}

json.apply = function(snapshot, op) {
  json.checkValidOp(op);

  op = clone$1(op);

  var container = {
    data: snapshot
  };

  for (var i = 0; i < op.length; i++) {
    var c = op[i];

    // convert old string ops to use subtype for backwards compatibility
    if (c.si != null || c.sd != null)
      convertFromText(c);

    var parent = null;
    var elem = container;
    var key = 'data';

    for (var j = 0; j < c.p.length; j++) {
      var p = c.p[j];

      parent = elem;
      elem = elem[key];
      key = p;

      if (parent == null)
        throw new Error('Path invalid');
    }

    // handle subtype ops
    if (c.t && c.o !== undefined && subtypes[c.t]) {
      elem[key] = subtypes[c.t].apply(elem[key], c.o);

    // Number add
    } else if (c.na !== undefined) {
      if (typeof elem[key] != 'number')
        throw new Error('Referenced element not a number');

      elem[key] += c.na;
    }

    // List replace
    else if (c.li !== undefined && c.ld !== undefined) {
      json.checkList(elem);
      // Should check the list element matches c.ld
      elem[key] = c.li;
    }

    // List insert
    else if (c.li !== undefined) {
      json.checkList(elem);
      elem.splice(key,0, c.li);
    }

    // List delete
    else if (c.ld !== undefined) {
      json.checkList(elem);
      // Should check the list element matches c.ld here too.
      elem.splice(key,1);
    }

    // List move
    else if (c.lm !== undefined) {
      json.checkList(elem);
      if (c.lm != key) {
        var e = elem[key];
        // Remove it...
        elem.splice(key,1);
        // And insert it back.
        elem.splice(c.lm,0,e);
      }
    }

    // Object insert / replace
    else if (c.oi !== undefined) {
      json.checkObj(elem);

      // Should check that elem[key] == c.od
      elem[key] = c.oi;
    }

    // Object delete
    else if (c.od !== undefined) {
      json.checkObj(elem);

      // Should check that elem[key] == c.od
      delete elem[key];
    }

    else {
      throw new Error('invalid / missing instruction in op');
    }
  }

  return container.data;
};

// Helper to break an operation up into a bunch of small ops.
json.shatter = function(op) {
  var results = [];
  for (var i = 0; i < op.length; i++) {
    results.push([op[i]]);
  }
  return results;
};

// Helper for incrementally applying an operation to a snapshot. Calls yield
// after each op component has been applied.
json.incrementalApply = function(snapshot, op, _yield) {
  for (var i = 0; i < op.length; i++) {
    var smallOp = [op[i]];
    snapshot = json.apply(snapshot, smallOp);
    // I'd just call this yield, but thats a reserved keyword. Bah!
    _yield(smallOp, snapshot);
  }

  return snapshot;
};

// Checks if two paths, p1 and p2 match.
var pathMatches = json.pathMatches = function(p1, p2, ignoreLast) {
  if (p1.length != p2.length)
    return false;

  for (var i = 0; i < p1.length; i++) {
    if (p1[i] !== p2[i] && (!ignoreLast || i !== p1.length - 1))
      return false;
  }

  return true;
};

json.append = function(dest,c) {
  c = clone$1(c);

  if (dest.length === 0) {
    dest.push(c);
    return;
  }

  var last = dest[dest.length - 1];

  // convert old string ops to use subtype for backwards compatibility
  if ((c.si != null || c.sd != null) && (last.si != null || last.sd != null)) {
    convertFromText(c);
    convertFromText(last);
  }

  if (pathMatches(c.p, last.p)) {
    // handle subtype ops
    if (c.t && last.t && c.t === last.t && subtypes[c.t]) {
      last.o = subtypes[c.t].compose(last.o, c.o);

      // convert back to old string ops
      if (c.si != null || c.sd != null) {
        var p = c.p;
        for (var i = 0; i < last.o.length - 1; i++) {
          c.o = [last.o.pop()];
          c.p = p.slice();
          convertToText(c);
          dest.push(c);
        }

        convertToText(last);
      }
    } else if (last.na != null && c.na != null) {
      dest[dest.length - 1] = {p: last.p, na: last.na + c.na};
    } else if (last.li !== undefined && c.li === undefined && c.ld === last.li) {
      // insert immediately followed by delete becomes a noop.
      if (last.ld !== undefined) {
        // leave the delete part of the replace
        delete last.li;
      } else {
        dest.pop();
      }
    } else if (last.od !== undefined && last.oi === undefined && c.oi !== undefined && c.od === undefined) {
      last.oi = c.oi;
    } else if (last.oi !== undefined && c.od !== undefined) {
      // The last path component inserted something that the new component deletes (or replaces).
      // Just merge them.
      if (c.oi !== undefined) {
        last.oi = c.oi;
      } else if (last.od !== undefined) {
        delete last.oi;
      } else {
        // An insert directly followed by a delete turns into a no-op and can be removed.
        dest.pop();
      }
    } else if (c.lm !== undefined && c.p[c.p.length - 1] === c.lm) ; else {
      dest.push(c);
    }
  } else {
    // convert string ops back
    if ((c.si != null || c.sd != null) && (last.si != null || last.sd != null)) {
      convertToText(c);
      convertToText(last);
    }

    dest.push(c);
  }
};

json.compose = function(op1,op2) {
  json.checkValidOp(op1);
  json.checkValidOp(op2);

  var newOp = clone$1(op1);

  for (var i = 0; i < op2.length; i++) {
    json.append(newOp,op2[i]);
  }

  return newOp;
};

json.normalize = function(op) {
  var newOp = [];

  op = isArray$1(op) ? op : [op];

  for (var i = 0; i < op.length; i++) {
    var c = op[i];
    if (c.p == null) c.p = [];

    json.append(newOp,c);
  }

  return newOp;
};

// Returns the common length of the paths of ops a and b
json.commonLengthForOps = function(a, b) {
  var alen = a.p.length;
  var blen = b.p.length;
  if (a.na != null || a.t)
    alen++;

  if (b.na != null || b.t)
    blen++;

  if (alen === 0) return -1;
  if (blen === 0) return null;

  alen--;
  blen--;

  for (var i = 0; i < alen; i++) {
    var p = a.p[i];
    if (i >= blen || p !== b.p[i])
      return null;
  }

  return alen;
};

// Returns true if an op can affect the given path
json.canOpAffectPath = function(op, path) {
  return json.commonLengthForOps({p:path}, op) != null;
};

// transform c so it applies to a document with otherC applied.
json.transformComponent = function(dest, c, otherC, type) {
  c = clone$1(c);

  var common = json.commonLengthForOps(otherC, c);
  var common2 = json.commonLengthForOps(c, otherC);
  var cplength = c.p.length;
  var otherCplength = otherC.p.length;

  if (c.na != null || c.t)
    cplength++;

  if (otherC.na != null || otherC.t)
    otherCplength++;

  // if c is deleting something, and that thing is changed by otherC, we need to
  // update c to reflect that change for invertibility.
  if (common2 != null && otherCplength > cplength && c.p[common2] == otherC.p[common2]) {
    if (c.ld !== undefined) {
      var oc = clone$1(otherC);
      oc.p = oc.p.slice(cplength);
      c.ld = json.apply(clone$1(c.ld),[oc]);
    } else if (c.od !== undefined) {
      var oc = clone$1(otherC);
      oc.p = oc.p.slice(cplength);
      c.od = json.apply(clone$1(c.od),[oc]);
    }
  }

  if (common != null) {
    var commonOperand = cplength == otherCplength;

    // backward compatibility for old string ops
    var oc = otherC;
    if ((c.si != null || c.sd != null) && (otherC.si != null || otherC.sd != null)) {
      convertFromText(c);
      oc = clone$1(otherC);
      convertFromText(oc);
    }

    // handle subtype ops
    if (oc.t && subtypes[oc.t]) {
      if (c.t && c.t === oc.t) {
        var res = subtypes[c.t].transform(c.o, oc.o, type);

        // convert back to old string ops
        if (c.si != null || c.sd != null) {
          var p = c.p;
          for (var i = 0; i < res.length; i++) {
            c.o = [res[i]];
            c.p = p.slice();
            convertToText(c);
            json.append(dest, c);
          }
        } else if (!isArray$1(res) || res.length > 0) {
          c.o = res;
          json.append(dest, c);
        }

        return dest;
      }
    }

    // transform based on otherC
    else if (otherC.na !== undefined) ; else if (otherC.li !== undefined && otherC.ld !== undefined) {
      if (otherC.p[common] === c.p[common]) {
        // noop

        if (!commonOperand) {
          return dest;
        } else if (c.ld !== undefined) {
          // we're trying to delete the same element, -> noop
          if (c.li !== undefined && type === 'left') {
            // we're both replacing one element with another. only one can survive
            c.ld = clone$1(otherC.li);
          } else {
            return dest;
          }
        }
      }
    } else if (otherC.li !== undefined) {
      if (c.li !== undefined && c.ld === undefined && commonOperand && c.p[common] === otherC.p[common]) {
        // in li vs. li, left wins.
        if (type === 'right')
          c.p[common]++;
      } else if (otherC.p[common] <= c.p[common]) {
        c.p[common]++;
      }

      if (c.lm !== undefined) {
        if (commonOperand) {
          // otherC edits the same list we edit
          if (otherC.p[common] <= c.lm)
            c.lm++;
          // changing c.from is handled above.
        }
      }
    } else if (otherC.ld !== undefined) {
      if (c.lm !== undefined) {
        if (commonOperand) {
          if (otherC.p[common] === c.p[common]) {
            // they deleted the thing we're trying to move
            return dest;
          }
          // otherC edits the same list we edit
          var p = otherC.p[common];
          var from = c.p[common];
          var to = c.lm;
          if (p < to || (p === to && from < to))
            c.lm--;

        }
      }

      if (otherC.p[common] < c.p[common]) {
        c.p[common]--;
      } else if (otherC.p[common] === c.p[common]) {
        if (otherCplength < cplength) {
          // we're below the deleted element, so -> noop
          return dest;
        } else if (c.ld !== undefined) {
          if (c.li !== undefined) {
            // we're replacing, they're deleting. we become an insert.
            delete c.ld;
          } else {
            // we're trying to delete the same element, -> noop
            return dest;
          }
        }
      }

    } else if (otherC.lm !== undefined) {
      if (c.lm !== undefined && cplength === otherCplength) {
        // lm vs lm, here we go!
        var from = c.p[common];
        var to = c.lm;
        var otherFrom = otherC.p[common];
        var otherTo = otherC.lm;
        if (otherFrom !== otherTo) {
          // if otherFrom == otherTo, we don't need to change our op.

          // where did my thing go?
          if (from === otherFrom) {
            // they moved it! tie break.
            if (type === 'left') {
              c.p[common] = otherTo;
              if (from === to) // ugh
                c.lm = otherTo;
            } else {
              return dest;
            }
          } else {
            // they moved around it
            if (from > otherFrom) c.p[common]--;
            if (from > otherTo) c.p[common]++;
            else if (from === otherTo) {
              if (otherFrom > otherTo) {
                c.p[common]++;
                if (from === to) // ugh, again
                  c.lm++;
              }
            }

            // step 2: where am i going to put it?
            if (to > otherFrom) {
              c.lm--;
            } else if (to === otherFrom) {
              if (to > from)
                c.lm--;
            }
            if (to > otherTo) {
              c.lm++;
            } else if (to === otherTo) {
              // if we're both moving in the same direction, tie break
              if ((otherTo > otherFrom && to > from) ||
                  (otherTo < otherFrom && to < from)) {
                if (type === 'right') c.lm++;
              } else {
                if (to > from) c.lm++;
                else if (to === otherFrom) c.lm--;
              }
            }
          }
        }
      } else if (c.li !== undefined && c.ld === undefined && commonOperand) {
        // li
        var from = otherC.p[common];
        var to = otherC.lm;
        p = c.p[common];
        if (p > from) c.p[common]--;
        if (p > to) c.p[common]++;
      } else {
        // ld, ld+li, si, sd, na, oi, od, oi+od, any li on an element beneath
        // the lm
        //
        // i.e. things care about where their item is after the move.
        var from = otherC.p[common];
        var to = otherC.lm;
        p = c.p[common];
        if (p === from) {
          c.p[common] = to;
        } else {
          if (p > from) c.p[common]--;
          if (p > to) c.p[common]++;
          else if (p === to && from > to) c.p[common]++;
        }
      }
    }
    else if (otherC.oi !== undefined && otherC.od !== undefined) {
      if (c.p[common] === otherC.p[common]) {
        if (c.oi !== undefined && commonOperand) {
          // we inserted where someone else replaced
          if (type === 'right') {
            // left wins
            return dest;
          } else {
            // we win, make our op replace what they inserted
            c.od = otherC.oi;
          }
        } else {
          // -> noop if the other component is deleting the same object (or any parent)
          return dest;
        }
      }
    } else if (otherC.oi !== undefined) {
      if (c.oi !== undefined && c.p[common] === otherC.p[common]) {
        // left wins if we try to insert at the same place
        if (type === 'left') {
          json.append(dest,{p: c.p, od:otherC.oi});
        } else {
          return dest;
        }
      }
    } else if (otherC.od !== undefined) {
      if (c.p[common] == otherC.p[common]) {
        if (!commonOperand)
          return dest;
        if (c.oi !== undefined) {
          delete c.od;
        } else {
          return dest;
        }
      }
    }
  }

  json.append(dest,c);
  return dest;
};

bootstrapTransform_1(json, json.transformComponent, json.checkValidOp, json.append);

/**
 * Register a subtype for string operations, using the text0 type.
 */
var text = text0Exports;

json.registerSubtype(text);
var json0 = json;

// Only the JSON type is exported, because the text type is deprecated
// otherwise. (If you want to use it somewhere, you're welcome to pull it out
// into a separate module that json0 can depend on).

var lib = {
  type: json0
};

(function (exports) {
	exports.defaultType = lib.type;

	exports.map = {};

	exports.register = function(type) {
	  if (type.name) exports.map[type.name] = type;
	  if (type.uri) exports.map[type.uri] = type;
	};

	exports.register(exports.defaultType); 
} (types$3));

var global$1 = (typeof global !== "undefined" ? global :
  typeof self !== "undefined" ? self :
  typeof window !== "undefined" ? window : {});

// shim for using process in browser
// based off https://github.com/defunctzombie/node-process/blob/master/browser.js

function defaultSetTimout() {
    throw new Error('setTimeout has not been defined');
}
function defaultClearTimeout () {
    throw new Error('clearTimeout has not been defined');
}
var cachedSetTimeout = defaultSetTimout;
var cachedClearTimeout = defaultClearTimeout;
if (typeof global$1.setTimeout === 'function') {
    cachedSetTimeout = setTimeout;
}
if (typeof global$1.clearTimeout === 'function') {
    cachedClearTimeout = clearTimeout;
}

function runTimeout(fun) {
    if (cachedSetTimeout === setTimeout) {
        //normal enviroments in sane situations
        return setTimeout(fun, 0);
    }
    // if setTimeout wasn't available but was latter defined
    if ((cachedSetTimeout === defaultSetTimout || !cachedSetTimeout) && setTimeout) {
        cachedSetTimeout = setTimeout;
        return setTimeout(fun, 0);
    }
    try {
        // when when somebody has screwed with setTimeout but no I.E. maddness
        return cachedSetTimeout(fun, 0);
    } catch(e){
        try {
            // When we are in I.E. but the script has been evaled so I.E. doesn't trust the global object when called normally
            return cachedSetTimeout.call(null, fun, 0);
        } catch(e){
            // same as above but when it's a version of I.E. that must have the global object for 'this', hopfully our context correct otherwise it will throw a global error
            return cachedSetTimeout.call(this, fun, 0);
        }
    }


}
function runClearTimeout(marker) {
    if (cachedClearTimeout === clearTimeout) {
        //normal enviroments in sane situations
        return clearTimeout(marker);
    }
    // if clearTimeout wasn't available but was latter defined
    if ((cachedClearTimeout === defaultClearTimeout || !cachedClearTimeout) && clearTimeout) {
        cachedClearTimeout = clearTimeout;
        return clearTimeout(marker);
    }
    try {
        // when when somebody has screwed with setTimeout but no I.E. maddness
        return cachedClearTimeout(marker);
    } catch (e){
        try {
            // When we are in I.E. but the script has been evaled so I.E. doesn't  trust the global object when called normally
            return cachedClearTimeout.call(null, marker);
        } catch (e){
            // same as above but when it's a version of I.E. that must have the global object for 'this', hopfully our context correct otherwise it will throw a global error.
            // Some versions of I.E. have different rules for clearTimeout vs setTimeout
            return cachedClearTimeout.call(this, marker);
        }
    }



}
var queue = [];
var draining = false;
var currentQueue;
var queueIndex = -1;

function cleanUpNextTick() {
    if (!draining || !currentQueue) {
        return;
    }
    draining = false;
    if (currentQueue.length) {
        queue = currentQueue.concat(queue);
    } else {
        queueIndex = -1;
    }
    if (queue.length) {
        drainQueue();
    }
}

function drainQueue() {
    if (draining) {
        return;
    }
    var timeout = runTimeout(cleanUpNextTick);
    draining = true;

    var len = queue.length;
    while(len) {
        currentQueue = queue;
        queue = [];
        while (++queueIndex < len) {
            if (currentQueue) {
                currentQueue[queueIndex].run();
            }
        }
        queueIndex = -1;
        len = queue.length;
    }
    currentQueue = null;
    draining = false;
    runClearTimeout(timeout);
}
function nextTick(fun) {
    var args = new Array(arguments.length - 1);
    if (arguments.length > 1) {
        for (var i = 1; i < arguments.length; i++) {
            args[i - 1] = arguments[i];
        }
    }
    queue.push(new Item(fun, args));
    if (queue.length === 1 && !draining) {
        runTimeout(drainQueue);
    }
}
// v8 likes predictible objects
function Item(fun, array) {
    this.fun = fun;
    this.array = array;
}
Item.prototype.run = function () {
    this.fun.apply(null, this.array);
};
var title = 'browser';
var platform = 'browser';
var browser = true;
var env = {};
var argv = [];
var version$1 = ''; // empty string to avoid regexp issues
var versions = {};
var release = {};
var config = {};

function noop() {}

var on = noop;
var addListener = noop;
var once = noop;
var off = noop;
var removeListener = noop;
var removeAllListeners = noop;
var emit = noop;

function binding(name) {
    throw new Error('process.binding is not supported');
}

function cwd () { return '/' }
function chdir (dir) {
    throw new Error('process.chdir is not supported');
}function umask() { return 0; }

// from https://github.com/kumavis/browser-process-hrtime/blob/master/index.js
var performance = global$1.performance || {};
var performanceNow =
  performance.now        ||
  performance.mozNow     ||
  performance.msNow      ||
  performance.oNow       ||
  performance.webkitNow  ||
  function(){ return (new Date()).getTime() };

// generate timestamp or delta
// see http://nodejs.org/api/process.html#process_process_hrtime
function hrtime(previousTimestamp){
  var clocktime = performanceNow.call(performance)*1e-3;
  var seconds = Math.floor(clocktime);
  var nanoseconds = Math.floor((clocktime%1)*1e9);
  if (previousTimestamp) {
    seconds = seconds - previousTimestamp[0];
    nanoseconds = nanoseconds - previousTimestamp[1];
    if (nanoseconds<0) {
      seconds--;
      nanoseconds += 1e9;
    }
  }
  return [seconds,nanoseconds]
}

var startTime = new Date();
function uptime() {
  var currentTime = new Date();
  var dif = currentTime - startTime;
  return dif / 1000;
}

var browser$1 = {
  nextTick: nextTick,
  title: title,
  browser: browser,
  env: env,
  argv: argv,
  version: version$1,
  versions: versions,
  on: on,
  addListener: addListener,
  once: once,
  off: off,
  removeListener: removeListener,
  removeAllListeners: removeAllListeners,
  emit: emit,
  binding: binding,
  cwd: cwd,
  chdir: chdir,
  umask: umask,
  hrtime: hrtime,
  platform: platform,
  release: release,
  config: config,
  uptime: uptime
};

var util$a = {};

(function (exports) {
	exports.doNothing = doNothing;
	function doNothing() {}

	exports.hasKeys = function(object) {
	  for (var key in object) return true;
	  return false;
	};

	// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/isInteger#Polyfill
	exports.isInteger = Number.isInteger || function(value) {
	  return typeof value === 'number' &&
	    isFinite(value) &&
	    Math.floor(value) === value;
	};

	exports.isValidVersion = function(version) {
	  if (version === null) return true;
	  return exports.isInteger(version) && version >= 0;
	};

	exports.isValidTimestamp = function(timestamp) {
	  return exports.isValidVersion(timestamp);
	};

	exports.MAX_SAFE_INTEGER = 9007199254740991;

	exports.dig = function() {
	  var obj = arguments[0];
	  for (var i = 1; i < arguments.length; i++) {
	    var key = arguments[i];
	    obj = obj[key] || (i === arguments.length - 1 ? undefined : {});
	  }
	  return obj;
	};

	exports.digOrCreate = function() {
	  var obj = arguments[0];
	  var createCallback = arguments[arguments.length - 1];
	  for (var i = 1; i < arguments.length - 1; i++) {
	    var key = arguments[i];
	    obj = obj[key] ||
	      (obj[key] = i === arguments.length - 2 ? createCallback() : {});
	  }
	  return obj;
	};

	exports.digAndRemove = function() {
	  var obj = arguments[0];
	  var objects = [obj];
	  for (var i = 1; i < arguments.length - 1; i++) {
	    var key = arguments[i];
	    if (!obj.hasOwnProperty(key)) break;
	    obj = obj[key];
	    objects.push(obj);
	  }
	  for (var i = objects.length - 1; i >= 0; i--) {
	    var parent = objects[i];
	    var key = arguments[i + 1];
	    var child = parent[key];
	    if (i === objects.length - 1 || !exports.hasKeys(child)) delete parent[key];
	  }
	};

	exports.supportsPresence = function(type) {
	  return type && typeof type.transformPresence === 'function';
	};

	exports.callEach = function(callbacks, error) {
	  var called = false;
	  callbacks.forEach(function(callback) {
	    if (callback) {
	      callback(error);
	      called = true;
	    }
	  });
	  return called;
	};

	exports.truthy = function(arg) {
	  return !!arg;
	};

	exports.nextTick = function(callback) {
	  if (typeof browser$1 !== 'undefined' && browser$1.nextTick) {
	    return browser$1.nextTick.apply(null, arguments);
	  }

	  var args = [];
	  for (var i = 1; i < arguments.length; i++) {
	    args[i - 1] = arguments[i];
	  }

	  setTimeout(function() {
	    callback.apply(null, args);
	  });
	};

	exports.clone = function(obj) {
	  return (obj === undefined) ? undefined : JSON.parse(JSON.stringify(obj));
	}; 
} (util$a));

var isArray = Array.isArray;
var keyList = Object.keys;
var hasProp = Object.prototype.hasOwnProperty;

var fastDeepEqual = function equal(a, b) {
  if (a === b) return true;

  if (a && b && typeof a == 'object' && typeof b == 'object') {
    var arrA = isArray(a)
      , arrB = isArray(b)
      , i
      , length
      , key;

    if (arrA && arrB) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0;)
        if (!equal(a[i], b[i])) return false;
      return true;
    }

    if (arrA != arrB) return false;

    var dateA = a instanceof Date
      , dateB = b instanceof Date;
    if (dateA != dateB) return false;
    if (dateA && dateB) return a.getTime() == b.getTime();

    var regexpA = a instanceof RegExp
      , regexpB = b instanceof RegExp;
    if (regexpA != regexpB) return false;
    if (regexpA && regexpB) return a.toString() == b.toString();

    var keys = keyList(a);
    length = keys.length;

    if (length !== keyList(b).length)
      return false;

    for (i = length; i-- !== 0;)
      if (!hasProp.call(b, keys[i])) return false;

    for (i = length; i-- !== 0;) {
      key = keys[i];
      if (!equal(a[key], b[key])) return false;
    }

    return true;
  }

  return a!==a && b!==b;
};

var messageActions = {};

messageActions.ACTIONS = {
  initLegacy: 'init',
  handshake: 'hs',
  queryFetch: 'qf',
  querySubscribe: 'qs',
  queryUnsubscribe: 'qu',
  queryUpdate: 'q',
  bulkFetch: 'bf',
  bulkSubscribe: 'bs',
  bulkUnsubscribe: 'bu',
  fetch: 'f',
  fixup: 'fixup',
  subscribe: 's',
  unsubscribe: 'u',
  op: 'op',
  snapshotFetch: 'nf',
  snapshotFetchByTimestamp: 'nt',
  pingPong: 'pp',
  presence: 'p',
  presenceSubscribe: 'ps',
  presenceUnsubscribe: 'pu',
  presenceRequest: 'pr'
};

var emitter$5 = emitter$6;
var logger$1 = logger_1;
var ShareDBError$2 = error;
var types$2 = types$3;
var util$9 = util$a;
var clone = util$9.clone;
var deepEqual = fastDeepEqual;
var ACTIONS$6 = messageActions.ACTIONS;

var ERROR_CODE$2 = ShareDBError$2.CODES;

/**
 * A Doc is a client's view on a sharejs document.
 *
 * It is is uniquely identified by its `id` and `collection`.  Documents
 * should not be created directly. Create them with connection.get()
 *
 *
 * Subscriptions
 * -------------
 *
 * We can subscribe a document to stay in sync with the server.
 *   doc.subscribe(function(error) {
 *     doc.subscribed // = true
 *   })
 * The server now sends us all changes concerning this document and these are
 * applied to our data. If the subscription was successful the initial
 * data and version sent by the server are loaded into the document.
 *
 * To stop listening to the changes we call `doc.unsubscribe()`.
 *
 * If we just want to load the data but not stay up-to-date, we call
 *   doc.fetch(function(error) {
 *     doc.data // sent by server
 *   })
 *
 *
 * Events
 * ------
 *
 * You can use doc.on(eventName, callback) to subscribe to the following events:
 * - `before op (op, source)` Fired before a partial operation is applied to the data.
 *   It may be used to read the old data just before applying an operation
 * - `op (op, source)` Fired after every partial operation with this operation as the
 *   first argument
 * - `create (source)` The document was created. That means its type was
 *   set and it has some initial data.
 * - `del (data, source)` Fired after the document is deleted, that is
 *   the data is null. It is passed the data before deletion as an
 *   argument
 * - `load ()` Fired when a new snapshot is ingested from a fetch, subscribe, or query
 */

var doc = Doc$1;
function Doc$1(connection, collection, id) {
  emitter$5.EventEmitter.call(this);

  this.connection = connection;

  this.collection = collection;
  this.id = id;

  this.version = null;
  this.type = null;
  this.data = undefined;

  // Array of callbacks or nulls as placeholders
  this.inflightFetch = [];
  this.inflightSubscribe = null;
  this.pendingFetch = [];
  this.pendingSubscribe = [];

  // Whether we think we are subscribed on the server. Synchronously set to
  // false on calls to unsubscribe and disconnect. Should never be true when
  // this.wantSubscribe is false
  this.subscribed = false;
  // Whether to re-establish the subscription on reconnect
  this.wantSubscribe = false;

  this._wantsDestroy = false;

  // The op that is currently roundtripping to the server, or null.
  //
  // When the connection reconnects, the inflight op is resubmitted.
  //
  // This has the same format as an entry in pendingOps
  this.inflightOp = null;

  // All ops that are waiting for the server to acknowledge this.inflightOp
  // This used to just be a single operation, but creates & deletes can't be
  // composed with regular operations.
  //
  // This is a list of {[create:{...}], [del:true], [op:...], callbacks:[...]}
  this.pendingOps = [];

  // The OT type of this document. An uncreated document has type `null`
  this.type = null;

  // The applyStack enables us to track any ops submitted while we are
  // applying an op incrementally. This value is an array when we are
  // performing an incremental apply and null otherwise. When it is an array,
  // all submitted ops should be pushed onto it. The `_otApply` method will
  // reset it back to null when all incremental apply loops are complete.
  this.applyStack = null;

  // Disable the default behavior of composing submitted ops. This is read at
  // the time of op submit, so it may be toggled on before submitting a
  // specifc op and toggled off afterward
  this.preventCompose = false;

  // If set to true, the source will be submitted over the connection. This
  // will also have the side-effect of only composing ops whose sources are
  // equal
  this.submitSource = false;

  // Prevent own ops being submitted to the server. If subscribed, remote
  // ops are still received. Should be toggled through the pause() and
  // resume() methods to correctly flush on resume.
  this.paused = false;

  // Internal counter that gets incremented every time doc.data is updated.
  // Used as a cheap way to check if doc.data has changed.
  this._dataStateVersion = 0;
}
emitter$5.mixin(Doc$1);

Doc$1.prototype.destroy = function(callback) {
  this._wantsDestroy = true;
  var doc = this;
  doc.whenNothingPending(function() {
    if (doc.wantSubscribe) {
      doc.unsubscribe(function(err) {
        if (err) {
          if (callback) return callback(err);
          return doc.emit('error', err);
        }
        doc.connection._destroyDoc(doc);
        if (callback) callback();
      });
    } else {
      doc.connection._destroyDoc(doc);
      if (callback) callback();
    }
  });
};


// ****** Manipulating the document data, version and type.

// Set the document's type, and associated properties. Most of the logic in
// this function exists to update the document based on any added & removed API
// methods.
//
// @param newType OT type provided by the ottypes library or its name or uri
Doc$1.prototype._setType = function(newType) {
  if (typeof newType === 'string') {
    newType = types$2.map[newType];
  }

  if (newType) {
    this.type = newType;
  } else if (newType === null) {
    this.type = newType;
    // If we removed the type from the object, also remove its data
    this._setData(undefined);
  } else {
    var err = new ShareDBError$2(ERROR_CODE$2.ERR_DOC_TYPE_NOT_RECOGNIZED, 'Missing type ' + newType);
    return this.emit('error', err);
  }
};

Doc$1.prototype._setData = function(data) {
  this.data = data;
  this._dataStateVersion++;
};

// Ingest snapshot data. This data must include a version, snapshot and type.
// This is used both to ingest data that was exported with a webpage and data
// that was received from the server during a fetch.
//
// @param snapshot.v    version
// @param snapshot.data
// @param snapshot.type
// @param callback
Doc$1.prototype.ingestSnapshot = function(snapshot, callback) {
  if (!snapshot) return callback && callback();

  if (typeof snapshot.v !== 'number') {
    var err = new ShareDBError$2(
      ERROR_CODE$2.ERR_INGESTED_SNAPSHOT_HAS_NO_VERSION,
      'Missing version in ingested snapshot. ' + this.collection + '.' + this.id
    );
    if (callback) return callback(err);
    return this.emit('error', err);
  }

  // If the doc is already created or there are ops pending, we cannot use the
  // ingested snapshot and need ops in order to update the document
  if (this.type || this.hasWritePending()) {
    // The version should only be null on a created document when it was
    // created locally without fetching
    if (this.version == null) {
      if (this.hasWritePending()) {
        // If we have pending ops and we get a snapshot for a locally created
        // document, we have to wait for the pending ops to complete, because
        // we don't know what version to fetch ops from. It is possible that
        // the snapshot came from our local op, but it is also possible that
        // the doc was created remotely (which would conflict and be an error)
        return callback && this.once('no write pending', callback);
      }
      // Otherwise, we've encounted an error state
      var err = new ShareDBError$2(
        ERROR_CODE$2.ERR_DOC_MISSING_VERSION,
        'Cannot ingest snapshot in doc with null version. ' + this.collection + '.' + this.id
      );
      if (callback) return callback(err);
      return this.emit('error', err);
    }
    // If we got a snapshot for a version further along than the document is
    // currently, issue a fetch to get the latest ops and catch us up
    if (snapshot.v > this.version) return this.fetch(callback);
    return callback && callback();
  }

  // Ignore the snapshot if we are already at a newer version. Under no
  // circumstance should we ever set the current version backward
  if (this.version > snapshot.v) return callback && callback();

  this.version = snapshot.v;
  var type = (snapshot.type === undefined) ? types$2.defaultType : snapshot.type;
  this._setType(type);
  this._setData(
    (this.type && this.type.deserialize) ?
      this.type.deserialize(snapshot.data) :
      snapshot.data
  );
  this.emit('load');
  callback && callback();
};

Doc$1.prototype.whenNothingPending = function(callback) {
  var doc = this;
  util$9.nextTick(function() {
    if (doc.hasPending()) {
      doc.once('nothing pending', callback);
      return;
    }
    callback();
  });
};

Doc$1.prototype.hasPending = function() {
  return !!(
    this.inflightOp ||
    this.pendingOps.length ||
    this.inflightFetch.length ||
    this.inflightSubscribe ||
    this.pendingFetch.length ||
    this.pendingSubscribe.length
  );
};

Doc$1.prototype.hasWritePending = function() {
  return !!(this.inflightOp || this.pendingOps.length);
};

Doc$1.prototype._emitNothingPending = function() {
  if (this.hasWritePending()) return;
  this.emit('no write pending');
  if (this.hasPending()) return;
  this.emit('nothing pending');
};

// **** Helpers for network messages

Doc$1.prototype._emitResponseError = function(err, callback) {
  if (err && err.code === ERROR_CODE$2.ERR_SNAPSHOT_READ_SILENT_REJECTION) {
    this.wantSubscribe = false;
    if (callback) {
      callback();
    }
    this._emitNothingPending();
    return;
  }
  if (callback) {
    callback(err);
    this._emitNothingPending();
    return;
  }
  this._emitNothingPending();
  this.emit('error', err);
};

Doc$1.prototype._handleFetch = function(error, snapshot) {
  var callbacks = this.pendingFetch;
  this.pendingFetch = [];
  var callback = this.inflightFetch.shift();
  if (callback) callbacks.push(callback);
  if (callbacks.length) {
    callback = function(error) {
      util$9.callEach(callbacks, error);
    };
  }
  if (error) return this._emitResponseError(error, callback);
  this.ingestSnapshot(snapshot, callback);
  this._emitNothingPending();
};

Doc$1.prototype._handleSubscribe = function(error, snapshot) {
  var request = this.inflightSubscribe;
  this.inflightSubscribe = null;
  var callbacks = this.pendingFetch;
  this.pendingFetch = [];
  if (request.callback) callbacks.push(request.callback);
  var callback;
  if (callbacks.length) {
    callback = function(error) {
      util$9.callEach(callbacks, error);
    };
  }
  if (error) return this._emitResponseError(error, callback);
  this.subscribed = request.wantSubscribe;
  if (this.subscribed) this.ingestSnapshot(snapshot, callback);
  else if (callback) callback();
  this._emitNothingPending();
  this._flushSubscribe();
};

Doc$1.prototype._handleOp = function(err, message) {
  if (err) {
    if (this.inflightOp) {
      // The server has rejected submission of the current operation. If we get
      // an "Op submit rejected" error, this was done intentionally
      // and we should roll back but not return an error to the user.
      if (err.code === ERROR_CODE$2.ERR_OP_SUBMIT_REJECTED) err = null;
      return this._rollback(err);
    }
    return this.emit('error', err);
  }

  if (this.inflightOp &&
      message.src === this.inflightOp.src &&
      message.seq === this.inflightOp.seq) {
    // The op has already been applied locally. Just update the version
    // and pending state appropriately
    this._opAcknowledged(message);
    return;
  }

  if (this.version == null || message.v > this.version) {
    // This will happen in normal operation if we become subscribed to a
    // new document via a query. It can also happen if we get an op for
    // a future version beyond the version we are expecting next. This
    // could happen if the server doesn't publish an op for whatever reason
    // or because of a race condition. In any case, we can send a fetch
    // command to catch back up.
    //
    // Fetch only sends a new fetch command if no fetches are inflight, which
    // will act as a natural debouncing so we don't send multiple fetch
    // requests for many ops received at once.
    this.fetch();
    return;
  }

  if (message.v < this.version) {
    // We can safely ignore the old (duplicate) operation.
    return;
  }

  if (this.inflightOp) {
    var transformErr = transformX(this.inflightOp, message);
    if (transformErr) return this._hardRollback(transformErr);
  }

  for (var i = 0; i < this.pendingOps.length; i++) {
    var transformErr = transformX(this.pendingOps[i], message);
    if (transformErr) return this._hardRollback(transformErr);
  }

  this.version++;
  try {
    this._otApply(message, false);
  } catch (error) {
    return this._hardRollback(error);
  }
};

// Called whenever (you guessed it!) the connection state changes. This will
// happen when we get disconnected & reconnect.
Doc$1.prototype._onConnectionStateChanged = function() {
  if (this.connection.canSend) {
    this.flush();
    this._resubscribe();
  } else {
    if (this.inflightOp) {
      this.pendingOps.unshift(this.inflightOp);
      this.inflightOp = null;
    }
    this.subscribed = false;
    if (this.inflightSubscribe) {
      if (this.inflightSubscribe.wantSubscribe) {
        this.pendingSubscribe.unshift(this.inflightSubscribe);
        this.inflightSubscribe = null;
      } else {
        this._handleSubscribe();
      }
    }
    if (this.inflightFetch.length) {
      this.pendingFetch = this.pendingFetch.concat(this.inflightFetch);
      this.inflightFetch.length = 0;
    }
  }
};

Doc$1.prototype._resubscribe = function() {
  if (!this.pendingSubscribe.length && this.wantSubscribe) {
    return this.subscribe();
  }
  var willFetch = this.pendingSubscribe.some(function(request) {
    return request.wantSubscribe;
  });
  if (!willFetch && this.pendingFetch.length) this.fetch();
  this._flushSubscribe();
};

// Request the current document snapshot or ops that bring us up to date
Doc$1.prototype.fetch = function(callback) {
  if (this.connection.canSend) {
    var isDuplicate = this.connection.sendFetch(this);
    pushActionCallback(this.inflightFetch, isDuplicate, callback);
    return;
  }
  this.pendingFetch.push(callback);
};

// Fetch the initial document and keep receiving updates
Doc$1.prototype.subscribe = function(callback) {
  var wantSubscribe = true;
  this._queueSubscribe(wantSubscribe, callback);
};

// Unsubscribe. The data will stay around in local memory, but we'll stop
// receiving updates
Doc$1.prototype.unsubscribe = function(callback) {
  var wantSubscribe = false;
  this._queueSubscribe(wantSubscribe, callback);
};

Doc$1.prototype._queueSubscribe = function(wantSubscribe, callback) {
  var lastRequest = this.pendingSubscribe[this.pendingSubscribe.length - 1] || this.inflightSubscribe;
  var isDuplicateRequest = lastRequest && lastRequest.wantSubscribe === wantSubscribe;
  if (isDuplicateRequest) {
    lastRequest.callback = combineCallbacks([lastRequest.callback, callback]);
    return;
  }
  this.pendingSubscribe.push({
    wantSubscribe: !!wantSubscribe,
    callback: callback
  });
  this._flushSubscribe();
};

Doc$1.prototype._flushSubscribe = function() {
  if (this.inflightSubscribe || !this.pendingSubscribe.length) return;

  if (this.connection.canSend) {
    this.inflightSubscribe = this.pendingSubscribe.shift();
    this.wantSubscribe = this.inflightSubscribe.wantSubscribe;
    if (this.wantSubscribe) {
      this.connection.sendSubscribe(this);
    } else {
      // Be conservative about our subscription state. We'll be unsubscribed
      // some time between sending this request, and receiving the callback,
      // so let's just set ourselves to unsubscribed now.
      this.subscribed = false;
      this.connection.sendUnsubscribe(this);
    }

    return;
  }

  // If we're offline, then we're already unsubscribed. Therefore, call back
  // the next request immediately if it's an unsubscribe request.
  if (!this.pendingSubscribe[0].wantSubscribe) {
    this.inflightSubscribe = this.pendingSubscribe.shift();
    var doc = this;
    util$9.nextTick(function() {
      doc._handleSubscribe();
    });
  }
};

function pushActionCallback(inflight, isDuplicate, callback) {
  if (isDuplicate) {
    var lastCallback = inflight.pop();
    inflight.push(function(err) {
      lastCallback && lastCallback(err);
      callback && callback(err);
    });
  } else {
    inflight.push(callback);
  }
}

function combineCallbacks(callbacks) {
  callbacks = callbacks.filter(util$9.truthy);
  if (!callbacks.length) return null;
  return function(error) {
    util$9.callEach(callbacks, error);
  };
}


// Operations //

// Send the next pending op to the server, if we can.
//
// Only one operation can be in-flight at a time. If an operation is already on
// its way, or we're not currently connected, this method does nothing.
Doc$1.prototype.flush = function() {
  // Ignore if we can't send or we are already sending an op
  if (!this.connection.canSend || this.inflightOp) return;

  // Send first pending op unless paused
  if (!this.paused && this.pendingOps.length) {
    this._sendOp();
  }
};

// Helper function to set op to contain a no-op.
function setNoOp(op) {
  delete op.op;
  delete op.create;
  delete op.del;
}

// Transform server op data by a client op, and vice versa. Ops are edited in place.
function transformX(client, server) {
  // Order of statements in this function matters. Be especially careful if
  // refactoring this function

  // A client delete op should dominate if both the server and the client
  // delete the document. Thus, any ops following the client delete (such as a
  // subsequent create) will be maintained, since the server op is transformed
  // to a no-op
  if (client.del) return setNoOp(server);

  if (server.del) {
    return new ShareDBError$2(ERROR_CODE$2.ERR_DOC_WAS_DELETED, 'Document was deleted');
  }
  if (server.create) {
    return new ShareDBError$2(ERROR_CODE$2.ERR_DOC_ALREADY_CREATED, 'Document already created');
  }

  // Ignore no-op coming from server
  if (!('op' in server)) return;

  // I believe that this should not occur, but check just in case
  if (client.create) {
    return new ShareDBError$2(ERROR_CODE$2.ERR_DOC_ALREADY_CREATED, 'Document already created');
  }

  // They both edited the document. This is the normal case for this function -
  // as in, most of the time we'll end up down here.
  //
  // You should be wondering why I'm using client.type instead of this.type.
  // The reason is, if we get ops at an old version of the document, this.type
  // might be undefined or a totally different type. By pinning the type to the
  // op data, we make sure the right type has its transform function called.
  if (client.type.transformX) {
    var result = client.type.transformX(client.op, server.op);
    client.op = result[0];
    server.op = result[1];
  } else {
    var clientOp = client.type.transform(client.op, server.op, 'left');
    var serverOp = client.type.transform(server.op, client.op, 'right');
    client.op = clientOp;
    server.op = serverOp;
  }
}
/**
 * Applies the operation to the snapshot
 *
 * If the operation is create or delete it emits `create` or `del`. Then the
 * operation is applied to the snapshot and `op` and `after op` are emitted.
 * If the type supports incremental updates and `this.incremental` is true we
 * fire `op` after every small operation.
 *
 * This is the only function to fire the above mentioned events.
 *
 * @private
 */
Doc$1.prototype._otApply = function(op, source) {
  if ('op' in op) {
    if (!this.type) {
      // Throw here, because all usage of _otApply should be wrapped with a try/catch
      throw new ShareDBError$2(
        ERROR_CODE$2.ERR_DOC_DOES_NOT_EXIST,
        'Cannot apply op to uncreated document. ' + this.collection + '.' + this.id
      );
    }

    // NB: If we need to add another argument to this event, we should consider
    // the fact that the 'op' event has op.src as its 3rd argument
    this.emit('before op batch', op.op, source);

    // Iteratively apply multi-component remote operations and rollback ops
    // (source === false) for the default JSON0 OT type. It could use
    // type.shatter(), but since this code is so specific to use cases for the
    // JSON0 type and ShareDB explicitly bundles the default type, we might as
    // well write it this way and save needing to iterate through the op
    // components twice.
    //
    // Ideally, we would not need this extra complexity. However, it is
    // helpful for implementing bindings that update DOM nodes and other
    // stateful objects by translating op events directly into corresponding
    // mutations. Such bindings are most easily written as responding to
    // individual op components one at a time in order, and it is important
    // that the snapshot only include updates from the particular op component
    // at the time of emission. Eliminating this would require rethinking how
    // such external bindings are implemented.
    if (!source && this.type === types$2.defaultType && op.op.length > 1) {
      if (!this.applyStack) this.applyStack = [];
      var stackLength = this.applyStack.length;
      for (var i = 0; i < op.op.length; i++) {
        var component = op.op[i];
        var componentOp = {op: [component]};
        // Apply the individual op component
        this.emit('before op', componentOp.op, source, op.src);
        // Transform componentOp against any ops that have been submitted
        // sychronously inside of an op event handler since we began apply of
        // our operation
        for (var j = stackLength; j < this.applyStack.length; j++) {
          var transformErr = transformX(this.applyStack[j], componentOp);
          if (transformErr) return this._hardRollback(transformErr);
        }
        this._setData(this.type.apply(this.data, componentOp.op));
        this.emit('op', componentOp.op, source, op.src);
      }
      this.emit('op batch', op.op, source);
      // Pop whatever was submitted since we started applying this op
      this._popApplyStack(stackLength);
      return;
    }

    // The 'before op' event enables clients to pull any necessary data out of
    // the snapshot before it gets changed
    this.emit('before op', op.op, source, op.src);
    // Apply the operation to the local data, mutating it in place
    this._setData(this.type.apply(this.data, op.op));
    // Emit an 'op' event once the local data includes the changes from the
    // op. For locally submitted ops, this will be synchronously with
    // submission and before the server or other clients have received the op.
    // For ops from other clients, this will be after the op has been
    // committed to the database and published
    this.emit('op', op.op, source, op.src);
    this.emit('op batch', op.op, source);
    return;
  }

  if (op.create) {
    this._setType(op.create.type);
    if (this.type.deserialize) {
      if (this.type.createDeserialized) {
        this._setData(this.type.createDeserialized(op.create.data));
      } else {
        this._setData(this.type.deserialize(this.type.create(op.create.data)));
      }
    } else {
      this._setData(this.type.create(op.create.data));
    }
    this.emit('create', source);
    return;
  }

  if (op.del) {
    var oldData = this.data;
    this._setType(null);
    this.emit('del', oldData, source);
    return;
  }
};


// ***** Sending operations

// Actually send op to the server.
Doc$1.prototype._sendOp = function() {
  if (!this.connection.canSend) return;
  var src = this.connection.id;

  // When there is no inflightOp, send the first item in pendingOps. If
  // there is inflightOp, try sending it again
  if (!this.inflightOp) {
    // Send first pending op
    this.inflightOp = this.pendingOps.shift();
  }
  var op = this.inflightOp;
  if (!op) {
    var err = new ShareDBError$2(ERROR_CODE$2.ERR_INFLIGHT_OP_MISSING, 'No op to send on call to _sendOp');
    return this.emit('error', err);
  }

  // Track data for retrying ops
  op.sentAt = Date.now();
  op.retries = (op.retries == null) ? 0 : op.retries + 1;

  // The src + seq number is a unique ID representing this operation. This tuple
  // is used on the server to detect when ops have been sent multiple times and
  // on the client to match acknowledgement of an op back to the inflightOp.
  // Note that the src could be different from this.connection.id after a
  // reconnect, since an op may still be pending after the reconnection and
  // this.connection.id will change. In case an op is sent multiple times, we
  // also need to be careful not to override the original seq value.
  if (op.seq == null) {
    if (this.connection.seq >= util$9.MAX_SAFE_INTEGER) {
      return this.emit('error', new ShareDBError$2(
        ERROR_CODE$2.ERR_CONNECTION_SEQ_INTEGER_OVERFLOW,
        'Connection seq has exceeded the max safe integer, maybe from being open for too long'
      ));
    }

    op.seq = this.connection.seq++;
  }

  this.connection.sendOp(this, op);

  // src isn't needed on the first try, since the server session will have the
  // same id, but it must be set on the inflightOp in case it is sent again
  // after a reconnect and the connection's id has changed by then
  if (op.src == null) op.src = src;
};


// Queues the operation for submission to the server and applies it locally.
//
// Internal method called to do the actual work for submit(), create() and del().
// @private
//
// @param op
// @param [op.op]
// @param [op.del]
// @param [op.create]
// @param [callback] called when operation is submitted
Doc$1.prototype._submit = function(op, source, callback) {
  // Locally submitted ops must always have a truthy source
  if (!source) source = true;

  // The op contains either op, create, delete, or none of the above (a no-op).
  if ('op' in op) {
    if (!this.type) {
      var err = new ShareDBError$2(
        ERROR_CODE$2.ERR_DOC_DOES_NOT_EXIST,
        'Cannot submit op. Document has not been created. ' + this.collection + '.' + this.id
      );
      if (callback) return callback(err);
      return this.emit('error', err);
    }
    // Try to normalize the op. This removes trailing skip:0's and things like that.
    if (this.type.normalize) op.op = this.type.normalize(op.op);
  }

  try {
    this._pushOp(op, source, callback);
    this._otApply(op, source);
  } catch (error) {
    return this._hardRollback(error);
  }

  // The call to flush is delayed so if submit() is called multiple times
  // synchronously, all the ops are combined before being sent to the server.
  var doc = this;
  util$9.nextTick(function() {
    doc.flush();
  });
};

Doc$1.prototype._pushOp = function(op, source, callback) {
  op.source = source;
  if (this.applyStack) {
    // If we are in the process of incrementally applying an operation, don't
    // compose the op and push it onto the applyStack so it can be transformed
    // against other components from the op or ops being applied
    this.applyStack.push(op);
  } else {
    // If the type supports composes, try to compose the operation onto the
    // end of the last pending operation.
    var composed = this._tryCompose(op);
    if (composed) {
      composed.callbacks.push(callback);
      return;
    }
  }
  // Push on to the pendingOps queue of ops to submit if we didn't compose
  op.type = this.type;
  op.callbacks = [callback];
  this.pendingOps.push(op);
};

Doc$1.prototype._popApplyStack = function(to) {
  if (to > 0) {
    this.applyStack.length = to;
    return;
  }
  // Once we have completed the outermost apply loop, reset to null and no
  // longer add ops to the applyStack as they are submitted
  var op = this.applyStack[0];
  this.applyStack = null;
  if (!op) return;
  // Compose the ops added since the beginning of the apply stack, since we
  // had to skip compose when they were originally pushed
  var i = this.pendingOps.indexOf(op);
  if (i === -1) return;
  var ops = this.pendingOps.splice(i);
  for (var i = 0; i < ops.length; i++) {
    var op = ops[i];
    var composed = this._tryCompose(op);
    if (composed) {
      composed.callbacks = composed.callbacks.concat(op.callbacks);
    } else {
      this.pendingOps.push(op);
    }
  }
};

// Try to compose a submitted op into the last pending op. Returns the
// composed op if it succeeds, undefined otherwise
Doc$1.prototype._tryCompose = function(op) {
  if (this.preventCompose) return;

  // We can only compose into the last pending op. Inflight ops have already
  // been sent to the server, so we can't modify them
  var last = this.pendingOps[this.pendingOps.length - 1];
  if (!last || last.sentAt) return;

  // If we're submitting the op source, we can only combine ops that have
  // a matching source
  if (this.submitSource && !deepEqual(op.source, last.source)) return;

  // Compose an op into a create by applying it. This effectively makes the op
  // invisible, as if the document were created including the op originally
  if (last.create && 'op' in op) {
    last.create.data = this.type.apply(last.create.data, op.op);
    return last;
  }

  // Compose two ops into a single op if supported by the type. Types that
  // support compose must be able to compose any two ops together
  if ('op' in last && 'op' in op && this.type.compose) {
    last.op = this.type.compose(last.op, op.op);
    return last;
  }
};

// *** Client OT entrypoints.

// Submit an operation to the document.
//
// @param operation handled by the OT type
// @param options  {source: ...}
// @param [callback] called after operation submitted
//
// @fires before op, op, after op
Doc$1.prototype.submitOp = function(component, options, callback) {
  if (typeof options === 'function') {
    callback = options;
    options = null;
  }
  var op = {op: component};
  var source = options && options.source;
  this._submit(op, source, callback);
};

// Create the document, which in ShareJS semantics means to set its type. Every
// object implicitly exists in the database but has no data and no type. Create
// sets the type of the object and can optionally set some initial data on the
// object, depending on the type.
//
// @param data  initial
// @param type  OT type
// @param options  {source: ...}
// @param callback  called when operation submitted
Doc$1.prototype.create = function(data, type, options, callback) {
  if (typeof type === 'function') {
    callback = type;
    options = null;
    type = null;
  } else if (typeof options === 'function') {
    callback = options;
    options = null;
  }
  if (!type) {
    type = types$2.defaultType.uri;
  }
  if (this.type) {
    var err = new ShareDBError$2(ERROR_CODE$2.ERR_DOC_ALREADY_CREATED, 'Document already exists');
    if (callback) return callback(err);
    return this.emit('error', err);
  }
  var op = {create: {type: type, data: data}};
  var source = options && options.source;
  this._submit(op, source, callback);
};

// Delete the document. This creates and submits a delete operation to the
// server. Deleting resets the object's type to null and deletes its data. The
// document still exists, and still has the version it used to have before you
// deleted it (well, old version +1).
//
// @param options  {source: ...}
// @param callback  called when operation submitted
Doc$1.prototype.del = function(options, callback) {
  if (typeof options === 'function') {
    callback = options;
    options = null;
  }
  if (!this.type) {
    var err = new ShareDBError$2(ERROR_CODE$2.ERR_DOC_DOES_NOT_EXIST, 'Document does not exist');
    if (callback) return callback(err);
    return this.emit('error', err);
  }
  var op = {del: true};
  var source = options && options.source;
  this._submit(op, source, callback);
};


// Stops the document from sending any operations to the server.
Doc$1.prototype.pause = function() {
  this.paused = true;
};

// Continue sending operations to the server
Doc$1.prototype.resume = function() {
  this.paused = false;
  this.flush();
};

// Create a snapshot that can be serialized, deserialized, and passed into `Doc.ingestSnapshot`.
Doc$1.prototype.toSnapshot = function() {
  return {
    v: this.version,
    data: clone(this.data),
    type: this.type.uri
  };
};

// *** Receiving operations

// This is called when the server acknowledges an operation from the client.
Doc$1.prototype._opAcknowledged = function(message) {
  if (this.inflightOp.create) {
    this.version = message.v;
  } else if (message.v !== this.version) {
    // We should already be at the same version, because the server should
    // have sent all the ops that have happened before acknowledging our op
    logger$1.warn('Invalid version from server. Expected: ' + this.version + ' Received: ' + message.v, message);

    // Fetching should get us back to a working document state
    return this.fetch();
  }

  if (message[ACTIONS$6.fixup]) {
    for (var i = 0; i < message[ACTIONS$6.fixup].length; i++) {
      var fixupOp = message[ACTIONS$6.fixup][i];

      for (var j = 0; j < this.pendingOps.length; j++) {
        var transformErr = transformX(this.pendingOps[i], fixupOp);
        if (transformErr) return this._hardRollback(transformErr);
      }

      try {
        this._otApply(fixupOp, false);
      } catch (error) {
        return this._hardRollback(error);
      }
    }
  }

  // The op was committed successfully. Increment the version number
  this.version++;

  this._clearInflightOp();
};

Doc$1.prototype._rollback = function(err) {
  // The server has rejected submission of the current operation. Invert by
  // just the inflight op if possible. If not possible to invert, cancel all
  // pending ops and fetch the latest from the server to get us back into a
  // working state, then call back
  var op = this.inflightOp;

  if ('op' in op && op.type.invert) {
    try {
      op.op = op.type.invert(op.op);
    } catch (error) {
      // If the op doesn't support `.invert()`, we just reload the doc
      // instead of trying to locally revert it.
      return this._hardRollback(err);
    }

    // Transform the undo operation by any pending ops.
    for (var i = 0; i < this.pendingOps.length; i++) {
      var transformErr = transformX(this.pendingOps[i], op);
      if (transformErr) return this._hardRollback(transformErr);
    }

    // ... and apply it locally, reverting the changes.
    //
    // This operation is applied to look like it comes from a remote source.
    // I'm still not 100% sure about this functionality, because its really a
    // local op. Basically, the problem is that if the client's op is rejected
    // by the server, the editor window should update to reflect the undo.
    try {
      this._otApply(op, false);
    } catch (error) {
      return this._hardRollback(error);
    }

    this._clearInflightOp(err);
    return;
  }

  this._hardRollback(err);
};

Doc$1.prototype._hardRollback = function(err) {
  // Store pending ops so that we can notify their callbacks of the error.
  // We combine the inflight op and the pending ops, because it's possible
  // to hit a condition where we have no inflight op, but we do have pending
  // ops. This can happen when an invalid op is submitted, which causes us
  // to hard rollback before the pending op was flushed.
  var pendingOps = [];
  if (this.inflightOp) pendingOps.push(this.inflightOp);
  pendingOps = pendingOps.concat(this.pendingOps);

  // Cancel all pending ops and reset if we can't invert
  this._setType(null);
  this.version = null;
  this.inflightOp = null;
  this.pendingOps = [];

  // Fetch the latest version from the server to get us back into a working state
  var doc = this;
  this.fetch(function() {
    // We want to check that no errors are swallowed, so we check that:
    // - there are callbacks to call, and
    // - that every single pending op called a callback
    // If there are no ops queued, or one of them didn't handle the error,
    // then we emit the error.
    var allOpsHadCallbacks = !!pendingOps.length;
    for (var i = 0; i < pendingOps.length; i++) {
      allOpsHadCallbacks = util$9.callEach(pendingOps[i].callbacks, err) && allOpsHadCallbacks;
    }
    if (err && !allOpsHadCallbacks) return doc.emit('error', err);
  });
};

Doc$1.prototype._clearInflightOp = function(err) {
  var inflightOp = this.inflightOp;

  this.inflightOp = null;

  var called = util$9.callEach(inflightOp.callbacks, err);

  this.flush();
  this._emitNothingPending();

  if (err && !called) return this.emit('error', err);
};

var emitter$4 = emitter$6;
var ACTIONS$5 = messageActions.ACTIONS;
var util$8 = util$a;

// Queries are live requests to the database for particular sets of fields.
//
// The server actively tells the client when there's new data that matches
// a set of conditions.
var query = Query$1;
function Query$1(action, connection, id, collection, query, options, callback) {
  emitter$4.EventEmitter.call(this);

  // 'qf' or 'qs'
  this.action = action;

  this.connection = connection;
  this.id = id;
  this.collection = collection;

  // The query itself. For mongo, this should look something like {"data.x":5}
  this.query = query;

  // A list of resulting documents. These are actual documents, complete with
  // data and all the rest. It is possible to pass in an initial results set,
  // so that a query can be serialized and then re-established
  this.results = null;
  if (options && options.results) {
    this.results = options.results;
    delete options.results;
  }
  this.extra = undefined;

  // Options to pass through with the query
  this.options = options;

  this.callback = callback;
  this.ready = false;
  this.sent = false;
}
emitter$4.mixin(Query$1);

Query$1.prototype.hasPending = function() {
  return !this.ready;
};

// Helper for subscribe & fetch, since they share the same message format.
//
// This function actually issues the query.
Query$1.prototype.send = function() {
  if (!this.connection.canSend) return;

  var message = {
    a: this.action,
    id: this.id,
    c: this.collection,
    q: this.query
  };
  if (this.options) {
    message.o = this.options;
  }
  if (this.results) {
    // Collect the version of all the documents in the current result set so we
    // don't need to be sent their snapshots again.
    var results = [];
    for (var i = 0; i < this.results.length; i++) {
      var doc = this.results[i];
      results.push([doc.id, doc.version]);
    }
    message.r = results;
  }

  this.connection.send(message);
  this.sent = true;
};

// Destroy the query object. Any subsequent messages for the query will be
// ignored by the connection.
Query$1.prototype.destroy = function(callback) {
  if (this.connection.canSend && this.action === ACTIONS$5.querySubscribe) {
    this.connection.send({a: ACTIONS$5.queryUnsubscribe, id: this.id});
  }
  this.connection._destroyQuery(this);
  // There is a callback for consistency, but we don't actually wait for the
  // server's unsubscribe message currently
  if (callback) util$8.nextTick(callback);
};

Query$1.prototype._onConnectionStateChanged = function() {
  if (this.connection.canSend && !this.sent) {
    this.send();
  } else {
    this.sent = false;
  }
};

Query$1.prototype._handleFetch = function(err, data, extra) {
  // Once a fetch query gets its data, it is destroyed.
  this.connection._destroyQuery(this);
  this._handleResponse(err, data, extra);
};

Query$1.prototype._handleSubscribe = function(err, data, extra) {
  this._handleResponse(err, data, extra);
};

Query$1.prototype._handleResponse = function(err, data, extra) {
  var callback = this.callback;
  this.callback = null;
  if (err) return this._finishResponse(err, callback);
  if (!data) return this._finishResponse(null, callback);

  var query = this;
  var wait = 1;
  var finish = function(err) {
    if (err) return query._finishResponse(err, callback);
    if (--wait) return;
    query._finishResponse(null, callback);
  };

  if (Array.isArray(data)) {
    wait += data.length;
    this.results = this._ingestSnapshots(data, finish);
    this.extra = extra;
  } else {
    for (var id in data) {
      wait++;
      var snapshot = data[id];
      var doc = this.connection.get(snapshot.c || this.collection, id);
      doc.ingestSnapshot(snapshot, finish);
    }
  }

  finish();
};

Query$1.prototype._ingestSnapshots = function(snapshots, finish) {
  var results = [];
  for (var i = 0; i < snapshots.length; i++) {
    var snapshot = snapshots[i];
    var doc = this.connection.get(snapshot.c || this.collection, snapshot.d);
    doc.ingestSnapshot(snapshot, finish);
    results.push(doc);
  }
  return results;
};

Query$1.prototype._finishResponse = function(err, callback) {
  this.emit('ready');
  this.ready = true;
  if (err) {
    this.connection._destroyQuery(this);
    if (callback) return callback(err);
    return this.emit('error', err);
  }
  if (callback) callback(null, this.results, this.extra);
};

Query$1.prototype._handleError = function(err) {
  this.emit('error', err);
};

Query$1.prototype._handleDiff = function(diff) {
  // We need to go through the list twice. First, we'll ingest all the new
  // documents. After that we'll emit events and actually update our list.
  // This avoids race conditions around setting documents to be subscribed &
  // unsubscribing documents in event callbacks.
  for (var i = 0; i < diff.length; i++) {
    var d = diff[i];
    if (d.type === 'insert') d.values = this._ingestSnapshots(d.values);
  }

  for (var i = 0; i < diff.length; i++) {
    var d = diff[i];
    switch (d.type) {
      case 'insert':
        var newDocs = d.values;
        Array.prototype.splice.apply(this.results, [d.index, 0].concat(newDocs));
        this.emit('insert', newDocs, d.index);
        break;
      case 'remove':
        var howMany = d.howMany || 1;
        var removed = this.results.splice(d.index, howMany);
        this.emit('remove', removed, d.index);
        break;
      case 'move':
        var howMany = d.howMany || 1;
        var docs = this.results.splice(d.from, howMany);
        Array.prototype.splice.apply(this.results, [d.to, 0].concat(docs));
        this.emit('move', docs, d.from, d.to);
        break;
    }
  }

  this.emit('changed', this.results);
};

Query$1.prototype._handleExtra = function(extra) {
  this.extra = extra;
  this.emit('extra', extra);
};

var emitter$3 = emitter$6;
var ACTIONS$4 = messageActions.ACTIONS;
var util$7 = util$a;

var localPresence = LocalPresence$2;
function LocalPresence$2(presence, presenceId) {
  emitter$3.EventEmitter.call(this);

  if (!presenceId || typeof presenceId !== 'string') {
    throw new Error('LocalPresence presenceId must be a string');
  }

  this.presence = presence;
  this.presenceId = presenceId;
  this.connection = presence.connection;
  this.presenceVersion = 0;

  this.value = null;

  this._pendingMessages = [];
  this._callbacksByPresenceVersion = {};
}
emitter$3.mixin(LocalPresence$2);

LocalPresence$2.prototype.submit = function(value, callback) {
  this.value = value;
  this.send(callback);
};

LocalPresence$2.prototype.send = function(callback) {
  var message = this._message();
  this._pendingMessages.push(message);
  this._callbacksByPresenceVersion[message.pv] = callback;
  this._sendPending();
};

LocalPresence$2.prototype.destroy = function(callback) {
  var presence = this;
  this.submit(null, function(error) {
    if (error) return presence._callbackOrEmit(error, callback);
    delete presence.presence.localPresences[presence.presenceId];
    if (callback) callback();
  });
};

LocalPresence$2.prototype._sendPending = function() {
  if (!this.connection.canSend) return;
  var presence = this;
  this._pendingMessages.forEach(function(message) {
    presence.connection.send(message);
  });

  this._pendingMessages = [];
};

LocalPresence$2.prototype._ack = function(error, presenceVersion) {
  var callback = this._getCallback(presenceVersion);
  this._callbackOrEmit(error, callback);
};

LocalPresence$2.prototype._message = function() {
  return {
    a: ACTIONS$4.presence,
    ch: this.presence.channel,
    id: this.presenceId,
    p: this.value,
    pv: this.presenceVersion++
  };
};

LocalPresence$2.prototype._getCallback = function(presenceVersion) {
  var callback = this._callbacksByPresenceVersion[presenceVersion];
  delete this._callbacksByPresenceVersion[presenceVersion];
  return callback;
};

LocalPresence$2.prototype._callbackOrEmit = function(error, callback) {
  if (callback) return util$7.nextTick(callback, error);
  if (error) this.emit('error', error);
};

var util$6 = util$a;

var remotePresence = RemotePresence$2;
function RemotePresence$2(presence, presenceId) {
  this.presence = presence;
  this.presenceId = presenceId;
  this.connection = this.presence.connection;

  this.value = null;
  this.presenceVersion = 0;
}

RemotePresence$2.prototype.receiveUpdate = function(message) {
  if (message.pv < this.presenceVersion) return;
  this.value = message.p;
  this.presenceVersion = message.pv;
  this.presence._updateRemotePresence(this);
};

RemotePresence$2.prototype.destroy = function(callback) {
  delete this.presence._remotePresenceInstances[this.presenceId];
  delete this.presence.remotePresences[this.presenceId];
  if (callback) util$6.nextTick(callback);
};

var async$1 = {exports: {}};

async$1.exports;

(function (module, exports) {
	(function (global, factory) {
	  factory(exports) ;
	}(commonjsGlobal, (function (exports) {
	function slice(arrayLike, start) {
	    start = start|0;
	    var newLen = Math.max(arrayLike.length - start, 0);
	    var newArr = Array(newLen);
	    for(var idx = 0; idx < newLen; idx++)  {
	        newArr[idx] = arrayLike[start + idx];
	    }
	    return newArr;
	}

	/**
	 * Creates a continuation function with some arguments already applied.
	 *
	 * Useful as a shorthand when combined with other control flow functions. Any
	 * arguments passed to the returned function are added to the arguments
	 * originally passed to apply.
	 *
	 * @name apply
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @category Util
	 * @param {Function} fn - The function you want to eventually apply all
	 * arguments to. Invokes with (arguments...).
	 * @param {...*} arguments... - Any number of arguments to automatically apply
	 * when the continuation is called.
	 * @returns {Function} the partially-applied function
	 * @example
	 *
	 * // using apply
	 * async.parallel([
	 *     async.apply(fs.writeFile, 'testfile1', 'test1'),
	 *     async.apply(fs.writeFile, 'testfile2', 'test2')
	 * ]);
	 *
	 *
	 * // the same process without using apply
	 * async.parallel([
	 *     function(callback) {
	 *         fs.writeFile('testfile1', 'test1', callback);
	 *     },
	 *     function(callback) {
	 *         fs.writeFile('testfile2', 'test2', callback);
	 *     }
	 * ]);
	 *
	 * // It's possible to pass any number of additional arguments when calling the
	 * // continuation:
	 *
	 * node> var fn = async.apply(sys.puts, 'one');
	 * node> fn('two', 'three');
	 * one
	 * two
	 * three
	 */
	var apply = function(fn/*, ...args*/) {
	    var args = slice(arguments, 1);
	    return function(/*callArgs*/) {
	        var callArgs = slice(arguments);
	        return fn.apply(null, args.concat(callArgs));
	    };
	};

	var initialParams = function (fn) {
	    return function (/*...args, callback*/) {
	        var args = slice(arguments);
	        var callback = args.pop();
	        fn.call(this, args, callback);
	    };
	};

	/**
	 * Checks if `value` is the
	 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
	 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
	 *
	 * @static
	 * @memberOf _
	 * @since 0.1.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
	 * @example
	 *
	 * _.isObject({});
	 * // => true
	 *
	 * _.isObject([1, 2, 3]);
	 * // => true
	 *
	 * _.isObject(_.noop);
	 * // => true
	 *
	 * _.isObject(null);
	 * // => false
	 */
	function isObject(value) {
	  var type = typeof value;
	  return value != null && (type == 'object' || type == 'function');
	}

	var hasSetImmediate = typeof setImmediate === 'function' && setImmediate;
	var hasNextTick = typeof browser$1 === 'object' && typeof browser$1.nextTick === 'function';

	function fallback(fn) {
	    setTimeout(fn, 0);
	}

	function wrap(defer) {
	    return function (fn/*, ...args*/) {
	        var args = slice(arguments, 1);
	        defer(function () {
	            fn.apply(null, args);
	        });
	    };
	}

	var _defer;

	if (hasSetImmediate) {
	    _defer = setImmediate;
	} else if (hasNextTick) {
	    _defer = browser$1.nextTick;
	} else {
	    _defer = fallback;
	}

	var setImmediate$1 = wrap(_defer);

	/**
	 * Take a sync function and make it async, passing its return value to a
	 * callback. This is useful for plugging sync functions into a waterfall,
	 * series, or other async functions. Any arguments passed to the generated
	 * function will be passed to the wrapped function (except for the final
	 * callback argument). Errors thrown will be passed to the callback.
	 *
	 * If the function passed to `asyncify` returns a Promise, that promises's
	 * resolved/rejected state will be used to call the callback, rather than simply
	 * the synchronous return value.
	 *
	 * This also means you can asyncify ES2017 `async` functions.
	 *
	 * @name asyncify
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @alias wrapSync
	 * @category Util
	 * @param {Function} func - The synchronous function, or Promise-returning
	 * function to convert to an {@link AsyncFunction}.
	 * @returns {AsyncFunction} An asynchronous wrapper of the `func`. To be
	 * invoked with `(args..., callback)`.
	 * @example
	 *
	 * // passing a regular synchronous function
	 * async.waterfall([
	 *     async.apply(fs.readFile, filename, "utf8"),
	 *     async.asyncify(JSON.parse),
	 *     function (data, next) {
	 *         // data is the result of parsing the text.
	 *         // If there was a parsing error, it would have been caught.
	 *     }
	 * ], callback);
	 *
	 * // passing a function returning a promise
	 * async.waterfall([
	 *     async.apply(fs.readFile, filename, "utf8"),
	 *     async.asyncify(function (contents) {
	 *         return db.model.create(contents);
	 *     }),
	 *     function (model, next) {
	 *         // `model` is the instantiated model object.
	 *         // If there was an error, this function would be skipped.
	 *     }
	 * ], callback);
	 *
	 * // es2017 example, though `asyncify` is not needed if your JS environment
	 * // supports async functions out of the box
	 * var q = async.queue(async.asyncify(async function(file) {
	 *     var intermediateStep = await processFile(file);
	 *     return await somePromise(intermediateStep)
	 * }));
	 *
	 * q.push(files);
	 */
	function asyncify(func) {
	    return initialParams(function (args, callback) {
	        var result;
	        try {
	            result = func.apply(this, args);
	        } catch (e) {
	            return callback(e);
	        }
	        // if result is Promise object
	        if (isObject(result) && typeof result.then === 'function') {
	            result.then(function(value) {
	                invokeCallback(callback, null, value);
	            }, function(err) {
	                invokeCallback(callback, err.message ? err : new Error(err));
	            });
	        } else {
	            callback(null, result);
	        }
	    });
	}

	function invokeCallback(callback, error, value) {
	    try {
	        callback(error, value);
	    } catch (e) {
	        setImmediate$1(rethrow, e);
	    }
	}

	function rethrow(error) {
	    throw error;
	}

	var supportsSymbol = typeof Symbol === 'function';

	function isAsync(fn) {
	    return supportsSymbol && fn[Symbol.toStringTag] === 'AsyncFunction';
	}

	function wrapAsync(asyncFn) {
	    return isAsync(asyncFn) ? asyncify(asyncFn) : asyncFn;
	}

	function applyEach$1(eachfn) {
	    return function(fns/*, ...args*/) {
	        var args = slice(arguments, 1);
	        var go = initialParams(function(args, callback) {
	            var that = this;
	            return eachfn(fns, function (fn, cb) {
	                wrapAsync(fn).apply(that, args.concat(cb));
	            }, callback);
	        });
	        if (args.length) {
	            return go.apply(this, args);
	        }
	        else {
	            return go;
	        }
	    };
	}

	/** Detect free variable `global` from Node.js. */
	var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

	/** Detect free variable `self`. */
	var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

	/** Used as a reference to the global object. */
	var root = freeGlobal || freeSelf || Function('return this')();

	/** Built-in value references. */
	var Symbol$1 = root.Symbol;

	/** Used for built-in method references. */
	var objectProto = Object.prototype;

	/** Used to check objects for own properties. */
	var hasOwnProperty = objectProto.hasOwnProperty;

	/**
	 * Used to resolve the
	 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
	 * of values.
	 */
	var nativeObjectToString = objectProto.toString;

	/** Built-in value references. */
	var symToStringTag$1 = Symbol$1 ? Symbol$1.toStringTag : undefined;

	/**
	 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
	 *
	 * @private
	 * @param {*} value The value to query.
	 * @returns {string} Returns the raw `toStringTag`.
	 */
	function getRawTag(value) {
	  var isOwn = hasOwnProperty.call(value, symToStringTag$1),
	      tag = value[symToStringTag$1];

	  try {
	    value[symToStringTag$1] = undefined;
	    var unmasked = true;
	  } catch (e) {}

	  var result = nativeObjectToString.call(value);
	  if (unmasked) {
	    if (isOwn) {
	      value[symToStringTag$1] = tag;
	    } else {
	      delete value[symToStringTag$1];
	    }
	  }
	  return result;
	}

	/** Used for built-in method references. */
	var objectProto$1 = Object.prototype;

	/**
	 * Used to resolve the
	 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
	 * of values.
	 */
	var nativeObjectToString$1 = objectProto$1.toString;

	/**
	 * Converts `value` to a string using `Object.prototype.toString`.
	 *
	 * @private
	 * @param {*} value The value to convert.
	 * @returns {string} Returns the converted string.
	 */
	function objectToString(value) {
	  return nativeObjectToString$1.call(value);
	}

	/** `Object#toString` result references. */
	var nullTag = '[object Null]';
	var undefinedTag = '[object Undefined]';

	/** Built-in value references. */
	var symToStringTag = Symbol$1 ? Symbol$1.toStringTag : undefined;

	/**
	 * The base implementation of `getTag` without fallbacks for buggy environments.
	 *
	 * @private
	 * @param {*} value The value to query.
	 * @returns {string} Returns the `toStringTag`.
	 */
	function baseGetTag(value) {
	  if (value == null) {
	    return value === undefined ? undefinedTag : nullTag;
	  }
	  return (symToStringTag && symToStringTag in Object(value))
	    ? getRawTag(value)
	    : objectToString(value);
	}

	/** `Object#toString` result references. */
	var asyncTag = '[object AsyncFunction]';
	var funcTag = '[object Function]';
	var genTag = '[object GeneratorFunction]';
	var proxyTag = '[object Proxy]';

	/**
	 * Checks if `value` is classified as a `Function` object.
	 *
	 * @static
	 * @memberOf _
	 * @since 0.1.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
	 * @example
	 *
	 * _.isFunction(_);
	 * // => true
	 *
	 * _.isFunction(/abc/);
	 * // => false
	 */
	function isFunction(value) {
	  if (!isObject(value)) {
	    return false;
	  }
	  // The use of `Object#toString` avoids issues with the `typeof` operator
	  // in Safari 9 which returns 'object' for typed arrays and other constructors.
	  var tag = baseGetTag(value);
	  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
	}

	/** Used as references for various `Number` constants. */
	var MAX_SAFE_INTEGER = 9007199254740991;

	/**
	 * Checks if `value` is a valid array-like length.
	 *
	 * **Note:** This method is loosely based on
	 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
	 *
	 * @static
	 * @memberOf _
	 * @since 4.0.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
	 * @example
	 *
	 * _.isLength(3);
	 * // => true
	 *
	 * _.isLength(Number.MIN_VALUE);
	 * // => false
	 *
	 * _.isLength(Infinity);
	 * // => false
	 *
	 * _.isLength('3');
	 * // => false
	 */
	function isLength(value) {
	  return typeof value == 'number' &&
	    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
	}

	/**
	 * Checks if `value` is array-like. A value is considered array-like if it's
	 * not a function and has a `value.length` that's an integer greater than or
	 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
	 *
	 * @static
	 * @memberOf _
	 * @since 4.0.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
	 * @example
	 *
	 * _.isArrayLike([1, 2, 3]);
	 * // => true
	 *
	 * _.isArrayLike(document.body.children);
	 * // => true
	 *
	 * _.isArrayLike('abc');
	 * // => true
	 *
	 * _.isArrayLike(_.noop);
	 * // => false
	 */
	function isArrayLike(value) {
	  return value != null && isLength(value.length) && !isFunction(value);
	}

	// A temporary value used to identify if the loop should be broken.
	// See #1064, #1293
	var breakLoop = {};

	/**
	 * This method returns `undefined`.
	 *
	 * @static
	 * @memberOf _
	 * @since 2.3.0
	 * @category Util
	 * @example
	 *
	 * _.times(2, _.noop);
	 * // => [undefined, undefined]
	 */
	function noop() {
	  // No operation performed.
	}

	function once(fn) {
	    return function () {
	        if (fn === null) return;
	        var callFn = fn;
	        fn = null;
	        callFn.apply(this, arguments);
	    };
	}

	var iteratorSymbol = typeof Symbol === 'function' && Symbol.iterator;

	var getIterator = function (coll) {
	    return iteratorSymbol && coll[iteratorSymbol] && coll[iteratorSymbol]();
	};

	/**
	 * The base implementation of `_.times` without support for iteratee shorthands
	 * or max array length checks.
	 *
	 * @private
	 * @param {number} n The number of times to invoke `iteratee`.
	 * @param {Function} iteratee The function invoked per iteration.
	 * @returns {Array} Returns the array of results.
	 */
	function baseTimes(n, iteratee) {
	  var index = -1,
	      result = Array(n);

	  while (++index < n) {
	    result[index] = iteratee(index);
	  }
	  return result;
	}

	/**
	 * Checks if `value` is object-like. A value is object-like if it's not `null`
	 * and has a `typeof` result of "object".
	 *
	 * @static
	 * @memberOf _
	 * @since 4.0.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
	 * @example
	 *
	 * _.isObjectLike({});
	 * // => true
	 *
	 * _.isObjectLike([1, 2, 3]);
	 * // => true
	 *
	 * _.isObjectLike(_.noop);
	 * // => false
	 *
	 * _.isObjectLike(null);
	 * // => false
	 */
	function isObjectLike(value) {
	  return value != null && typeof value == 'object';
	}

	/** `Object#toString` result references. */
	var argsTag = '[object Arguments]';

	/**
	 * The base implementation of `_.isArguments`.
	 *
	 * @private
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
	 */
	function baseIsArguments(value) {
	  return isObjectLike(value) && baseGetTag(value) == argsTag;
	}

	/** Used for built-in method references. */
	var objectProto$3 = Object.prototype;

	/** Used to check objects for own properties. */
	var hasOwnProperty$2 = objectProto$3.hasOwnProperty;

	/** Built-in value references. */
	var propertyIsEnumerable = objectProto$3.propertyIsEnumerable;

	/**
	 * Checks if `value` is likely an `arguments` object.
	 *
	 * @static
	 * @memberOf _
	 * @since 0.1.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
	 *  else `false`.
	 * @example
	 *
	 * _.isArguments(function() { return arguments; }());
	 * // => true
	 *
	 * _.isArguments([1, 2, 3]);
	 * // => false
	 */
	var isArguments = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
	  return isObjectLike(value) && hasOwnProperty$2.call(value, 'callee') &&
	    !propertyIsEnumerable.call(value, 'callee');
	};

	/**
	 * Checks if `value` is classified as an `Array` object.
	 *
	 * @static
	 * @memberOf _
	 * @since 0.1.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
	 * @example
	 *
	 * _.isArray([1, 2, 3]);
	 * // => true
	 *
	 * _.isArray(document.body.children);
	 * // => false
	 *
	 * _.isArray('abc');
	 * // => false
	 *
	 * _.isArray(_.noop);
	 * // => false
	 */
	var isArray = Array.isArray;

	/**
	 * This method returns `false`.
	 *
	 * @static
	 * @memberOf _
	 * @since 4.13.0
	 * @category Util
	 * @returns {boolean} Returns `false`.
	 * @example
	 *
	 * _.times(2, _.stubFalse);
	 * // => [false, false]
	 */
	function stubFalse() {
	  return false;
	}

	/** Detect free variable `exports`. */
	var freeExports = typeof exports == 'object' && exports && !exports.nodeType && exports;

	/** Detect free variable `module`. */
	var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

	/** Detect the popular CommonJS extension `module.exports`. */
	var moduleExports = freeModule && freeModule.exports === freeExports;

	/** Built-in value references. */
	var Buffer = moduleExports ? root.Buffer : undefined;

	/* Built-in method references for those with the same name as other `lodash` methods. */
	var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

	/**
	 * Checks if `value` is a buffer.
	 *
	 * @static
	 * @memberOf _
	 * @since 4.3.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
	 * @example
	 *
	 * _.isBuffer(new Buffer(2));
	 * // => true
	 *
	 * _.isBuffer(new Uint8Array(2));
	 * // => false
	 */
	var isBuffer = nativeIsBuffer || stubFalse;

	/** Used as references for various `Number` constants. */
	var MAX_SAFE_INTEGER$1 = 9007199254740991;

	/** Used to detect unsigned integer values. */
	var reIsUint = /^(?:0|[1-9]\d*)$/;

	/**
	 * Checks if `value` is a valid array-like index.
	 *
	 * @private
	 * @param {*} value The value to check.
	 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
	 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
	 */
	function isIndex(value, length) {
	  var type = typeof value;
	  length = length == null ? MAX_SAFE_INTEGER$1 : length;

	  return !!length &&
	    (type == 'number' ||
	      (type != 'symbol' && reIsUint.test(value))) &&
	        (value > -1 && value % 1 == 0 && value < length);
	}

	/** `Object#toString` result references. */
	var argsTag$1 = '[object Arguments]';
	var arrayTag = '[object Array]';
	var boolTag = '[object Boolean]';
	var dateTag = '[object Date]';
	var errorTag = '[object Error]';
	var funcTag$1 = '[object Function]';
	var mapTag = '[object Map]';
	var numberTag = '[object Number]';
	var objectTag = '[object Object]';
	var regexpTag = '[object RegExp]';
	var setTag = '[object Set]';
	var stringTag = '[object String]';
	var weakMapTag = '[object WeakMap]';

	var arrayBufferTag = '[object ArrayBuffer]';
	var dataViewTag = '[object DataView]';
	var float32Tag = '[object Float32Array]';
	var float64Tag = '[object Float64Array]';
	var int8Tag = '[object Int8Array]';
	var int16Tag = '[object Int16Array]';
	var int32Tag = '[object Int32Array]';
	var uint8Tag = '[object Uint8Array]';
	var uint8ClampedTag = '[object Uint8ClampedArray]';
	var uint16Tag = '[object Uint16Array]';
	var uint32Tag = '[object Uint32Array]';

	/** Used to identify `toStringTag` values of typed arrays. */
	var typedArrayTags = {};
	typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
	typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
	typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
	typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
	typedArrayTags[uint32Tag] = true;
	typedArrayTags[argsTag$1] = typedArrayTags[arrayTag] =
	typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
	typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
	typedArrayTags[errorTag] = typedArrayTags[funcTag$1] =
	typedArrayTags[mapTag] = typedArrayTags[numberTag] =
	typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
	typedArrayTags[setTag] = typedArrayTags[stringTag] =
	typedArrayTags[weakMapTag] = false;

	/**
	 * The base implementation of `_.isTypedArray` without Node.js optimizations.
	 *
	 * @private
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
	 */
	function baseIsTypedArray(value) {
	  return isObjectLike(value) &&
	    isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
	}

	/**
	 * The base implementation of `_.unary` without support for storing metadata.
	 *
	 * @private
	 * @param {Function} func The function to cap arguments for.
	 * @returns {Function} Returns the new capped function.
	 */
	function baseUnary(func) {
	  return function(value) {
	    return func(value);
	  };
	}

	/** Detect free variable `exports`. */
	var freeExports$1 = typeof exports == 'object' && exports && !exports.nodeType && exports;

	/** Detect free variable `module`. */
	var freeModule$1 = freeExports$1 && 'object' == 'object' && module && !module.nodeType && module;

	/** Detect the popular CommonJS extension `module.exports`. */
	var moduleExports$1 = freeModule$1 && freeModule$1.exports === freeExports$1;

	/** Detect free variable `process` from Node.js. */
	var freeProcess = moduleExports$1 && freeGlobal.process;

	/** Used to access faster Node.js helpers. */
	var nodeUtil = (function() {
	  try {
	    // Use `util.types` for Node.js 10+.
	    var types = freeModule$1 && freeModule$1.require && freeModule$1.require('util').types;

	    if (types) {
	      return types;
	    }

	    // Legacy `process.binding('util')` for Node.js < 10.
	    return freeProcess && freeProcess.binding && freeProcess.binding('util');
	  } catch (e) {}
	}());

	/* Node.js helper references. */
	var nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;

	/**
	 * Checks if `value` is classified as a typed array.
	 *
	 * @static
	 * @memberOf _
	 * @since 3.0.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
	 * @example
	 *
	 * _.isTypedArray(new Uint8Array);
	 * // => true
	 *
	 * _.isTypedArray([]);
	 * // => false
	 */
	var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

	/** Used for built-in method references. */
	var objectProto$2 = Object.prototype;

	/** Used to check objects for own properties. */
	var hasOwnProperty$1 = objectProto$2.hasOwnProperty;

	/**
	 * Creates an array of the enumerable property names of the array-like `value`.
	 *
	 * @private
	 * @param {*} value The value to query.
	 * @param {boolean} inherited Specify returning inherited property names.
	 * @returns {Array} Returns the array of property names.
	 */
	function arrayLikeKeys(value, inherited) {
	  var isArr = isArray(value),
	      isArg = !isArr && isArguments(value),
	      isBuff = !isArr && !isArg && isBuffer(value),
	      isType = !isArr && !isArg && !isBuff && isTypedArray(value),
	      skipIndexes = isArr || isArg || isBuff || isType,
	      result = skipIndexes ? baseTimes(value.length, String) : [],
	      length = result.length;

	  for (var key in value) {
	    if ((hasOwnProperty$1.call(value, key)) &&
	        !(skipIndexes && (
	           // Safari 9 has enumerable `arguments.length` in strict mode.
	           key == 'length' ||
	           // Node.js 0.10 has enumerable non-index properties on buffers.
	           (isBuff && (key == 'offset' || key == 'parent')) ||
	           // PhantomJS 2 has enumerable non-index properties on typed arrays.
	           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
	           // Skip index properties.
	           isIndex(key, length)
	        ))) {
	      result.push(key);
	    }
	  }
	  return result;
	}

	/** Used for built-in method references. */
	var objectProto$5 = Object.prototype;

	/**
	 * Checks if `value` is likely a prototype object.
	 *
	 * @private
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
	 */
	function isPrototype(value) {
	  var Ctor = value && value.constructor,
	      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$5;

	  return value === proto;
	}

	/**
	 * Creates a unary function that invokes `func` with its argument transformed.
	 *
	 * @private
	 * @param {Function} func The function to wrap.
	 * @param {Function} transform The argument transform.
	 * @returns {Function} Returns the new function.
	 */
	function overArg(func, transform) {
	  return function(arg) {
	    return func(transform(arg));
	  };
	}

	/* Built-in method references for those with the same name as other `lodash` methods. */
	var nativeKeys = overArg(Object.keys, Object);

	/** Used for built-in method references. */
	var objectProto$4 = Object.prototype;

	/** Used to check objects for own properties. */
	var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

	/**
	 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
	 *
	 * @private
	 * @param {Object} object The object to query.
	 * @returns {Array} Returns the array of property names.
	 */
	function baseKeys(object) {
	  if (!isPrototype(object)) {
	    return nativeKeys(object);
	  }
	  var result = [];
	  for (var key in Object(object)) {
	    if (hasOwnProperty$3.call(object, key) && key != 'constructor') {
	      result.push(key);
	    }
	  }
	  return result;
	}

	/**
	 * Creates an array of the own enumerable property names of `object`.
	 *
	 * **Note:** Non-object values are coerced to objects. See the
	 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
	 * for more details.
	 *
	 * @static
	 * @since 0.1.0
	 * @memberOf _
	 * @category Object
	 * @param {Object} object The object to query.
	 * @returns {Array} Returns the array of property names.
	 * @example
	 *
	 * function Foo() {
	 *   this.a = 1;
	 *   this.b = 2;
	 * }
	 *
	 * Foo.prototype.c = 3;
	 *
	 * _.keys(new Foo);
	 * // => ['a', 'b'] (iteration order is not guaranteed)
	 *
	 * _.keys('hi');
	 * // => ['0', '1']
	 */
	function keys(object) {
	  return isArrayLike(object) ? arrayLikeKeys(object) : baseKeys(object);
	}

	function createArrayIterator(coll) {
	    var i = -1;
	    var len = coll.length;
	    return function next() {
	        return ++i < len ? {value: coll[i], key: i} : null;
	    }
	}

	function createES2015Iterator(iterator) {
	    var i = -1;
	    return function next() {
	        var item = iterator.next();
	        if (item.done)
	            return null;
	        i++;
	        return {value: item.value, key: i};
	    }
	}

	function createObjectIterator(obj) {
	    var okeys = keys(obj);
	    var i = -1;
	    var len = okeys.length;
	    return function next() {
	        var key = okeys[++i];
	        if (key === '__proto__') {
	            return next();
	        }
	        return i < len ? {value: obj[key], key: key} : null;
	    };
	}

	function iterator(coll) {
	    if (isArrayLike(coll)) {
	        return createArrayIterator(coll);
	    }

	    var iterator = getIterator(coll);
	    return iterator ? createES2015Iterator(iterator) : createObjectIterator(coll);
	}

	function onlyOnce(fn) {
	    return function() {
	        if (fn === null) throw new Error("Callback was already called.");
	        var callFn = fn;
	        fn = null;
	        callFn.apply(this, arguments);
	    };
	}

	function _eachOfLimit(limit) {
	    return function (obj, iteratee, callback) {
	        callback = once(callback || noop);
	        if (limit <= 0 || !obj) {
	            return callback(null);
	        }
	        var nextElem = iterator(obj);
	        var done = false;
	        var running = 0;
	        var looping = false;

	        function iterateeCallback(err, value) {
	            running -= 1;
	            if (err) {
	                done = true;
	                callback(err);
	            }
	            else if (value === breakLoop || (done && running <= 0)) {
	                done = true;
	                return callback(null);
	            }
	            else if (!looping) {
	                replenish();
	            }
	        }

	        function replenish () {
	            looping = true;
	            while (running < limit && !done) {
	                var elem = nextElem();
	                if (elem === null) {
	                    done = true;
	                    if (running <= 0) {
	                        callback(null);
	                    }
	                    return;
	                }
	                running += 1;
	                iteratee(elem.value, elem.key, onlyOnce(iterateeCallback));
	            }
	            looping = false;
	        }

	        replenish();
	    };
	}

	/**
	 * The same as [`eachOf`]{@link module:Collections.eachOf} but runs a maximum of `limit` async operations at a
	 * time.
	 *
	 * @name eachOfLimit
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.eachOf]{@link module:Collections.eachOf}
	 * @alias forEachOfLimit
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {AsyncFunction} iteratee - An async function to apply to each
	 * item in `coll`. The `key` is the item's key, or index in the case of an
	 * array.
	 * Invoked with (item, key, callback).
	 * @param {Function} [callback] - A callback which is called when all
	 * `iteratee` functions have finished, or an error occurs. Invoked with (err).
	 */
	function eachOfLimit(coll, limit, iteratee, callback) {
	    _eachOfLimit(limit)(coll, wrapAsync(iteratee), callback);
	}

	function doLimit(fn, limit) {
	    return function (iterable, iteratee, callback) {
	        return fn(iterable, limit, iteratee, callback);
	    };
	}

	// eachOf implementation optimized for array-likes
	function eachOfArrayLike(coll, iteratee, callback) {
	    callback = once(callback || noop);
	    var index = 0,
	        completed = 0,
	        length = coll.length;
	    if (length === 0) {
	        callback(null);
	    }

	    function iteratorCallback(err, value) {
	        if (err) {
	            callback(err);
	        } else if ((++completed === length) || value === breakLoop) {
	            callback(null);
	        }
	    }

	    for (; index < length; index++) {
	        iteratee(coll[index], index, onlyOnce(iteratorCallback));
	    }
	}

	// a generic version of eachOf which can handle array, object, and iterator cases.
	var eachOfGeneric = doLimit(eachOfLimit, Infinity);

	/**
	 * Like [`each`]{@link module:Collections.each}, except that it passes the key (or index) as the second argument
	 * to the iteratee.
	 *
	 * @name eachOf
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @alias forEachOf
	 * @category Collection
	 * @see [async.each]{@link module:Collections.each}
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - A function to apply to each
	 * item in `coll`.
	 * The `key` is the item's key, or index in the case of an array.
	 * Invoked with (item, key, callback).
	 * @param {Function} [callback] - A callback which is called when all
	 * `iteratee` functions have finished, or an error occurs. Invoked with (err).
	 * @example
	 *
	 * var obj = {dev: "/dev.json", test: "/test.json", prod: "/prod.json"};
	 * var configs = {};
	 *
	 * async.forEachOf(obj, function (value, key, callback) {
	 *     fs.readFile(__dirname + value, "utf8", function (err, data) {
	 *         if (err) return callback(err);
	 *         try {
	 *             configs[key] = JSON.parse(data);
	 *         } catch (e) {
	 *             return callback(e);
	 *         }
	 *         callback();
	 *     });
	 * }, function (err) {
	 *     if (err) console.error(err.message);
	 *     // configs is now a map of JSON data
	 *     doSomethingWith(configs);
	 * });
	 */
	var eachOf = function(coll, iteratee, callback) {
	    var eachOfImplementation = isArrayLike(coll) ? eachOfArrayLike : eachOfGeneric;
	    eachOfImplementation(coll, wrapAsync(iteratee), callback);
	};

	function doParallel(fn) {
	    return function (obj, iteratee, callback) {
	        return fn(eachOf, obj, wrapAsync(iteratee), callback);
	    };
	}

	function _asyncMap(eachfn, arr, iteratee, callback) {
	    callback = callback || noop;
	    arr = arr || [];
	    var results = [];
	    var counter = 0;
	    var _iteratee = wrapAsync(iteratee);

	    eachfn(arr, function (value, _, callback) {
	        var index = counter++;
	        _iteratee(value, function (err, v) {
	            results[index] = v;
	            callback(err);
	        });
	    }, function (err) {
	        callback(err, results);
	    });
	}

	/**
	 * Produces a new collection of values by mapping each value in `coll` through
	 * the `iteratee` function. The `iteratee` is called with an item from `coll`
	 * and a callback for when it has finished processing. Each of these callback
	 * takes 2 arguments: an `error`, and the transformed item from `coll`. If
	 * `iteratee` passes an error to its callback, the main `callback` (for the
	 * `map` function) is immediately called with the error.
	 *
	 * Note, that since this function applies the `iteratee` to each item in
	 * parallel, there is no guarantee that the `iteratee` functions will complete
	 * in order. However, the results array will be in the same order as the
	 * original `coll`.
	 *
	 * If `map` is passed an Object, the results will be an Array.  The results
	 * will roughly be in the order of the original Objects' keys (but this can
	 * vary across JavaScript engines).
	 *
	 * @name map
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - An async function to apply to each item in
	 * `coll`.
	 * The iteratee should complete with the transformed item.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called when all `iteratee`
	 * functions have finished, or an error occurs. Results is an Array of the
	 * transformed items from the `coll`. Invoked with (err, results).
	 * @example
	 *
	 * async.map(['file1','file2','file3'], fs.stat, function(err, results) {
	 *     // results is now an array of stats for each file
	 * });
	 */
	var map = doParallel(_asyncMap);

	/**
	 * Applies the provided arguments to each function in the array, calling
	 * `callback` after all functions have completed. If you only provide the first
	 * argument, `fns`, then it will return a function which lets you pass in the
	 * arguments as if it were a single function call. If more arguments are
	 * provided, `callback` is required while `args` is still optional.
	 *
	 * @name applyEach
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @param {Array|Iterable|Object} fns - A collection of {@link AsyncFunction}s
	 * to all call with the same arguments
	 * @param {...*} [args] - any number of separate arguments to pass to the
	 * function.
	 * @param {Function} [callback] - the final argument should be the callback,
	 * called when all functions have completed processing.
	 * @returns {Function} - If only the first argument, `fns`, is provided, it will
	 * return a function which lets you pass in the arguments as if it were a single
	 * function call. The signature is `(..args, callback)`. If invoked with any
	 * arguments, `callback` is required.
	 * @example
	 *
	 * async.applyEach([enableSearch, updateSchema], 'bucket', callback);
	 *
	 * // partial application example:
	 * async.each(
	 *     buckets,
	 *     async.applyEach([enableSearch, updateSchema]),
	 *     callback
	 * );
	 */
	var applyEach = applyEach$1(map);

	function doParallelLimit(fn) {
	    return function (obj, limit, iteratee, callback) {
	        return fn(_eachOfLimit(limit), obj, wrapAsync(iteratee), callback);
	    };
	}

	/**
	 * The same as [`map`]{@link module:Collections.map} but runs a maximum of `limit` async operations at a time.
	 *
	 * @name mapLimit
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.map]{@link module:Collections.map}
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {AsyncFunction} iteratee - An async function to apply to each item in
	 * `coll`.
	 * The iteratee should complete with the transformed item.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called when all `iteratee`
	 * functions have finished, or an error occurs. Results is an array of the
	 * transformed items from the `coll`. Invoked with (err, results).
	 */
	var mapLimit = doParallelLimit(_asyncMap);

	/**
	 * The same as [`map`]{@link module:Collections.map} but runs only a single async operation at a time.
	 *
	 * @name mapSeries
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.map]{@link module:Collections.map}
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - An async function to apply to each item in
	 * `coll`.
	 * The iteratee should complete with the transformed item.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called when all `iteratee`
	 * functions have finished, or an error occurs. Results is an array of the
	 * transformed items from the `coll`. Invoked with (err, results).
	 */
	var mapSeries = doLimit(mapLimit, 1);

	/**
	 * The same as [`applyEach`]{@link module:ControlFlow.applyEach} but runs only a single async operation at a time.
	 *
	 * @name applyEachSeries
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.applyEach]{@link module:ControlFlow.applyEach}
	 * @category Control Flow
	 * @param {Array|Iterable|Object} fns - A collection of {@link AsyncFunction}s to all
	 * call with the same arguments
	 * @param {...*} [args] - any number of separate arguments to pass to the
	 * function.
	 * @param {Function} [callback] - the final argument should be the callback,
	 * called when all functions have completed processing.
	 * @returns {Function} - If only the first argument is provided, it will return
	 * a function which lets you pass in the arguments as if it were a single
	 * function call.
	 */
	var applyEachSeries = applyEach$1(mapSeries);

	/**
	 * A specialized version of `_.forEach` for arrays without support for
	 * iteratee shorthands.
	 *
	 * @private
	 * @param {Array} [array] The array to iterate over.
	 * @param {Function} iteratee The function invoked per iteration.
	 * @returns {Array} Returns `array`.
	 */
	function arrayEach(array, iteratee) {
	  var index = -1,
	      length = array == null ? 0 : array.length;

	  while (++index < length) {
	    if (iteratee(array[index], index, array) === false) {
	      break;
	    }
	  }
	  return array;
	}

	/**
	 * Creates a base function for methods like `_.forIn` and `_.forOwn`.
	 *
	 * @private
	 * @param {boolean} [fromRight] Specify iterating from right to left.
	 * @returns {Function} Returns the new base function.
	 */
	function createBaseFor(fromRight) {
	  return function(object, iteratee, keysFunc) {
	    var index = -1,
	        iterable = Object(object),
	        props = keysFunc(object),
	        length = props.length;

	    while (length--) {
	      var key = props[++index];
	      if (iteratee(iterable[key], key, iterable) === false) {
	        break;
	      }
	    }
	    return object;
	  };
	}

	/**
	 * The base implementation of `baseForOwn` which iterates over `object`
	 * properties returned by `keysFunc` and invokes `iteratee` for each property.
	 * Iteratee functions may exit iteration early by explicitly returning `false`.
	 *
	 * @private
	 * @param {Object} object The object to iterate over.
	 * @param {Function} iteratee The function invoked per iteration.
	 * @param {Function} keysFunc The function to get the keys of `object`.
	 * @returns {Object} Returns `object`.
	 */
	var baseFor = createBaseFor();

	/**
	 * The base implementation of `_.forOwn` without support for iteratee shorthands.
	 *
	 * @private
	 * @param {Object} object The object to iterate over.
	 * @param {Function} iteratee The function invoked per iteration.
	 * @returns {Object} Returns `object`.
	 */
	function baseForOwn(object, iteratee) {
	  return object && baseFor(object, iteratee, keys);
	}

	/**
	 * The base implementation of `_.findIndex` and `_.findLastIndex` without
	 * support for iteratee shorthands.
	 *
	 * @private
	 * @param {Array} array The array to inspect.
	 * @param {Function} predicate The function invoked per iteration.
	 * @param {number} fromIndex The index to search from.
	 * @param {boolean} [fromRight] Specify iterating from right to left.
	 * @returns {number} Returns the index of the matched value, else `-1`.
	 */
	function baseFindIndex(array, predicate, fromIndex, fromRight) {
	  var length = array.length,
	      index = fromIndex + (-1);

	  while ((++index < length)) {
	    if (predicate(array[index], index, array)) {
	      return index;
	    }
	  }
	  return -1;
	}

	/**
	 * The base implementation of `_.isNaN` without support for number objects.
	 *
	 * @private
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
	 */
	function baseIsNaN(value) {
	  return value !== value;
	}

	/**
	 * A specialized version of `_.indexOf` which performs strict equality
	 * comparisons of values, i.e. `===`.
	 *
	 * @private
	 * @param {Array} array The array to inspect.
	 * @param {*} value The value to search for.
	 * @param {number} fromIndex The index to search from.
	 * @returns {number} Returns the index of the matched value, else `-1`.
	 */
	function strictIndexOf(array, value, fromIndex) {
	  var index = fromIndex - 1,
	      length = array.length;

	  while (++index < length) {
	    if (array[index] === value) {
	      return index;
	    }
	  }
	  return -1;
	}

	/**
	 * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
	 *
	 * @private
	 * @param {Array} array The array to inspect.
	 * @param {*} value The value to search for.
	 * @param {number} fromIndex The index to search from.
	 * @returns {number} Returns the index of the matched value, else `-1`.
	 */
	function baseIndexOf(array, value, fromIndex) {
	  return value === value
	    ? strictIndexOf(array, value, fromIndex)
	    : baseFindIndex(array, baseIsNaN, fromIndex);
	}

	/**
	 * Determines the best order for running the {@link AsyncFunction}s in `tasks`, based on
	 * their requirements. Each function can optionally depend on other functions
	 * being completed first, and each function is run as soon as its requirements
	 * are satisfied.
	 *
	 * If any of the {@link AsyncFunction}s pass an error to their callback, the `auto` sequence
	 * will stop. Further tasks will not execute (so any other functions depending
	 * on it will not run), and the main `callback` is immediately called with the
	 * error.
	 *
	 * {@link AsyncFunction}s also receive an object containing the results of functions which
	 * have completed so far as the first argument, if they have dependencies. If a
	 * task function has no dependencies, it will only be passed a callback.
	 *
	 * @name auto
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @param {Object} tasks - An object. Each of its properties is either a
	 * function or an array of requirements, with the {@link AsyncFunction} itself the last item
	 * in the array. The object's key of a property serves as the name of the task
	 * defined by that property, i.e. can be used when specifying requirements for
	 * other tasks. The function receives one or two arguments:
	 * * a `results` object, containing the results of the previously executed
	 *   functions, only passed if the task has any dependencies,
	 * * a `callback(err, result)` function, which must be called when finished,
	 *   passing an `error` (which can be `null`) and the result of the function's
	 *   execution.
	 * @param {number} [concurrency=Infinity] - An optional `integer` for
	 * determining the maximum number of tasks that can be run in parallel. By
	 * default, as many as possible.
	 * @param {Function} [callback] - An optional callback which is called when all
	 * the tasks have been completed. It receives the `err` argument if any `tasks`
	 * pass an error to their callback. Results are always returned; however, if an
	 * error occurs, no further `tasks` will be performed, and the results object
	 * will only contain partial results. Invoked with (err, results).
	 * @returns undefined
	 * @example
	 *
	 * async.auto({
	 *     // this function will just be passed a callback
	 *     readData: async.apply(fs.readFile, 'data.txt', 'utf-8'),
	 *     showData: ['readData', function(results, cb) {
	 *         // results.readData is the file's contents
	 *         // ...
	 *     }]
	 * }, callback);
	 *
	 * async.auto({
	 *     get_data: function(callback) {
	 *         console.log('in get_data');
	 *         // async code to get some data
	 *         callback(null, 'data', 'converted to array');
	 *     },
	 *     make_folder: function(callback) {
	 *         console.log('in make_folder');
	 *         // async code to create a directory to store a file in
	 *         // this is run at the same time as getting the data
	 *         callback(null, 'folder');
	 *     },
	 *     write_file: ['get_data', 'make_folder', function(results, callback) {
	 *         console.log('in write_file', JSON.stringify(results));
	 *         // once there is some data and the directory exists,
	 *         // write the data to a file in the directory
	 *         callback(null, 'filename');
	 *     }],
	 *     email_link: ['write_file', function(results, callback) {
	 *         console.log('in email_link', JSON.stringify(results));
	 *         // once the file is written let's email a link to it...
	 *         // results.write_file contains the filename returned by write_file.
	 *         callback(null, {'file':results.write_file, 'email':'user@example.com'});
	 *     }]
	 * }, function(err, results) {
	 *     console.log('err = ', err);
	 *     console.log('results = ', results);
	 * });
	 */
	var auto = function (tasks, concurrency, callback) {
	    if (typeof concurrency === 'function') {
	        // concurrency is optional, shift the args.
	        callback = concurrency;
	        concurrency = null;
	    }
	    callback = once(callback || noop);
	    var keys$$1 = keys(tasks);
	    var numTasks = keys$$1.length;
	    if (!numTasks) {
	        return callback(null);
	    }
	    if (!concurrency) {
	        concurrency = numTasks;
	    }

	    var results = {};
	    var runningTasks = 0;
	    var hasError = false;

	    var listeners = Object.create(null);

	    var readyTasks = [];

	    // for cycle detection:
	    var readyToCheck = []; // tasks that have been identified as reachable
	    // without the possibility of returning to an ancestor task
	    var uncheckedDependencies = {};

	    baseForOwn(tasks, function (task, key) {
	        if (!isArray(task)) {
	            // no dependencies
	            enqueueTask(key, [task]);
	            readyToCheck.push(key);
	            return;
	        }

	        var dependencies = task.slice(0, task.length - 1);
	        var remainingDependencies = dependencies.length;
	        if (remainingDependencies === 0) {
	            enqueueTask(key, task);
	            readyToCheck.push(key);
	            return;
	        }
	        uncheckedDependencies[key] = remainingDependencies;

	        arrayEach(dependencies, function (dependencyName) {
	            if (!tasks[dependencyName]) {
	                throw new Error('async.auto task `' + key +
	                    '` has a non-existent dependency `' +
	                    dependencyName + '` in ' +
	                    dependencies.join(', '));
	            }
	            addListener(dependencyName, function () {
	                remainingDependencies--;
	                if (remainingDependencies === 0) {
	                    enqueueTask(key, task);
	                }
	            });
	        });
	    });

	    checkForDeadlocks();
	    processQueue();

	    function enqueueTask(key, task) {
	        readyTasks.push(function () {
	            runTask(key, task);
	        });
	    }

	    function processQueue() {
	        if (readyTasks.length === 0 && runningTasks === 0) {
	            return callback(null, results);
	        }
	        while(readyTasks.length && runningTasks < concurrency) {
	            var run = readyTasks.shift();
	            run();
	        }

	    }

	    function addListener(taskName, fn) {
	        var taskListeners = listeners[taskName];
	        if (!taskListeners) {
	            taskListeners = listeners[taskName] = [];
	        }

	        taskListeners.push(fn);
	    }

	    function taskComplete(taskName) {
	        var taskListeners = listeners[taskName] || [];
	        arrayEach(taskListeners, function (fn) {
	            fn();
	        });
	        processQueue();
	    }


	    function runTask(key, task) {
	        if (hasError) return;

	        var taskCallback = onlyOnce(function(err, result) {
	            runningTasks--;
	            if (arguments.length > 2) {
	                result = slice(arguments, 1);
	            }
	            if (err) {
	                var safeResults = {};
	                baseForOwn(results, function(val, rkey) {
	                    safeResults[rkey] = val;
	                });
	                safeResults[key] = result;
	                hasError = true;
	                listeners = Object.create(null);

	                callback(err, safeResults);
	            } else {
	                results[key] = result;
	                taskComplete(key);
	            }
	        });

	        runningTasks++;
	        var taskFn = wrapAsync(task[task.length - 1]);
	        if (task.length > 1) {
	            taskFn(results, taskCallback);
	        } else {
	            taskFn(taskCallback);
	        }
	    }

	    function checkForDeadlocks() {
	        // Kahn's algorithm
	        // https://en.wikipedia.org/wiki/Topological_sorting#Kahn.27s_algorithm
	        // http://connalle.blogspot.com/2013/10/topological-sortingkahn-algorithm.html
	        var currentTask;
	        var counter = 0;
	        while (readyToCheck.length) {
	            currentTask = readyToCheck.pop();
	            counter++;
	            arrayEach(getDependents(currentTask), function (dependent) {
	                if (--uncheckedDependencies[dependent] === 0) {
	                    readyToCheck.push(dependent);
	                }
	            });
	        }

	        if (counter !== numTasks) {
	            throw new Error(
	                'async.auto cannot execute tasks due to a recursive dependency'
	            );
	        }
	    }

	    function getDependents(taskName) {
	        var result = [];
	        baseForOwn(tasks, function (task, key) {
	            if (isArray(task) && baseIndexOf(task, taskName, 0) >= 0) {
	                result.push(key);
	            }
	        });
	        return result;
	    }
	};

	/**
	 * A specialized version of `_.map` for arrays without support for iteratee
	 * shorthands.
	 *
	 * @private
	 * @param {Array} [array] The array to iterate over.
	 * @param {Function} iteratee The function invoked per iteration.
	 * @returns {Array} Returns the new mapped array.
	 */
	function arrayMap(array, iteratee) {
	  var index = -1,
	      length = array == null ? 0 : array.length,
	      result = Array(length);

	  while (++index < length) {
	    result[index] = iteratee(array[index], index, array);
	  }
	  return result;
	}

	/** `Object#toString` result references. */
	var symbolTag = '[object Symbol]';

	/**
	 * Checks if `value` is classified as a `Symbol` primitive or object.
	 *
	 * @static
	 * @memberOf _
	 * @since 4.0.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
	 * @example
	 *
	 * _.isSymbol(Symbol.iterator);
	 * // => true
	 *
	 * _.isSymbol('abc');
	 * // => false
	 */
	function isSymbol(value) {
	  return typeof value == 'symbol' ||
	    (isObjectLike(value) && baseGetTag(value) == symbolTag);
	}

	/** Used to convert symbols to primitives and strings. */
	var symbolProto = Symbol$1 ? Symbol$1.prototype : undefined;
	var symbolToString = symbolProto ? symbolProto.toString : undefined;

	/**
	 * The base implementation of `_.toString` which doesn't convert nullish
	 * values to empty strings.
	 *
	 * @private
	 * @param {*} value The value to process.
	 * @returns {string} Returns the string.
	 */
	function baseToString(value) {
	  // Exit early for strings to avoid a performance hit in some environments.
	  if (typeof value == 'string') {
	    return value;
	  }
	  if (isArray(value)) {
	    // Recursively convert values (susceptible to call stack limits).
	    return arrayMap(value, baseToString) + '';
	  }
	  if (isSymbol(value)) {
	    return symbolToString ? symbolToString.call(value) : '';
	  }
	  var result = (value + '');
	  return (result == '0' && (1 / value) == -Infinity) ? '-0' : result;
	}

	/**
	 * The base implementation of `_.slice` without an iteratee call guard.
	 *
	 * @private
	 * @param {Array} array The array to slice.
	 * @param {number} [start=0] The start position.
	 * @param {number} [end=array.length] The end position.
	 * @returns {Array} Returns the slice of `array`.
	 */
	function baseSlice(array, start, end) {
	  var index = -1,
	      length = array.length;

	  if (start < 0) {
	    start = -start > length ? 0 : (length + start);
	  }
	  end = end > length ? length : end;
	  if (end < 0) {
	    end += length;
	  }
	  length = start > end ? 0 : ((end - start) >>> 0);
	  start >>>= 0;

	  var result = Array(length);
	  while (++index < length) {
	    result[index] = array[index + start];
	  }
	  return result;
	}

	/**
	 * Casts `array` to a slice if it's needed.
	 *
	 * @private
	 * @param {Array} array The array to inspect.
	 * @param {number} start The start position.
	 * @param {number} [end=array.length] The end position.
	 * @returns {Array} Returns the cast slice.
	 */
	function castSlice(array, start, end) {
	  var length = array.length;
	  end = end === undefined ? length : end;
	  return (!start && end >= length) ? array : baseSlice(array, start, end);
	}

	/**
	 * Used by `_.trim` and `_.trimEnd` to get the index of the last string symbol
	 * that is not found in the character symbols.
	 *
	 * @private
	 * @param {Array} strSymbols The string symbols to inspect.
	 * @param {Array} chrSymbols The character symbols to find.
	 * @returns {number} Returns the index of the last unmatched string symbol.
	 */
	function charsEndIndex(strSymbols, chrSymbols) {
	  var index = strSymbols.length;

	  while (index-- && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
	  return index;
	}

	/**
	 * Used by `_.trim` and `_.trimStart` to get the index of the first string symbol
	 * that is not found in the character symbols.
	 *
	 * @private
	 * @param {Array} strSymbols The string symbols to inspect.
	 * @param {Array} chrSymbols The character symbols to find.
	 * @returns {number} Returns the index of the first unmatched string symbol.
	 */
	function charsStartIndex(strSymbols, chrSymbols) {
	  var index = -1,
	      length = strSymbols.length;

	  while (++index < length && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
	  return index;
	}

	/**
	 * Converts an ASCII `string` to an array.
	 *
	 * @private
	 * @param {string} string The string to convert.
	 * @returns {Array} Returns the converted array.
	 */
	function asciiToArray(string) {
	  return string.split('');
	}

	/** Used to compose unicode character classes. */
	var rsAstralRange = '\\ud800-\\udfff';
	var rsComboMarksRange = '\\u0300-\\u036f';
	var reComboHalfMarksRange = '\\ufe20-\\ufe2f';
	var rsComboSymbolsRange = '\\u20d0-\\u20ff';
	var rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange;
	var rsVarRange = '\\ufe0e\\ufe0f';

	/** Used to compose unicode capture groups. */
	var rsZWJ = '\\u200d';

	/** Used to detect strings with [zero-width joiners or code points from the astral planes](http://eev.ee/blog/2015/09/12/dark-corners-of-unicode/). */
	var reHasUnicode = RegExp('[' + rsZWJ + rsAstralRange  + rsComboRange + rsVarRange + ']');

	/**
	 * Checks if `string` contains Unicode symbols.
	 *
	 * @private
	 * @param {string} string The string to inspect.
	 * @returns {boolean} Returns `true` if a symbol is found, else `false`.
	 */
	function hasUnicode(string) {
	  return reHasUnicode.test(string);
	}

	/** Used to compose unicode character classes. */
	var rsAstralRange$1 = '\\ud800-\\udfff';
	var rsComboMarksRange$1 = '\\u0300-\\u036f';
	var reComboHalfMarksRange$1 = '\\ufe20-\\ufe2f';
	var rsComboSymbolsRange$1 = '\\u20d0-\\u20ff';
	var rsComboRange$1 = rsComboMarksRange$1 + reComboHalfMarksRange$1 + rsComboSymbolsRange$1;
	var rsVarRange$1 = '\\ufe0e\\ufe0f';

	/** Used to compose unicode capture groups. */
	var rsAstral = '[' + rsAstralRange$1 + ']';
	var rsCombo = '[' + rsComboRange$1 + ']';
	var rsFitz = '\\ud83c[\\udffb-\\udfff]';
	var rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')';
	var rsNonAstral = '[^' + rsAstralRange$1 + ']';
	var rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}';
	var rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]';
	var rsZWJ$1 = '\\u200d';

	/** Used to compose unicode regexes. */
	var reOptMod = rsModifier + '?';
	var rsOptVar = '[' + rsVarRange$1 + ']?';
	var rsOptJoin = '(?:' + rsZWJ$1 + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*';
	var rsSeq = rsOptVar + reOptMod + rsOptJoin;
	var rsSymbol = '(?:' + [rsNonAstral + rsCombo + '?', rsCombo, rsRegional, rsSurrPair, rsAstral].join('|') + ')';

	/** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */
	var reUnicode = RegExp(rsFitz + '(?=' + rsFitz + ')|' + rsSymbol + rsSeq, 'g');

	/**
	 * Converts a Unicode `string` to an array.
	 *
	 * @private
	 * @param {string} string The string to convert.
	 * @returns {Array} Returns the converted array.
	 */
	function unicodeToArray(string) {
	  return string.match(reUnicode) || [];
	}

	/**
	 * Converts `string` to an array.
	 *
	 * @private
	 * @param {string} string The string to convert.
	 * @returns {Array} Returns the converted array.
	 */
	function stringToArray(string) {
	  return hasUnicode(string)
	    ? unicodeToArray(string)
	    : asciiToArray(string);
	}

	/**
	 * Converts `value` to a string. An empty string is returned for `null`
	 * and `undefined` values. The sign of `-0` is preserved.
	 *
	 * @static
	 * @memberOf _
	 * @since 4.0.0
	 * @category Lang
	 * @param {*} value The value to convert.
	 * @returns {string} Returns the converted string.
	 * @example
	 *
	 * _.toString(null);
	 * // => ''
	 *
	 * _.toString(-0);
	 * // => '-0'
	 *
	 * _.toString([1, 2, 3]);
	 * // => '1,2,3'
	 */
	function toString(value) {
	  return value == null ? '' : baseToString(value);
	}

	/** Used to match leading and trailing whitespace. */
	var reTrim = /^\s+|\s+$/g;

	/**
	 * Removes leading and trailing whitespace or specified characters from `string`.
	 *
	 * @static
	 * @memberOf _
	 * @since 3.0.0
	 * @category String
	 * @param {string} [string=''] The string to trim.
	 * @param {string} [chars=whitespace] The characters to trim.
	 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
	 * @returns {string} Returns the trimmed string.
	 * @example
	 *
	 * _.trim('  abc  ');
	 * // => 'abc'
	 *
	 * _.trim('-_-abc-_-', '_-');
	 * // => 'abc'
	 *
	 * _.map(['  foo  ', '  bar  '], _.trim);
	 * // => ['foo', 'bar']
	 */
	function trim(string, chars, guard) {
	  string = toString(string);
	  if (string && (chars === undefined)) {
	    return string.replace(reTrim, '');
	  }
	  if (!string || !(chars = baseToString(chars))) {
	    return string;
	  }
	  var strSymbols = stringToArray(string),
	      chrSymbols = stringToArray(chars),
	      start = charsStartIndex(strSymbols, chrSymbols),
	      end = charsEndIndex(strSymbols, chrSymbols) + 1;

	  return castSlice(strSymbols, start, end).join('');
	}

	var FN_ARGS = /^(?:async\s+)?(function)?\s*[^\(]*\(\s*([^\)]*)\)/m;
	var FN_ARG_SPLIT = /,/;
	var FN_ARG = /(=.+)?(\s*)$/;
	var STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/mg;

	function parseParams(func) {
	    func = func.toString().replace(STRIP_COMMENTS, '');
	    func = func.match(FN_ARGS)[2].replace(' ', '');
	    func = func ? func.split(FN_ARG_SPLIT) : [];
	    func = func.map(function (arg){
	        return trim(arg.replace(FN_ARG, ''));
	    });
	    return func;
	}

	/**
	 * A dependency-injected version of the [async.auto]{@link module:ControlFlow.auto} function. Dependent
	 * tasks are specified as parameters to the function, after the usual callback
	 * parameter, with the parameter names matching the names of the tasks it
	 * depends on. This can provide even more readable task graphs which can be
	 * easier to maintain.
	 *
	 * If a final callback is specified, the task results are similarly injected,
	 * specified as named parameters after the initial error parameter.
	 *
	 * The autoInject function is purely syntactic sugar and its semantics are
	 * otherwise equivalent to [async.auto]{@link module:ControlFlow.auto}.
	 *
	 * @name autoInject
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.auto]{@link module:ControlFlow.auto}
	 * @category Control Flow
	 * @param {Object} tasks - An object, each of whose properties is an {@link AsyncFunction} of
	 * the form 'func([dependencies...], callback). The object's key of a property
	 * serves as the name of the task defined by that property, i.e. can be used
	 * when specifying requirements for other tasks.
	 * * The `callback` parameter is a `callback(err, result)` which must be called
	 *   when finished, passing an `error` (which can be `null`) and the result of
	 *   the function's execution. The remaining parameters name other tasks on
	 *   which the task is dependent, and the results from those tasks are the
	 *   arguments of those parameters.
	 * @param {Function} [callback] - An optional callback which is called when all
	 * the tasks have been completed. It receives the `err` argument if any `tasks`
	 * pass an error to their callback, and a `results` object with any completed
	 * task results, similar to `auto`.
	 * @example
	 *
	 * //  The example from `auto` can be rewritten as follows:
	 * async.autoInject({
	 *     get_data: function(callback) {
	 *         // async code to get some data
	 *         callback(null, 'data', 'converted to array');
	 *     },
	 *     make_folder: function(callback) {
	 *         // async code to create a directory to store a file in
	 *         // this is run at the same time as getting the data
	 *         callback(null, 'folder');
	 *     },
	 *     write_file: function(get_data, make_folder, callback) {
	 *         // once there is some data and the directory exists,
	 *         // write the data to a file in the directory
	 *         callback(null, 'filename');
	 *     },
	 *     email_link: function(write_file, callback) {
	 *         // once the file is written let's email a link to it...
	 *         // write_file contains the filename returned by write_file.
	 *         callback(null, {'file':write_file, 'email':'user@example.com'});
	 *     }
	 * }, function(err, results) {
	 *     console.log('err = ', err);
	 *     console.log('email_link = ', results.email_link);
	 * });
	 *
	 * // If you are using a JS minifier that mangles parameter names, `autoInject`
	 * // will not work with plain functions, since the parameter names will be
	 * // collapsed to a single letter identifier.  To work around this, you can
	 * // explicitly specify the names of the parameters your task function needs
	 * // in an array, similar to Angular.js dependency injection.
	 *
	 * // This still has an advantage over plain `auto`, since the results a task
	 * // depends on are still spread into arguments.
	 * async.autoInject({
	 *     //...
	 *     write_file: ['get_data', 'make_folder', function(get_data, make_folder, callback) {
	 *         callback(null, 'filename');
	 *     }],
	 *     email_link: ['write_file', function(write_file, callback) {
	 *         callback(null, {'file':write_file, 'email':'user@example.com'});
	 *     }]
	 *     //...
	 * }, function(err, results) {
	 *     console.log('err = ', err);
	 *     console.log('email_link = ', results.email_link);
	 * });
	 */
	function autoInject(tasks, callback) {
	    var newTasks = {};

	    baseForOwn(tasks, function (taskFn, key) {
	        var params;
	        var fnIsAsync = isAsync(taskFn);
	        var hasNoDeps =
	            (!fnIsAsync && taskFn.length === 1) ||
	            (fnIsAsync && taskFn.length === 0);

	        if (isArray(taskFn)) {
	            params = taskFn.slice(0, -1);
	            taskFn = taskFn[taskFn.length - 1];

	            newTasks[key] = params.concat(params.length > 0 ? newTask : taskFn);
	        } else if (hasNoDeps) {
	            // no dependencies, use the function as-is
	            newTasks[key] = taskFn;
	        } else {
	            params = parseParams(taskFn);
	            if (taskFn.length === 0 && !fnIsAsync && params.length === 0) {
	                throw new Error("autoInject task functions require explicit parameters.");
	            }

	            // remove callback param
	            if (!fnIsAsync) params.pop();

	            newTasks[key] = params.concat(newTask);
	        }

	        function newTask(results, taskCb) {
	            var newArgs = arrayMap(params, function (name) {
	                return results[name];
	            });
	            newArgs.push(taskCb);
	            wrapAsync(taskFn).apply(null, newArgs);
	        }
	    });

	    auto(newTasks, callback);
	}

	// Simple doubly linked list (https://en.wikipedia.org/wiki/Doubly_linked_list) implementation
	// used for queues. This implementation assumes that the node provided by the user can be modified
	// to adjust the next and last properties. We implement only the minimal functionality
	// for queue support.
	function DLL() {
	    this.head = this.tail = null;
	    this.length = 0;
	}

	function setInitial(dll, node) {
	    dll.length = 1;
	    dll.head = dll.tail = node;
	}

	DLL.prototype.removeLink = function(node) {
	    if (node.prev) node.prev.next = node.next;
	    else this.head = node.next;
	    if (node.next) node.next.prev = node.prev;
	    else this.tail = node.prev;

	    node.prev = node.next = null;
	    this.length -= 1;
	    return node;
	};

	DLL.prototype.empty = function () {
	    while(this.head) this.shift();
	    return this;
	};

	DLL.prototype.insertAfter = function(node, newNode) {
	    newNode.prev = node;
	    newNode.next = node.next;
	    if (node.next) node.next.prev = newNode;
	    else this.tail = newNode;
	    node.next = newNode;
	    this.length += 1;
	};

	DLL.prototype.insertBefore = function(node, newNode) {
	    newNode.prev = node.prev;
	    newNode.next = node;
	    if (node.prev) node.prev.next = newNode;
	    else this.head = newNode;
	    node.prev = newNode;
	    this.length += 1;
	};

	DLL.prototype.unshift = function(node) {
	    if (this.head) this.insertBefore(this.head, node);
	    else setInitial(this, node);
	};

	DLL.prototype.push = function(node) {
	    if (this.tail) this.insertAfter(this.tail, node);
	    else setInitial(this, node);
	};

	DLL.prototype.shift = function() {
	    return this.head && this.removeLink(this.head);
	};

	DLL.prototype.pop = function() {
	    return this.tail && this.removeLink(this.tail);
	};

	DLL.prototype.toArray = function () {
	    var arr = Array(this.length);
	    var curr = this.head;
	    for(var idx = 0; idx < this.length; idx++) {
	        arr[idx] = curr.data;
	        curr = curr.next;
	    }
	    return arr;
	};

	DLL.prototype.remove = function (testFn) {
	    var curr = this.head;
	    while(!!curr) {
	        var next = curr.next;
	        if (testFn(curr)) {
	            this.removeLink(curr);
	        }
	        curr = next;
	    }
	    return this;
	};

	function queue(worker, concurrency, payload) {
	    if (concurrency == null) {
	        concurrency = 1;
	    }
	    else if(concurrency === 0) {
	        throw new Error('Concurrency must not be zero');
	    }

	    var _worker = wrapAsync(worker);
	    var numRunning = 0;
	    var workersList = [];

	    var processingScheduled = false;
	    function _insert(data, insertAtFront, callback) {
	        if (callback != null && typeof callback !== 'function') {
	            throw new Error('task callback must be a function');
	        }
	        q.started = true;
	        if (!isArray(data)) {
	            data = [data];
	        }
	        if (data.length === 0 && q.idle()) {
	            // call drain immediately if there are no tasks
	            return setImmediate$1(function() {
	                q.drain();
	            });
	        }

	        for (var i = 0, l = data.length; i < l; i++) {
	            var item = {
	                data: data[i],
	                callback: callback || noop
	            };

	            if (insertAtFront) {
	                q._tasks.unshift(item);
	            } else {
	                q._tasks.push(item);
	            }
	        }

	        if (!processingScheduled) {
	            processingScheduled = true;
	            setImmediate$1(function() {
	                processingScheduled = false;
	                q.process();
	            });
	        }
	    }

	    function _next(tasks) {
	        return function(err){
	            numRunning -= 1;

	            for (var i = 0, l = tasks.length; i < l; i++) {
	                var task = tasks[i];

	                var index = baseIndexOf(workersList, task, 0);
	                if (index === 0) {
	                    workersList.shift();
	                } else if (index > 0) {
	                    workersList.splice(index, 1);
	                }

	                task.callback.apply(task, arguments);

	                if (err != null) {
	                    q.error(err, task.data);
	                }
	            }

	            if (numRunning <= (q.concurrency - q.buffer) ) {
	                q.unsaturated();
	            }

	            if (q.idle()) {
	                q.drain();
	            }
	            q.process();
	        };
	    }

	    var isProcessing = false;
	    var q = {
	        _tasks: new DLL(),
	        concurrency: concurrency,
	        payload: payload,
	        saturated: noop,
	        unsaturated:noop,
	        buffer: concurrency / 4,
	        empty: noop,
	        drain: noop,
	        error: noop,
	        started: false,
	        paused: false,
	        push: function (data, callback) {
	            _insert(data, false, callback);
	        },
	        kill: function () {
	            q.drain = noop;
	            q._tasks.empty();
	        },
	        unshift: function (data, callback) {
	            _insert(data, true, callback);
	        },
	        remove: function (testFn) {
	            q._tasks.remove(testFn);
	        },
	        process: function () {
	            // Avoid trying to start too many processing operations. This can occur
	            // when callbacks resolve synchronously (#1267).
	            if (isProcessing) {
	                return;
	            }
	            isProcessing = true;
	            while(!q.paused && numRunning < q.concurrency && q._tasks.length){
	                var tasks = [], data = [];
	                var l = q._tasks.length;
	                if (q.payload) l = Math.min(l, q.payload);
	                for (var i = 0; i < l; i++) {
	                    var node = q._tasks.shift();
	                    tasks.push(node);
	                    workersList.push(node);
	                    data.push(node.data);
	                }

	                numRunning += 1;

	                if (q._tasks.length === 0) {
	                    q.empty();
	                }

	                if (numRunning === q.concurrency) {
	                    q.saturated();
	                }

	                var cb = onlyOnce(_next(tasks));
	                _worker(data, cb);
	            }
	            isProcessing = false;
	        },
	        length: function () {
	            return q._tasks.length;
	        },
	        running: function () {
	            return numRunning;
	        },
	        workersList: function () {
	            return workersList;
	        },
	        idle: function() {
	            return q._tasks.length + numRunning === 0;
	        },
	        pause: function () {
	            q.paused = true;
	        },
	        resume: function () {
	            if (q.paused === false) { return; }
	            q.paused = false;
	            setImmediate$1(q.process);
	        }
	    };
	    return q;
	}

	/**
	 * A cargo of tasks for the worker function to complete. Cargo inherits all of
	 * the same methods and event callbacks as [`queue`]{@link module:ControlFlow.queue}.
	 * @typedef {Object} CargoObject
	 * @memberOf module:ControlFlow
	 * @property {Function} length - A function returning the number of items
	 * waiting to be processed. Invoke like `cargo.length()`.
	 * @property {number} payload - An `integer` for determining how many tasks
	 * should be process per round. This property can be changed after a `cargo` is
	 * created to alter the payload on-the-fly.
	 * @property {Function} push - Adds `task` to the `queue`. The callback is
	 * called once the `worker` has finished processing the task. Instead of a
	 * single task, an array of `tasks` can be submitted. The respective callback is
	 * used for every task in the list. Invoke like `cargo.push(task, [callback])`.
	 * @property {Function} saturated - A callback that is called when the
	 * `queue.length()` hits the concurrency and further tasks will be queued.
	 * @property {Function} empty - A callback that is called when the last item
	 * from the `queue` is given to a `worker`.
	 * @property {Function} drain - A callback that is called when the last item
	 * from the `queue` has returned from the `worker`.
	 * @property {Function} idle - a function returning false if there are items
	 * waiting or being processed, or true if not. Invoke like `cargo.idle()`.
	 * @property {Function} pause - a function that pauses the processing of tasks
	 * until `resume()` is called. Invoke like `cargo.pause()`.
	 * @property {Function} resume - a function that resumes the processing of
	 * queued tasks when the queue is paused. Invoke like `cargo.resume()`.
	 * @property {Function} kill - a function that removes the `drain` callback and
	 * empties remaining tasks from the queue forcing it to go idle. Invoke like `cargo.kill()`.
	 */

	/**
	 * Creates a `cargo` object with the specified payload. Tasks added to the
	 * cargo will be processed altogether (up to the `payload` limit). If the
	 * `worker` is in progress, the task is queued until it becomes available. Once
	 * the `worker` has completed some tasks, each callback of those tasks is
	 * called. Check out [these](https://camo.githubusercontent.com/6bbd36f4cf5b35a0f11a96dcd2e97711ffc2fb37/68747470733a2f2f662e636c6f75642e6769746875622e636f6d2f6173736574732f313637363837312f36383130382f62626330636662302d356632392d313165322d393734662d3333393763363464633835382e676966) [animations](https://camo.githubusercontent.com/f4810e00e1c5f5f8addbe3e9f49064fd5d102699/68747470733a2f2f662e636c6f75642e6769746875622e636f6d2f6173736574732f313637363837312f36383130312f38346339323036362d356632392d313165322d383134662d3964336430323431336266642e676966)
	 * for how `cargo` and `queue` work.
	 *
	 * While [`queue`]{@link module:ControlFlow.queue} passes only one task to one of a group of workers
	 * at a time, cargo passes an array of tasks to a single worker, repeating
	 * when the worker is finished.
	 *
	 * @name cargo
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.queue]{@link module:ControlFlow.queue}
	 * @category Control Flow
	 * @param {AsyncFunction} worker - An asynchronous function for processing an array
	 * of queued tasks. Invoked with `(tasks, callback)`.
	 * @param {number} [payload=Infinity] - An optional `integer` for determining
	 * how many tasks should be processed per round; if omitted, the default is
	 * unlimited.
	 * @returns {module:ControlFlow.CargoObject} A cargo object to manage the tasks. Callbacks can
	 * attached as certain properties to listen for specific events during the
	 * lifecycle of the cargo and inner queue.
	 * @example
	 *
	 * // create a cargo object with payload 2
	 * var cargo = async.cargo(function(tasks, callback) {
	 *     for (var i=0; i<tasks.length; i++) {
	 *         console.log('hello ' + tasks[i].name);
	 *     }
	 *     callback();
	 * }, 2);
	 *
	 * // add some items
	 * cargo.push({name: 'foo'}, function(err) {
	 *     console.log('finished processing foo');
	 * });
	 * cargo.push({name: 'bar'}, function(err) {
	 *     console.log('finished processing bar');
	 * });
	 * cargo.push({name: 'baz'}, function(err) {
	 *     console.log('finished processing baz');
	 * });
	 */
	function cargo(worker, payload) {
	    return queue(worker, 1, payload);
	}

	/**
	 * The same as [`eachOf`]{@link module:Collections.eachOf} but runs only a single async operation at a time.
	 *
	 * @name eachOfSeries
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.eachOf]{@link module:Collections.eachOf}
	 * @alias forEachOfSeries
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - An async function to apply to each item in
	 * `coll`.
	 * Invoked with (item, key, callback).
	 * @param {Function} [callback] - A callback which is called when all `iteratee`
	 * functions have finished, or an error occurs. Invoked with (err).
	 */
	var eachOfSeries = doLimit(eachOfLimit, 1);

	/**
	 * Reduces `coll` into a single value using an async `iteratee` to return each
	 * successive step. `memo` is the initial state of the reduction. This function
	 * only operates in series.
	 *
	 * For performance reasons, it may make sense to split a call to this function
	 * into a parallel map, and then use the normal `Array.prototype.reduce` on the
	 * results. This function is for situations where each step in the reduction
	 * needs to be async; if you can get the data before reducing it, then it's
	 * probably a good idea to do so.
	 *
	 * @name reduce
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @alias inject
	 * @alias foldl
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {*} memo - The initial state of the reduction.
	 * @param {AsyncFunction} iteratee - A function applied to each item in the
	 * array to produce the next step in the reduction.
	 * The `iteratee` should complete with the next state of the reduction.
	 * If the iteratee complete with an error, the reduction is stopped and the
	 * main `callback` is immediately called with the error.
	 * Invoked with (memo, item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Result is the reduced value. Invoked with
	 * (err, result).
	 * @example
	 *
	 * async.reduce([1,2,3], 0, function(memo, item, callback) {
	 *     // pointless async:
	 *     process.nextTick(function() {
	 *         callback(null, memo + item)
	 *     });
	 * }, function(err, result) {
	 *     // result is now equal to the last value of memo, which is 6
	 * });
	 */
	function reduce(coll, memo, iteratee, callback) {
	    callback = once(callback || noop);
	    var _iteratee = wrapAsync(iteratee);
	    eachOfSeries(coll, function(x, i, callback) {
	        _iteratee(memo, x, function(err, v) {
	            memo = v;
	            callback(err);
	        });
	    }, function(err) {
	        callback(err, memo);
	    });
	}

	/**
	 * Version of the compose function that is more natural to read. Each function
	 * consumes the return value of the previous function. It is the equivalent of
	 * [compose]{@link module:ControlFlow.compose} with the arguments reversed.
	 *
	 * Each function is executed with the `this` binding of the composed function.
	 *
	 * @name seq
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.compose]{@link module:ControlFlow.compose}
	 * @category Control Flow
	 * @param {...AsyncFunction} functions - the asynchronous functions to compose
	 * @returns {Function} a function that composes the `functions` in order
	 * @example
	 *
	 * // Requires lodash (or underscore), express3 and dresende's orm2.
	 * // Part of an app, that fetches cats of the logged user.
	 * // This example uses `seq` function to avoid overnesting and error
	 * // handling clutter.
	 * app.get('/cats', function(request, response) {
	 *     var User = request.models.User;
	 *     async.seq(
	 *         _.bind(User.get, User),  // 'User.get' has signature (id, callback(err, data))
	 *         function(user, fn) {
	 *             user.getCats(fn);      // 'getCats' has signature (callback(err, data))
	 *         }
	 *     )(req.session.user_id, function (err, cats) {
	 *         if (err) {
	 *             console.error(err);
	 *             response.json({ status: 'error', message: err.message });
	 *         } else {
	 *             response.json({ status: 'ok', message: 'Cats found', data: cats });
	 *         }
	 *     });
	 * });
	 */
	function seq(/*...functions*/) {
	    var _functions = arrayMap(arguments, wrapAsync);
	    return function(/*...args*/) {
	        var args = slice(arguments);
	        var that = this;

	        var cb = args[args.length - 1];
	        if (typeof cb == 'function') {
	            args.pop();
	        } else {
	            cb = noop;
	        }

	        reduce(_functions, args, function(newargs, fn, cb) {
	            fn.apply(that, newargs.concat(function(err/*, ...nextargs*/) {
	                var nextargs = slice(arguments, 1);
	                cb(err, nextargs);
	            }));
	        },
	        function(err, results) {
	            cb.apply(that, [err].concat(results));
	        });
	    };
	}

	/**
	 * Creates a function which is a composition of the passed asynchronous
	 * functions. Each function consumes the return value of the function that
	 * follows. Composing functions `f()`, `g()`, and `h()` would produce the result
	 * of `f(g(h()))`, only this version uses callbacks to obtain the return values.
	 *
	 * Each function is executed with the `this` binding of the composed function.
	 *
	 * @name compose
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @param {...AsyncFunction} functions - the asynchronous functions to compose
	 * @returns {Function} an asynchronous function that is the composed
	 * asynchronous `functions`
	 * @example
	 *
	 * function add1(n, callback) {
	 *     setTimeout(function () {
	 *         callback(null, n + 1);
	 *     }, 10);
	 * }
	 *
	 * function mul3(n, callback) {
	 *     setTimeout(function () {
	 *         callback(null, n * 3);
	 *     }, 10);
	 * }
	 *
	 * var add1mul3 = async.compose(mul3, add1);
	 * add1mul3(4, function (err, result) {
	 *     // result now equals 15
	 * });
	 */
	var compose = function(/*...args*/) {
	    return seq.apply(null, slice(arguments).reverse());
	};

	var _concat = Array.prototype.concat;

	/**
	 * The same as [`concat`]{@link module:Collections.concat} but runs a maximum of `limit` async operations at a time.
	 *
	 * @name concatLimit
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.concat]{@link module:Collections.concat}
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {AsyncFunction} iteratee - A function to apply to each item in `coll`,
	 * which should use an array as its result. Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished, or an error occurs. Results is an array
	 * containing the concatenated results of the `iteratee` function. Invoked with
	 * (err, results).
	 */
	var concatLimit = function(coll, limit, iteratee, callback) {
	    callback = callback || noop;
	    var _iteratee = wrapAsync(iteratee);
	    mapLimit(coll, limit, function(val, callback) {
	        _iteratee(val, function(err /*, ...args*/) {
	            if (err) return callback(err);
	            return callback(null, slice(arguments, 1));
	        });
	    }, function(err, mapResults) {
	        var result = [];
	        for (var i = 0; i < mapResults.length; i++) {
	            if (mapResults[i]) {
	                result = _concat.apply(result, mapResults[i]);
	            }
	        }

	        return callback(err, result);
	    });
	};

	/**
	 * Applies `iteratee` to each item in `coll`, concatenating the results. Returns
	 * the concatenated list. The `iteratee`s are called in parallel, and the
	 * results are concatenated as they return. There is no guarantee that the
	 * results array will be returned in the original order of `coll` passed to the
	 * `iteratee` function.
	 *
	 * @name concat
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - A function to apply to each item in `coll`,
	 * which should use an array as its result. Invoked with (item, callback).
	 * @param {Function} [callback(err)] - A callback which is called after all the
	 * `iteratee` functions have finished, or an error occurs. Results is an array
	 * containing the concatenated results of the `iteratee` function. Invoked with
	 * (err, results).
	 * @example
	 *
	 * async.concat(['dir1','dir2','dir3'], fs.readdir, function(err, files) {
	 *     // files is now a list of filenames that exist in the 3 directories
	 * });
	 */
	var concat = doLimit(concatLimit, Infinity);

	/**
	 * The same as [`concat`]{@link module:Collections.concat} but runs only a single async operation at a time.
	 *
	 * @name concatSeries
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.concat]{@link module:Collections.concat}
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - A function to apply to each item in `coll`.
	 * The iteratee should complete with an array an array of results.
	 * Invoked with (item, callback).
	 * @param {Function} [callback(err)] - A callback which is called after all the
	 * `iteratee` functions have finished, or an error occurs. Results is an array
	 * containing the concatenated results of the `iteratee` function. Invoked with
	 * (err, results).
	 */
	var concatSeries = doLimit(concatLimit, 1);

	/**
	 * Returns a function that when called, calls-back with the values provided.
	 * Useful as the first function in a [`waterfall`]{@link module:ControlFlow.waterfall}, or for plugging values in to
	 * [`auto`]{@link module:ControlFlow.auto}.
	 *
	 * @name constant
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @category Util
	 * @param {...*} arguments... - Any number of arguments to automatically invoke
	 * callback with.
	 * @returns {AsyncFunction} Returns a function that when invoked, automatically
	 * invokes the callback with the previous given arguments.
	 * @example
	 *
	 * async.waterfall([
	 *     async.constant(42),
	 *     function (value, next) {
	 *         // value === 42
	 *     },
	 *     //...
	 * ], callback);
	 *
	 * async.waterfall([
	 *     async.constant(filename, "utf8"),
	 *     fs.readFile,
	 *     function (fileData, next) {
	 *         //...
	 *     }
	 *     //...
	 * ], callback);
	 *
	 * async.auto({
	 *     hostname: async.constant("https://server.net/"),
	 *     port: findFreePort,
	 *     launchServer: ["hostname", "port", function (options, cb) {
	 *         startServer(options, cb);
	 *     }],
	 *     //...
	 * }, callback);
	 */
	var constant = function(/*...values*/) {
	    var values = slice(arguments);
	    var args = [null].concat(values);
	    return function (/*...ignoredArgs, callback*/) {
	        var callback = arguments[arguments.length - 1];
	        return callback.apply(this, args);
	    };
	};

	/**
	 * This method returns the first argument it receives.
	 *
	 * @static
	 * @since 0.1.0
	 * @memberOf _
	 * @category Util
	 * @param {*} value Any value.
	 * @returns {*} Returns `value`.
	 * @example
	 *
	 * var object = { 'a': 1 };
	 *
	 * console.log(_.identity(object) === object);
	 * // => true
	 */
	function identity(value) {
	  return value;
	}

	function _createTester(check, getResult) {
	    return function(eachfn, arr, iteratee, cb) {
	        cb = cb || noop;
	        var testPassed = false;
	        var testResult;
	        eachfn(arr, function(value, _, callback) {
	            iteratee(value, function(err, result) {
	                if (err) {
	                    callback(err);
	                } else if (check(result) && !testResult) {
	                    testPassed = true;
	                    testResult = getResult(true, value);
	                    callback(null, breakLoop);
	                } else {
	                    callback();
	                }
	            });
	        }, function(err) {
	            if (err) {
	                cb(err);
	            } else {
	                cb(null, testPassed ? testResult : getResult(false));
	            }
	        });
	    };
	}

	function _findGetResult(v, x) {
	    return x;
	}

	/**
	 * Returns the first value in `coll` that passes an async truth test. The
	 * `iteratee` is applied in parallel, meaning the first iteratee to return
	 * `true` will fire the detect `callback` with that result. That means the
	 * result might not be the first item in the original `coll` (in terms of order)
	 * that passes the test.

	 * If order within the original `coll` is important, then look at
	 * [`detectSeries`]{@link module:Collections.detectSeries}.
	 *
	 * @name detect
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @alias find
	 * @category Collections
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - A truth test to apply to each item in `coll`.
	 * The iteratee must complete with a boolean value as its result.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called as soon as any
	 * iteratee returns `true`, or after all the `iteratee` functions have finished.
	 * Result will be the first item in the array that passes the truth test
	 * (iteratee) or the value `undefined` if none passed. Invoked with
	 * (err, result).
	 * @example
	 *
	 * async.detect(['file1','file2','file3'], function(filePath, callback) {
	 *     fs.access(filePath, function(err) {
	 *         callback(null, !err)
	 *     });
	 * }, function(err, result) {
	 *     // result now equals the first file in the list that exists
	 * });
	 */
	var detect = doParallel(_createTester(identity, _findGetResult));

	/**
	 * The same as [`detect`]{@link module:Collections.detect} but runs a maximum of `limit` async operations at a
	 * time.
	 *
	 * @name detectLimit
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.detect]{@link module:Collections.detect}
	 * @alias findLimit
	 * @category Collections
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {AsyncFunction} iteratee - A truth test to apply to each item in `coll`.
	 * The iteratee must complete with a boolean value as its result.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called as soon as any
	 * iteratee returns `true`, or after all the `iteratee` functions have finished.
	 * Result will be the first item in the array that passes the truth test
	 * (iteratee) or the value `undefined` if none passed. Invoked with
	 * (err, result).
	 */
	var detectLimit = doParallelLimit(_createTester(identity, _findGetResult));

	/**
	 * The same as [`detect`]{@link module:Collections.detect} but runs only a single async operation at a time.
	 *
	 * @name detectSeries
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.detect]{@link module:Collections.detect}
	 * @alias findSeries
	 * @category Collections
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - A truth test to apply to each item in `coll`.
	 * The iteratee must complete with a boolean value as its result.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called as soon as any
	 * iteratee returns `true`, or after all the `iteratee` functions have finished.
	 * Result will be the first item in the array that passes the truth test
	 * (iteratee) or the value `undefined` if none passed. Invoked with
	 * (err, result).
	 */
	var detectSeries = doLimit(detectLimit, 1);

	function consoleFunc(name) {
	    return function (fn/*, ...args*/) {
	        var args = slice(arguments, 1);
	        args.push(function (err/*, ...args*/) {
	            var args = slice(arguments, 1);
	            if (typeof console === 'object') {
	                if (err) {
	                    if (console.error) {
	                        console.error(err);
	                    }
	                } else if (console[name]) {
	                    arrayEach(args, function (x) {
	                        console[name](x);
	                    });
	                }
	            }
	        });
	        wrapAsync(fn).apply(null, args);
	    };
	}

	/**
	 * Logs the result of an [`async` function]{@link AsyncFunction} to the
	 * `console` using `console.dir` to display the properties of the resulting object.
	 * Only works in Node.js or in browsers that support `console.dir` and
	 * `console.error` (such as FF and Chrome).
	 * If multiple arguments are returned from the async function,
	 * `console.dir` is called on each argument in order.
	 *
	 * @name dir
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @category Util
	 * @param {AsyncFunction} function - The function you want to eventually apply
	 * all arguments to.
	 * @param {...*} arguments... - Any number of arguments to apply to the function.
	 * @example
	 *
	 * // in a module
	 * var hello = function(name, callback) {
	 *     setTimeout(function() {
	 *         callback(null, {hello: name});
	 *     }, 1000);
	 * };
	 *
	 * // in the node repl
	 * node> async.dir(hello, 'world');
	 * {hello: 'world'}
	 */
	var dir = consoleFunc('dir');

	/**
	 * The post-check version of [`during`]{@link module:ControlFlow.during}. To reflect the difference in
	 * the order of operations, the arguments `test` and `fn` are switched.
	 *
	 * Also a version of [`doWhilst`]{@link module:ControlFlow.doWhilst} with asynchronous `test` function.
	 * @name doDuring
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.during]{@link module:ControlFlow.during}
	 * @category Control Flow
	 * @param {AsyncFunction} fn - An async function which is called each time
	 * `test` passes. Invoked with (callback).
	 * @param {AsyncFunction} test - asynchronous truth test to perform before each
	 * execution of `fn`. Invoked with (...args, callback), where `...args` are the
	 * non-error args from the previous callback of `fn`.
	 * @param {Function} [callback] - A callback which is called after the test
	 * function has failed and repeated execution of `fn` has stopped. `callback`
	 * will be passed an error if one occurred, otherwise `null`.
	 */
	function doDuring(fn, test, callback) {
	    callback = onlyOnce(callback || noop);
	    var _fn = wrapAsync(fn);
	    var _test = wrapAsync(test);

	    function next(err/*, ...args*/) {
	        if (err) return callback(err);
	        var args = slice(arguments, 1);
	        args.push(check);
	        _test.apply(this, args);
	    }

	    function check(err, truth) {
	        if (err) return callback(err);
	        if (!truth) return callback(null);
	        _fn(next);
	    }

	    check(null, true);

	}

	/**
	 * The post-check version of [`whilst`]{@link module:ControlFlow.whilst}. To reflect the difference in
	 * the order of operations, the arguments `test` and `iteratee` are switched.
	 *
	 * `doWhilst` is to `whilst` as `do while` is to `while` in plain JavaScript.
	 *
	 * @name doWhilst
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.whilst]{@link module:ControlFlow.whilst}
	 * @category Control Flow
	 * @param {AsyncFunction} iteratee - A function which is called each time `test`
	 * passes. Invoked with (callback).
	 * @param {Function} test - synchronous truth test to perform after each
	 * execution of `iteratee`. Invoked with any non-error callback results of
	 * `iteratee`.
	 * @param {Function} [callback] - A callback which is called after the test
	 * function has failed and repeated execution of `iteratee` has stopped.
	 * `callback` will be passed an error and any arguments passed to the final
	 * `iteratee`'s callback. Invoked with (err, [results]);
	 */
	function doWhilst(iteratee, test, callback) {
	    callback = onlyOnce(callback || noop);
	    var _iteratee = wrapAsync(iteratee);
	    var next = function(err/*, ...args*/) {
	        if (err) return callback(err);
	        var args = slice(arguments, 1);
	        if (test.apply(this, args)) return _iteratee(next);
	        callback.apply(null, [null].concat(args));
	    };
	    _iteratee(next);
	}

	/**
	 * Like ['doWhilst']{@link module:ControlFlow.doWhilst}, except the `test` is inverted. Note the
	 * argument ordering differs from `until`.
	 *
	 * @name doUntil
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.doWhilst]{@link module:ControlFlow.doWhilst}
	 * @category Control Flow
	 * @param {AsyncFunction} iteratee - An async function which is called each time
	 * `test` fails. Invoked with (callback).
	 * @param {Function} test - synchronous truth test to perform after each
	 * execution of `iteratee`. Invoked with any non-error callback results of
	 * `iteratee`.
	 * @param {Function} [callback] - A callback which is called after the test
	 * function has passed and repeated execution of `iteratee` has stopped. `callback`
	 * will be passed an error and any arguments passed to the final `iteratee`'s
	 * callback. Invoked with (err, [results]);
	 */
	function doUntil(iteratee, test, callback) {
	    doWhilst(iteratee, function() {
	        return !test.apply(this, arguments);
	    }, callback);
	}

	/**
	 * Like [`whilst`]{@link module:ControlFlow.whilst}, except the `test` is an asynchronous function that
	 * is passed a callback in the form of `function (err, truth)`. If error is
	 * passed to `test` or `fn`, the main callback is immediately called with the
	 * value of the error.
	 *
	 * @name during
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.whilst]{@link module:ControlFlow.whilst}
	 * @category Control Flow
	 * @param {AsyncFunction} test - asynchronous truth test to perform before each
	 * execution of `fn`. Invoked with (callback).
	 * @param {AsyncFunction} fn - An async function which is called each time
	 * `test` passes. Invoked with (callback).
	 * @param {Function} [callback] - A callback which is called after the test
	 * function has failed and repeated execution of `fn` has stopped. `callback`
	 * will be passed an error, if one occurred, otherwise `null`.
	 * @example
	 *
	 * var count = 0;
	 *
	 * async.during(
	 *     function (callback) {
	 *         return callback(null, count < 5);
	 *     },
	 *     function (callback) {
	 *         count++;
	 *         setTimeout(callback, 1000);
	 *     },
	 *     function (err) {
	 *         // 5 seconds have passed
	 *     }
	 * );
	 */
	function during(test, fn, callback) {
	    callback = onlyOnce(callback || noop);
	    var _fn = wrapAsync(fn);
	    var _test = wrapAsync(test);

	    function next(err) {
	        if (err) return callback(err);
	        _test(check);
	    }

	    function check(err, truth) {
	        if (err) return callback(err);
	        if (!truth) return callback(null);
	        _fn(next);
	    }

	    _test(check);
	}

	function _withoutIndex(iteratee) {
	    return function (value, index, callback) {
	        return iteratee(value, callback);
	    };
	}

	/**
	 * Applies the function `iteratee` to each item in `coll`, in parallel.
	 * The `iteratee` is called with an item from the list, and a callback for when
	 * it has finished. If the `iteratee` passes an error to its `callback`, the
	 * main `callback` (for the `each` function) is immediately called with the
	 * error.
	 *
	 * Note, that since this function applies `iteratee` to each item in parallel,
	 * there is no guarantee that the iteratee functions will complete in order.
	 *
	 * @name each
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @alias forEach
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - An async function to apply to
	 * each item in `coll`. Invoked with (item, callback).
	 * The array index is not passed to the iteratee.
	 * If you need the index, use `eachOf`.
	 * @param {Function} [callback] - A callback which is called when all
	 * `iteratee` functions have finished, or an error occurs. Invoked with (err).
	 * @example
	 *
	 * // assuming openFiles is an array of file names and saveFile is a function
	 * // to save the modified contents of that file:
	 *
	 * async.each(openFiles, saveFile, function(err){
	 *   // if any of the saves produced an error, err would equal that error
	 * });
	 *
	 * // assuming openFiles is an array of file names
	 * async.each(openFiles, function(file, callback) {
	 *
	 *     // Perform operation on file here.
	 *     console.log('Processing file ' + file);
	 *
	 *     if( file.length > 32 ) {
	 *       console.log('This file name is too long');
	 *       callback('File name too long');
	 *     } else {
	 *       // Do work to process file here
	 *       console.log('File processed');
	 *       callback();
	 *     }
	 * }, function(err) {
	 *     // if any of the file processing produced an error, err would equal that error
	 *     if( err ) {
	 *       // One of the iterations produced an error.
	 *       // All processing will now stop.
	 *       console.log('A file failed to process');
	 *     } else {
	 *       console.log('All files have been processed successfully');
	 *     }
	 * });
	 */
	function eachLimit(coll, iteratee, callback) {
	    eachOf(coll, _withoutIndex(wrapAsync(iteratee)), callback);
	}

	/**
	 * The same as [`each`]{@link module:Collections.each} but runs a maximum of `limit` async operations at a time.
	 *
	 * @name eachLimit
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.each]{@link module:Collections.each}
	 * @alias forEachLimit
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {AsyncFunction} iteratee - An async function to apply to each item in
	 * `coll`.
	 * The array index is not passed to the iteratee.
	 * If you need the index, use `eachOfLimit`.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called when all
	 * `iteratee` functions have finished, or an error occurs. Invoked with (err).
	 */
	function eachLimit$1(coll, limit, iteratee, callback) {
	    _eachOfLimit(limit)(coll, _withoutIndex(wrapAsync(iteratee)), callback);
	}

	/**
	 * The same as [`each`]{@link module:Collections.each} but runs only a single async operation at a time.
	 *
	 * @name eachSeries
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.each]{@link module:Collections.each}
	 * @alias forEachSeries
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - An async function to apply to each
	 * item in `coll`.
	 * The array index is not passed to the iteratee.
	 * If you need the index, use `eachOfSeries`.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called when all
	 * `iteratee` functions have finished, or an error occurs. Invoked with (err).
	 */
	var eachSeries = doLimit(eachLimit$1, 1);

	/**
	 * Wrap an async function and ensure it calls its callback on a later tick of
	 * the event loop.  If the function already calls its callback on a next tick,
	 * no extra deferral is added. This is useful for preventing stack overflows
	 * (`RangeError: Maximum call stack size exceeded`) and generally keeping
	 * [Zalgo](http://blog.izs.me/post/59142742143/designing-apis-for-asynchrony)
	 * contained. ES2017 `async` functions are returned as-is -- they are immune
	 * to Zalgo's corrupting influences, as they always resolve on a later tick.
	 *
	 * @name ensureAsync
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @category Util
	 * @param {AsyncFunction} fn - an async function, one that expects a node-style
	 * callback as its last argument.
	 * @returns {AsyncFunction} Returns a wrapped function with the exact same call
	 * signature as the function passed in.
	 * @example
	 *
	 * function sometimesAsync(arg, callback) {
	 *     if (cache[arg]) {
	 *         return callback(null, cache[arg]); // this would be synchronous!!
	 *     } else {
	 *         doSomeIO(arg, callback); // this IO would be asynchronous
	 *     }
	 * }
	 *
	 * // this has a risk of stack overflows if many results are cached in a row
	 * async.mapSeries(args, sometimesAsync, done);
	 *
	 * // this will defer sometimesAsync's callback if necessary,
	 * // preventing stack overflows
	 * async.mapSeries(args, async.ensureAsync(sometimesAsync), done);
	 */
	function ensureAsync(fn) {
	    if (isAsync(fn)) return fn;
	    return initialParams(function (args, callback) {
	        var sync = true;
	        args.push(function () {
	            var innerArgs = arguments;
	            if (sync) {
	                setImmediate$1(function () {
	                    callback.apply(null, innerArgs);
	                });
	            } else {
	                callback.apply(null, innerArgs);
	            }
	        });
	        fn.apply(this, args);
	        sync = false;
	    });
	}

	function notId(v) {
	    return !v;
	}

	/**
	 * Returns `true` if every element in `coll` satisfies an async test. If any
	 * iteratee call returns `false`, the main `callback` is immediately called.
	 *
	 * @name every
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @alias all
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - An async truth test to apply to each item
	 * in the collection in parallel.
	 * The iteratee must complete with a boolean result value.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Result will be either `true` or `false`
	 * depending on the values of the async tests. Invoked with (err, result).
	 * @example
	 *
	 * async.every(['file1','file2','file3'], function(filePath, callback) {
	 *     fs.access(filePath, function(err) {
	 *         callback(null, !err)
	 *     });
	 * }, function(err, result) {
	 *     // if result is true then every file exists
	 * });
	 */
	var every = doParallel(_createTester(notId, notId));

	/**
	 * The same as [`every`]{@link module:Collections.every} but runs a maximum of `limit` async operations at a time.
	 *
	 * @name everyLimit
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.every]{@link module:Collections.every}
	 * @alias allLimit
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {AsyncFunction} iteratee - An async truth test to apply to each item
	 * in the collection in parallel.
	 * The iteratee must complete with a boolean result value.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Result will be either `true` or `false`
	 * depending on the values of the async tests. Invoked with (err, result).
	 */
	var everyLimit = doParallelLimit(_createTester(notId, notId));

	/**
	 * The same as [`every`]{@link module:Collections.every} but runs only a single async operation at a time.
	 *
	 * @name everySeries
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.every]{@link module:Collections.every}
	 * @alias allSeries
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - An async truth test to apply to each item
	 * in the collection in series.
	 * The iteratee must complete with a boolean result value.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Result will be either `true` or `false`
	 * depending on the values of the async tests. Invoked with (err, result).
	 */
	var everySeries = doLimit(everyLimit, 1);

	/**
	 * The base implementation of `_.property` without support for deep paths.
	 *
	 * @private
	 * @param {string} key The key of the property to get.
	 * @returns {Function} Returns the new accessor function.
	 */
	function baseProperty(key) {
	  return function(object) {
	    return object == null ? undefined : object[key];
	  };
	}

	function filterArray(eachfn, arr, iteratee, callback) {
	    var truthValues = new Array(arr.length);
	    eachfn(arr, function (x, index, callback) {
	        iteratee(x, function (err, v) {
	            truthValues[index] = !!v;
	            callback(err);
	        });
	    }, function (err) {
	        if (err) return callback(err);
	        var results = [];
	        for (var i = 0; i < arr.length; i++) {
	            if (truthValues[i]) results.push(arr[i]);
	        }
	        callback(null, results);
	    });
	}

	function filterGeneric(eachfn, coll, iteratee, callback) {
	    var results = [];
	    eachfn(coll, function (x, index, callback) {
	        iteratee(x, function (err, v) {
	            if (err) {
	                callback(err);
	            } else {
	                if (v) {
	                    results.push({index: index, value: x});
	                }
	                callback();
	            }
	        });
	    }, function (err) {
	        if (err) {
	            callback(err);
	        } else {
	            callback(null, arrayMap(results.sort(function (a, b) {
	                return a.index - b.index;
	            }), baseProperty('value')));
	        }
	    });
	}

	function _filter(eachfn, coll, iteratee, callback) {
	    var filter = isArrayLike(coll) ? filterArray : filterGeneric;
	    filter(eachfn, coll, wrapAsync(iteratee), callback || noop);
	}

	/**
	 * Returns a new array of all the values in `coll` which pass an async truth
	 * test. This operation is performed in parallel, but the results array will be
	 * in the same order as the original.
	 *
	 * @name filter
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @alias select
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {Function} iteratee - A truth test to apply to each item in `coll`.
	 * The `iteratee` is passed a `callback(err, truthValue)`, which must be called
	 * with a boolean argument once it has completed. Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Invoked with (err, results).
	 * @example
	 *
	 * async.filter(['file1','file2','file3'], function(filePath, callback) {
	 *     fs.access(filePath, function(err) {
	 *         callback(null, !err)
	 *     });
	 * }, function(err, results) {
	 *     // results now equals an array of the existing files
	 * });
	 */
	var filter = doParallel(_filter);

	/**
	 * The same as [`filter`]{@link module:Collections.filter} but runs a maximum of `limit` async operations at a
	 * time.
	 *
	 * @name filterLimit
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.filter]{@link module:Collections.filter}
	 * @alias selectLimit
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {Function} iteratee - A truth test to apply to each item in `coll`.
	 * The `iteratee` is passed a `callback(err, truthValue)`, which must be called
	 * with a boolean argument once it has completed. Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Invoked with (err, results).
	 */
	var filterLimit = doParallelLimit(_filter);

	/**
	 * The same as [`filter`]{@link module:Collections.filter} but runs only a single async operation at a time.
	 *
	 * @name filterSeries
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.filter]{@link module:Collections.filter}
	 * @alias selectSeries
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {Function} iteratee - A truth test to apply to each item in `coll`.
	 * The `iteratee` is passed a `callback(err, truthValue)`, which must be called
	 * with a boolean argument once it has completed. Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Invoked with (err, results)
	 */
	var filterSeries = doLimit(filterLimit, 1);

	/**
	 * Calls the asynchronous function `fn` with a callback parameter that allows it
	 * to call itself again, in series, indefinitely.

	 * If an error is passed to the callback then `errback` is called with the
	 * error, and execution stops, otherwise it will never be called.
	 *
	 * @name forever
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @param {AsyncFunction} fn - an async function to call repeatedly.
	 * Invoked with (next).
	 * @param {Function} [errback] - when `fn` passes an error to it's callback,
	 * this function will be called, and execution stops. Invoked with (err).
	 * @example
	 *
	 * async.forever(
	 *     function(next) {
	 *         // next is suitable for passing to things that need a callback(err [, whatever]);
	 *         // it will result in this function being called again.
	 *     },
	 *     function(err) {
	 *         // if next is called with a value in its first parameter, it will appear
	 *         // in here as 'err', and execution will stop.
	 *     }
	 * );
	 */
	function forever(fn, errback) {
	    var done = onlyOnce(errback || noop);
	    var task = wrapAsync(ensureAsync(fn));

	    function next(err) {
	        if (err) return done(err);
	        task(next);
	    }
	    next();
	}

	/**
	 * The same as [`groupBy`]{@link module:Collections.groupBy} but runs a maximum of `limit` async operations at a time.
	 *
	 * @name groupByLimit
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.groupBy]{@link module:Collections.groupBy}
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {AsyncFunction} iteratee - An async function to apply to each item in
	 * `coll`.
	 * The iteratee should complete with a `key` to group the value under.
	 * Invoked with (value, callback).
	 * @param {Function} [callback] - A callback which is called when all `iteratee`
	 * functions have finished, or an error occurs. Result is an `Object` whoses
	 * properties are arrays of values which returned the corresponding key.
	 */
	var groupByLimit = function(coll, limit, iteratee, callback) {
	    callback = callback || noop;
	    var _iteratee = wrapAsync(iteratee);
	    mapLimit(coll, limit, function(val, callback) {
	        _iteratee(val, function(err, key) {
	            if (err) return callback(err);
	            return callback(null, {key: key, val: val});
	        });
	    }, function(err, mapResults) {
	        var result = {};
	        // from MDN, handle object having an `hasOwnProperty` prop
	        var hasOwnProperty = Object.prototype.hasOwnProperty;

	        for (var i = 0; i < mapResults.length; i++) {
	            if (mapResults[i]) {
	                var key = mapResults[i].key;
	                var val = mapResults[i].val;

	                if (hasOwnProperty.call(result, key)) {
	                    result[key].push(val);
	                } else {
	                    result[key] = [val];
	                }
	            }
	        }

	        return callback(err, result);
	    });
	};

	/**
	 * Returns a new object, where each value corresponds to an array of items, from
	 * `coll`, that returned the corresponding key. That is, the keys of the object
	 * correspond to the values passed to the `iteratee` callback.
	 *
	 * Note: Since this function applies the `iteratee` to each item in parallel,
	 * there is no guarantee that the `iteratee` functions will complete in order.
	 * However, the values for each key in the `result` will be in the same order as
	 * the original `coll`. For Objects, the values will roughly be in the order of
	 * the original Objects' keys (but this can vary across JavaScript engines).
	 *
	 * @name groupBy
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - An async function to apply to each item in
	 * `coll`.
	 * The iteratee should complete with a `key` to group the value under.
	 * Invoked with (value, callback).
	 * @param {Function} [callback] - A callback which is called when all `iteratee`
	 * functions have finished, or an error occurs. Result is an `Object` whoses
	 * properties are arrays of values which returned the corresponding key.
	 * @example
	 *
	 * async.groupBy(['userId1', 'userId2', 'userId3'], function(userId, callback) {
	 *     db.findById(userId, function(err, user) {
	 *         if (err) return callback(err);
	 *         return callback(null, user.age);
	 *     });
	 * }, function(err, result) {
	 *     // result is object containing the userIds grouped by age
	 *     // e.g. { 30: ['userId1', 'userId3'], 42: ['userId2']};
	 * });
	 */
	var groupBy = doLimit(groupByLimit, Infinity);

	/**
	 * The same as [`groupBy`]{@link module:Collections.groupBy} but runs only a single async operation at a time.
	 *
	 * @name groupBySeries
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.groupBy]{@link module:Collections.groupBy}
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {AsyncFunction} iteratee - An async function to apply to each item in
	 * `coll`.
	 * The iteratee should complete with a `key` to group the value under.
	 * Invoked with (value, callback).
	 * @param {Function} [callback] - A callback which is called when all `iteratee`
	 * functions have finished, or an error occurs. Result is an `Object` whoses
	 * properties are arrays of values which returned the corresponding key.
	 */
	var groupBySeries = doLimit(groupByLimit, 1);

	/**
	 * Logs the result of an `async` function to the `console`. Only works in
	 * Node.js or in browsers that support `console.log` and `console.error` (such
	 * as FF and Chrome). If multiple arguments are returned from the async
	 * function, `console.log` is called on each argument in order.
	 *
	 * @name log
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @category Util
	 * @param {AsyncFunction} function - The function you want to eventually apply
	 * all arguments to.
	 * @param {...*} arguments... - Any number of arguments to apply to the function.
	 * @example
	 *
	 * // in a module
	 * var hello = function(name, callback) {
	 *     setTimeout(function() {
	 *         callback(null, 'hello ' + name);
	 *     }, 1000);
	 * };
	 *
	 * // in the node repl
	 * node> async.log(hello, 'world');
	 * 'hello world'
	 */
	var log = consoleFunc('log');

	/**
	 * The same as [`mapValues`]{@link module:Collections.mapValues} but runs a maximum of `limit` async operations at a
	 * time.
	 *
	 * @name mapValuesLimit
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.mapValues]{@link module:Collections.mapValues}
	 * @category Collection
	 * @param {Object} obj - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {AsyncFunction} iteratee - A function to apply to each value and key
	 * in `coll`.
	 * The iteratee should complete with the transformed value as its result.
	 * Invoked with (value, key, callback).
	 * @param {Function} [callback] - A callback which is called when all `iteratee`
	 * functions have finished, or an error occurs. `result` is a new object consisting
	 * of each key from `obj`, with each transformed value on the right-hand side.
	 * Invoked with (err, result).
	 */
	function mapValuesLimit(obj, limit, iteratee, callback) {
	    callback = once(callback || noop);
	    var newObj = {};
	    var _iteratee = wrapAsync(iteratee);
	    eachOfLimit(obj, limit, function(val, key, next) {
	        _iteratee(val, key, function (err, result) {
	            if (err) return next(err);
	            newObj[key] = result;
	            next();
	        });
	    }, function (err) {
	        callback(err, newObj);
	    });
	}

	/**
	 * A relative of [`map`]{@link module:Collections.map}, designed for use with objects.
	 *
	 * Produces a new Object by mapping each value of `obj` through the `iteratee`
	 * function. The `iteratee` is called each `value` and `key` from `obj` and a
	 * callback for when it has finished processing. Each of these callbacks takes
	 * two arguments: an `error`, and the transformed item from `obj`. If `iteratee`
	 * passes an error to its callback, the main `callback` (for the `mapValues`
	 * function) is immediately called with the error.
	 *
	 * Note, the order of the keys in the result is not guaranteed.  The keys will
	 * be roughly in the order they complete, (but this is very engine-specific)
	 *
	 * @name mapValues
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @category Collection
	 * @param {Object} obj - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - A function to apply to each value and key
	 * in `coll`.
	 * The iteratee should complete with the transformed value as its result.
	 * Invoked with (value, key, callback).
	 * @param {Function} [callback] - A callback which is called when all `iteratee`
	 * functions have finished, or an error occurs. `result` is a new object consisting
	 * of each key from `obj`, with each transformed value on the right-hand side.
	 * Invoked with (err, result).
	 * @example
	 *
	 * async.mapValues({
	 *     f1: 'file1',
	 *     f2: 'file2',
	 *     f3: 'file3'
	 * }, function (file, key, callback) {
	 *   fs.stat(file, callback);
	 * }, function(err, result) {
	 *     // result is now a map of stats for each file, e.g.
	 *     // {
	 *     //     f1: [stats for file1],
	 *     //     f2: [stats for file2],
	 *     //     f3: [stats for file3]
	 *     // }
	 * });
	 */

	var mapValues = doLimit(mapValuesLimit, Infinity);

	/**
	 * The same as [`mapValues`]{@link module:Collections.mapValues} but runs only a single async operation at a time.
	 *
	 * @name mapValuesSeries
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.mapValues]{@link module:Collections.mapValues}
	 * @category Collection
	 * @param {Object} obj - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - A function to apply to each value and key
	 * in `coll`.
	 * The iteratee should complete with the transformed value as its result.
	 * Invoked with (value, key, callback).
	 * @param {Function} [callback] - A callback which is called when all `iteratee`
	 * functions have finished, or an error occurs. `result` is a new object consisting
	 * of each key from `obj`, with each transformed value on the right-hand side.
	 * Invoked with (err, result).
	 */
	var mapValuesSeries = doLimit(mapValuesLimit, 1);

	function has(obj, key) {
	    return key in obj;
	}

	/**
	 * Caches the results of an async function. When creating a hash to store
	 * function results against, the callback is omitted from the hash and an
	 * optional hash function can be used.
	 *
	 * If no hash function is specified, the first argument is used as a hash key,
	 * which may work reasonably if it is a string or a data type that converts to a
	 * distinct string. Note that objects and arrays will not behave reasonably.
	 * Neither will cases where the other arguments are significant. In such cases,
	 * specify your own hash function.
	 *
	 * The cache of results is exposed as the `memo` property of the function
	 * returned by `memoize`.
	 *
	 * @name memoize
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @category Util
	 * @param {AsyncFunction} fn - The async function to proxy and cache results from.
	 * @param {Function} hasher - An optional function for generating a custom hash
	 * for storing results. It has all the arguments applied to it apart from the
	 * callback, and must be synchronous.
	 * @returns {AsyncFunction} a memoized version of `fn`
	 * @example
	 *
	 * var slow_fn = function(name, callback) {
	 *     // do something
	 *     callback(null, result);
	 * };
	 * var fn = async.memoize(slow_fn);
	 *
	 * // fn can now be used as if it were slow_fn
	 * fn('some name', function() {
	 *     // callback
	 * });
	 */
	function memoize(fn, hasher) {
	    var memo = Object.create(null);
	    var queues = Object.create(null);
	    hasher = hasher || identity;
	    var _fn = wrapAsync(fn);
	    var memoized = initialParams(function memoized(args, callback) {
	        var key = hasher.apply(null, args);
	        if (has(memo, key)) {
	            setImmediate$1(function() {
	                callback.apply(null, memo[key]);
	            });
	        } else if (has(queues, key)) {
	            queues[key].push(callback);
	        } else {
	            queues[key] = [callback];
	            _fn.apply(null, args.concat(function(/*args*/) {
	                var args = slice(arguments);
	                memo[key] = args;
	                var q = queues[key];
	                delete queues[key];
	                for (var i = 0, l = q.length; i < l; i++) {
	                    q[i].apply(null, args);
	                }
	            }));
	        }
	    });
	    memoized.memo = memo;
	    memoized.unmemoized = fn;
	    return memoized;
	}

	/**
	 * Calls `callback` on a later loop around the event loop. In Node.js this just
	 * calls `process.nextTick`.  In the browser it will use `setImmediate` if
	 * available, otherwise `setTimeout(callback, 0)`, which means other higher
	 * priority events may precede the execution of `callback`.
	 *
	 * This is used internally for browser-compatibility purposes.
	 *
	 * @name nextTick
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @see [async.setImmediate]{@link module:Utils.setImmediate}
	 * @category Util
	 * @param {Function} callback - The function to call on a later loop around
	 * the event loop. Invoked with (args...).
	 * @param {...*} args... - any number of additional arguments to pass to the
	 * callback on the next tick.
	 * @example
	 *
	 * var call_order = [];
	 * async.nextTick(function() {
	 *     call_order.push('two');
	 *     // call_order now equals ['one','two']
	 * });
	 * call_order.push('one');
	 *
	 * async.setImmediate(function (a, b, c) {
	 *     // a, b, and c equal 1, 2, and 3
	 * }, 1, 2, 3);
	 */
	var _defer$1;

	if (hasNextTick) {
	    _defer$1 = browser$1.nextTick;
	} else if (hasSetImmediate) {
	    _defer$1 = setImmediate;
	} else {
	    _defer$1 = fallback;
	}

	var nextTick = wrap(_defer$1);

	function _parallel(eachfn, tasks, callback) {
	    callback = callback || noop;
	    var results = isArrayLike(tasks) ? [] : {};

	    eachfn(tasks, function (task, key, callback) {
	        wrapAsync(task)(function (err, result) {
	            if (arguments.length > 2) {
	                result = slice(arguments, 1);
	            }
	            results[key] = result;
	            callback(err);
	        });
	    }, function (err) {
	        callback(err, results);
	    });
	}

	/**
	 * Run the `tasks` collection of functions in parallel, without waiting until
	 * the previous function has completed. If any of the functions pass an error to
	 * its callback, the main `callback` is immediately called with the value of the
	 * error. Once the `tasks` have completed, the results are passed to the final
	 * `callback` as an array.
	 *
	 * **Note:** `parallel` is about kicking-off I/O tasks in parallel, not about
	 * parallel execution of code.  If your tasks do not use any timers or perform
	 * any I/O, they will actually be executed in series.  Any synchronous setup
	 * sections for each task will happen one after the other.  JavaScript remains
	 * single-threaded.
	 *
	 * **Hint:** Use [`reflect`]{@link module:Utils.reflect} to continue the
	 * execution of other tasks when a task fails.
	 *
	 * It is also possible to use an object instead of an array. Each property will
	 * be run as a function and the results will be passed to the final `callback`
	 * as an object instead of an array. This can be a more readable way of handling
	 * results from {@link async.parallel}.
	 *
	 * @name parallel
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @param {Array|Iterable|Object} tasks - A collection of
	 * [async functions]{@link AsyncFunction} to run.
	 * Each async function can complete with any number of optional `result` values.
	 * @param {Function} [callback] - An optional callback to run once all the
	 * functions have completed successfully. This function gets a results array
	 * (or object) containing all the result arguments passed to the task callbacks.
	 * Invoked with (err, results).
	 *
	 * @example
	 * async.parallel([
	 *     function(callback) {
	 *         setTimeout(function() {
	 *             callback(null, 'one');
	 *         }, 200);
	 *     },
	 *     function(callback) {
	 *         setTimeout(function() {
	 *             callback(null, 'two');
	 *         }, 100);
	 *     }
	 * ],
	 * // optional callback
	 * function(err, results) {
	 *     // the results array will equal ['one','two'] even though
	 *     // the second function had a shorter timeout.
	 * });
	 *
	 * // an example using an object instead of an array
	 * async.parallel({
	 *     one: function(callback) {
	 *         setTimeout(function() {
	 *             callback(null, 1);
	 *         }, 200);
	 *     },
	 *     two: function(callback) {
	 *         setTimeout(function() {
	 *             callback(null, 2);
	 *         }, 100);
	 *     }
	 * }, function(err, results) {
	 *     // results is now equals to: {one: 1, two: 2}
	 * });
	 */
	function parallelLimit(tasks, callback) {
	    _parallel(eachOf, tasks, callback);
	}

	/**
	 * The same as [`parallel`]{@link module:ControlFlow.parallel} but runs a maximum of `limit` async operations at a
	 * time.
	 *
	 * @name parallelLimit
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.parallel]{@link module:ControlFlow.parallel}
	 * @category Control Flow
	 * @param {Array|Iterable|Object} tasks - A collection of
	 * [async functions]{@link AsyncFunction} to run.
	 * Each async function can complete with any number of optional `result` values.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {Function} [callback] - An optional callback to run once all the
	 * functions have completed successfully. This function gets a results array
	 * (or object) containing all the result arguments passed to the task callbacks.
	 * Invoked with (err, results).
	 */
	function parallelLimit$1(tasks, limit, callback) {
	    _parallel(_eachOfLimit(limit), tasks, callback);
	}

	/**
	 * A queue of tasks for the worker function to complete.
	 * @typedef {Object} QueueObject
	 * @memberOf module:ControlFlow
	 * @property {Function} length - a function returning the number of items
	 * waiting to be processed. Invoke with `queue.length()`.
	 * @property {boolean} started - a boolean indicating whether or not any
	 * items have been pushed and processed by the queue.
	 * @property {Function} running - a function returning the number of items
	 * currently being processed. Invoke with `queue.running()`.
	 * @property {Function} workersList - a function returning the array of items
	 * currently being processed. Invoke with `queue.workersList()`.
	 * @property {Function} idle - a function returning false if there are items
	 * waiting or being processed, or true if not. Invoke with `queue.idle()`.
	 * @property {number} concurrency - an integer for determining how many `worker`
	 * functions should be run in parallel. This property can be changed after a
	 * `queue` is created to alter the concurrency on-the-fly.
	 * @property {Function} push - add a new task to the `queue`. Calls `callback`
	 * once the `worker` has finished processing the task. Instead of a single task,
	 * a `tasks` array can be submitted. The respective callback is used for every
	 * task in the list. Invoke with `queue.push(task, [callback])`,
	 * @property {Function} unshift - add a new task to the front of the `queue`.
	 * Invoke with `queue.unshift(task, [callback])`.
	 * @property {Function} remove - remove items from the queue that match a test
	 * function.  The test function will be passed an object with a `data` property,
	 * and a `priority` property, if this is a
	 * [priorityQueue]{@link module:ControlFlow.priorityQueue} object.
	 * Invoked with `queue.remove(testFn)`, where `testFn` is of the form
	 * `function ({data, priority}) {}` and returns a Boolean.
	 * @property {Function} saturated - a callback that is called when the number of
	 * running workers hits the `concurrency` limit, and further tasks will be
	 * queued.
	 * @property {Function} unsaturated - a callback that is called when the number
	 * of running workers is less than the `concurrency` & `buffer` limits, and
	 * further tasks will not be queued.
	 * @property {number} buffer - A minimum threshold buffer in order to say that
	 * the `queue` is `unsaturated`.
	 * @property {Function} empty - a callback that is called when the last item
	 * from the `queue` is given to a `worker`.
	 * @property {Function} drain - a callback that is called when the last item
	 * from the `queue` has returned from the `worker`.
	 * @property {Function} error - a callback that is called when a task errors.
	 * Has the signature `function(error, task)`.
	 * @property {boolean} paused - a boolean for determining whether the queue is
	 * in a paused state.
	 * @property {Function} pause - a function that pauses the processing of tasks
	 * until `resume()` is called. Invoke with `queue.pause()`.
	 * @property {Function} resume - a function that resumes the processing of
	 * queued tasks when the queue is paused. Invoke with `queue.resume()`.
	 * @property {Function} kill - a function that removes the `drain` callback and
	 * empties remaining tasks from the queue forcing it to go idle. No more tasks
	 * should be pushed to the queue after calling this function. Invoke with `queue.kill()`.
	 */

	/**
	 * Creates a `queue` object with the specified `concurrency`. Tasks added to the
	 * `queue` are processed in parallel (up to the `concurrency` limit). If all
	 * `worker`s are in progress, the task is queued until one becomes available.
	 * Once a `worker` completes a `task`, that `task`'s callback is called.
	 *
	 * @name queue
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @param {AsyncFunction} worker - An async function for processing a queued task.
	 * If you want to handle errors from an individual task, pass a callback to
	 * `q.push()`. Invoked with (task, callback).
	 * @param {number} [concurrency=1] - An `integer` for determining how many
	 * `worker` functions should be run in parallel.  If omitted, the concurrency
	 * defaults to `1`.  If the concurrency is `0`, an error is thrown.
	 * @returns {module:ControlFlow.QueueObject} A queue object to manage the tasks. Callbacks can
	 * attached as certain properties to listen for specific events during the
	 * lifecycle of the queue.
	 * @example
	 *
	 * // create a queue object with concurrency 2
	 * var q = async.queue(function(task, callback) {
	 *     console.log('hello ' + task.name);
	 *     callback();
	 * }, 2);
	 *
	 * // assign a callback
	 * q.drain = function() {
	 *     console.log('all items have been processed');
	 * };
	 *
	 * // add some items to the queue
	 * q.push({name: 'foo'}, function(err) {
	 *     console.log('finished processing foo');
	 * });
	 * q.push({name: 'bar'}, function (err) {
	 *     console.log('finished processing bar');
	 * });
	 *
	 * // add some items to the queue (batch-wise)
	 * q.push([{name: 'baz'},{name: 'bay'},{name: 'bax'}], function(err) {
	 *     console.log('finished processing item');
	 * });
	 *
	 * // add some items to the front of the queue
	 * q.unshift({name: 'bar'}, function (err) {
	 *     console.log('finished processing bar');
	 * });
	 */
	var queue$1 = function (worker, concurrency) {
	    var _worker = wrapAsync(worker);
	    return queue(function (items, cb) {
	        _worker(items[0], cb);
	    }, concurrency, 1);
	};

	/**
	 * The same as [async.queue]{@link module:ControlFlow.queue} only tasks are assigned a priority and
	 * completed in ascending priority order.
	 *
	 * @name priorityQueue
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.queue]{@link module:ControlFlow.queue}
	 * @category Control Flow
	 * @param {AsyncFunction} worker - An async function for processing a queued task.
	 * If you want to handle errors from an individual task, pass a callback to
	 * `q.push()`.
	 * Invoked with (task, callback).
	 * @param {number} concurrency - An `integer` for determining how many `worker`
	 * functions should be run in parallel.  If omitted, the concurrency defaults to
	 * `1`.  If the concurrency is `0`, an error is thrown.
	 * @returns {module:ControlFlow.QueueObject} A priorityQueue object to manage the tasks. There are two
	 * differences between `queue` and `priorityQueue` objects:
	 * * `push(task, priority, [callback])` - `priority` should be a number. If an
	 *   array of `tasks` is given, all tasks will be assigned the same priority.
	 * * The `unshift` method was removed.
	 */
	var priorityQueue = function(worker, concurrency) {
	    // Start with a normal queue
	    var q = queue$1(worker, concurrency);

	    // Override push to accept second parameter representing priority
	    q.push = function(data, priority, callback) {
	        if (callback == null) callback = noop;
	        if (typeof callback !== 'function') {
	            throw new Error('task callback must be a function');
	        }
	        q.started = true;
	        if (!isArray(data)) {
	            data = [data];
	        }
	        if (data.length === 0) {
	            // call drain immediately if there are no tasks
	            return setImmediate$1(function() {
	                q.drain();
	            });
	        }

	        priority = priority || 0;
	        var nextNode = q._tasks.head;
	        while (nextNode && priority >= nextNode.priority) {
	            nextNode = nextNode.next;
	        }

	        for (var i = 0, l = data.length; i < l; i++) {
	            var item = {
	                data: data[i],
	                priority: priority,
	                callback: callback
	            };

	            if (nextNode) {
	                q._tasks.insertBefore(nextNode, item);
	            } else {
	                q._tasks.push(item);
	            }
	        }
	        setImmediate$1(q.process);
	    };

	    // Remove unshift function
	    delete q.unshift;

	    return q;
	};

	/**
	 * Runs the `tasks` array of functions in parallel, without waiting until the
	 * previous function has completed. Once any of the `tasks` complete or pass an
	 * error to its callback, the main `callback` is immediately called. It's
	 * equivalent to `Promise.race()`.
	 *
	 * @name race
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @param {Array} tasks - An array containing [async functions]{@link AsyncFunction}
	 * to run. Each function can complete with an optional `result` value.
	 * @param {Function} callback - A callback to run once any of the functions have
	 * completed. This function gets an error or result from the first function that
	 * completed. Invoked with (err, result).
	 * @returns undefined
	 * @example
	 *
	 * async.race([
	 *     function(callback) {
	 *         setTimeout(function() {
	 *             callback(null, 'one');
	 *         }, 200);
	 *     },
	 *     function(callback) {
	 *         setTimeout(function() {
	 *             callback(null, 'two');
	 *         }, 100);
	 *     }
	 * ],
	 * // main callback
	 * function(err, result) {
	 *     // the result will be equal to 'two' as it finishes earlier
	 * });
	 */
	function race(tasks, callback) {
	    callback = once(callback || noop);
	    if (!isArray(tasks)) return callback(new TypeError('First argument to race must be an array of functions'));
	    if (!tasks.length) return callback();
	    for (var i = 0, l = tasks.length; i < l; i++) {
	        wrapAsync(tasks[i])(callback);
	    }
	}

	/**
	 * Same as [`reduce`]{@link module:Collections.reduce}, only operates on `array` in reverse order.
	 *
	 * @name reduceRight
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.reduce]{@link module:Collections.reduce}
	 * @alias foldr
	 * @category Collection
	 * @param {Array} array - A collection to iterate over.
	 * @param {*} memo - The initial state of the reduction.
	 * @param {AsyncFunction} iteratee - A function applied to each item in the
	 * array to produce the next step in the reduction.
	 * The `iteratee` should complete with the next state of the reduction.
	 * If the iteratee complete with an error, the reduction is stopped and the
	 * main `callback` is immediately called with the error.
	 * Invoked with (memo, item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Result is the reduced value. Invoked with
	 * (err, result).
	 */
	function reduceRight (array, memo, iteratee, callback) {
	    var reversed = slice(array).reverse();
	    reduce(reversed, memo, iteratee, callback);
	}

	/**
	 * Wraps the async function in another function that always completes with a
	 * result object, even when it errors.
	 *
	 * The result object has either the property `error` or `value`.
	 *
	 * @name reflect
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @category Util
	 * @param {AsyncFunction} fn - The async function you want to wrap
	 * @returns {Function} - A function that always passes null to it's callback as
	 * the error. The second argument to the callback will be an `object` with
	 * either an `error` or a `value` property.
	 * @example
	 *
	 * async.parallel([
	 *     async.reflect(function(callback) {
	 *         // do some stuff ...
	 *         callback(null, 'one');
	 *     }),
	 *     async.reflect(function(callback) {
	 *         // do some more stuff but error ...
	 *         callback('bad stuff happened');
	 *     }),
	 *     async.reflect(function(callback) {
	 *         // do some more stuff ...
	 *         callback(null, 'two');
	 *     })
	 * ],
	 * // optional callback
	 * function(err, results) {
	 *     // values
	 *     // results[0].value = 'one'
	 *     // results[1].error = 'bad stuff happened'
	 *     // results[2].value = 'two'
	 * });
	 */
	function reflect(fn) {
	    var _fn = wrapAsync(fn);
	    return initialParams(function reflectOn(args, reflectCallback) {
	        args.push(function callback(error, cbArg) {
	            if (error) {
	                reflectCallback(null, { error: error });
	            } else {
	                var value;
	                if (arguments.length <= 2) {
	                    value = cbArg;
	                } else {
	                    value = slice(arguments, 1);
	                }
	                reflectCallback(null, { value: value });
	            }
	        });

	        return _fn.apply(this, args);
	    });
	}

	/**
	 * A helper function that wraps an array or an object of functions with `reflect`.
	 *
	 * @name reflectAll
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @see [async.reflect]{@link module:Utils.reflect}
	 * @category Util
	 * @param {Array|Object|Iterable} tasks - The collection of
	 * [async functions]{@link AsyncFunction} to wrap in `async.reflect`.
	 * @returns {Array} Returns an array of async functions, each wrapped in
	 * `async.reflect`
	 * @example
	 *
	 * let tasks = [
	 *     function(callback) {
	 *         setTimeout(function() {
	 *             callback(null, 'one');
	 *         }, 200);
	 *     },
	 *     function(callback) {
	 *         // do some more stuff but error ...
	 *         callback(new Error('bad stuff happened'));
	 *     },
	 *     function(callback) {
	 *         setTimeout(function() {
	 *             callback(null, 'two');
	 *         }, 100);
	 *     }
	 * ];
	 *
	 * async.parallel(async.reflectAll(tasks),
	 * // optional callback
	 * function(err, results) {
	 *     // values
	 *     // results[0].value = 'one'
	 *     // results[1].error = Error('bad stuff happened')
	 *     // results[2].value = 'two'
	 * });
	 *
	 * // an example using an object instead of an array
	 * let tasks = {
	 *     one: function(callback) {
	 *         setTimeout(function() {
	 *             callback(null, 'one');
	 *         }, 200);
	 *     },
	 *     two: function(callback) {
	 *         callback('two');
	 *     },
	 *     three: function(callback) {
	 *         setTimeout(function() {
	 *             callback(null, 'three');
	 *         }, 100);
	 *     }
	 * };
	 *
	 * async.parallel(async.reflectAll(tasks),
	 * // optional callback
	 * function(err, results) {
	 *     // values
	 *     // results.one.value = 'one'
	 *     // results.two.error = 'two'
	 *     // results.three.value = 'three'
	 * });
	 */
	function reflectAll(tasks) {
	    var results;
	    if (isArray(tasks)) {
	        results = arrayMap(tasks, reflect);
	    } else {
	        results = {};
	        baseForOwn(tasks, function(task, key) {
	            results[key] = reflect.call(this, task);
	        });
	    }
	    return results;
	}

	function reject$1(eachfn, arr, iteratee, callback) {
	    _filter(eachfn, arr, function(value, cb) {
	        iteratee(value, function(err, v) {
	            cb(err, !v);
	        });
	    }, callback);
	}

	/**
	 * The opposite of [`filter`]{@link module:Collections.filter}. Removes values that pass an `async` truth test.
	 *
	 * @name reject
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.filter]{@link module:Collections.filter}
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {Function} iteratee - An async truth test to apply to each item in
	 * `coll`.
	 * The should complete with a boolean value as its `result`.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Invoked with (err, results).
	 * @example
	 *
	 * async.reject(['file1','file2','file3'], function(filePath, callback) {
	 *     fs.access(filePath, function(err) {
	 *         callback(null, !err)
	 *     });
	 * }, function(err, results) {
	 *     // results now equals an array of missing files
	 *     createFiles(results);
	 * });
	 */
	var reject = doParallel(reject$1);

	/**
	 * The same as [`reject`]{@link module:Collections.reject} but runs a maximum of `limit` async operations at a
	 * time.
	 *
	 * @name rejectLimit
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.reject]{@link module:Collections.reject}
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {Function} iteratee - An async truth test to apply to each item in
	 * `coll`.
	 * The should complete with a boolean value as its `result`.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Invoked with (err, results).
	 */
	var rejectLimit = doParallelLimit(reject$1);

	/**
	 * The same as [`reject`]{@link module:Collections.reject} but runs only a single async operation at a time.
	 *
	 * @name rejectSeries
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.reject]{@link module:Collections.reject}
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {Function} iteratee - An async truth test to apply to each item in
	 * `coll`.
	 * The should complete with a boolean value as its `result`.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Invoked with (err, results).
	 */
	var rejectSeries = doLimit(rejectLimit, 1);

	/**
	 * Creates a function that returns `value`.
	 *
	 * @static
	 * @memberOf _
	 * @since 2.4.0
	 * @category Util
	 * @param {*} value The value to return from the new function.
	 * @returns {Function} Returns the new constant function.
	 * @example
	 *
	 * var objects = _.times(2, _.constant({ 'a': 1 }));
	 *
	 * console.log(objects);
	 * // => [{ 'a': 1 }, { 'a': 1 }]
	 *
	 * console.log(objects[0] === objects[1]);
	 * // => true
	 */
	function constant$1(value) {
	  return function() {
	    return value;
	  };
	}

	/**
	 * Attempts to get a successful response from `task` no more than `times` times
	 * before returning an error. If the task is successful, the `callback` will be
	 * passed the result of the successful task. If all attempts fail, the callback
	 * will be passed the error and result (if any) of the final attempt.
	 *
	 * @name retry
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @see [async.retryable]{@link module:ControlFlow.retryable}
	 * @param {Object|number} [opts = {times: 5, interval: 0}| 5] - Can be either an
	 * object with `times` and `interval` or a number.
	 * * `times` - The number of attempts to make before giving up.  The default
	 *   is `5`.
	 * * `interval` - The time to wait between retries, in milliseconds.  The
	 *   default is `0`. The interval may also be specified as a function of the
	 *   retry count (see example).
	 * * `errorFilter` - An optional synchronous function that is invoked on
	 *   erroneous result. If it returns `true` the retry attempts will continue;
	 *   if the function returns `false` the retry flow is aborted with the current
	 *   attempt's error and result being returned to the final callback.
	 *   Invoked with (err).
	 * * If `opts` is a number, the number specifies the number of times to retry,
	 *   with the default interval of `0`.
	 * @param {AsyncFunction} task - An async function to retry.
	 * Invoked with (callback).
	 * @param {Function} [callback] - An optional callback which is called when the
	 * task has succeeded, or after the final failed attempt. It receives the `err`
	 * and `result` arguments of the last attempt at completing the `task`. Invoked
	 * with (err, results).
	 *
	 * @example
	 *
	 * // The `retry` function can be used as a stand-alone control flow by passing
	 * // a callback, as shown below:
	 *
	 * // try calling apiMethod 3 times
	 * async.retry(3, apiMethod, function(err, result) {
	 *     // do something with the result
	 * });
	 *
	 * // try calling apiMethod 3 times, waiting 200 ms between each retry
	 * async.retry({times: 3, interval: 200}, apiMethod, function(err, result) {
	 *     // do something with the result
	 * });
	 *
	 * // try calling apiMethod 10 times with exponential backoff
	 * // (i.e. intervals of 100, 200, 400, 800, 1600, ... milliseconds)
	 * async.retry({
	 *   times: 10,
	 *   interval: function(retryCount) {
	 *     return 50 * Math.pow(2, retryCount);
	 *   }
	 * }, apiMethod, function(err, result) {
	 *     // do something with the result
	 * });
	 *
	 * // try calling apiMethod the default 5 times no delay between each retry
	 * async.retry(apiMethod, function(err, result) {
	 *     // do something with the result
	 * });
	 *
	 * // try calling apiMethod only when error condition satisfies, all other
	 * // errors will abort the retry control flow and return to final callback
	 * async.retry({
	 *   errorFilter: function(err) {
	 *     return err.message === 'Temporary error'; // only retry on a specific error
	 *   }
	 * }, apiMethod, function(err, result) {
	 *     // do something with the result
	 * });
	 *
	 * // to retry individual methods that are not as reliable within other
	 * // control flow functions, use the `retryable` wrapper:
	 * async.auto({
	 *     users: api.getUsers.bind(api),
	 *     payments: async.retryable(3, api.getPayments.bind(api))
	 * }, function(err, results) {
	 *     // do something with the results
	 * });
	 *
	 */
	function retry(opts, task, callback) {
	    var DEFAULT_TIMES = 5;
	    var DEFAULT_INTERVAL = 0;

	    var options = {
	        times: DEFAULT_TIMES,
	        intervalFunc: constant$1(DEFAULT_INTERVAL)
	    };

	    function parseTimes(acc, t) {
	        if (typeof t === 'object') {
	            acc.times = +t.times || DEFAULT_TIMES;

	            acc.intervalFunc = typeof t.interval === 'function' ?
	                t.interval :
	                constant$1(+t.interval || DEFAULT_INTERVAL);

	            acc.errorFilter = t.errorFilter;
	        } else if (typeof t === 'number' || typeof t === 'string') {
	            acc.times = +t || DEFAULT_TIMES;
	        } else {
	            throw new Error("Invalid arguments for async.retry");
	        }
	    }

	    if (arguments.length < 3 && typeof opts === 'function') {
	        callback = task || noop;
	        task = opts;
	    } else {
	        parseTimes(options, opts);
	        callback = callback || noop;
	    }

	    if (typeof task !== 'function') {
	        throw new Error("Invalid arguments for async.retry");
	    }

	    var _task = wrapAsync(task);

	    var attempt = 1;
	    function retryAttempt() {
	        _task(function(err) {
	            if (err && attempt++ < options.times &&
	                (typeof options.errorFilter != 'function' ||
	                    options.errorFilter(err))) {
	                setTimeout(retryAttempt, options.intervalFunc(attempt));
	            } else {
	                callback.apply(null, arguments);
	            }
	        });
	    }

	    retryAttempt();
	}

	/**
	 * A close relative of [`retry`]{@link module:ControlFlow.retry}.  This method
	 * wraps a task and makes it retryable, rather than immediately calling it
	 * with retries.
	 *
	 * @name retryable
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.retry]{@link module:ControlFlow.retry}
	 * @category Control Flow
	 * @param {Object|number} [opts = {times: 5, interval: 0}| 5] - optional
	 * options, exactly the same as from `retry`
	 * @param {AsyncFunction} task - the asynchronous function to wrap.
	 * This function will be passed any arguments passed to the returned wrapper.
	 * Invoked with (...args, callback).
	 * @returns {AsyncFunction} The wrapped function, which when invoked, will
	 * retry on an error, based on the parameters specified in `opts`.
	 * This function will accept the same parameters as `task`.
	 * @example
	 *
	 * async.auto({
	 *     dep1: async.retryable(3, getFromFlakyService),
	 *     process: ["dep1", async.retryable(3, function (results, cb) {
	 *         maybeProcessData(results.dep1, cb);
	 *     })]
	 * }, callback);
	 */
	var retryable = function (opts, task) {
	    if (!task) {
	        task = opts;
	        opts = null;
	    }
	    var _task = wrapAsync(task);
	    return initialParams(function (args, callback) {
	        function taskFn(cb) {
	            _task.apply(null, args.concat(cb));
	        }

	        if (opts) retry(opts, taskFn, callback);
	        else retry(taskFn, callback);

	    });
	};

	/**
	 * Run the functions in the `tasks` collection in series, each one running once
	 * the previous function has completed. If any functions in the series pass an
	 * error to its callback, no more functions are run, and `callback` is
	 * immediately called with the value of the error. Otherwise, `callback`
	 * receives an array of results when `tasks` have completed.
	 *
	 * It is also possible to use an object instead of an array. Each property will
	 * be run as a function, and the results will be passed to the final `callback`
	 * as an object instead of an array. This can be a more readable way of handling
	 *  results from {@link async.series}.
	 *
	 * **Note** that while many implementations preserve the order of object
	 * properties, the [ECMAScript Language Specification](http://www.ecma-international.org/ecma-262/5.1/#sec-8.6)
	 * explicitly states that
	 *
	 * > The mechanics and order of enumerating the properties is not specified.
	 *
	 * So if you rely on the order in which your series of functions are executed,
	 * and want this to work on all platforms, consider using an array.
	 *
	 * @name series
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @param {Array|Iterable|Object} tasks - A collection containing
	 * [async functions]{@link AsyncFunction} to run in series.
	 * Each function can complete with any number of optional `result` values.
	 * @param {Function} [callback] - An optional callback to run once all the
	 * functions have completed. This function gets a results array (or object)
	 * containing all the result arguments passed to the `task` callbacks. Invoked
	 * with (err, result).
	 * @example
	 * async.series([
	 *     function(callback) {
	 *         // do some stuff ...
	 *         callback(null, 'one');
	 *     },
	 *     function(callback) {
	 *         // do some more stuff ...
	 *         callback(null, 'two');
	 *     }
	 * ],
	 * // optional callback
	 * function(err, results) {
	 *     // results is now equal to ['one', 'two']
	 * });
	 *
	 * async.series({
	 *     one: function(callback) {
	 *         setTimeout(function() {
	 *             callback(null, 1);
	 *         }, 200);
	 *     },
	 *     two: function(callback){
	 *         setTimeout(function() {
	 *             callback(null, 2);
	 *         }, 100);
	 *     }
	 * }, function(err, results) {
	 *     // results is now equal to: {one: 1, two: 2}
	 * });
	 */
	function series(tasks, callback) {
	    _parallel(eachOfSeries, tasks, callback);
	}

	/**
	 * Returns `true` if at least one element in the `coll` satisfies an async test.
	 * If any iteratee call returns `true`, the main `callback` is immediately
	 * called.
	 *
	 * @name some
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @alias any
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - An async truth test to apply to each item
	 * in the collections in parallel.
	 * The iteratee should complete with a boolean `result` value.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called as soon as any
	 * iteratee returns `true`, or after all the iteratee functions have finished.
	 * Result will be either `true` or `false` depending on the values of the async
	 * tests. Invoked with (err, result).
	 * @example
	 *
	 * async.some(['file1','file2','file3'], function(filePath, callback) {
	 *     fs.access(filePath, function(err) {
	 *         callback(null, !err)
	 *     });
	 * }, function(err, result) {
	 *     // if result is true then at least one of the files exists
	 * });
	 */
	var some = doParallel(_createTester(Boolean, identity));

	/**
	 * The same as [`some`]{@link module:Collections.some} but runs a maximum of `limit` async operations at a time.
	 *
	 * @name someLimit
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.some]{@link module:Collections.some}
	 * @alias anyLimit
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {AsyncFunction} iteratee - An async truth test to apply to each item
	 * in the collections in parallel.
	 * The iteratee should complete with a boolean `result` value.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called as soon as any
	 * iteratee returns `true`, or after all the iteratee functions have finished.
	 * Result will be either `true` or `false` depending on the values of the async
	 * tests. Invoked with (err, result).
	 */
	var someLimit = doParallelLimit(_createTester(Boolean, identity));

	/**
	 * The same as [`some`]{@link module:Collections.some} but runs only a single async operation at a time.
	 *
	 * @name someSeries
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @see [async.some]{@link module:Collections.some}
	 * @alias anySeries
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - An async truth test to apply to each item
	 * in the collections in series.
	 * The iteratee should complete with a boolean `result` value.
	 * Invoked with (item, callback).
	 * @param {Function} [callback] - A callback which is called as soon as any
	 * iteratee returns `true`, or after all the iteratee functions have finished.
	 * Result will be either `true` or `false` depending on the values of the async
	 * tests. Invoked with (err, result).
	 */
	var someSeries = doLimit(someLimit, 1);

	/**
	 * Sorts a list by the results of running each `coll` value through an async
	 * `iteratee`.
	 *
	 * @name sortBy
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {AsyncFunction} iteratee - An async function to apply to each item in
	 * `coll`.
	 * The iteratee should complete with a value to use as the sort criteria as
	 * its `result`.
	 * Invoked with (item, callback).
	 * @param {Function} callback - A callback which is called after all the
	 * `iteratee` functions have finished, or an error occurs. Results is the items
	 * from the original `coll` sorted by the values returned by the `iteratee`
	 * calls. Invoked with (err, results).
	 * @example
	 *
	 * async.sortBy(['file1','file2','file3'], function(file, callback) {
	 *     fs.stat(file, function(err, stats) {
	 *         callback(err, stats.mtime);
	 *     });
	 * }, function(err, results) {
	 *     // results is now the original array of files sorted by
	 *     // modified date
	 * });
	 *
	 * // By modifying the callback parameter the
	 * // sorting order can be influenced:
	 *
	 * // ascending order
	 * async.sortBy([1,9,3,5], function(x, callback) {
	 *     callback(null, x);
	 * }, function(err,result) {
	 *     // result callback
	 * });
	 *
	 * // descending order
	 * async.sortBy([1,9,3,5], function(x, callback) {
	 *     callback(null, x*-1);    //<- x*-1 instead of x, turns the order around
	 * }, function(err,result) {
	 *     // result callback
	 * });
	 */
	function sortBy (coll, iteratee, callback) {
	    var _iteratee = wrapAsync(iteratee);
	    map(coll, function (x, callback) {
	        _iteratee(x, function (err, criteria) {
	            if (err) return callback(err);
	            callback(null, {value: x, criteria: criteria});
	        });
	    }, function (err, results) {
	        if (err) return callback(err);
	        callback(null, arrayMap(results.sort(comparator), baseProperty('value')));
	    });

	    function comparator(left, right) {
	        var a = left.criteria, b = right.criteria;
	        return a < b ? -1 : a > b ? 1 : 0;
	    }
	}

	/**
	 * Sets a time limit on an asynchronous function. If the function does not call
	 * its callback within the specified milliseconds, it will be called with a
	 * timeout error. The code property for the error object will be `'ETIMEDOUT'`.
	 *
	 * @name timeout
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @category Util
	 * @param {AsyncFunction} asyncFn - The async function to limit in time.
	 * @param {number} milliseconds - The specified time limit.
	 * @param {*} [info] - Any variable you want attached (`string`, `object`, etc)
	 * to timeout Error for more information..
	 * @returns {AsyncFunction} Returns a wrapped function that can be used with any
	 * of the control flow functions.
	 * Invoke this function with the same parameters as you would `asyncFunc`.
	 * @example
	 *
	 * function myFunction(foo, callback) {
	 *     doAsyncTask(foo, function(err, data) {
	 *         // handle errors
	 *         if (err) return callback(err);
	 *
	 *         // do some stuff ...
	 *
	 *         // return processed data
	 *         return callback(null, data);
	 *     });
	 * }
	 *
	 * var wrapped = async.timeout(myFunction, 1000);
	 *
	 * // call `wrapped` as you would `myFunction`
	 * wrapped({ bar: 'bar' }, function(err, data) {
	 *     // if `myFunction` takes < 1000 ms to execute, `err`
	 *     // and `data` will have their expected values
	 *
	 *     // else `err` will be an Error with the code 'ETIMEDOUT'
	 * });
	 */
	function timeout(asyncFn, milliseconds, info) {
	    var fn = wrapAsync(asyncFn);

	    return initialParams(function (args, callback) {
	        var timedOut = false;
	        var timer;

	        function timeoutCallback() {
	            var name = asyncFn.name || 'anonymous';
	            var error  = new Error('Callback function "' + name + '" timed out.');
	            error.code = 'ETIMEDOUT';
	            if (info) {
	                error.info = info;
	            }
	            timedOut = true;
	            callback(error);
	        }

	        args.push(function () {
	            if (!timedOut) {
	                callback.apply(null, arguments);
	                clearTimeout(timer);
	            }
	        });

	        // setup timer and call original function
	        timer = setTimeout(timeoutCallback, milliseconds);
	        fn.apply(null, args);
	    });
	}

	/* Built-in method references for those with the same name as other `lodash` methods. */
	var nativeCeil = Math.ceil;
	var nativeMax = Math.max;

	/**
	 * The base implementation of `_.range` and `_.rangeRight` which doesn't
	 * coerce arguments.
	 *
	 * @private
	 * @param {number} start The start of the range.
	 * @param {number} end The end of the range.
	 * @param {number} step The value to increment or decrement by.
	 * @param {boolean} [fromRight] Specify iterating from right to left.
	 * @returns {Array} Returns the range of numbers.
	 */
	function baseRange(start, end, step, fromRight) {
	  var index = -1,
	      length = nativeMax(nativeCeil((end - start) / (step)), 0),
	      result = Array(length);

	  while (length--) {
	    result[++index] = start;
	    start += step;
	  }
	  return result;
	}

	/**
	 * The same as [times]{@link module:ControlFlow.times} but runs a maximum of `limit` async operations at a
	 * time.
	 *
	 * @name timesLimit
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.times]{@link module:ControlFlow.times}
	 * @category Control Flow
	 * @param {number} count - The number of times to run the function.
	 * @param {number} limit - The maximum number of async operations at a time.
	 * @param {AsyncFunction} iteratee - The async function to call `n` times.
	 * Invoked with the iteration index and a callback: (n, next).
	 * @param {Function} callback - see [async.map]{@link module:Collections.map}.
	 */
	function timeLimit(count, limit, iteratee, callback) {
	    var _iteratee = wrapAsync(iteratee);
	    mapLimit(baseRange(0, count, 1), limit, _iteratee, callback);
	}

	/**
	 * Calls the `iteratee` function `n` times, and accumulates results in the same
	 * manner you would use with [map]{@link module:Collections.map}.
	 *
	 * @name times
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.map]{@link module:Collections.map}
	 * @category Control Flow
	 * @param {number} n - The number of times to run the function.
	 * @param {AsyncFunction} iteratee - The async function to call `n` times.
	 * Invoked with the iteration index and a callback: (n, next).
	 * @param {Function} callback - see {@link module:Collections.map}.
	 * @example
	 *
	 * // Pretend this is some complicated async factory
	 * var createUser = function(id, callback) {
	 *     callback(null, {
	 *         id: 'user' + id
	 *     });
	 * };
	 *
	 * // generate 5 users
	 * async.times(5, function(n, next) {
	 *     createUser(n, function(err, user) {
	 *         next(err, user);
	 *     });
	 * }, function(err, users) {
	 *     // we should now have 5 users
	 * });
	 */
	var times = doLimit(timeLimit, Infinity);

	/**
	 * The same as [times]{@link module:ControlFlow.times} but runs only a single async operation at a time.
	 *
	 * @name timesSeries
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.times]{@link module:ControlFlow.times}
	 * @category Control Flow
	 * @param {number} n - The number of times to run the function.
	 * @param {AsyncFunction} iteratee - The async function to call `n` times.
	 * Invoked with the iteration index and a callback: (n, next).
	 * @param {Function} callback - see {@link module:Collections.map}.
	 */
	var timesSeries = doLimit(timeLimit, 1);

	/**
	 * A relative of `reduce`.  Takes an Object or Array, and iterates over each
	 * element in series, each step potentially mutating an `accumulator` value.
	 * The type of the accumulator defaults to the type of collection passed in.
	 *
	 * @name transform
	 * @static
	 * @memberOf module:Collections
	 * @method
	 * @category Collection
	 * @param {Array|Iterable|Object} coll - A collection to iterate over.
	 * @param {*} [accumulator] - The initial state of the transform.  If omitted,
	 * it will default to an empty Object or Array, depending on the type of `coll`
	 * @param {AsyncFunction} iteratee - A function applied to each item in the
	 * collection that potentially modifies the accumulator.
	 * Invoked with (accumulator, item, key, callback).
	 * @param {Function} [callback] - A callback which is called after all the
	 * `iteratee` functions have finished. Result is the transformed accumulator.
	 * Invoked with (err, result).
	 * @example
	 *
	 * async.transform([1,2,3], function(acc, item, index, callback) {
	 *     // pointless async:
	 *     process.nextTick(function() {
	 *         acc.push(item * 2)
	 *         callback(null)
	 *     });
	 * }, function(err, result) {
	 *     // result is now equal to [2, 4, 6]
	 * });
	 *
	 * @example
	 *
	 * async.transform({a: 1, b: 2, c: 3}, function (obj, val, key, callback) {
	 *     setImmediate(function () {
	 *         obj[key] = val * 2;
	 *         callback();
	 *     })
	 * }, function (err, result) {
	 *     // result is equal to {a: 2, b: 4, c: 6}
	 * })
	 */
	function transform (coll, accumulator, iteratee, callback) {
	    if (arguments.length <= 3) {
	        callback = iteratee;
	        iteratee = accumulator;
	        accumulator = isArray(coll) ? [] : {};
	    }
	    callback = once(callback || noop);
	    var _iteratee = wrapAsync(iteratee);

	    eachOf(coll, function(v, k, cb) {
	        _iteratee(accumulator, v, k, cb);
	    }, function(err) {
	        callback(err, accumulator);
	    });
	}

	/**
	 * It runs each task in series but stops whenever any of the functions were
	 * successful. If one of the tasks were successful, the `callback` will be
	 * passed the result of the successful task. If all tasks fail, the callback
	 * will be passed the error and result (if any) of the final attempt.
	 *
	 * @name tryEach
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @param {Array|Iterable|Object} tasks - A collection containing functions to
	 * run, each function is passed a `callback(err, result)` it must call on
	 * completion with an error `err` (which can be `null`) and an optional `result`
	 * value.
	 * @param {Function} [callback] - An optional callback which is called when one
	 * of the tasks has succeeded, or all have failed. It receives the `err` and
	 * `result` arguments of the last attempt at completing the `task`. Invoked with
	 * (err, results).
	 * @example
	 * async.tryEach([
	 *     function getDataFromFirstWebsite(callback) {
	 *         // Try getting the data from the first website
	 *         callback(err, data);
	 *     },
	 *     function getDataFromSecondWebsite(callback) {
	 *         // First website failed,
	 *         // Try getting the data from the backup website
	 *         callback(err, data);
	 *     }
	 * ],
	 * // optional callback
	 * function(err, results) {
	 *     Now do something with the data.
	 * });
	 *
	 */
	function tryEach(tasks, callback) {
	    var error = null;
	    var result;
	    callback = callback || noop;
	    eachSeries(tasks, function(task, callback) {
	        wrapAsync(task)(function (err, res/*, ...args*/) {
	            if (arguments.length > 2) {
	                result = slice(arguments, 1);
	            } else {
	                result = res;
	            }
	            error = err;
	            callback(!err);
	        });
	    }, function () {
	        callback(error, result);
	    });
	}

	/**
	 * Undoes a [memoize]{@link module:Utils.memoize}d function, reverting it to the original,
	 * unmemoized form. Handy for testing.
	 *
	 * @name unmemoize
	 * @static
	 * @memberOf module:Utils
	 * @method
	 * @see [async.memoize]{@link module:Utils.memoize}
	 * @category Util
	 * @param {AsyncFunction} fn - the memoized function
	 * @returns {AsyncFunction} a function that calls the original unmemoized function
	 */
	function unmemoize(fn) {
	    return function () {
	        return (fn.unmemoized || fn).apply(null, arguments);
	    };
	}

	/**
	 * Repeatedly call `iteratee`, while `test` returns `true`. Calls `callback` when
	 * stopped, or an error occurs.
	 *
	 * @name whilst
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @param {Function} test - synchronous truth test to perform before each
	 * execution of `iteratee`. Invoked with ().
	 * @param {AsyncFunction} iteratee - An async function which is called each time
	 * `test` passes. Invoked with (callback).
	 * @param {Function} [callback] - A callback which is called after the test
	 * function has failed and repeated execution of `iteratee` has stopped. `callback`
	 * will be passed an error and any arguments passed to the final `iteratee`'s
	 * callback. Invoked with (err, [results]);
	 * @returns undefined
	 * @example
	 *
	 * var count = 0;
	 * async.whilst(
	 *     function() { return count < 5; },
	 *     function(callback) {
	 *         count++;
	 *         setTimeout(function() {
	 *             callback(null, count);
	 *         }, 1000);
	 *     },
	 *     function (err, n) {
	 *         // 5 seconds have passed, n = 5
	 *     }
	 * );
	 */
	function whilst(test, iteratee, callback) {
	    callback = onlyOnce(callback || noop);
	    var _iteratee = wrapAsync(iteratee);
	    if (!test()) return callback(null);
	    var next = function(err/*, ...args*/) {
	        if (err) return callback(err);
	        if (test()) return _iteratee(next);
	        var args = slice(arguments, 1);
	        callback.apply(null, [null].concat(args));
	    };
	    _iteratee(next);
	}

	/**
	 * Repeatedly call `iteratee` until `test` returns `true`. Calls `callback` when
	 * stopped, or an error occurs. `callback` will be passed an error and any
	 * arguments passed to the final `iteratee`'s callback.
	 *
	 * The inverse of [whilst]{@link module:ControlFlow.whilst}.
	 *
	 * @name until
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @see [async.whilst]{@link module:ControlFlow.whilst}
	 * @category Control Flow
	 * @param {Function} test - synchronous truth test to perform before each
	 * execution of `iteratee`. Invoked with ().
	 * @param {AsyncFunction} iteratee - An async function which is called each time
	 * `test` fails. Invoked with (callback).
	 * @param {Function} [callback] - A callback which is called after the test
	 * function has passed and repeated execution of `iteratee` has stopped. `callback`
	 * will be passed an error and any arguments passed to the final `iteratee`'s
	 * callback. Invoked with (err, [results]);
	 */
	function until(test, iteratee, callback) {
	    whilst(function() {
	        return !test.apply(this, arguments);
	    }, iteratee, callback);
	}

	/**
	 * Runs the `tasks` array of functions in series, each passing their results to
	 * the next in the array. However, if any of the `tasks` pass an error to their
	 * own callback, the next function is not executed, and the main `callback` is
	 * immediately called with the error.
	 *
	 * @name waterfall
	 * @static
	 * @memberOf module:ControlFlow
	 * @method
	 * @category Control Flow
	 * @param {Array} tasks - An array of [async functions]{@link AsyncFunction}
	 * to run.
	 * Each function should complete with any number of `result` values.
	 * The `result` values will be passed as arguments, in order, to the next task.
	 * @param {Function} [callback] - An optional callback to run once all the
	 * functions have completed. This will be passed the results of the last task's
	 * callback. Invoked with (err, [results]).
	 * @returns undefined
	 * @example
	 *
	 * async.waterfall([
	 *     function(callback) {
	 *         callback(null, 'one', 'two');
	 *     },
	 *     function(arg1, arg2, callback) {
	 *         // arg1 now equals 'one' and arg2 now equals 'two'
	 *         callback(null, 'three');
	 *     },
	 *     function(arg1, callback) {
	 *         // arg1 now equals 'three'
	 *         callback(null, 'done');
	 *     }
	 * ], function (err, result) {
	 *     // result now equals 'done'
	 * });
	 *
	 * // Or, with named functions:
	 * async.waterfall([
	 *     myFirstFunction,
	 *     mySecondFunction,
	 *     myLastFunction,
	 * ], function (err, result) {
	 *     // result now equals 'done'
	 * });
	 * function myFirstFunction(callback) {
	 *     callback(null, 'one', 'two');
	 * }
	 * function mySecondFunction(arg1, arg2, callback) {
	 *     // arg1 now equals 'one' and arg2 now equals 'two'
	 *     callback(null, 'three');
	 * }
	 * function myLastFunction(arg1, callback) {
	 *     // arg1 now equals 'three'
	 *     callback(null, 'done');
	 * }
	 */
	var waterfall = function(tasks, callback) {
	    callback = once(callback || noop);
	    if (!isArray(tasks)) return callback(new Error('First argument to waterfall must be an array of functions'));
	    if (!tasks.length) return callback();
	    var taskIndex = 0;

	    function nextTask(args) {
	        var task = wrapAsync(tasks[taskIndex++]);
	        args.push(onlyOnce(next));
	        task.apply(null, args);
	    }

	    function next(err/*, ...args*/) {
	        if (err || taskIndex === tasks.length) {
	            return callback.apply(null, arguments);
	        }
	        nextTask(slice(arguments, 1));
	    }

	    nextTask([]);
	};

	/**
	 * An "async function" in the context of Async is an asynchronous function with
	 * a variable number of parameters, with the final parameter being a callback.
	 * (`function (arg1, arg2, ..., callback) {}`)
	 * The final callback is of the form `callback(err, results...)`, which must be
	 * called once the function is completed.  The callback should be called with a
	 * Error as its first argument to signal that an error occurred.
	 * Otherwise, if no error occurred, it should be called with `null` as the first
	 * argument, and any additional `result` arguments that may apply, to signal
	 * successful completion.
	 * The callback must be called exactly once, ideally on a later tick of the
	 * JavaScript event loop.
	 *
	 * This type of function is also referred to as a "Node-style async function",
	 * or a "continuation passing-style function" (CPS). Most of the methods of this
	 * library are themselves CPS/Node-style async functions, or functions that
	 * return CPS/Node-style async functions.
	 *
	 * Wherever we accept a Node-style async function, we also directly accept an
	 * [ES2017 `async` function]{@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/async_function}.
	 * In this case, the `async` function will not be passed a final callback
	 * argument, and any thrown error will be used as the `err` argument of the
	 * implicit callback, and the return value will be used as the `result` value.
	 * (i.e. a `rejected` of the returned Promise becomes the `err` callback
	 * argument, and a `resolved` value becomes the `result`.)
	 *
	 * Note, due to JavaScript limitations, we can only detect native `async`
	 * functions and not transpilied implementations.
	 * Your environment must have `async`/`await` support for this to work.
	 * (e.g. Node > v7.6, or a recent version of a modern browser).
	 * If you are using `async` functions through a transpiler (e.g. Babel), you
	 * must still wrap the function with [asyncify]{@link module:Utils.asyncify},
	 * because the `async function` will be compiled to an ordinary function that
	 * returns a promise.
	 *
	 * @typedef {Function} AsyncFunction
	 * @static
	 */

	/**
	 * Async is a utility module which provides straight-forward, powerful functions
	 * for working with asynchronous JavaScript. Although originally designed for
	 * use with [Node.js](http://nodejs.org) and installable via
	 * `npm install --save async`, it can also be used directly in the browser.
	 * @module async
	 * @see AsyncFunction
	 */


	/**
	 * A collection of `async` functions for manipulating collections, such as
	 * arrays and objects.
	 * @module Collections
	 */

	/**
	 * A collection of `async` functions for controlling the flow through a script.
	 * @module ControlFlow
	 */

	/**
	 * A collection of `async` utility functions.
	 * @module Utils
	 */

	var index = {
	    apply: apply,
	    applyEach: applyEach,
	    applyEachSeries: applyEachSeries,
	    asyncify: asyncify,
	    auto: auto,
	    autoInject: autoInject,
	    cargo: cargo,
	    compose: compose,
	    concat: concat,
	    concatLimit: concatLimit,
	    concatSeries: concatSeries,
	    constant: constant,
	    detect: detect,
	    detectLimit: detectLimit,
	    detectSeries: detectSeries,
	    dir: dir,
	    doDuring: doDuring,
	    doUntil: doUntil,
	    doWhilst: doWhilst,
	    during: during,
	    each: eachLimit,
	    eachLimit: eachLimit$1,
	    eachOf: eachOf,
	    eachOfLimit: eachOfLimit,
	    eachOfSeries: eachOfSeries,
	    eachSeries: eachSeries,
	    ensureAsync: ensureAsync,
	    every: every,
	    everyLimit: everyLimit,
	    everySeries: everySeries,
	    filter: filter,
	    filterLimit: filterLimit,
	    filterSeries: filterSeries,
	    forever: forever,
	    groupBy: groupBy,
	    groupByLimit: groupByLimit,
	    groupBySeries: groupBySeries,
	    log: log,
	    map: map,
	    mapLimit: mapLimit,
	    mapSeries: mapSeries,
	    mapValues: mapValues,
	    mapValuesLimit: mapValuesLimit,
	    mapValuesSeries: mapValuesSeries,
	    memoize: memoize,
	    nextTick: nextTick,
	    parallel: parallelLimit,
	    parallelLimit: parallelLimit$1,
	    priorityQueue: priorityQueue,
	    queue: queue$1,
	    race: race,
	    reduce: reduce,
	    reduceRight: reduceRight,
	    reflect: reflect,
	    reflectAll: reflectAll,
	    reject: reject,
	    rejectLimit: rejectLimit,
	    rejectSeries: rejectSeries,
	    retry: retry,
	    retryable: retryable,
	    seq: seq,
	    series: series,
	    setImmediate: setImmediate$1,
	    some: some,
	    someLimit: someLimit,
	    someSeries: someSeries,
	    sortBy: sortBy,
	    timeout: timeout,
	    times: times,
	    timesLimit: timeLimit,
	    timesSeries: timesSeries,
	    transform: transform,
	    tryEach: tryEach,
	    unmemoize: unmemoize,
	    until: until,
	    waterfall: waterfall,
	    whilst: whilst,

	    // aliases
	    all: every,
	    allLimit: everyLimit,
	    allSeries: everySeries,
	    any: some,
	    anyLimit: someLimit,
	    anySeries: someSeries,
	    find: detect,
	    findLimit: detectLimit,
	    findSeries: detectSeries,
	    forEach: eachLimit,
	    forEachSeries: eachSeries,
	    forEachLimit: eachLimit$1,
	    forEachOf: eachOf,
	    forEachOfSeries: eachOfSeries,
	    forEachOfLimit: eachOfLimit,
	    inject: reduce,
	    foldl: reduce,
	    foldr: reduceRight,
	    select: filter,
	    selectLimit: filterLimit,
	    selectSeries: filterSeries,
	    wrapSync: asyncify
	};

	exports['default'] = index;
	exports.apply = apply;
	exports.applyEach = applyEach;
	exports.applyEachSeries = applyEachSeries;
	exports.asyncify = asyncify;
	exports.auto = auto;
	exports.autoInject = autoInject;
	exports.cargo = cargo;
	exports.compose = compose;
	exports.concat = concat;
	exports.concatLimit = concatLimit;
	exports.concatSeries = concatSeries;
	exports.constant = constant;
	exports.detect = detect;
	exports.detectLimit = detectLimit;
	exports.detectSeries = detectSeries;
	exports.dir = dir;
	exports.doDuring = doDuring;
	exports.doUntil = doUntil;
	exports.doWhilst = doWhilst;
	exports.during = during;
	exports.each = eachLimit;
	exports.eachLimit = eachLimit$1;
	exports.eachOf = eachOf;
	exports.eachOfLimit = eachOfLimit;
	exports.eachOfSeries = eachOfSeries;
	exports.eachSeries = eachSeries;
	exports.ensureAsync = ensureAsync;
	exports.every = every;
	exports.everyLimit = everyLimit;
	exports.everySeries = everySeries;
	exports.filter = filter;
	exports.filterLimit = filterLimit;
	exports.filterSeries = filterSeries;
	exports.forever = forever;
	exports.groupBy = groupBy;
	exports.groupByLimit = groupByLimit;
	exports.groupBySeries = groupBySeries;
	exports.log = log;
	exports.map = map;
	exports.mapLimit = mapLimit;
	exports.mapSeries = mapSeries;
	exports.mapValues = mapValues;
	exports.mapValuesLimit = mapValuesLimit;
	exports.mapValuesSeries = mapValuesSeries;
	exports.memoize = memoize;
	exports.nextTick = nextTick;
	exports.parallel = parallelLimit;
	exports.parallelLimit = parallelLimit$1;
	exports.priorityQueue = priorityQueue;
	exports.queue = queue$1;
	exports.race = race;
	exports.reduce = reduce;
	exports.reduceRight = reduceRight;
	exports.reflect = reflect;
	exports.reflectAll = reflectAll;
	exports.reject = reject;
	exports.rejectLimit = rejectLimit;
	exports.rejectSeries = rejectSeries;
	exports.retry = retry;
	exports.retryable = retryable;
	exports.seq = seq;
	exports.series = series;
	exports.setImmediate = setImmediate$1;
	exports.some = some;
	exports.someLimit = someLimit;
	exports.someSeries = someSeries;
	exports.sortBy = sortBy;
	exports.timeout = timeout;
	exports.times = times;
	exports.timesLimit = timeLimit;
	exports.timesSeries = timesSeries;
	exports.transform = transform;
	exports.tryEach = tryEach;
	exports.unmemoize = unmemoize;
	exports.until = until;
	exports.waterfall = waterfall;
	exports.whilst = whilst;
	exports.all = every;
	exports.allLimit = everyLimit;
	exports.allSeries = everySeries;
	exports.any = some;
	exports.anyLimit = someLimit;
	exports.anySeries = someSeries;
	exports.find = detect;
	exports.findLimit = detectLimit;
	exports.findSeries = detectSeries;
	exports.forEach = eachLimit;
	exports.forEachSeries = eachSeries;
	exports.forEachLimit = eachLimit$1;
	exports.forEachOf = eachOf;
	exports.forEachOfSeries = eachOfSeries;
	exports.forEachOfLimit = eachOfLimit;
	exports.inject = reduce;
	exports.foldl = reduce;
	exports.foldr = reduceRight;
	exports.select = filter;
	exports.selectLimit = filterLimit;
	exports.selectSeries = filterSeries;
	exports.wrapSync = asyncify;

	Object.defineProperty(exports, '__esModule', { value: true });

	}))); 
} (async$1, async$1.exports));

var asyncExports = async$1.exports;

var hat$2 = {exports: {}};

var hat$1 = hat$2.exports = function (bits, base) {
    if (!base) base = 16;
    if (bits === undefined) bits = 128;
    if (bits <= 0) return '0';
    
    var digits = Math.log(Math.pow(2, bits)) / Math.log(base);
    for (var i = 2; digits === Infinity; i *= 2) {
        digits = Math.log(Math.pow(2, bits / i)) / Math.log(base) * i;
    }
    
    var rem = digits - Math.floor(digits);
    
    var res = '';
    
    for (var i = 0; i < Math.floor(digits); i++) {
        var x = Math.floor(Math.random() * base).toString(base);
        res = x + res;
    }
    
    if (rem) {
        var b = Math.pow(base, rem);
        var x = Math.floor(Math.random() * b).toString(base);
        res = x + res;
    }
    
    var parsed = parseInt(res, base);
    if (parsed !== Infinity && parsed >= Math.pow(2, bits)) {
        return hat$1(bits, base)
    }
    else return res;
};

hat$1.rack = function (bits, base, expandBy) {
    var fn = function (data) {
        var iters = 0;
        do {
            if (iters ++ > 10) {
                if (expandBy) bits += expandBy;
                else throw new Error('too many ID collisions, use more bits')
            }
            
            var id = hat$1(bits, base);
        } while (Object.hasOwnProperty.call(hats, id));
        
        hats[id] = data;
        return id;
    };
    var hats = fn.hats = {};
    
    fn.get = function (id) {
        return fn.hats[id];
    };
    
    fn.set = function (id, value) {
        fn.hats[id] = value;
        return fn;
    };
    
    fn.bits = bits || 128;
    fn.base = base || 16;
    return fn;
};

var hatExports = hat$2.exports;

var emitter$2 = emitter$6;
var LocalPresence$1 = localPresence;
var RemotePresence$1 = remotePresence;
var util$5 = util$a;
var async = asyncExports;
var hat = hatExports;
var ACTIONS$3 = messageActions.ACTIONS;

var presence = Presence$2;
function Presence$2(connection, channel) {
  emitter$2.EventEmitter.call(this);

  if (!channel || typeof channel !== 'string') {
    throw new Error('Presence channel must be provided');
  }

  this.connection = connection;
  this.channel = channel;

  this.wantSubscribe = false;
  this.subscribed = false;
  this.remotePresences = {};
  this.localPresences = {};

  this._remotePresenceInstances = {};
  this._subscriptionCallbacksBySeq = {};
  this._wantsDestroy = false;
}
emitter$2.mixin(Presence$2);

Presence$2.prototype.subscribe = function(callback) {
  this._sendSubscriptionAction(true, callback);
};

Presence$2.prototype.unsubscribe = function(callback) {
  this._sendSubscriptionAction(false, callback);
};

Presence$2.prototype.create = function(id) {
  if (this._wantsDestroy) {
    throw new Error('Presence is being destroyed');
  }
  id = id || hat();
  var localPresence = this._createLocalPresence(id);
  this.localPresences[id] = localPresence;
  return localPresence;
};

Presence$2.prototype.destroy = function(callback) {
  this._wantsDestroy = true;
  var presence = this;
  // Store these at the time of destruction: any LocalPresence on this
  // instance at this time will be destroyed, but if the destroy is
  // cancelled, any future LocalPresence objects will be kept.
  // See: https://github.com/share/sharedb/pull/579
  var localIds = Object.keys(presence.localPresences);
  this.unsubscribe(function(error) {
    if (error) return presence._callbackOrEmit(error, callback);
    var remoteIds = Object.keys(presence._remotePresenceInstances);
    async.parallel(
      [
        function(next) {
          async.each(localIds, function(presenceId, next) {
            presence.localPresences[presenceId].destroy(next);
          }, next);
        },
        function(next) {
          // We don't bother stashing the RemotePresence instances because
          // they're not really bound to our local state: if we want to
          // destroy, we destroy them all, but if we cancel the destroy,
          // we'll want to keep them all
          if (!presence._wantsDestroy) return next();
          async.each(remoteIds, function(presenceId, next) {
            presence._remotePresenceInstances[presenceId].destroy(next);
          }, next);
        }
      ],
      function(error) {
        if (presence._wantsDestroy) delete presence.connection._presences[presence.channel];
        presence._callbackOrEmit(error, callback);
      }
    );
  });
};

Presence$2.prototype._sendSubscriptionAction = function(wantSubscribe, callback) {
  this.wantSubscribe = !!wantSubscribe;
  var action = this.wantSubscribe ? ACTIONS$3.presenceSubscribe : ACTIONS$3.presenceUnsubscribe;
  var seq = this.connection._presenceSeq++;
  this._subscriptionCallbacksBySeq[seq] = callback;
  if (this.connection.canSend) {
    this.connection._sendPresenceAction(action, seq, this);
  }
};

Presence$2.prototype._handleSubscribe = function(error, seq) {
  if (this.wantSubscribe) this.subscribed = true;
  var callback = this._subscriptionCallback(seq);
  this._callbackOrEmit(error, callback);
};

Presence$2.prototype._handleUnsubscribe = function(error, seq) {
  this.subscribed = false;
  var callback = this._subscriptionCallback(seq);
  this._callbackOrEmit(error, callback);
};

Presence$2.prototype._receiveUpdate = function(error, message) {
  var localPresence = util$5.dig(this.localPresences, message.id);
  if (localPresence) return localPresence._ack(error, message.pv);

  if (error) return this.emit('error', error);
  var presence = this;
  var remotePresence = util$5.digOrCreate(this._remotePresenceInstances, message.id, function() {
    return presence._createRemotePresence(message.id);
  });

  remotePresence.receiveUpdate(message);
};

Presence$2.prototype._updateRemotePresence = function(remotePresence) {
  this.remotePresences[remotePresence.presenceId] = remotePresence.value;
  if (remotePresence.value === null) this._removeRemotePresence(remotePresence.presenceId);
  this.emit('receive', remotePresence.presenceId, remotePresence.value);
};

Presence$2.prototype._broadcastAllLocalPresence = function(error) {
  if (error) return this.emit('error', error);
  for (var id in this.localPresences) {
    var localPresence = this.localPresences[id];
    if (localPresence.value !== null) localPresence.send();
  }
};

Presence$2.prototype._removeRemotePresence = function(id) {
  this._remotePresenceInstances[id].destroy();
  delete this._remotePresenceInstances[id];
  delete this.remotePresences[id];
};

Presence$2.prototype._onConnectionStateChanged = function() {
  if (!this.connection.canSend) return;
  this._resubscribe();
  for (var id in this.localPresences) {
    this.localPresences[id]._sendPending();
  }
};

Presence$2.prototype._resubscribe = function() {
  var callbacks = [];
  for (var seq in this._subscriptionCallbacksBySeq) {
    var callback = this._subscriptionCallback(seq);
    callbacks.push(callback);
  }

  if (!this.wantSubscribe) return this._callEachOrEmit(callbacks);

  var presence = this;
  this.subscribe(function(error) {
    presence._callEachOrEmit(callbacks, error);
  });
};

Presence$2.prototype._subscriptionCallback = function(seq) {
  var callback = this._subscriptionCallbacksBySeq[seq];
  delete this._subscriptionCallbacksBySeq[seq];
  return callback;
};

Presence$2.prototype._callbackOrEmit = function(error, callback) {
  if (callback) return util$5.nextTick(callback, error);
  if (error) this.emit('error', error);
};

Presence$2.prototype._createLocalPresence = function(id) {
  return new LocalPresence$1(this, id);
};

Presence$2.prototype._createRemotePresence = function(id) {
  return new RemotePresence$1(this, id);
};

Presence$2.prototype._callEachOrEmit = function(callbacks, error) {
  var called = util$5.callEach(callbacks, error);
  if (!called && error) this.emit('error', error);
};

var LocalPresence = localPresence;
var ShareDBError$1 = error;
var util$4 = util$a;
var ERROR_CODE$1 = ShareDBError$1.CODES;

var localDocPresence = LocalDocPresence$1;
function LocalDocPresence$1(presence, presenceId) {
  LocalPresence.call(this, presence, presenceId);

  this.collection = this.presence.collection;
  this.id = this.presence.id;

  this._doc = this.connection.get(this.collection, this.id);
  this._emitter = this.connection._docPresenceEmitter;
  this._isSending = false;
  this._docDataVersionByPresenceVersion = {};

  this._opHandler = this._transformAgainstOp.bind(this);
  this._createOrDelHandler = this._handleCreateOrDel.bind(this);
  this._loadHandler = this._handleLoad.bind(this);
  this._destroyHandler = this.destroy.bind(this);
  this._registerWithDoc();
}

LocalDocPresence$1.prototype = Object.create(LocalPresence.prototype);

LocalDocPresence$1.prototype.submit = function(value, callback) {
  if (!this._doc.type) {
    // If the Doc hasn't been created, we already assume all presence to
    // be null. Let's early return, instead of error since this is a harmless
    // no-op
    if (value === null) return this._callbackOrEmit(null, callback);
    var error = {
      code: ERROR_CODE$1.ERR_DOC_DOES_NOT_EXIST,
      message: 'Cannot submit presence. Document has not been created'
    };
    return this._callbackOrEmit(error, callback);
  }
  // Record the current data state version to check if we need to transform
  // the presence later
  this._docDataVersionByPresenceVersion[this.presenceVersion] = this._doc._dataStateVersion;
  LocalPresence.prototype.submit.call(this, value, callback);
};

LocalDocPresence$1.prototype.destroy = function(callback) {
  this._emitter.removeEventListener(this._doc, 'op', this._opHandler);
  this._emitter.removeEventListener(this._doc, 'create', this._createOrDelHandler);
  this._emitter.removeEventListener(this._doc, 'del', this._createOrDelHandler);
  this._emitter.removeEventListener(this._doc, 'load', this._loadHandler);
  this._emitter.removeEventListener(this._doc, 'destroy', this._destroyHandler);

  LocalPresence.prototype.destroy.call(this, callback);
};

LocalDocPresence$1.prototype._sendPending = function() {
  if (this._isSending) return;
  this._isSending = true;
  var presence = this;
  this._doc.whenNothingPending(function() {
    presence._isSending = false;
    if (!presence.connection.canSend) return;

    presence._pendingMessages.forEach(function(message) {
      message.t = presence._doc.type.uri;
      message.v = presence._doc.version;
      presence.connection.send(message);
    });

    presence._pendingMessages = [];
    presence._docDataVersionByPresenceVersion = {};
  });
};

LocalDocPresence$1.prototype._registerWithDoc = function() {
  this._emitter.addEventListener(this._doc, 'op', this._opHandler);
  this._emitter.addEventListener(this._doc, 'create', this._createOrDelHandler);
  this._emitter.addEventListener(this._doc, 'del', this._createOrDelHandler);
  this._emitter.addEventListener(this._doc, 'load', this._loadHandler);
  this._emitter.addEventListener(this._doc, 'destroy', this._destroyHandler);
};

LocalDocPresence$1.prototype._transformAgainstOp = function(op, source) {
  var presence = this;
  var docDataVersion = this._doc._dataStateVersion;

  this._pendingMessages.forEach(function(message) {
    // Check if the presence needs transforming against the op - this is to check against
    // edge cases where presence is submitted from an 'op' event
    var messageDocDataVersion = presence._docDataVersionByPresenceVersion[message.pv];
    if (messageDocDataVersion >= docDataVersion) return;
    try {
      message.p = presence._transformPresence(message.p, op, source);
      // Ensure the presence's data version is kept consistent to deal with "deep" op
      // submissions
      presence._docDataVersionByPresenceVersion[message.pv] = docDataVersion;
    } catch (error) {
      var callback = presence._getCallback(message.pv);
      presence._callbackOrEmit(error, callback);
    }
  });

  try {
    this.value = this._transformPresence(this.value, op, source);
  } catch (error) {
    this.emit('error', error);
  }
};

LocalDocPresence$1.prototype._handleCreateOrDel = function() {
  this._pendingMessages.forEach(function(message) {
    message.p = null;
  });

  this.value = null;
};

LocalDocPresence$1.prototype._handleLoad = function() {
  this.value = null;
  this._pendingMessages = [];
  this._docDataVersionByPresenceVersion = {};
};

LocalDocPresence$1.prototype._message = function() {
  var message = LocalPresence.prototype._message.call(this);
  message.c = this.collection,
  message.d = this.id,
  message.v = null;
  message.t = null;
  return message;
};

LocalDocPresence$1.prototype._transformPresence = function(value, op, source) {
  var type = this._doc.type;
  if (!util$4.supportsPresence(type)) {
    throw new ShareDBError$1(
      ERROR_CODE$1.ERR_TYPE_DOES_NOT_SUPPORT_PRESENCE,
      'Type does not support presence: ' + type.name
    );
  }
  return type.transformPresence(value, op, source);
};

var ot$1 = {};

(function (exports) {
	// This contains the master OT functions for the database. They look like
	// ot-types style operational transform functions, but they're a bit different.
	// These functions understand versions and can deal with out of bound create &
	// delete operations.

	var types = types$3;
	var ShareDBError = error;
	var util = util$a;

	var ERROR_CODE = ShareDBError.CODES;

	// Returns an error string on failure. Rockin' it C style.
	exports.checkOp = function(op) {
	  if (op == null || typeof op !== 'object') {
	    return new ShareDBError(ERROR_CODE.ERR_OT_OP_BADLY_FORMED, 'Op must be an object');
	  }

	  if (op.create != null) {
	    if (typeof op.create !== 'object') {
	      return new ShareDBError(ERROR_CODE.ERR_OT_OP_BADLY_FORMED, 'Create data must be an object');
	    }
	    var typeName = op.create.type;
	    if (typeof typeName !== 'string') {
	      return new ShareDBError(ERROR_CODE.ERR_OT_OP_BADLY_FORMED, 'Missing create type');
	    }
	    var type = types.map[typeName];
	    if (type == null || typeof type !== 'object') {
	      return new ShareDBError(ERROR_CODE.ERR_DOC_TYPE_NOT_RECOGNIZED, 'Unknown type');
	    }
	  } else if (op.del != null) {
	    if (op.del !== true) return new ShareDBError(ERROR_CODE.ERR_OT_OP_BADLY_FORMED, 'del value must be true');
	  } else if (!('op' in op)) {
	    return new ShareDBError(ERROR_CODE.ERR_OT_OP_BADLY_FORMED, 'Missing op, create, or del');
	  }

	  if (op.src != null && typeof op.src !== 'string') {
	    return new ShareDBError(ERROR_CODE.ERR_OT_OP_BADLY_FORMED, 'src must be a string');
	  }
	  if (op.seq != null && typeof op.seq !== 'number') {
	    return new ShareDBError(ERROR_CODE.ERR_OT_OP_BADLY_FORMED, 'seq must be a number');
	  }
	  if (
	    (op.src == null && op.seq != null) ||
	    (op.src != null && op.seq == null)
	  ) {
	    return new ShareDBError(ERROR_CODE.ERR_OT_OP_BADLY_FORMED, 'Both src and seq must be set together');
	  }

	  if (op.m != null && typeof op.m !== 'object') {
	    return new ShareDBError(ERROR_CODE.ERR_OT_OP_BADLY_FORMED, 'op.m must be an object or null');
	  }
	};

	// Takes in a string (type name or URI) and returns the normalized name (uri)
	exports.normalizeType = function(typeName) {
	  return types.map[typeName] && types.map[typeName].uri;
	};

	// This is the super apply function that takes in snapshot data (including the
	// type) and edits it in-place. Returns an error or null for success.
	exports.apply = function(snapshot, op) {
	  if (typeof snapshot !== 'object') {
	    return new ShareDBError(ERROR_CODE.ERR_APPLY_SNAPSHOT_NOT_PROVIDED, 'Missing snapshot');
	  }
	  if (snapshot.v != null && op.v != null && snapshot.v !== op.v) {
	    return new ShareDBError(ERROR_CODE.ERR_APPLY_OP_VERSION_DOES_NOT_MATCH_SNAPSHOT, 'Version mismatch');
	  }

	  // Create operation
	  if (op.create) {
	    if (snapshot.type) return new ShareDBError(ERROR_CODE.ERR_DOC_ALREADY_CREATED, 'Document already exists');

	    // The document doesn't exist, although it might have once existed
	    var create = op.create;
	    var type = types.map[create.type];
	    if (!type) return new ShareDBError(ERROR_CODE.ERR_DOC_TYPE_NOT_RECOGNIZED, 'Unknown type');

	    try {
	      snapshot.data = type.create(create.data);
	      snapshot.type = type.uri;
	      snapshot.v++;
	    } catch (err) {
	      return err;
	    }

	  // Delete operation
	  } else if (op.del) {
	    snapshot.data = undefined;
	    snapshot.type = null;
	    snapshot.v++;

	  // Edit operation
	  } else if ('op' in op) {
	    var err = applyOpEdit(snapshot, op.op);
	    if (err) return err;
	    snapshot.v++;

	  // No-op, and we don't have to do anything
	  } else {
	    snapshot.v++;
	  }
	};

	function applyOpEdit(snapshot, edit) {
	  if (!snapshot.type) return new ShareDBError(ERROR_CODE.ERR_DOC_DOES_NOT_EXIST, 'Document does not exist');

	  if (edit === undefined) return new ShareDBError(ERROR_CODE.ERR_OT_OP_NOT_PROVIDED, 'Missing op');
	  var type = types.map[snapshot.type];
	  if (!type) return new ShareDBError(ERROR_CODE.ERR_DOC_TYPE_NOT_RECOGNIZED, 'Unknown type');

	  try {
	    snapshot.data = type.apply(snapshot.data, edit);
	  } catch (err) {
	    return new ShareDBError(ERROR_CODE.ERR_OT_OP_NOT_APPLIED, err.message);
	  }
	}

	exports.transform = function(type, op, appliedOp) {
	  // There are 16 cases this function needs to deal with - which are all the
	  // combinations of create/delete/op/noop from both op and appliedOp
	  if (op.v != null && op.v !== appliedOp.v) {
	    return new ShareDBError(ERROR_CODE.ERR_OP_VERSION_MISMATCH_DURING_TRANSFORM, 'Version mismatch');
	  }

	  if (appliedOp.del) {
	    if (op.create || 'op' in op) {
	      return new ShareDBError(ERROR_CODE.ERR_DOC_WAS_DELETED, 'Document was deleted');
	    }
	  } else if (
	    (appliedOp.create && ('op' in op || op.create || op.del)) ||
	    ('op' in appliedOp && op.create)
	  ) {
	    // If appliedOp.create is not true, appliedOp contains an op - which
	    // also means the document exists remotely.
	    return new ShareDBError(ERROR_CODE.ERR_DOC_ALREADY_CREATED, 'Document was created remotely');
	  } else if ('op' in appliedOp && 'op' in op) {
	    // If we reach here, they both have a .op property.
	    if (!type) return new ShareDBError(ERROR_CODE.ERR_DOC_DOES_NOT_EXIST, 'Document does not exist');

	    if (typeof type === 'string') {
	      type = types.map[type];
	      if (!type) return new ShareDBError(ERROR_CODE.ERR_DOC_TYPE_NOT_RECOGNIZED, 'Unknown type');
	    }

	    try {
	      op.op = type.transform(op.op, appliedOp.op, 'left');
	    } catch (err) {
	      return err;
	    }
	  }

	  if (op.v != null) op.v++;
	};

	/**
	 * Apply an array of ops to the provided snapshot.
	 *
	 * @param snapshot - a Snapshot object which will be mutated by the provided ops
	 * @param ops - an array of ops to apply to the snapshot
	 * @param options - options (currently for internal use only)
	 * @return an error object if applicable
	 */
	exports.applyOps = function(snapshot, ops, options) {
	  options = options || {};
	  for (var index = 0; index < ops.length; index++) {
	    var op = ops[index];
	    if (options._normalizeLegacyJson0Ops) {
	      try {
	        normalizeLegacyJson0Ops(snapshot, op);
	      } catch (error) {
	        return new ShareDBError(
	          ERROR_CODE.ERR_OT_LEGACY_JSON0_OP_CANNOT_BE_NORMALIZED,
	          'Cannot normalize legacy json0 op'
	        );
	      }
	    }
	    snapshot.v = op.v;
	    var error = exports.apply(snapshot, op);
	    if (error) return error;
	  }
	};

	exports.transformPresence = function(presence, op, isOwnOp) {
	  var opError = this.checkOp(op);
	  if (opError) return opError;

	  var type = presence.t;
	  if (typeof type === 'string') {
	    type = types.map[type];
	  }
	  if (!type) return {code: ERROR_CODE.ERR_DOC_TYPE_NOT_RECOGNIZED, message: 'Unknown type'};
	  if (!util.supportsPresence(type)) {
	    return {code: ERROR_CODE.ERR_TYPE_DOES_NOT_SUPPORT_PRESENCE, message: 'Type does not support presence'};
	  }

	  if (op.create || op.del) {
	    presence.p = null;
	    presence.v++;
	    return;
	  }

	  try {
	    presence.p = presence.p === null ?
	      null :
	      type.transformPresence(presence.p, op.op, isOwnOp);
	  } catch (error) {
	    return {code: ERROR_CODE.ERR_PRESENCE_TRANSFORM_FAILED, message: error.message || error};
	  }

	  presence.v++;
	};

	/**
	 * json0 had a breaking change in https://github.com/ottypes/json0/pull/40
	 * The change added stricter type checking, which breaks fetchSnapshot()
	 * when trying to rebuild a snapshot from old, committed ops that didn't
	 * have this stricter validation. This method fixes up legacy ops to
	 * pass the stricter validation
	 */
	function normalizeLegacyJson0Ops(snapshot, json0Op) {
	  if (snapshot.type !== types.defaultType.uri) return;
	  var components = json0Op.op;
	  if (!components) return;
	  var data = snapshot.data;

	  // type.apply() makes no guarantees about mutating the original data, so
	  // we need to clone. However, we only need to apply() if we have multiple
	  // components, so avoid cloning if we don't have to.
	  if (components.length > 1) data = util.clone(data);

	  for (var i = 0; i < components.length; i++) {
	    var component = components[i];
	    if (typeof component.lm === 'string') component.lm = +component.lm;
	    var path = component.p;
	    var element = data;
	    for (var j = 0; j < path.length; j++) {
	      var key = path[j];
	      // https://github.com/ottypes/json0/blob/73db17e86adc5d801951d1a69453b01382e66c7d/lib/json0.js#L21
	      if (Object.prototype.toString.call(element) == '[object Array]') path[j] = +key;
	      // https://github.com/ottypes/json0/blob/73db17e86adc5d801951d1a69453b01382e66c7d/lib/json0.js#L32
	      else if (element.constructor === Object) path[j] = key.toString();
	      element = element[key];
	    }

	    // Apply to update the snapshot, so we can correctly check the path for
	    // the next component. We don't need to do this on the final iteration,
	    // since there's no more ops.
	    if (i < components.length - 1) data = types.defaultType.apply(data, [component]);
	  }
	} 
} (ot$1));

var RemotePresence = remotePresence;
var ot = ot$1;

var remoteDocPresence = RemoteDocPresence$1;
function RemoteDocPresence$1(presence, presenceId) {
  RemotePresence.call(this, presence, presenceId);

  this.collection = this.presence.collection;
  this.id = this.presence.id;
  this.src = null;
  this.presenceVersion = null;

  this._doc = this.connection.get(this.collection, this.id);
  this._emitter = this.connection._docPresenceEmitter;
  this._pending = null;
  this._opCache = null;
  this._pendingSetPending = false;

  this._opHandler = this._handleOp.bind(this);
  this._createDelHandler = this._handleCreateDel.bind(this);
  this._loadHandler = this._handleLoad.bind(this);
  this._registerWithDoc();
}

RemoteDocPresence$1.prototype = Object.create(RemotePresence.prototype);

RemoteDocPresence$1.prototype.receiveUpdate = function(message) {
  if (this._pending && message.pv < this._pending.pv) return;
  this.src = message.src;
  this._pending = message;
  this._setPendingPresence();
};

RemoteDocPresence$1.prototype.destroy = function(callback) {
  this._emitter.removeEventListener(this._doc, 'op', this._opHandler);
  this._emitter.removeEventListener(this._doc, 'create', this._createDelHandler);
  this._emitter.removeEventListener(this._doc, 'del', this._createDelHandler);
  this._emitter.removeEventListener(this._doc, 'load', this._loadHandler);

  RemotePresence.prototype.destroy.call(this, callback);
};

RemoteDocPresence$1.prototype._registerWithDoc = function() {
  this._emitter.addEventListener(this._doc, 'op', this._opHandler);
  this._emitter.addEventListener(this._doc, 'create', this._createDelHandler);
  this._emitter.addEventListener(this._doc, 'del', this._createDelHandler);
  this._emitter.addEventListener(this._doc, 'load', this._loadHandler);
};

RemoteDocPresence$1.prototype._setPendingPresence = function() {
  if (this._pendingSetPending) return;
  this._pendingSetPending = true;
  var presence = this;
  this._doc.whenNothingPending(function() {
    presence._pendingSetPending = false;
    if (!presence._pending) return;
    if (presence._pending.pv < presence.presenceVersion) return presence._pending = null;

    if (presence._pending.v > presence._doc.version) {
      return presence._doc.fetch();
    }

    if (!presence._catchUpStalePresence()) return;

    presence.value = presence._pending.p;
    presence.presenceVersion = presence._pending.pv;
    presence._pending = null;
    presence.presence._updateRemotePresence(presence);
  });
};

RemoteDocPresence$1.prototype._handleOp = function(op, source, connectionId) {
  var isOwnOp = connectionId === this.src;
  this._transformAgainstOp(op, isOwnOp);
  this._cacheOp(op, isOwnOp);
  this._setPendingPresence();
};

RemotePresence.prototype._handleCreateDel = function() {
  this._cacheOp(null);
  this._setPendingPresence();
};

RemotePresence.prototype._handleLoad = function() {
  this.value = null;
  this._pending = null;
  this._opCache = null;
  this.presence._updateRemotePresence(this);
};

RemoteDocPresence$1.prototype._transformAgainstOp = function(op, isOwnOp) {
  if (!this.value) return;

  try {
    this.value = this._doc.type.transformPresence(this.value, op, isOwnOp);
  } catch (error) {
    return this.presence.emit('error', error);
  }
  this.presence._updateRemotePresence(this);
};

RemoteDocPresence$1.prototype._catchUpStalePresence = function() {
  if (this._pending.v >= this._doc.version) return true;

  if (!this._opCache) {
    this._startCachingOps();
    this._doc.fetch();
    // We're already subscribed, but we send another subscribe message
    // to force presence updates from other clients
    this.presence.subscribe();
    return false;
  }

  while (this._opCache[this._pending.v]) {
    var item = this._opCache[this._pending.v];
    var op = item.op;
    var isOwnOp = item.isOwnOp;
    // We use a null op to signify a create or a delete operation. In both
    // cases we just want to reset the presence (which doesn't make sense
    // in a new document), so just set the presence to null.
    if (op === null) {
      this._pending.p = null;
      this._pending.v++;
    } else {
      ot.transformPresence(this._pending, op, isOwnOp);
    }
  }

  var hasCaughtUp = this._pending.v >= this._doc.version;
  if (hasCaughtUp) {
    this._stopCachingOps();
  }

  return hasCaughtUp;
};

RemoteDocPresence$1.prototype._startCachingOps = function() {
  this._opCache = [];
};

RemoteDocPresence$1.prototype._stopCachingOps = function() {
  this._opCache = null;
};

RemoteDocPresence$1.prototype._cacheOp = function(op, isOwnOp) {
  if (this._opCache) {
    op = op ? {op: op} : null;
    // Subtract 1 from the current doc version, because an op with v3
    // should be read as the op that takes a doc from v3 -> v4
    this._opCache[this._doc.version - 1] = {op: op, isOwnOp: isOwnOp};
  }
};

var Presence$1 = presence;
var LocalDocPresence = localDocPresence;
var RemoteDocPresence = remoteDocPresence;

function DocPresence$1(connection, collection, id) {
  var channel = DocPresence$1.channel(collection, id);
  Presence$1.call(this, connection, channel);

  this.collection = collection;
  this.id = id;
}
var docPresence = DocPresence$1;

DocPresence$1.prototype = Object.create(Presence$1.prototype);

DocPresence$1.channel = function(collection, id) {
  return collection + '.' + id;
};

DocPresence$1.prototype._createLocalPresence = function(id) {
  return new LocalDocPresence(this, id);
};

DocPresence$1.prototype._createRemotePresence = function(id) {
  return new RemoteDocPresence(this, id);
};

var snapshot = Snapshot$1;
function Snapshot$1(id, version, type, data, meta) {
  this.id = id;
  this.v = version;
  this.type = type;
  this.data = data;
  this.m = meta;
}

var Snapshot = snapshot;
var emitter$1 = emitter$6;

var snapshotRequest = SnapshotRequest$2;

function SnapshotRequest$2(connection, requestId, collection, id, callback) {
  emitter$1.EventEmitter.call(this);

  if (typeof callback !== 'function') {
    throw new Error('Callback is required for SnapshotRequest');
  }

  this.requestId = requestId;
  this.connection = connection;
  this.id = id;
  this.collection = collection;
  this.callback = callback;

  this.sent = false;
}
emitter$1.mixin(SnapshotRequest$2);

SnapshotRequest$2.prototype.send = function() {
  if (!this.connection.canSend) {
    return;
  }

  this.connection.send(this._message());
  this.sent = true;
};

SnapshotRequest$2.prototype._onConnectionStateChanged = function() {
  if (this.connection.canSend) {
    if (!this.sent) this.send();
  } else {
    // If the connection can't send, then we've had a disconnection, and even if we've already sent
    // the request previously, we need to re-send it over this reconnected client, so reset the
    // sent flag to false.
    this.sent = false;
  }
};

SnapshotRequest$2.prototype._handleResponse = function(error, message) {
  this.emit('ready');

  if (error) {
    return this.callback(error);
  }

  var metadata = message.meta ? message.meta : null;
  var snapshot = new Snapshot(this.id, message.v, message.type, message.data, metadata);

  this.callback(null, snapshot);
};

var SnapshotRequest$1 = snapshotRequest;
var util$3 = util$a;
var ACTIONS$2 = messageActions.ACTIONS;

var snapshotVersionRequest = SnapshotVersionRequest$1;

function SnapshotVersionRequest$1(connection, requestId, collection, id, version, callback) {
  SnapshotRequest$1.call(this, connection, requestId, collection, id, callback);

  if (!util$3.isValidVersion(version)) {
    throw new Error('Snapshot version must be a positive integer or null');
  }

  this.version = version;
}

SnapshotVersionRequest$1.prototype = Object.create(SnapshotRequest$1.prototype);

SnapshotVersionRequest$1.prototype._message = function() {
  return {
    a: ACTIONS$2.snapshotFetch,
    id: this.requestId,
    c: this.collection,
    d: this.id,
    v: this.version
  };
};

var SnapshotRequest = snapshotRequest;
var util$2 = util$a;
var ACTIONS$1 = messageActions.ACTIONS;

var snapshotTimestampRequest = SnapshotTimestampRequest$1;

function SnapshotTimestampRequest$1(connection, requestId, collection, id, timestamp, callback) {
  SnapshotRequest.call(this, connection, requestId, collection, id, callback);

  if (!util$2.isValidTimestamp(timestamp)) {
    throw new Error('Snapshot timestamp must be a positive integer or null');
  }

  this.timestamp = timestamp;
}

SnapshotTimestampRequest$1.prototype = Object.create(SnapshotRequest.prototype);

SnapshotTimestampRequest$1.prototype._message = function() {
  return {
    a: ACTIONS$1.snapshotFetchByTimestamp,
    id: this.requestId,
    c: this.collection,
    d: this.id,
    ts: this.timestamp
  };
};

var util$1 = util$a;
var EventEmitter = require$$1.EventEmitter;

var EVENTS = [
  'create',
  'del',
  'destroy',
  'load',
  'op'
];

var docPresenceEmitter = DocPresenceEmitter$1;

function DocPresenceEmitter$1() {
  this._docs = {};
  this._forwarders = {};
  this._emitters = {};
}

DocPresenceEmitter$1.prototype.addEventListener = function(doc, event, listener) {
  this._registerDoc(doc);
  var emitter = util$1.dig(this._emitters, doc.collection, doc.id);
  emitter.on(event, listener);
};

DocPresenceEmitter$1.prototype.removeEventListener = function(doc, event, listener) {
  var emitter = util$1.dig(this._emitters, doc.collection, doc.id);
  if (!emitter) return;
  emitter.off(event, listener);
  // We'll always have at least one, because of the destroy listener
  if (emitter._eventsCount === 1) this._unregisterDoc(doc);
};

DocPresenceEmitter$1.prototype._registerDoc = function(doc) {
  var alreadyRegistered = true;
  util$1.digOrCreate(this._docs, doc.collection, doc.id, function() {
    alreadyRegistered = false;
    return doc;
  });

  if (alreadyRegistered) return;

  var emitter = util$1.digOrCreate(this._emitters, doc.collection, doc.id, function() {
    var e = new EventEmitter();
    // Set a high limit to avoid unnecessary warnings, but still
    // retain some degree of memory leak detection
    e.setMaxListeners(1000);
    return e;
  });

  var self = this;
  EVENTS.forEach(function(event) {
    var forwarder = util$1.digOrCreate(self._forwarders, doc.collection, doc.id, event, function() {
      return emitter.emit.bind(emitter, event);
    });

    doc.on(event, forwarder);
  });

  this.addEventListener(doc, 'destroy', this._unregisterDoc.bind(this, doc));
};

DocPresenceEmitter$1.prototype._unregisterDoc = function(doc) {
  var forwarders = util$1.dig(this._forwarders, doc.collection, doc.id);
  for (var event in forwarders) {
    doc.off(event, forwarders[event]);
  }

  var emitter = util$1.dig(this._emitters, doc.collection, doc.id);
  emitter.removeAllListeners();

  util$1.digAndRemove(this._forwarders, doc.collection, doc.id);
  util$1.digAndRemove(this._emitters, doc.collection, doc.id);
  util$1.digAndRemove(this._docs, doc.collection, doc.id);
};

var Doc = doc;
var Query = query;
var Presence = presence;
var DocPresence = docPresence;
var SnapshotVersionRequest = snapshotVersionRequest;
var SnapshotTimestampRequest = snapshotTimestampRequest;
var emitter = emitter$6;
var ShareDBError = error;
var ACTIONS = messageActions.ACTIONS;
var types$1 = types$3;
var util = util$a;
var logger = logger_1;
var DocPresenceEmitter = docPresenceEmitter;

var ERROR_CODE = ShareDBError.CODES;

function connectionState(socket) {
  if (socket.readyState === 0 || socket.readyState === 1) return 'connecting';
  return 'disconnected';
}

/**
 * Handles communication with the sharejs server and provides queries and
 * documents.
 *
 * We create a connection with a socket object
 *   connection = new sharejs.Connection(sockset)
 * The socket may be any object handling the websocket protocol. See the
 * documentation of bindToSocket() for details. We then wait for the connection
 * to connect
 *   connection.on('connected', ...)
 * and are finally able to work with shared documents
 *   connection.get('food', 'steak') // Doc
 *
 * @param socket @see bindToSocket
 */
var connection = Connection$1;
function Connection$1(socket) {
  emitter.EventEmitter.call(this);

  // Map of collection -> id -> doc object for created documents.
  // (created documents MUST BE UNIQUE)
  this.collections = {};

  // Each query and snapshot request is created with an id that the server uses when it sends us
  // info about the request (updates, etc)
  this.nextQueryId = 1;
  this.nextSnapshotRequestId = 1;

  // Map from query ID -> query object.
  this.queries = {};

  // Maps from channel -> presence objects
  this._presences = {};
  this._docPresenceEmitter = new DocPresenceEmitter();

  // Map from snapshot request ID -> snapshot request
  this._snapshotRequests = {};

  // A unique message number for the given id
  this.seq = 1;

  // A unique message number for presence
  this._presenceSeq = 1;

  // Equals agent.src on the server
  this.id = null;

  // This direct reference from connection to agent is not used internal to
  // ShareDB, but it is handy for server-side only user code that may cache
  // state on the agent and read it in middleware
  this.agent = null;

  this.debug = false;

  this.state = connectionState(socket);

  this.bindToSocket(socket);
}
emitter.mixin(Connection$1);


/**
 * Use socket to communicate with server
 *
 * Socket is an object that can handle the websocket protocol. This method
 * installs the onopen, onclose, onmessage and onerror handlers on the socket to
 * handle communication and sends messages by calling socket.send(message). The
 * sockets `readyState` property is used to determine the initaial state.
 *
 * @param socket Handles the websocket protocol
 * @param socket.readyState
 * @param socket.close
 * @param socket.send
 * @param socket.onopen
 * @param socket.onclose
 * @param socket.onmessage
 * @param socket.onerror
 */
Connection$1.prototype.bindToSocket = function(socket) {
  if (this.socket) {
    this.socket.close();
    this.socket.onmessage = null;
    this.socket.onopen = null;
    this.socket.onerror = null;
    this.socket.onclose = null;
  }

  this.socket = socket;

  // State of the connection. The corresponding events are emitted when this changes
  //
  // - 'connecting'   The connection is still being established, or we are still
  //                    waiting on the server to send us the initialization message
  // - 'connected'    The connection is open and we have connected to a server
  //                    and recieved the initialization message
  // - 'disconnected' Connection is closed, but it will reconnect automatically
  // - 'closed'       The connection was closed by the client, and will not reconnect
  // - 'stopped'      The connection was closed by the server, and will not reconnect
  var newState = connectionState(socket);
  this._setState(newState);

  // This is a helper variable the document uses to see whether we're
  // currently in a 'live' state. It is true if and only if we're connected
  this.canSend = false;

  var connection = this;

  socket.onmessage = function(event) {
    try {
      var data = (typeof event.data === 'string') ?
        JSON.parse(event.data) : event.data;
    } catch (err) {
      logger.warn('Failed to parse message', event);
      return;
    }

    if (connection.debug) logger.info('RECV', JSON.stringify(data));

    var request = {data: data};
    connection.emit('receive', request);
    if (!request.data) return;

    try {
      connection.handleMessage(request.data);
    } catch (err) {
      util.nextTick(function() {
        connection.emit('error', err);
      });
    }
  };

  // If socket is already open, do handshake immediately.
  if (socket.readyState === 1) {
    connection._initializeHandshake();
  }
  socket.onopen = function() {
    connection._setState('connecting');
    connection._initializeHandshake();
  };

  socket.onerror = function(err) {
    // This isn't the same as a regular error, because it will happen normally
    // from time to time. Your connection should probably automatically
    // reconnect anyway, but that should be triggered off onclose not onerror.
    // (onclose happens when onerror gets called anyway).
    connection.emit('connection error', err);
  };

  socket.onclose = function(reason) {
    // node-browserchannel reason values:
    //   'Closed' - The socket was manually closed by calling socket.close()
    //   'Stopped by server' - The server sent the stop message to tell the client not to try connecting
    //   'Request failed' - Server didn't respond to request (temporary, usually offline)
    //   'Unknown session ID' - Server session for client is missing (temporary, will immediately reestablish)

    if (reason === 'closed' || reason === 'Closed') {
      connection._setState('closed', reason);
    } else if (reason === 'stopped' || reason === 'Stopped by server') {
      connection._setState('stopped', reason);
    } else {
      connection._setState('disconnected', reason);
    }
  };
};

/**
 * @param {object} message
 * @param {string} message.a action
 */
Connection$1.prototype.handleMessage = function(message) {
  var err = null;
  if (message.error) {
    err = wrapErrorData(message.error, message);
    delete message.error;
  }
  // Switch on the message action. Most messages are for documents and are
  // handled in the doc class.
  switch (message.a) {
    case ACTIONS.initLegacy:
      // Client initialization packet
      return this._handleLegacyInit(message);
    case ACTIONS.handshake:
      return this._handleHandshake(err, message);
    case ACTIONS.queryFetch:
      var query = this.queries[message.id];
      if (query) query._handleFetch(err, message.data, message.extra);
      return;
    case ACTIONS.querySubscribe:
      var query = this.queries[message.id];
      if (query) query._handleSubscribe(err, message.data, message.extra);
      return;
    case ACTIONS.queryUnsubscribe:
      // Queries are removed immediately on calls to destroy, so we ignore
      // replies to query unsubscribes. Perhaps there should be a callback for
      // destroy, but this is currently unimplemented
      return;
    case ACTIONS.queryUpdate:
      // Query message. Pass this to the appropriate query object.
      var query = this.queries[message.id];
      if (!query) return;
      if (err) return query._handleError(err);
      if (message.diff) query._handleDiff(message.diff);
      if (message.hasOwnProperty('extra')) query._handleExtra(message.extra);
      return;

    case ACTIONS.bulkFetch:
      return this._handleBulkMessage(err, message, '_handleFetch');
    case ACTIONS.bulkSubscribe:
    case ACTIONS.bulkUnsubscribe:
      return this._handleBulkMessage(err, message, '_handleSubscribe');

    case ACTIONS.snapshotFetch:
    case ACTIONS.snapshotFetchByTimestamp:
      return this._handleSnapshotFetch(err, message);

    case ACTIONS.fetch:
      var doc = this.getExisting(message.c, message.d);
      if (doc) doc._handleFetch(err, message.data);
      return;
    case ACTIONS.subscribe:
    case ACTIONS.unsubscribe:
      var doc = this.getExisting(message.c, message.d);
      if (doc) doc._handleSubscribe(err, message.data);
      return;
    case ACTIONS.op:
      var doc = this.getExisting(message.c, message.d);
      if (doc) doc._handleOp(err, message);
      return;
    case ACTIONS.presence:
      return this._handlePresence(err, message);
    case ACTIONS.presenceSubscribe:
      return this._handlePresenceSubscribe(err, message);
    case ACTIONS.presenceUnsubscribe:
      return this._handlePresenceUnsubscribe(err, message);
    case ACTIONS.presenceRequest:
      return this._handlePresenceRequest(err, message);
    case ACTIONS.pingPong:
      return this._handlePingPong(err);

    default:
      logger.warn('Ignoring unrecognized message', message);
  }
};

function wrapErrorData(errorData, fullMessage) {
  // wrap in Error object so can be passed through event emitters
  var err = new Error(errorData.message);
  err.code = errorData.code;
  if (fullMessage) {
    // Add the message data to the error object for more context
    err.data = fullMessage;
  }
  return err;
}

Connection$1.prototype._handleBulkMessage = function(err, message, method) {
  if (message.data) {
    for (var id in message.data) {
      var dataForId = message.data[id];
      var doc = this.getExisting(message.c, id);
      if (doc) {
        if (err) {
          doc[method](err);
        } else if (dataForId.error) {
          // Bulk reply snapshot-specific errorr - see agent.js getMapResult
          doc[method](wrapErrorData(dataForId.error));
        } else {
          doc[method](null, dataForId);
        }
      }
    }
  } else if (Array.isArray(message.b)) {
    for (var i = 0; i < message.b.length; i++) {
      var id = message.b[i];
      var doc = this.getExisting(message.c, id);
      if (doc) doc[method](err);
    }
  } else if (message.b) {
    for (var id in message.b) {
      var doc = this.getExisting(message.c, id);
      if (doc) doc[method](err);
    }
  } else {
    logger.error('Invalid bulk message', message);
  }
};

Connection$1.prototype._reset = function() {
  this.agent = null;
};

// Set the connection's state. The connection is basically a state machine.
Connection$1.prototype._setState = function(newState, reason) {
  if (this.state === newState) return;

  // I made a state diagram. The only invalid transitions are getting to
  // 'connecting' from anywhere other than 'disconnected' and getting to
  // 'connected' from anywhere other than 'connecting'.
  if (
    (
      newState === 'connecting' &&
      this.state !== 'disconnected' &&
      this.state !== 'stopped' &&
      this.state !== 'closed'
    ) || (
      newState === 'connected' &&
      this.state !== 'connecting'
    )
  ) {
    var err = new ShareDBError(
      ERROR_CODE.ERR_CONNECTION_STATE_TRANSITION_INVALID,
      'Cannot transition directly from ' + this.state + ' to ' + newState
    );
    return this.emit('error', err);
  }

  this.state = newState;
  this.canSend = (newState === 'connected');

  if (
    newState === 'disconnected' ||
    newState === 'stopped' ||
    newState === 'closed'
  ) {
    this._reset();
  }

  // Group subscribes together to help server make more efficient calls
  this.startBulk();
  // Emit the event to all queries
  for (var id in this.queries) {
    var query = this.queries[id];
    query._onConnectionStateChanged();
  }
  // Emit the event to all documents
  for (var collection in this.collections) {
    var docs = this.collections[collection];
    for (var id in docs) {
      docs[id]._onConnectionStateChanged();
    }
  }
  // Emit the event to all Presences
  for (var channel in this._presences) {
    this._presences[channel]._onConnectionStateChanged();
  }
  // Emit the event to all snapshots
  for (var id in this._snapshotRequests) {
    var snapshotRequest = this._snapshotRequests[id];
    snapshotRequest._onConnectionStateChanged();
  }
  this.endBulk();

  this.emit(newState, reason);
  this.emit('state', newState, reason);
};

Connection$1.prototype.startBulk = function() {
  if (!this.bulk) this.bulk = {};
};

Connection$1.prototype.endBulk = function() {
  if (this.bulk) {
    for (var collection in this.bulk) {
      var actions = this.bulk[collection];
      this._sendBulk('f', collection, actions.f);
      this._sendBulk('s', collection, actions.s);
      this._sendBulk('u', collection, actions.u);
    }
  }
  this.bulk = null;
};

Connection$1.prototype._sendBulk = function(action, collection, values) {
  if (!values) return;
  var ids = [];
  var versions = {};
  var versionsCount = 0;
  var versionId;
  for (var id in values) {
    var value = values[id];
    if (value == null) {
      ids.push(id);
    } else {
      versions[id] = value;
      versionId = id;
      versionsCount++;
    }
  }
  if (ids.length === 1) {
    var id = ids[0];
    this.send({a: action, c: collection, d: id});
  } else if (ids.length) {
    this.send({a: 'b' + action, c: collection, b: ids});
  }
  if (versionsCount === 1) {
    var version = versions[versionId];
    this.send({a: action, c: collection, d: versionId, v: version});
  } else if (versionsCount) {
    this.send({a: 'b' + action, c: collection, b: versions});
  }
};

Connection$1.prototype._sendActions = function(action, doc, version) {
  // Ensure the doc is registered so that it receives the reply message
  this._addDoc(doc);
  if (this.bulk) {
    // Bulk subscribe
    var actions = this.bulk[doc.collection] || (this.bulk[doc.collection] = {});
    var versions = actions[action] || (actions[action] = {});
    var isDuplicate = versions.hasOwnProperty(doc.id);
    versions[doc.id] = version;
    return isDuplicate;
  } else {
    // Send single doc subscribe message
    var message = {a: action, c: doc.collection, d: doc.id, v: version};
    this.send(message);
  }
};

Connection$1.prototype.sendFetch = function(doc) {
  return this._sendActions(ACTIONS.fetch, doc, doc.version);
};

Connection$1.prototype.sendSubscribe = function(doc) {
  return this._sendActions(ACTIONS.subscribe, doc, doc.version);
};

Connection$1.prototype.sendUnsubscribe = function(doc) {
  return this._sendActions(ACTIONS.unsubscribe, doc);
};

Connection$1.prototype.sendOp = function(doc, op) {
  // Ensure the doc is registered so that it receives the reply message
  this._addDoc(doc);
  var message = {
    a: ACTIONS.op,
    c: doc.collection,
    d: doc.id,
    v: doc.version,
    src: op.src,
    seq: op.seq,
    x: {}
  };
  if ('op' in op) message.op = op.op;
  if (op.create) message.create = op.create;
  if (op.del) message.del = op.del;
  if (doc.submitSource) message.x.source = op.source;
  this.send(message);
};


/**
 * Sends a message down the socket
 */
Connection$1.prototype.send = function(message) {
  if (this.debug) logger.info('SEND', JSON.stringify(message));

  this.emit('send', message);
  this.socket.send(JSON.stringify(message));
};

Connection$1.prototype.ping = function() {
  if (!this.canSend) {
    throw new ShareDBError(
      ERROR_CODE.ERR_CANNOT_PING_OFFLINE,
      'Socket must be CONNECTED to ping'
    );
  }

  var message = {
    a: ACTIONS.pingPong
  };
  this.send(message);
};

/**
 * Closes the socket and emits 'closed'
 */
Connection$1.prototype.close = function() {
  this.socket.close();
};

Connection$1.prototype.getExisting = function(collection, id) {
  if (this.collections[collection]) return this.collections[collection][id];
};


/**
 * Get or create a document.
 *
 * @param collection
 * @param id
 * @return {Doc}
 */
Connection$1.prototype.get = function(collection, id) {
  var docs = this.collections[collection] ||
    (this.collections[collection] = {});

  var doc = docs[id];
  if (!doc) {
    doc = docs[id] = new Doc(this, collection, id);
    this.emit('doc', doc);
  }

  doc._wantsDestroy = false;
  return doc;
};


/**
 * Remove document from this.collections
 *
 * @private
 */
Connection$1.prototype._destroyDoc = function(doc) {
  if (!doc._wantsDestroy) return;
  util.digAndRemove(this.collections, doc.collection, doc.id);
  doc.emit('destroy');
};

Connection$1.prototype._addDoc = function(doc) {
  var docs = this.collections[doc.collection];
  if (!docs) {
    docs = this.collections[doc.collection] = {};
  }
  if (docs[doc.id] !== doc) {
    docs[doc.id] = doc;
  }
};

// Helper for createFetchQuery and createSubscribeQuery, below.
Connection$1.prototype._createQuery = function(action, collection, q, options, callback) {
  var id = this.nextQueryId++;
  var query = new Query(action, this, id, collection, q, options, callback);
  this.queries[id] = query;
  query.send();
  return query;
};

// Internal function. Use query.destroy() to remove queries.
Connection$1.prototype._destroyQuery = function(query) {
  delete this.queries[query.id];
};

// The query options object can contain the following fields:
//
// db: Name of the db for the query. You can attach extraDbs to ShareDB and
//   pick which one the query should hit using this parameter.

// Create a fetch query. Fetch queries are only issued once, returning the
// results directly into the callback.
//
// The callback should have the signature function(error, results, extra)
// where results is a list of Doc objects.
Connection$1.prototype.createFetchQuery = function(collection, q, options, callback) {
  return this._createQuery(ACTIONS.queryFetch, collection, q, options, callback);
};

// Create a subscribe query. Subscribe queries return with the initial data
// through the callback, then update themselves whenever the query result set
// changes via their own event emitter.
//
// If present, the callback should have the signature function(error, results, extra)
// where results is a list of Doc objects.
Connection$1.prototype.createSubscribeQuery = function(collection, q, options, callback) {
  return this._createQuery(ACTIONS.querySubscribe, collection, q, options, callback);
};

Connection$1.prototype.hasPending = function() {
  return !!(
    this._firstDoc(hasPending) ||
    this._firstQuery(hasPending) ||
    this._firstSnapshotRequest()
  );
};
function hasPending(object) {
  return object.hasPending();
}

Connection$1.prototype.hasWritePending = function() {
  return !!this._firstDoc(hasWritePending);
};
function hasWritePending(object) {
  return object.hasWritePending();
}

Connection$1.prototype.whenNothingPending = function(callback) {
  var doc = this._firstDoc(hasPending);
  if (doc) {
    // If a document is found with a pending operation, wait for it to emit
    // that nothing is pending anymore, and then recheck all documents again.
    // We have to recheck all documents, just in case another mutation has
    // been made in the meantime as a result of an event callback
    doc.once('nothing pending', this._nothingPendingRetry(callback));
    return;
  }
  var query = this._firstQuery(hasPending);
  if (query) {
    query.once('ready', this._nothingPendingRetry(callback));
    return;
  }
  var snapshotRequest = this._firstSnapshotRequest();
  if (snapshotRequest) {
    snapshotRequest.once('ready', this._nothingPendingRetry(callback));
    return;
  }
  // Call back when no pending operations
  util.nextTick(callback);
};
Connection$1.prototype._nothingPendingRetry = function(callback) {
  var connection = this;
  return function() {
    util.nextTick(function() {
      connection.whenNothingPending(callback);
    });
  };
};

Connection$1.prototype._firstDoc = function(fn) {
  for (var collection in this.collections) {
    var docs = this.collections[collection];
    for (var id in docs) {
      var doc = docs[id];
      if (fn(doc)) {
        return doc;
      }
    }
  }
};

Connection$1.prototype._firstQuery = function(fn) {
  for (var id in this.queries) {
    var query = this.queries[id];
    if (fn(query)) {
      return query;
    }
  }
};

Connection$1.prototype._firstSnapshotRequest = function() {
  for (var id in this._snapshotRequests) {
    return this._snapshotRequests[id];
  }
};

/**
 * Fetch a read-only snapshot at a given version
 *
 * @param collection - the collection name of the snapshot
 * @param id - the ID of the snapshot
 * @param version (optional) - the version number to fetch. If null, the latest version is fetched.
 * @param callback - (error, snapshot) => void, where snapshot takes the following schema:
 *
 * {
 *   id: string;         // ID of the snapshot
 *   v: number;          // version number of the snapshot
 *   type: string;       // the OT type of the snapshot, or null if it doesn't exist or is deleted
 *   data: any;          // the snapshot
 * }
 *
 */
Connection$1.prototype.fetchSnapshot = function(collection, id, version, callback) {
  if (typeof version === 'function') {
    callback = version;
    version = null;
  }

  var requestId = this.nextSnapshotRequestId++;
  var snapshotRequest = new SnapshotVersionRequest(this, requestId, collection, id, version, callback);
  this._snapshotRequests[snapshotRequest.requestId] = snapshotRequest;
  snapshotRequest.send();
};

/**
 * Fetch a read-only snapshot at a given timestamp
 *
 * @param collection - the collection name of the snapshot
 * @param id - the ID of the snapshot
 * @param timestamp (optional) - the timestamp to fetch. If null, the latest version is fetched.
 * @param callback - (error, snapshot) => void, where snapshot takes the following schema:
 *
 * {
 *   id: string;         // ID of the snapshot
 *   v: number;          // version number of the snapshot
 *   type: string;       // the OT type of the snapshot, or null if it doesn't exist or is deleted
 *   data: any;          // the snapshot
 * }
 *
 */
Connection$1.prototype.fetchSnapshotByTimestamp = function(collection, id, timestamp, callback) {
  if (typeof timestamp === 'function') {
    callback = timestamp;
    timestamp = null;
  }

  var requestId = this.nextSnapshotRequestId++;
  var snapshotRequest = new SnapshotTimestampRequest(this, requestId, collection, id, timestamp, callback);
  this._snapshotRequests[snapshotRequest.requestId] = snapshotRequest;
  snapshotRequest.send();
};

Connection$1.prototype._handleSnapshotFetch = function(error, message) {
  var snapshotRequest = this._snapshotRequests[message.id];
  if (!snapshotRequest) return;
  delete this._snapshotRequests[message.id];
  snapshotRequest._handleResponse(error, message);
};

Connection$1.prototype._handleLegacyInit = function(message) {
  // If the minor protocol version has been set, we want to use the
  // new handshake protocol. Let's send a handshake initialize, because
  // we now know the server is ready. If we've already sent it, we'll
  // just ignore the response anyway.
  if (message.protocolMinor) return this._initializeHandshake();
  this._initialize(message);
};

Connection$1.prototype._initializeHandshake = function() {
  this.send({a: ACTIONS.handshake, id: this.id});
};

Connection$1.prototype._handleHandshake = function(error, message) {
  if (error) return this.emit('error', error);
  this._initialize(message);
};

Connection$1.prototype._handlePingPong = function(error) {
  if (error) return this.emit('error', error);
  this.emit('pong');
};

Connection$1.prototype._initialize = function(message) {
  if (this.state !== 'connecting') return;

  if (message.protocol !== 1) {
    return this.emit('error', new ShareDBError(
      ERROR_CODE.ERR_PROTOCOL_VERSION_NOT_SUPPORTED,
      'Unsupported protocol version: ' + message.protocol
    ));
  }
  if (types$1.map[message.type] !== types$1.defaultType) {
    return this.emit('error', new ShareDBError(
      ERROR_CODE.ERR_DEFAULT_TYPE_MISMATCH,
      message.type + ' does not match the server default type'
    ));
  }
  if (typeof message.id !== 'string') {
    return this.emit('error', new ShareDBError(
      ERROR_CODE.ERR_CLIENT_ID_BADLY_FORMED,
      'Client id must be a string'
    ));
  }
  this.id = message.id;

  this._setState('connected');
};

Connection$1.prototype.getPresence = function(channel) {
  var connection = this;
  var presence = util.digOrCreate(this._presences, channel, function() {
    return new Presence(connection, channel);
  });
  presence._wantsDestroy = false;
  return presence;
};

Connection$1.prototype.getDocPresence = function(collection, id) {
  var channel = DocPresence.channel(collection, id);
  var connection = this;
  var presence = util.digOrCreate(this._presences, channel, function() {
    return new DocPresence(connection, collection, id);
  });
  presence._wantsDestroy = false;
  return presence;
};

Connection$1.prototype._sendPresenceAction = function(action, seq, presence) {
  // Ensure the presence is registered so that it receives the reply message
  this._addPresence(presence);
  var message = {a: action, ch: presence.channel, seq: seq};
  this.send(message);
  return message.seq;
};

Connection$1.prototype._addPresence = function(presence) {
  util.digOrCreate(this._presences, presence.channel, function() {
    return presence;
  });
};

Connection$1.prototype._handlePresenceSubscribe = function(error, message) {
  var presence = util.dig(this._presences, message.ch);
  if (presence) presence._handleSubscribe(error, message.seq);
};

Connection$1.prototype._handlePresenceUnsubscribe = function(error, message) {
  var presence = util.dig(this._presences, message.ch);
  if (presence) presence._handleUnsubscribe(error, message.seq);
};

Connection$1.prototype._handlePresence = function(error, message) {
  var presence = util.dig(this._presences, message.ch);
  if (presence) presence._receiveUpdate(error, message);
};

Connection$1.prototype._handlePresenceRequest = function(error, message) {
  var presence = util.dig(this._presences, message.ch);
  if (presence) presence._broadcastAllLocalPresence(error, message);
};

var Connection = connection;
var types = types$3;

types.register(lib$1.type);

const RECONNECT_INTERVAL = 3;
const MAX_ATTEMPTS = 3;
/**
 * Handles connecting and communicating with the Realtime server.
 *
 * @category Internal
 */
class RealtimeConnection extends Events {
    /**
     * Constructor
     *
     * @param realtime - The realtime API
     */
    constructor(realtime) {
        super();
        this._sendQueue = [];
        this._url = null;
        this._realtime = realtime;
        this._socket = null;
        this._sharedb = null;
        this._reconnectAttempts = 0;
        this._reconnectInterval = RECONNECT_INTERVAL;
        this._connected = false;
        this._authenticated = false;
        this._domEvtVisibilityChange = this._onVisibilityChange.bind(this);
    }
    /**
     * Connect to the realtime server
     *
     * @param url - The server URL
     */
    connect(url) {
        this._url = url;
        if (this._reconnectAttempts > MAX_ATTEMPTS) {
            this._realtime.emit('cannotConnect');
            return;
        }
        this._reconnectAttempts++;
        this._realtime.emit('connecting', this._reconnectAttempts);
        // create new socket
        this._socket = new WebSocket(url);
        // create new sharedb connection
        this._sharedb = new Connection(this._socket);
        this._sharedb.on('connected', this._onConnect.bind(this));
        this._sharedb.on('error', this._onError.bind(this));
        this._sharedb.on('bs error', this._onBulkSubscribeError.bind(this));
        const send = this._socket.send;
        this._socket.send = (data) => {
            if (this._socket.readyState === WebSocket.OPEN) {
                send.call(this._socket, data);
            }
            else {
                this._sendQueue.push(data);
            }
        };
        const onmessage = this._socket.onmessage;
        this._socket.onmessage = (msg) => {
            if (!this._onMessage(msg)) {
                onmessage.call(this._socket, msg);
            }
        };
        const onopen = this._socket.onopen;
        this._socket.onopen = (ev) => {
            while (this._sendQueue.length) {
                this._socket.send(this._sendQueue.shift());
            }
            onopen.call(this._socket, ev);
        };
        const onclose = this._socket.onclose;
        this._socket.onclose = (reason) => {
            this._onClose(reason, onclose.bind(this._socket));
        };
        document.addEventListener('visibilitychange', this._domEvtVisibilityChange);
    }
    /**
     * Disconnect from the server
     */
    disconnect() {
        if (this._socket) {
            this._socket.close();
        }
        document.removeEventListener('visibilitychange', this._domEvtVisibilityChange);
    }
    /**
     * Send message to server
     *
     * @param name - The message name
     * @param data - The message data
     */
    sendMessage(name, data) {
        this.send(name + JSON.stringify(data));
    }
    /**
     * Sends a string to the server
     *
     * @param data - The message data
     */
    send(data) {
        if (this._socket && this._socket.readyState === WebSocket.OPEN) {
            this._socket.send(data);
        }
    }
    /**
     * Gets a sharedb document
     *
     * @param collection - The collection name
     * @param id - The document id
     * @returns The sharedb document
     */
    getDocument(collection, id) {
        return this._sharedb.get(collection, id.toString());
    }
    /**
     * Start bulk subscribing to documents
     */
    startBulkSubscribe() {
        this._sharedb.startBulk();
    }
    /**
     * Stop bulk subscribing to documents
     */
    endBulkSubscribe() {
        this._sharedb.endBulk();
    }
    _onVisibilityChange() {
        if (document.hidden)
            return;
        if (!this.connected && this._url) {
            this.connect(this._url);
        }
    }
    _onConnect() {
        this._connected = true;
        this._reconnectAttempts = 0;
        this._reconnectInterval = RECONNECT_INTERVAL;
        this._sendAuth();
        this._realtime.emit('connected');
    }
    _onError(msg) {
        if (this._sharedb.state === 'connected')
            return;
        this._realtime.emit('error', msg);
    }
    _onBulkSubscribeError(msg) {
        this._realtime.emit('error:bs', msg);
    }
    _onClose(reason, originalOnClose) {
        this._connected = false;
        this._authenticated = false;
        this._realtime.emit('disconnect', reason);
        originalOnClose();
        this._realtime.emit('nextAttempt', this._reconnectInterval);
        if (!document.hidden) {
            setTimeout(() => {
                this.connect(this._url);
            }, this._reconnectInterval * 1000);
        }
    }
    _onMessage(msg) {
        try {
            if (msg.data.startsWith('auth')) {
                return this._onMessageAuth(msg);
            }
            else if (msg.data.startsWith('selection')) {
                return this._onMessageSelection(msg);
            }
            return false;
        }
        catch (err) {
            console.error(err);
        }
    }
    _onMessageAuth(_msg) {
        if (!this._authenticated) {
            this._authenticated = true;
            this._realtime.emit('authenticated');
        }
        return true;
    }
    _onMessageSelection(msg) {
        const data = msg.data.slice('selection:'.length);
        this._realtime.emit('selection', data);
        return true;
    }
    _onMessageFs(msg) {
        let data = msg.data.slice('fs:'.length);
        const ind = data.indexOf(':');
        if (ind !== -1) {
            const op = data.slice(0, ind);
            if (op === 'paths') {
                data = JSON.parse(data.slice(ind + 1));
                this._realtime.emit('fs:paths', data);
            }
            return true;
        }
        return false;
    }
    _sendAuth() {
        this.sendMessage('auth', {
            accessToken: globals.accessToken
        });
    }
    /**
     * Whether the user is connected to the server
     */
    get connected() {
        return this._connected;
    }
    /**
     * Whether the server has authenticated the user
     */
    get authenticated() {
        return this._authenticated;
    }
    /**
     * Gets the sharedb instance
     */
    get sharedb() {
        return this._sharedb;
    }
}

/**
 * Represents a scene in sharedb
 *
 * @category Internal
 */
class RealtimeScene extends Events {
    /**
     * Constructor
     *
     * @param uniqueId - The unique scene id
     * @param realtime - The realtime API
     * @param connection - The realtime connection
     */
    constructor(uniqueId, realtime, connection) {
        super();
        this._uniqueId = uniqueId;
        this._realtime = realtime;
        this._connection = connection;
        this._document = null;
        this._loaded = false;
        this._evtConnection = null;
    }
    /**
     * Loads scene from sharedb and subscribes to changes.
     */
    load() {
        if (this._document)
            return;
        this._document = this._connection.getDocument('scenes', this._uniqueId);
        this._document.on('error', this._onError.bind(this));
        this._document.on('load', this._onLoad.bind(this));
        this._evtConnection = this._realtime.on('disconnect', this.unload.bind(this));
        this._document.subscribe();
    }
    /**
     * Unloads scene from sharedb and unsubscribes from changes.
     */
    unload() {
        if (!this._document)
            return;
        this._document.unsubscribe();
        this._document.destroy();
        this._document = null;
        this._loaded = false;
        this._connection.send(`close:scene:${this._uniqueId}`);
        this._evtConnection.unbind();
        this._evtConnection = null;
        this.emit('unload');
    }
    /**
     * Add entity to scene
     *
     * @param entity - The entity
     */
    addEntity(entity) {
        this.submitOp({
            p: ['entities', entity.get('resource_id')],
            oi: entity.json()
        });
    }
    /**
     * Removes entity from scene (not from children of another entity)
     *
     * @param entity - The entity
     */
    removeEntity(entity) {
        this.submitOp({
            p: ['entities', entity.get('resource_id')],
            od: {}
        });
    }
    /**
     * Submits sharedb operation
     *
     * @param op - The operation
     */
    submitOp(op) {
        if (!this._loaded)
            return;
        try {
            this._document.submitOp([op]);
        }
        catch (err) {
            console.error(err);
            this._realtime.emit('error:scene', err, this._uniqueId);
        }
    }
    /**
     * Calls the callback when there are no changes pending to be
     * sent to the server
     *
     * @param callback - The callback
     */
    whenNothingPending(callback) {
        if (this._document) {
            this._document.whenNothingPending(callback);
        }
    }
    _onError(err) {
        this._realtime.emit('error:scene', err, this._uniqueId);
    }
    _onLoad() {
        // notify of operations
        this._document.on('op', this._onOp.bind(this));
        this._loaded = true;
        this.emit('load');
    }
    _onOp(ops, local) {
        if (local)
            return;
        for (let i = 0; i < ops.length; i++) {
            if (ops[i].p[0]) {
                this._realtime.emit('scene:op', ops[i].p[0], ops[i]);
            }
        }
    }
    /**
     * Whether the scene is loaded
     */
    get loaded() {
        return this._loaded;
    }
    /**
     * The scene data
     */
    get data() {
        return ((this._loaded && this._document) ? this._document.data : null);
    }
    /**
     * The scene id - used in combination with the branch id
     */
    get id() {
        var _a;
        return (_a = this.data) === null || _a === undefined ? undefined : _a.item_id;
    }
    /**
     * The scene's unique id
     */
    get uniqueId() {
        return this._uniqueId;
    }
}

/**
 * Provides methods to load scenes from sharedb
 *
 * @category Internal
 */
class RealtimeScenes extends Events {
    /**
     * Constructor
     *
     * @param realtime - The realtime API
     * @param connection - The realtime connection
     */
    constructor(realtime, connection) {
        super();
        this._realtime = realtime;
        this._connection = connection;
        this._scenes = {};
        this._currentScene = null;
    }
    /**
     * Loads a scene
     *
     * @param sceneId - The scene id
     * @returns The scene
     */
    load(sceneId) {
        this._currentScene = this._scenes[sceneId];
        if (!this._currentScene) {
            this._currentScene = new RealtimeScene(sceneId, this._realtime, this._connection);
            this._scenes[sceneId] = this._currentScene;
        }
        if (!this._currentScene.loaded) {
            this._currentScene.load();
            this._currentScene.once('load', () => {
                this._realtime.emit('load:scene', this._currentScene);
            });
        }
        return this._currentScene;
    }
    /**
     * Unloads a scene
     *
     * @param sceneId - The scene id
     */
    unload(sceneId) {
        if (this._scenes[sceneId]) {
            this._scenes[sceneId].unload();
            if (this._currentScene === this._scenes[sceneId]) {
                this._currentScene = null;
            }
            delete this._scenes[sceneId];
        }
    }
    /**
     * The current scene
     */
    get current() {
        return this._currentScene;
    }
}

/**
 * Represents an asset in sharedb
 *
 * @category Internal
 */
class RealtimeAsset extends Events {
    /**
     * Constructor
     *
     * @param uniqueId - The unique asset id
     * @param realtime - The realtime API
     * @param connection - The realtime connection
     *
     */
    constructor(uniqueId, realtime, connection) {
        super();
        this._uniqueId = uniqueId;
        this._realtime = realtime;
        this._connection = connection;
        this._document = null;
        this._loaded = false;
        this._evtConnection = null;
    }
    /**
     * Loads asset from sharedb and subscribes to changes.
     */
    load() {
        if (this._document)
            return;
        this._document = this._connection.getDocument('assets', this._uniqueId);
        this._document.on('error', this._onError.bind(this));
        this._document.on('load', this._onLoad.bind(this));
        this._evtConnection = this._realtime.on('disconnect', this.unload.bind(this));
        this._document.subscribe();
    }
    /**
     * Unloads asset from sharedb and unsubscribes from changes.
     */
    unload() {
        if (!this._document)
            return;
        this._document.unsubscribe();
        this._document.destroy();
        this._document = null;
        this._loaded = false;
        this._evtConnection.unbind();
        this._evtConnection = null;
        this.emit('unload');
    }
    /**
     * Submits sharedb operation
     *
     * @param op - The operation
     * @param callback - The callback
     */
    submitOp(op, callback) {
        if (!this._loaded)
            return;
        try {
            this._document.submitOp([op], callback);
        }
        catch (err) {
            console.error(err);
            this._realtime.emit('error:asset', err, this._uniqueId);
        }
    }
    /**
     * Calls the callback when there are no changes pending to be
     * sent to the server
     *
     * @param callback - The callback
     */
    whenNothingPending(callback) {
        if (this._document) {
            this._document.whenNothingPending(callback);
        }
    }
    _onError(err) {
        if (this._connection.connected) {
            console.log(err);
        }
        else {
            this._realtime.emit('error:asset', err, this._uniqueId);
        }
    }
    _onLoad() {
        const assetData = this._document.data;
        if (!assetData) {
            const err = `Could not load asset: ${this._uniqueId}`;
            this._onError(err);
            this.unload();
            this.emit('error:load', err);
            return;
        }
        // notify of operations
        this._document.on('op', this._onOp.bind(this));
        this._loaded = true;
        this.emit('load');
    }
    _onOp(ops, local) {
        if (local)
            return;
        for (let i = 0; i < ops.length; i++) {
            if (ops[i].p[0]) {
                this._realtime.emit('asset:op', ops[i], this._uniqueId);
            }
        }
    }
    /**
     * Whether the asset is loaded
     */
    get loaded() {
        return this._loaded;
    }
    /**
     * The asset data
     */
    get data() {
        return ((this._loaded && this._document) ? this._document.data : null);
    }
    /**
     * The asset id - used in combination with branch id
     */
    get id() {
        var _a;
        return (_a = this.data) === null || _a === undefined ? undefined : _a.item_id;
    }
    /**
     * The asset's unique id
     */
    get uniqueId() {
        return this._uniqueId;
    }
}

/**
 * Provides methods to load assets from sharedb
 *
 * @category Internal
 */
class RealtimeAssets extends Events {
    /**
     * Constructor
     *
     * @param realtime - The realtime API
     * @param connection - The realtime connection
     */
    constructor(realtime, connection) {
        super();
        this._realtime = realtime;
        this._connection = connection;
        this._assets = {};
    }
    /**
     * Loads an asset
     *
     * @param id - The asset's unique id
     * @returns The asset
     */
    load(id) {
        let asset = this._assets[id];
        if (!asset) {
            asset = new RealtimeAsset(id, this._realtime, this._connection);
            this._assets[id] = asset;
        }
        if (!asset.loaded) {
            asset.load();
        }
        return asset;
    }
    /**
     * Gets an already loaded asset
     *
     * @param id - The asset's unique id
     * @returns The asset
     */
    get(id) {
        return this._assets[id] || null;
    }
    /**
     * Unloads an asset
     *
     * @param id - The asset's unique id
     */
    unload(id) {
        if (this._assets[id]) {
            this._assets[id].unload();
            delete this._assets[id];
        }
    }
}

/**
 * Provides methods to communicate and load / save data to the realtime server
 *
 * @category Internal
 */
class Realtime extends Events {
    constructor() {
        super();
        this._connection = new RealtimeConnection(this);
        this._scenes = new RealtimeScenes(this, this.connection);
        this._assets = new RealtimeAssets(this, this.connection);
    }
    /**
     * Gets the realtime connection
     */
    get connection() {
        return this._connection;
    }
    /**
     * Gets the realtime scenes API
     */
    get scenes() {
        return this._scenes;
    }
    /**
     * Gets the realtime assets API
     */
    get assets() {
        return this._assets;
    }
}

/**
 * The Messenger API. The messenger receives messages
 * when various things happen e.g. an asset is created etc.
 *
 * @category Internal
 */
class Messenger extends Events {
    /**
     * Constructor
     *
     * @param messenger - The instance of the Messenger client - this is
     * a different class which requires the Messenger client library to be downloaded.
     */
    constructor(messenger) {
        super();
        this._messenger = messenger;
        this._url = null;
        messenger.on('connect', this._onConnect.bind(this));
        messenger.on('welcome', this._onWelcome.bind(this));
        messenger.on('message', this._onMessage.bind(this));
    }
    _onConnect() {
        this._messenger.authenticate(globals.accessToken, 'designer');
        this.emit('connected');
    }
    _onWelcome() {
        this._messenger.projectWatch(globals.projectId);
    }
    _onMessage(data) {
        this.emit(data.name, data.data);
        this.emit('message', data.name, data.data);
    }
    /**
     * Connects to the messenger server.
     *
     * @param url - The server URL
     */
    connect(url) {
        this._url = url;
        this._messenger.connect(url);
    }
    /**
     * Returns true if we are connected to the messenger server.
     */
    get isConnected() {
        return this._messenger.isConnected;
    }
}

/**
 * Facilitates tracking of asynchronous jobs.
 *
 * @category Internal
 */
class Jobs extends Events {
    constructor() {
        super(...arguments);
        this._jobsInProgress = {};
    }
    /**
     * Adds a new job. The specified function will be returned when the
     * job is finished.
     *
     * @param fn - A function to be stored for this job.
     * @returns Returns a job id
     * @example
     * ```javascript
     * const jobId = editor.jobs.start(() => console.log('job was finished'));
     * editor.jobs.finish(jobId)(); // prints 'job was finished'
     * ```
     */
    start(fn) {
        const jobId = Guid.create().substring(0, 8);
        this._jobsInProgress[jobId] = fn;
        this.emit('start', jobId);
        return jobId;
    }
    /**
     * Notifies that a job has finished. The specified job
     * id is removed and the callback stored when the job was
     * started is returned.
     *
     * @param jobId - The job id
     * @returns The function stored when the job was started
     * @example
     * ```javascript
     * const jobId = editor.jobs.start(() => console.log('job was finished'));
     * editor.jobs.finish(jobId)(); // prints 'job was finished'
     * ```
     */
    finish(jobId) {
        const callback = this._jobsInProgress[jobId];
        delete this._jobsInProgress[jobId];
        this.emit('finish', jobId);
        return callback;
    }
}

/**
 * Wrapper around native local storage
 *
 * @category Internal
 */
class LocalStorage {
    constructor() {
        this._cache = {};
    }
    /**
     * Gets a key from localStorage
     *
     * @param key - The key
     * @returns The value
     */
    get(key) {
        const value = localStorage.getItem(key);
        if (value) {
            if (!this._cache[key] || this._cache[key].value !== value) {
                try {
                    this._cache[key] = {
                        value: value,
                        json: JSON.parse(value)
                    };
                }
                catch (e) {
                    console.error(e);
                }
            }
            return this._cache[key].json;
        }
        delete this._cache[key];
        return value;
    }
    /**
     * Stores a key-value to localStorage
     *
     * @param key - The key
     * @param value - The value
     */
    set(key, value) {
        const text = JSON.stringify(value);
        localStorage.setItem(key, text);
        this._cache[key] = {
            value: text,
            json: value
        };
    }
    /**
     * Removes a key from localStorage
     *
     * @param key - The key
     */
    unset(key) {
        localStorage.removeItem(key);
    }
    /**
     * Checks if key exists in local storage
     *
     * @param key - The key
     * @returns True or false
     */
    has(key) {
        return !!localStorage.getItem(key);
    }
}

/**
 * Represents a custom clipboard with a specific name
 * which stores a value in localStorage under that name
 *
 * @category Internal
 */
class Clipboard {
    /**
     * Constructor
     *
     * @param name - The name of the clipboard.
     */
    constructor(name) {
        this._name = name;
        // we could pass the desired storage as a parameter to change to SessionStorage for example
        this._storage = new LocalStorage();
    }
    /**
     * Gets whether the clipboard is empty
     */
    get empty() {
        return !this._storage.has(this._name);
    }
    /**
     * Sets the value to be stored in the clipboard. Pass null to clear the value from storage.
     */
    set value(value) {
        if (value !== null) {
            this._storage.set(this._name, value);
        }
        else {
            this._storage.unset(this._name);
        }
    }
    /**
     * Gets the value stored in the clipboard.
     */
    get value() {
        return this._storage.get(this._name);
    }
}

class Ajax extends Events {
    /**
     * @param args - Arguments for the request.
     * @returns The Ajax instance.
     */
    static get(args) {
        return new Ajax(Object.assign(args, { method: 'GET' }));
    }
    /**
     * @param args - Arguments for the request.
     * @returns The Ajax instance.
     */
    static post(args) {
        return new Ajax(Object.assign(args, { method: 'POST' }));
    }
    /**
     * @param args - Arguments for the request.
     * @returns The Ajax instance.
     */
    static put(args) {
        return new Ajax(Object.assign(args, { method: 'PUT' }));
    }
    /**
     * @param args - Arguments for the request.
     * @returns The Ajax instance.
     */
    static delete(args) {
        return new Ajax(Object.assign(args, { method: 'DELETE' }));
    }
    /**
     * @param args - Arguments for the request.
     */
    constructor(args) {
        super();
        if (!args) {
            throw new Error('no arguments provided');
        }
        // progress
        this._progress = 0;
        this.emit('progress', this._progress);
        // xhr
        this._xhr = new XMLHttpRequest();
        // send cookies
        if (args.cookies) {
            this._xhr.withCredentials = true;
        }
        // events
        this._xhr.addEventListener('load', this._onLoad.bind(this), false);
        this._xhr.upload.addEventListener('progress', this._onProgress.bind(this), false);
        this._xhr.addEventListener('error', this._onError.bind(this), false);
        this._xhr.addEventListener('abort', this._onAbort.bind(this), false);
        // open request
        if (!args.url) {
            throw new Error('no url provided');
        }
        this._xhr.open(args.method || 'GET', args.url, true);
        this._notJson = args.notJson || false;
        // header for PUT/POST (don't add automatically if sending form [binary] data)
        if (!args.ignoreContentType && (args.method === 'PUT' || args.method === 'POST' || args.method === 'DELETE') && (args.mimeType !== 'multipart/form-data')) {
            this._xhr.setRequestHeader('Content-Type', 'application/json');
        }
        if (args.auth && globals.accessToken) {
            this._xhr.setRequestHeader('Authorization', `Bearer ${globals.accessToken}`);
        }
        if (args.headers) {
            for (const key in args.headers) {
                this._xhr.setRequestHeader(key, args.headers[key]);
            }
        }
        // stringify data if needed
        let body = null;
        if (args.data) {
            if (args.data instanceof FormData) {
                body = args.data;
            }
            else if (typeof (args.data) === 'string') {
                body = args.data;
            }
            else {
                body = JSON.stringify(args.data);
            }
        }
        // make request
        this._xhr.send(body);
    }
    /**
     * @param _evt - The load event.
     */
    _onLoad(_evt) {
        this._progress = 1;
        this.emit('progress', 1);
        if (this._xhr.status === 200 || this._xhr.status === 201) {
            if (this._notJson) {
                this.emit('load', this._xhr.status, this._xhr.responseText);
            }
            else {
                let json;
                try {
                    json = JSON.parse(this._xhr.responseText);
                }
                catch (ex) {
                    this.emit('error', this._xhr.status || 0, new Error('invalid json'));
                    return;
                }
                this.emit('load', this._xhr.status, json);
            }
        }
        else {
            try {
                const json = JSON.parse(this._xhr.responseText);
                let msg = json.message;
                if (!msg) {
                    msg = json.error || (json.response && json.response.error);
                }
                if (!msg) {
                    msg = this._xhr.responseText;
                }
                this.emit('error', this._xhr.status, msg);
            }
            catch (ex) {
                this.emit('error', this._xhr.status);
            }
        }
    }
    /**
     * @param evt - The error event.
     */
    _onError(evt) {
        this.emit('error', 0, evt);
    }
    /**
     * @param evt - The abort event.
     */
    _onAbort(evt) {
        this.emit('error', 0, evt);
    }
    /**
     * @param evt - The progress event.
     */
    _onProgress(evt) {
        if (!evt.lengthComputable) {
            return;
        }
        const progress = evt.loaded / evt.total;
        if (progress !== this._progress) {
            this._progress = progress;
            this.emit('progress', this._progress);
        }
    }
    /**
     * @param onprogress - The progress callback.
     * @returns A promise that resolves with the response.
     */
    promisify(onprogress) {
        this.unbind('progress');
        if (onprogress) {
            this.on('progress', onprogress);
        }
        return new Promise((resolve, reject) => {
            this.once('load', (_status, response) => {
                resolve(response);
            });
            this.once('error', (_status, error) => {
                reject(error);
            });
        });
    }
    abort() {
        this._xhr.abort();
    }
}

/**
 * Creates a new app
 *
 * @param data - The app data to create
 * @returns A request that responds with the app data
 */
const appCreate = (data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/apps`,
        auth: true,
        data
    });
};
/**
 * Deletes the app with the given ID
 *
 * @param appId - The ID of the app to delete
 * @returns A request that responds when the app is deleted
 */
const appDelete = (appId) => {
    return Ajax.delete({
        url: `${globals.apiUrl}/apps/${appId}`,
        auth: true
    });
};
/**
 * Get list of apps
 *
 * @param tags - The tags to filter the apps by
 * @returns A request that responds with the list of apps
 */
const appList = (tags) => {
    return Ajax.get({
        url: `${globals.apiUrl}/apps?tags=${tags}`,
        auth: true
    });
};
/**
 * Fetches the app with the given ID
 *
 * @param appId - The ID of the app to get
 * @returns A request that responds with the app data
 */
const appGet = (appId) => {
    return Ajax.get({
        url: `${globals.apiUrl}/apps/${appId}`,
        auth: true
    });
};
/**
 * Downloads the app with the given ID
 *
 * @param data - The app data to download
 * @returns A request that responds with the app data
 */
const appDownload = (data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/apps/download`,
        auth: true,
        data
    });
};

var apps = /*#__PURE__*/Object.freeze({
    __proto__: null,
    appCreate: appCreate,
    appDelete: appDelete,
    appDownload: appDownload,
    appGet: appGet,
    appList: appList
});

/**
 * Fetches asset data for the given asset
 *
 * @param options - The options for the request
 * @returns A request that responds with the asset data
 */
const assetsList = (options) => {
    const params = [];
    if (options.search) {
        params.push(`search=${options.search}`);
    }
    if (options.regex) {
        params.push(`regexp=${options.regex}`);
    }
    if (options.sort) {
        params.push(`sort=${options.sort}`);
    }
    if (options.order) {
        params.push(`order=${options.order}`);
    }
    if (options.skip) {
        params.push(`skip=${options.skip}`);
    }
    if (options.limit) {
        params.push(`limit=${options.limit}`);
    }
    if (options.tags) {
        params.push(`tags=${options.tags}`);
    }
    return Ajax.get({
        url: `${globals.apiUrl}/assets?${params.join('&')}`
    });
};
/**
 * Gets the asset with the given ID
 *
 * @param assetId - The ID of the asset to get
 * @returns A request that responds with the asset
 */
const assetGet = (assetId) => {
    return Ajax.get({
        url: `${globals.apiUrl}/assets/${assetId}`
    });
};
/**
 * Fetches file data for the given asset
 *
 * @param assetId - The ID of the asset to get
 * @param fileName - The name of the file to get
 * @param options - The options for the request
 * @returns A request that responds with the file data
 */
const assetGetFile = (assetId, fileName, options) => {
    const params = [];
    if (options.branchId) {
        params.push(`branchId=${options.branchId}`);
    }
    if (options.immutableBackup) {
        params.push(`immutableBackup=${options.immutableBackup}`);
    }
    return Ajax.get({
        url: `${globals.apiUrl}/assets/${assetId}/file/${fileName}?${params.join('&')}`,
        auth: true,
        notJson: true
    });
};
/**
 * Reimport asset
 *
 * @param assetId - The ID of the asset to reimport
 * @param data - The data to reimport
 * @returns A request that responds with the result of the reimport
 */
const assetReimport = (assetId, data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/assets/${assetId}/reimport?branchId=${globals.branchId}`,
        auth: true,
        data
    });
};
/**
 * Duplicates the asset with the given ID
 *
 * @param assetId - The ID of the asset to duplicate
 * @param data - The data to duplicate
 * @returns A request that responds with the result of the duplicate
 */
const assetDuplicate = (assetId, data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/assets/${assetId}/duplicate`,
        auth: true,
        data,
        headers: {
            Accept: 'application/json'
        }
    });
};
/**
 * Pastes the given assets
 *
 * @param data - The data to paste
 * @param data.assets - The assets to paste
 * @returns A request that responds with the result of the paste
 */
const assetPaste = (data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/assets/paste`,
        auth: true,
        data
    });
};
/**
 * Adds common asset fields to the given form
 *
 * @param form - The form to add the fields to
 * @param data - The data to use for the asset
 * @param pipeline - The pipeline options to use for the asset
 * @returns The form with the added fields
 */
const assetUpdateFields = (form, data, pipeline) => {
    // branch
    form.append('branchId', globals.branchId);
    // name
    if (data.name) {
        form.append('name', data.name);
    }
    // parent
    if (data.parent) {
        if (data.parent instanceof Observer) {
            form.append('parent', data.parent.get('id'));
        }
        else {
            const id = parseInt(data.parent, 10);
            if (!isNaN(id)) {
                form.append('parent', `${id}`);
            }
        }
    }
    if (data.type) {
        switch (data.type) {
            case 'texture':
            case 'textureatlas':
                form.append('pow2', `${pipeline.pow2}`);
                form.append('searchRelatedAssets', `${pipeline.searchRelatedAssets}`);
                break;
            case 'scene':
                form.append('searchRelatedAssets', `${pipeline.searchRelatedAssets}`);
                form.append('overwriteModel', `${pipeline.overwriteModel}`);
                form.append('overwriteAnimation', `${pipeline.overwriteAnimation}`);
                form.append('overwriteMaterial', `${pipeline.overwriteMaterial}`);
                form.append('overwriteTexture', `${pipeline.overwriteTexture}`);
                form.append('pow2', `${pipeline.pow2}`);
                form.append('preserveMapping', `${pipeline.preserveMapping}`);
                form.append('useGlb', `${pipeline.useGlb}`);
                form.append('animSampleRate', `${pipeline.animSampleRate}`);
                form.append('animCurveTolerance', `${pipeline.animCurveTolerance}`);
                form.append('animEnableCubic', `${pipeline.animEnableCubic}`);
                form.append('animUseFbxFilename', `${pipeline.animUseFbxFilename}`);
                form.append('useContainers', `${pipeline.useContainers}`);
                form.append('meshCompression', `${pipeline.meshCompression}`);
                form.append('unwrapUv', `${pipeline.unwrapUv}`);
                form.append('unwrapUvTexelsPerMeter', `${pipeline.unwrapUvTexelsPerMeter}`);
                form.append('importMorphNormals', `${pipeline.importMorphNormals}`);
                break;
        }
    }
    // filename
    if (data.filename) {
        form.append('filename', data.filename);
    }
    // file
    if (data.file && data.file.size) {
        form.append('file', data.file, data.filename || data.name);
    }
    return form;
};
/**
 * Creates a new asset
 *
 * @param data - The data to upload
 * @param pipeline - The pipeline options to use for the asset
 * @returns A request that responds with the result of the upload
 */
const assetCreate = (data, pipeline = {}) => {
    // NOTE
    // non-file form data should be above file,
    // to make it parsed on back-end first
    var _a;
    const form = new FormData();
    // scope
    form.append('projectId', `${globals.projectId}`);
    // type
    if (!data.type) {
        throw new Error('Type is required for asset upload');
    }
    form.append('type', data.type);
    // update asset
    if (data.asset) {
        form.append('asset', data.asset.get('id'));
    }
    // tags
    if (data.tags) {
        form.append('tags', data.tags.join('\n'));
    }
    // source_asset_id
    if (data.source_asset_id) {
        form.append('source_asset_id', data.source_asset_id);
    }
    // data
    if (data.data) {
        form.append('data', JSON.stringify(data.data));
    }
    // meta
    if (data.meta) {
        form.append('meta', JSON.stringify(data.meta));
    }
    // preload
    form.append('preload', `${data.preload === undefined ? ((_a = data.preloadDefault) !== null && _a !== undefined ? _a : true) : data.preload}`);
    // update fields
    assetUpdateFields(form, data, pipeline);
    return Ajax.post({
        url: `${globals.apiUrl}/assets`,
        auth: true,
        data: form,
        ignoreContentType: true,
        headers: {
            Accept: 'application/json'
        }
    });
};
/**
 * Updates the asset with the given ID
 *
 * @param assetId - The ID of the asset to update
 * @param data - The data to update
 * @param pipeline - The pipeline options to use for the asset
 * @returns A request that responds with the result of the update
 */
const assetUpdate = (assetId, data, pipeline = {}) => {
    // NOTE
    // non-file form data should be above file,
    // to make it parsed on back-end first
    const form = new FormData();
    // update fields
    assetUpdateFields(form, data, pipeline);
    return Ajax.put({
        url: `${globals.apiUrl}/assets/${assetId}`,
        auth: true,
        data: form,
        ignoreContentType: true,
        headers: {
            Accept: 'application/json' // Verified to be JSON response
        }
    });
};

var assets = /*#__PURE__*/Object.freeze({
    __proto__: null,
    assetCreate: assetCreate,
    assetDuplicate: assetDuplicate,
    assetGet: assetGet,
    assetGetFile: assetGetFile,
    assetPaste: assetPaste,
    assetReimport: assetReimport,
    assetUpdate: assetUpdate,
    assetsList: assetsList
});

/**
 * Creates a new branch
 */
const branchCreate = (args) => {
    return Ajax.post({
        url: `${globals.apiUrl}/branches`,
        auth: true,
        data: {
            name: args.name,
            projectId: args.projectId,
            sourceBranchId: args.sourceBranchId,
            sourceCheckpointId: args.sourceCheckpointId
        }
    });
};
/**
 * Checks out the master branch
 */
const branchCheckout = (args) => {
    return Ajax.post({
        url: `${globals.apiUrl}/branches/${args.branchId}/checkout`,
        auth: true
    });
};
/**
 * Opens a branch
 */
const branchOpen = (args) => {
    return Ajax.post({
        url: `${globals.apiUrl}/branches/${args.branchId}/open`,
        auth: true
    });
};
/**
 * Closes a branch
 */
const branchClose = (args) => {
    return Ajax.post({
        url: `${globals.apiUrl}/branches/${args.branchId}/close`,
        auth: true
    });
};
/**
 * Deletes a branch
 */
const branchDelete = (args) => {
    return Ajax.delete({
        url: `${globals.apiUrl}/branches/${args.branchId}`,
        auth: true
    });
};
/**
 * Get branch checkpoints
 */
const branchCheckpoints = (args) => {
    const params = [];
    if (args.taskType) {
        params.push(`task_type=${args.taskType}`);
    }
    if (args.limit) {
        params.push(`limit=${args.limit}`);
    }
    if (args.skip) {
        params.push(`skip=${args.skip}`);
    }
    if (args.graphStartId) {
        params.push(`graphStartId=${args.graphStartId}`);
    }
    if (args.vcHistItem) {
        params.push(`vcHistItem=${args.vcHistItem}`);
    }
    return Ajax.get({
        url: `${globals.apiUrl}/branches/${args.branchId}/checkpoints?${params.join('&')}`,
        auth: true
    });
};

var branches = /*#__PURE__*/Object.freeze({
    __proto__: null,
    branchCheckout: branchCheckout,
    branchCheckpoints: branchCheckpoints,
    branchClose: branchClose,
    branchCreate: branchCreate,
    branchDelete: branchDelete,
    branchOpen: branchOpen
});

/**
 * Creates a new checkpoint
 */
const checkpointCreate = (args) => {
    return Ajax.post({
        url: `${globals.apiUrl}/checkpoints`,
        auth: true,
        data: {
            projectId: args.projectId,
            branchId: args.branchId,
            description: args.description
        }
    });
};
/**
 * Gets a checkpoint
 *
 * @param args - The arguments for the request
 * @returns The Ajax wrapped response
 */
const checkpointGet = (args) => {
    return Ajax.get({
        url: `${globals.apiUrl}/checkpoints/${args.checkpointId}`,
        auth: true
    });
};
/**
 * Restores a checkpoint
 */
const checkpointRestore = (args) => {
    return Ajax.post({
        url: `${globals.apiUrl}/checkpoints/${args.checkpointId}/restore`,
        auth: true,
        data: {
            branchId: args.branchId
        }
    });
};
/**
 * Hard resets a checkpoint
 */
const checkpointHardReset = (args) => {
    return Ajax.post({
        url: `${globals.apiUrl}/checkpoints/${args.checkpointId}/hardreset`,
        auth: true,
        data: {
            branchId: args.branchId
        }
    });
};

var checkpoints = /*#__PURE__*/Object.freeze({
    __proto__: null,
    checkpointCreate: checkpointCreate,
    checkpointGet: checkpointGet,
    checkpointHardReset: checkpointHardReset,
    checkpointRestore: checkpointRestore
});

/**
 * Resolve conflicts
 */
const conflictsResolve = (args) => {
    return Ajax.post({
        url: `${globals.apiUrl}/conflicts/resolve`,
        auth: true,
        data: {
            mergeId: args.mergeId,
            conflictIds: args.conflictIds,
            useSrc: args.useSrc,
            useDst: args.useDst,
            revert: args.revert
        }
    });
};
/**
 * Uploads the specified file to resolve a conflict
 */
const conflictsUpload = (args) => {
    const form = new FormData();
    form.append('file', args.file);
    return Ajax.put({
        url: `${globals.apiUrl}/conflicts/${args.conflictId}/file`,
        auth: true,
        data: form,
        ignoreContentType: true,
        headers: {
            Accept: 'application/json'
        }
    });
};

var conflicts = /*#__PURE__*/Object.freeze({
    __proto__: null,
    conflictsResolve: conflictsResolve,
    conflictsUpload: conflictsUpload
});

/**
 * Creates a new diff
 *
 * @param args - The options for the diff
 * @returns A request that responds with the new diff
 */
const diffCreate = (args) => {
    const data = {
        srcBranchId: args.srcBranchId,
        dstBranchId: args.dstBranchId
    };
    if (args.srcCheckpointId) {
        data.srcCheckpointId = args.srcCheckpointId;
    }
    if (args.dstCheckpointId) {
        data.dstCheckpointId = args.dstCheckpointId;
    }
    if (args.mergeId) {
        data.mergeId = args.mergeId;
    }
    if (args.histItem) {
        data.vcHistItem = args.histItem;
    }
    return Ajax.post({
        url: `${globals.apiUrl}/diff`,
        auth: true,
        data: {
            srcBranchId: data.srcBranchId,
            dstBranchId: data.dstBranchId,
            srcCheckpointId: data.srcCheckpointId,
            dstCheckpointId: data.dstCheckpointId,
            mergeId: data.mergeId,
            vcHistItem: data.vcHistItem
        }
    });
};

var diff = /*#__PURE__*/Object.freeze({
    __proto__: null,
    diffCreate: diffCreate
});

/**
 * Gets the current checked out branch from the server
 *
 * @returns The request object
 */
const homeBranch = () => {
    return Ajax.get({
        url: `${globals.homeUrl}/editor/project/${globals.projectId}/branch`,
        auth: true
    });
};
/**
 * Gets raw file from the server
 *
 * @param url - The URL of the file to get
 * @param notJson - If the file is not JSON
 * @returns The request object
 */
const homeFile = (url, notJson = false) => {
    return Ajax.get({
        url: `${url}`,
        notJson
    });
};
/**
 * Set the users editorOpened flag to true
 *
 * @param sceneId - The ID of the scene
 * @returns The request object
 */
const homeSceneOpened = (sceneId) => {
    return Ajax.post({
        url: `${globals.homeUrl}/editor/scene/${sceneId}/opened`
    });
};
/**
 * Store event on the scene
 *
 * @param sceneId - The ID of the scene
 * @param data - The event data
 * @returns The request object
 */
const homeSceneEvent = (sceneId, data) => {
    return Ajax.post({
        url: `${globals.homeUrl}/editor/scene/${sceneId}/events`,
        data
    });
};
/**
 * Update tip on the scene
 *
 * @param sceneId - The ID of the scene
 * @param tip - The name of the tip
 * @returns The request object
 */
const homeSceneTip = (sceneId, tip) => {
    return Ajax.post({
        url: `${globals.homeUrl}/editor/scene/${sceneId}/tips/${tip}`
    });
};

var home = /*#__PURE__*/Object.freeze({
    __proto__: null,
    homeBranch: homeBranch,
    homeFile: homeFile,
    homeSceneEvent: homeSceneEvent,
    homeSceneOpened: homeSceneOpened,
    homeSceneTip: homeSceneTip
});

/**
 * Get the project invitations
 *
 * @param options - The options to use when fetching the invitations
 * @returns A request that responds with the list of invitations
 */
const invitationList = (options) => {
    const params = [];
    if (options.project) {
        params.push(`project_owner=${options.project.owner}`);
        params.push(`project_name=${options.project.name}`);
    }
    if (options.username) {
        params.push(`username=${options.username}`);
    }
    else if (options.id) {
        params.push(`user_id=${options.id}`);
    }
    if (options.accepted) {
        params.push(`accepted=${options.accepted}`);
    }
    if (options.pending) {
        params.push(`pending=${options.pending}`);
    }
    return Ajax.get({
        url: `${globals.apiUrl}/invitations?${params.join('&')}`,
        auth: true
    });
};
/**
 * Creates a new invitation
 *
 * @param data - The invitation data to create
 * @returns A request that responds with the invitation data
 */
const invitationCreate = (data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/invitations`,
        auth: true,
        data
    });
};
/**
 * Deletes an invitation
 *
 * @param invId - The ID of the invitation to delete
 * @returns A request that responds when the invitation is deleted
 */
const invitationDelete = (invId) => {
    return Ajax.delete({
        url: `${globals.apiUrl}/invitations/${invId}`,
        auth: true
    });
};

var invitations = /*#__PURE__*/Object.freeze({
    __proto__: null,
    invitationCreate: invitationCreate,
    invitationDelete: invitationDelete,
    invitationList: invitationList
});

/**
 * Gets the job with the given ID
 *
 * @param jobId - The ID of the job to get
 * @returns A request that responds with the job data
 */
const jobGet = (jobId) => {
    return Ajax.get({
        url: `${globals.apiUrl}/jobs/${jobId}`,
        auth: true
    });
};

var jobs = /*#__PURE__*/Object.freeze({
    __proto__: null,
    jobGet: jobGet
});

/**
 * Create a merge
 *
 */
const mergeCreate = (args) => {
    return Ajax.post({
        url: `${globals.apiUrl}/merge`,
        auth: true,
        data: {
            srcBranchId: args.srcBranchId,
            dstBranchId: args.dstBranchId,
            srcBranchClose: args.srcBranchClose
        }
    });
};
/**
 * Apply a merge
 */
const mergeApply = (args) => {
    return Ajax.post({
        url: `${globals.apiUrl}/merge/${args.mergeId}/apply`,
        auth: true,
        data: {
            finalize: args.finalize
        }
    });
};
/**
 * Get a merge object by merge id including all of its conflicts
 */
const mergeGet = (args) => {
    return Ajax.get({
        url: `${globals.apiUrl}/merge/${args.mergeId}`,
        auth: true
    });
};
/**
 * Force stops a merge which deletes the merge and all of its conflicts
 */
const mergeDelete = (args) => {
    return Ajax.delete({
        url: `${globals.apiUrl}/merge/${args.mergeId}`,
        auth: true
    });
};
/**
 * Get conflicts for a merge
 */
const mergeConflicts = (args) => {
    const params = [];
    if (args.resolved) {
        params.push('resolved=true');
    }
    return Ajax.get({
        url: `${globals.apiUrl}/merge/${args.mergeId}/conflicts/${args.conflictId}/file/${args.fileName}?${params.join('&')}`,
        auth: true,
        notJson: true
    });
};

var merge = /*#__PURE__*/Object.freeze({
    __proto__: null,
    mergeApply: mergeApply,
    mergeConflicts: mergeConflicts,
    mergeCreate: mergeCreate,
    mergeDelete: mergeDelete,
    mergeGet: mergeGet
});

/**
 * Updates the payment subscription for the given user
 *
 * @param userId - The ID of the user to update the subscription for
 * @param data - The data to update the subscription with
 * @returns A request that responds with the result of the update
 */
const paymentSubUpdate = (userId, data) => {
    return Ajax.put({
        url: `${globals.apiUrl}/payment/subscription/users/${userId}`,
        auth: true,
        data
    });
};

var payment = /*#__PURE__*/Object.freeze({
    __proto__: null,
    paymentSubUpdate: paymentSubUpdate
});

/**
 * Creates a new project
 *
 * @param data - The data for the new project
 * @returns A request that responds with the new project
 */
const projectCreate = (data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/projects`,
        auth: true,
        data
    });
};
/**
 * Updates a project
 *
 * @param projectId - The ID of the project to update
 * @param data - The data to update the project with
 * @returns A request that responds with the updated project
 */
const projectUpdate = (projectId, data) => {
    return Ajax.put({
        url: `${globals.apiUrl}/projects/${projectId}`,
        auth: true,
        data
    });
};
/**
 * Gets a project by ID
 *
 * @param projectId - The ID of the project to get
 * @returns A request that responds with the project
 */
const projectGet = (projectId) => {
    return Ajax.get({
        url: `${globals.apiUrl}/projects/${projectId}`,
        auth: true
    });
};
/**
 * Deletes a project by ID
 *
 * @param projectId - The ID of the project to delete
 * @returns A request that responds when the project is deleted
 */
const projectDelete = (projectId) => {
    return Ajax.delete({
        url: `${globals.apiUrl}/projects/${projectId}`,
        auth: true,
        notJson: true
    });
};
/**
 * Imports a project
 *
 * @param data - The data for the project import
 * @returns A request that responds when the project is imported
 */
const projectImport = (data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/projects/import`,
        auth: true,
        data
    });
};
/**
 * Exports a project by ID
 *
 * @param projectId - The ID of the project to export
 * @returns A request that responds when the project is exported
 */
const projectExport = (projectId) => {
    return Ajax.post({
        url: `${globals.apiUrl}/projects/${projectId}/export`,
        auth: true
    });
};
/**
 * Unlocks a project by ID
 *
 * @param projectId - The ID of the project to unlock
 * @returns A request that responds when the project is unlocked
 */
const projectUnlock = (projectId) => {
    return Ajax.post({
        url: `${globals.apiUrl}/projects/${projectId}/unlock`,
        auth: true
    });
};
/**
 * Transfers a project by ID
 *
 * @param projectId - The ID of the project to transfer
 * @param data - The data for the project transfer
 * @returns A request that responds when the project is transferred
 */
const projectTransfer = (projectId, data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/projects/${projectId}/transfer`,
        auth: true,
        data
    });
};
/**
 * Accepts a project transfer by ID
 *
 * @param projectId - The ID of the project to accept the transfer for
 * @returns A request that responds when the project transfer is accepted
 */
const projectAcceptTransfer = (projectId) => {
    return Ajax.post({
        url: `${globals.apiUrl}/projects/${projectId}/accept_transfer`,
        auth: true
    });
};
/**
 * Declines a project transfer by ID
 *
 * @param projectId - The ID of the project to decline the transfer for
 * @returns A request that responds when the project transfer is declined
 */
const projectDeclineTransfer = (projectId) => {
    return Ajax.post({
        url: `${globals.apiUrl}/projects/${projectId}/decline_transfer`,
        auth: true
    });
};
/**
 * Get the project activity
 *
 * @param projectId - The ID of the project to get the activity for
 * @returns A request that responds with the project activity
 */
const projectActivity = (projectId) => {
    return Ajax.get({
        url: `${globals.apiUrl}/projects/${projectId}/activity`,
        auth: true
    });
};
/**
 * Get the project collaborators
 *
 * @param projectId - The ID of the project to get the collaborators for
 * @returns A request that responds with the project collaborators
 */
const projectCollabList = (projectId) => {
    return Ajax.get({
        url: `${globals.apiUrl}/projects/${projectId}/collaborators`,
        auth: true
    });
};
/**
 * Creates a new project collaborator
 *
 * @param projectId - The ID of the project to create the collaborator for
 * @param collab - The data for the new collaborator
 * @returns A request that responds with the new collaborator
 */
const projectCollabCreate = (projectId, collab) => {
    return Ajax.post({
        url: `${globals.apiUrl}/projects/${projectId}/collaborators`,
        auth: true,
        data: collab
    });
};
/**
 * Updates a project collaborator
 *
 * @param projectId - The ID of the project to update the collaborator for
 * @param collab - The data for the collaborator
 * @returns A request that responds with the updated collaborator
 */
const projectCollabUpdate = (projectId, collab) => {
    return Ajax.put({
        url: `${globals.apiUrl}/projects/${projectId}/collaborators/${collab.id}`,
        auth: true,
        data: {
            access_level: collab.access_level
        }
    });
};
/**
 * Deletes a project collaborator
 *
 * @param projectId - The ID of the project to delete the collaborator for
 * @param collabId - The ID of the collaborator to delete
 * @returns A request that responds when the collaborator is deleted
 */
const projectCollabDelete = (projectId, collabId) => {
    return Ajax.delete({
        url: `${globals.apiUrl}/projects/${projectId}/collaborators/${collabId}`,
        auth: true
    });
};
/**
 * Uploads a project image
 *
 * @param projectId - The ID of the project to upload the image for
 * @param file - The image file to upload
 * @returns A request that responds with the uploaded image
 */
const projectImage = (projectId, file) => {
    const form = new FormData();
    form.append('file', file);
    return Ajax.post({
        url: `${globals.apiUrl}/projects/${projectId}/image`,
        auth: true,
        data: form,
        ignoreContentType: true,
        headers: {
            Accept: 'application/json'
        }
    });
};
/**
 * Fetches a list of assets for the current project
 *
 * @param view - The view to get assets for
 * @param cookies - Whether to include cookies in the request
 * @returns A request that responds with the list of assets
 */
const projectAssets = (view, cookies = false) => {
    return Ajax.get({
        url: `${globals.apiUrl}/projects/${globals.projectId}/assets?branchId=${globals.branchId}&view=${view}`,
        auth: true,
        cookies
    });
};
/**
 * Fetches a list of scenes for the current project
 *
 * @returns A request that responds with the list of scenes
 */
const projectScenes = () => {
    return Ajax.get({
        url: `${globals.apiUrl}/projects/${globals.projectId}/scenes?branchId=${globals.branchId}`,
        auth: true
    });
};
/**
 * Fetches a list of branches for the current project
 *
 * @param options - The options for the request
 * @returns A request that responds with the list of branches
 */
const projectBranches = (options) => {
    const params = [];
    if (options.limit) {
        params.push(`limit=${options.limit}`);
    }
    if (options.skip) {
        params.push(`skip=${options.skip}`);
    }
    if (options.closed) {
        params.push(`closed=${options.closed}`);
    }
    if (options.favorite) {
        params.push(`favorite=${options.favorite}`);
    }
    return Ajax.get({
        url: `${globals.apiUrl}/projects/${globals.projectId}/branches?${params.join('&')}`,
        auth: true
    });
};
/**
 * Fetches a list of apps for the current project
 *
 * @param limit - The maximum number of apps to return
 * @param skip - The number of apps to skip
 * @returns A request that responds with the list of apps
 */
const projectApps = (limit = 0, skip = 0) => {
    const params = [];
    params.push(`limit=${limit}`);
    params.push(`skip=${skip}`);
    return Ajax.get({
        url: `${globals.apiUrl}/projects/${globals.projectId}/apps?${params.join('&')}`,
        auth: true
    });
};
/**
 * Fetches a list of repositories for the current project
 *
 * @returns A request that responds with the list of repositories
 */
const projectRepoList = () => {
    return Ajax.get({
        url: `${globals.apiUrl}/projects/${globals.projectId}/repositories`,
        auth: true
    });
};
/**
 * Gets list of source files in the repository
 *
 * @param repoService - The repository to get the source files for
 * @returns A request that responds with the list of source files
 */
const projectRepoSourcefilesList = (repoService) => {
    return Ajax.get({
        url: `${globals.apiUrl}/projects/${globals.projectId}/repositories/${repoService}/sourcefiles`,
        auth: true
    });
};
/**
 * Gets a source file from the repository
 *
 * @param repoService - The repository to get the source file from
 * @param relativePath - The relative path of the source file
 * @returns A request that responds with the source file
 */
const projectRepoSourcefile = (repoService, relativePath) => {
    return Ajax.get({
        url: `${globals.apiUrl}/projects/${globals.projectId}/repositories/${repoService}/sourcefiles/${relativePath}`,
        auth: true,
        notJson: true
    });
};
/**
 * Deletes a source file from the repository
 *
 * @param fileName - The name of the source file to remove
 * @param repoService - The repository to remove the source file from
 * @returns A request that responds when the source file is removed
 */
const projectRepoSourcefilesDelete = (fileName, repoService = 'directory') => {
    return Ajax.delete({
        url: `${globals.apiUrl}/projects/${globals.projectId}/repositories/${repoService}/sourcefiles/${fileName}`,
        auth: true
    });
};

var projects = /*#__PURE__*/Object.freeze({
    __proto__: null,
    projectAcceptTransfer: projectAcceptTransfer,
    projectActivity: projectActivity,
    projectApps: projectApps,
    projectAssets: projectAssets,
    projectBranches: projectBranches,
    projectCollabCreate: projectCollabCreate,
    projectCollabDelete: projectCollabDelete,
    projectCollabList: projectCollabList,
    projectCollabUpdate: projectCollabUpdate,
    projectCreate: projectCreate,
    projectDeclineTransfer: projectDeclineTransfer,
    projectDelete: projectDelete,
    projectExport: projectExport,
    projectGet: projectGet,
    projectImage: projectImage,
    projectImport: projectImport,
    projectRepoList: projectRepoList,
    projectRepoSourcefile: projectRepoSourcefile,
    projectRepoSourcefilesDelete: projectRepoSourcefilesDelete,
    projectRepoSourcefilesList: projectRepoSourcefilesList,
    projectScenes: projectScenes,
    projectTransfer: projectTransfer,
    projectUnlock: projectUnlock,
    projectUpdate: projectUpdate
});

/**
 * Creates a new scene
 *
 * @param data - The data for the new scene
 * @returns A request that responds with the new scene
 */
const sceneCreate = (data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/scenes`,
        auth: true,
        data
    });
};
/**
 * Deletes the scene with the given ID
 *
 * @param sceneId - The ID of the scene to delete
 * @returns A request that responds when the scene is deleted
 */
const sceneDelete = (sceneId) => {
    return Ajax.delete({
        url: `${globals.apiUrl}/scenes/${sceneId}?branchId=${globals.branchId}`,
        auth: true
    });
};
/**
 * Gets the scene with the given ID
 *
 * @param sceneId - The ID of the scene to get
 * @param cookies - Whether to include cookies in the request
 * @returns A request that responds with the scene data
 */
const sceneGet = (sceneId, cookies = false) => {
    return Ajax.get({
        url: `${globals.apiUrl}/scenes/${sceneId}?branchId=${globals.branchId}`,
        auth: true,
        cookies
    });
};

var scenes = /*#__PURE__*/Object.freeze({
    __proto__: null,
    sceneCreate: sceneCreate,
    sceneDelete: sceneDelete,
    sceneGet: sceneGet
});

/**
 * Creates a new star
 *
 * @param data - The data for the new star
 * @returns A request that responds with the new star
 */
const starCreate = (data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/star`,
        auth: true,
        data
    });
};
/**
 * Deletes a star by ID
 *
 * @param starId - The ID of the star to delete
 * @returns A request that responds when the star is deleted
 */
const starDelete = (starId) => {
    return Ajax.delete({
        url: `${globals.apiUrl}/star/${starId}`,
        auth: true
    });
};

var star = /*#__PURE__*/Object.freeze({
    __proto__: null,
    starCreate: starCreate,
    starDelete: starDelete
});

/**
 * Fetches store item
 *
 * @param storeId - The ID of the store item to fetch
 * @returns A request that responds with the store item
 */
const storeGet = (storeId) => {
    return Ajax.get({
        url: `${globals.apiUrl}/store/${storeId}`
    });
};
/**
 * Uploads a store item
 *
 * @param data - The data for the new store item
 * @param mimeType - The MIME type of the store item
 * @returns A request that responds with the new store item
 */
const storeUpload = (data, mimeType) => {
    return Ajax.post({
        url: `${globals.apiUrl}/store/upload`,
        auth: true,
        data,
        mimeType
    });
};
/**
 * Clones store item
 *
 * @param storeId - The ID of the store item to clone
 * @param data - The data for the new store item
 * @returns A request that responds with the new store item
 */
const storeClone = (storeId, data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/store/${storeId}/clone`,
        auth: true,
        data,
        notJson: true
    });
};
/**
 * Fetches store assets
 *
 * @param storeId - The ID of the store to fetch assets from
 * @returns A request that responds with the store asset
 */
const storeAssets = (storeId) => {
    return Ajax.get({
        url: `${globals.apiUrl}/store/${storeId}/assets`
    });
};
/**
 * Fetches store asset file
 *
 * @param assetId - The ID of the asset to fetch
 * @param assetName - The name of the asset to fetch
 * @returns A request that responds with the store asset
 */
const storeAssetFile = (assetId, assetName) => {
    return Ajax.get({
        url: `${globals.apiUrl}/store/assets/${assetId}/file/${assetName}`,
        notJson: true
    });
};
/**
 * Fetches store licenses
 *
 * @returns A request that responds with the store licenses
 */
const storeLicenses = () => {
    return Ajax.get({
        url: `${globals.apiUrl}/store/licenses`
    });
};
/**
 * List store items filtered by search
 *
 * @param options - The options to filter store items by
 * @returns A request that responds with the list of store items
 */
const storeList = (options) => {
    const params = [];
    if (options.search) {
        params.push(`search=${options.search}`);
    }
    if (options.regex) {
        params.push(`regexp=${options.regex}`);
    }
    if (options.sort) {
        params.push(`sort=${options.sort}`);
    }
    if (options.order) {
        params.push(`order=${options.order}`);
    }
    if (options.skip) {
        params.push(`skip=${options.skip}`);
    }
    if (options.limit) {
        params.push(`limit=${options.limit}`);
    }
    if (options.tags) {
        params.push(`tags=${options.tags}`);
    }
    if (options.excludeTags) {
        params.push(`excludeTags=${options.excludeTags}`);
    }
    return Ajax.get({
        url: `${globals.apiUrl}/store?${params.join('&')}`,
        auth: true
    });
};
/**
 * Move assets to store
 *
 * @param storeId - The storeId to move the assets to
 * @param data - The data for the assets to move
 * @returns A request that responds when the assets are moved
 */
const storeMove = (storeId, data) => {
    return Ajax.put({
        url: `${globals.apiUrl}/store/move/${storeId}`,
        auth: true,
        data
    });
};

var store = /*#__PURE__*/Object.freeze({
    __proto__: null,
    storeAssetFile: storeAssetFile,
    storeAssets: storeAssets,
    storeClone: storeClone,
    storeGet: storeGet,
    storeLicenses: storeLicenses,
    storeList: storeList,
    storeMove: storeMove,
    storeUpload: storeUpload
});

/**
 * Start upload process
 *
 * @param data - The data for the upload
 * @param data.filename - The name of the file to upload
 * @returns {Promise<{ uploadId: string, key: string }>} - A promise that resolves with the upload
 * ID and s3 key
 */
const uploadStart = (data) => {
    return fetch(`${globals.apiUrl}/upload/start-upload`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            'Authorization': `Bearer ${globals.accessToken}`,
            'Content-Type': 'application/json'
        }
    }).then(res => res.json());
};
/**
 * Upload a file and get signed URLs
 *
 * @param data - The data for the upload
 * @param data.uploadId - The ID of the upload
 * @param data.parts - The number of parts to upload
 * @param data.key - The s3 key of the file
 * @returns {Promise<{ signedUrls: string[] }>} - A promise that resolves with the signed URLs
 */
const uploadUrls = (data) => {
    return fetch(`${globals.apiUrl}/upload/signed-urls`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            'Authorization': `Bearer ${globals.accessToken}`,
            'Content-Type': 'application/json'
        }
    }).then(res => res.json());
};
/**
 * Complete the upload process
 *
 * @param data - The data for the upload
 * @param data.uploadId - The ID of the upload
 * @param {{ PartNumber: number, ETag: string }[]} data.parts - The parts of the file to upload
 * @param data.key - The s3 key of the file
 * @returns {Promise<Response>} - A promise that resolves when the upload is complete
 */
const uploadComplete = (data) => {
    return fetch(`${globals.apiUrl}/upload/complete-upload`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
            'Authorization': `Bearer ${globals.accessToken}`,
            'Content-Type': 'application/json'
        }
    });
};

var upload = /*#__PURE__*/Object.freeze({
    __proto__: null,
    uploadComplete: uploadComplete,
    uploadStart: uploadStart,
    uploadUrls: uploadUrls
});

/**
 * Creates a new user
 *
 * @param data - The data for the new user
 * @returns A request that responds with the new user
 */
const userCreate = (data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/users`,
        auth: true,
        data
    });
};
/**
 * Deletes the user with the given ID
 *
 * @param userId - The ID of the user to delete
 * @returns A request that responds when the user is deleted
 */
const userDelete = (userId) => {
    return Ajax.delete({
        url: `${globals.apiUrl}/users/${userId}`,
        auth: true,
        notJson: true
    });
};
/**
 * Fetches the user with the given ID
 *
 * @param userId - The ID of the user to get
 * @returns A request that responds with the user data
 */
const userGet = (userId) => {
    return Ajax.get({
        url: `${globals.apiUrl}/users/${userId}`,
        auth: true
    });
};
/**
 * Fetches a list of collaborators for the given user
 *
 * @param userId - The ID of the user to get collaborators for
 * @returns A request that responds with the list of collaborators
 */
const userCollabList = (userId) => {
    return Ajax.get({
        url: `${globals.apiUrl}/users/${userId}/collaborators`,
        auth: true
    });
};
/**
 * Fetches a list of projects for the given user
 *
 * @param userId - The ID of the user to get projects for
 * @param view - The view to get projects for
 * @returns A request that responds with the list of projects
 */
const userProjects = (userId, view) => {
    return Ajax.get({
        url: `${globals.apiUrl}/users/${userId}/projects?view=${view}`,
        auth: true
    });
};
/**
 * Fetches the storage data for the given user
 *
 * @param userId - The ID of the user to get storage data for
 * @returns A request that responds with the storage data
 */
const userUsage = (userId) => {
    return Ajax.get({
        url: `${globals.apiUrl}/users/${userId}/usage`,
        auth: true
    });
};

var users = /*#__PURE__*/Object.freeze({
    __proto__: null,
    userCollabList: userCollabList,
    userCreate: userCreate,
    userDelete: userDelete,
    userGet: userGet,
    userProjects: userProjects,
    userUsage: userUsage
});

/**
 * Creates a new watch
 *
 * @param data - The data to watch
 * @returns A request that responds with the watched data
 */
const watchCreate = (data) => {
    return Ajax.post({
        url: `${globals.apiUrl}/watch`,
        auth: true,
        data
    });
};
/**
 * Deletes the watch with the given ID
 *
 * @param watchId - The ID of the watch to delete
 * @returns A request that responds when the watch is deleted
 */
const watchDelete = (watchId) => {
    return Ajax.delete({
        url: `${globals.apiUrl}/watch/${watchId}`,
        auth: true
    });
};

var watch = /*#__PURE__*/Object.freeze({
    __proto__: null,
    watchCreate: watchCreate,
    watchDelete: watchDelete
});

/**
 * The Rest API provides a set of methods for interacting with the PlayCanvas
 * Editor's RESTful API. This includes methods for managing assets, branches,
 * checkpoints, and other resources. The API is designed to be used in conjunction
 * with the PlayCanvas Editor.
 *
 * @category Internal
 * @ignore
 */
class Rest {
    constructor() {
        /**
         * The apps API
         */
        this.apps = apps;
        /**
         * The assets API
         */
        this.assets = assets;
        /**
         * The branches API
         */
        this.branches = branches;
        /**
         * The checkpoints API
         */
        this.checkpoints = checkpoints;
        /**
         * The conflicts API
         */
        this.conflicts = conflicts;
        /**
         * The diff API
         */
        this.diff = diff;
        /**
         * The home API
         */
        this.home = home;
        /**
         * The invitations API
         */
        this.invitations = invitations;
        /**
         * The jobs API
         */
        this.jobs = jobs;
        /**
         * The merge API
         */
        this.merge = merge;
        /**
         * The payment API
         */
        this.payment = payment;
        /**
         * The projects API
         */
        this.projects = projects;
        /**
         * The scenes API
         */
        this.scenes = scenes;
        /**
         * The star API
         */
        this.star = star;
        /**
         * The store API
         */
        this.store = store;
        /**
         * The upload API
         */
        this.upload = upload;
        /**
         * The users API
         */
        this.users = users;
        /**
         * The watch API
         */
        this.watch = watch;
    }
}

// core
/**
 * The version of the Editor API library. This is a string in semantic version format of `major.minor.patch`.
 */
const version = '1.1.19';
/**
 * The git revision of the Editor API library. This is a string of the git commit hash.
 */
const revision = '426fec2';

export { Asset, Assets, AssetsSchema, Clipboard, ComponentSchema, Entities, Entity, Guid, History, Jobs, LocalStorage, Messenger, Realtime, RealtimeAsset, RealtimeAssets, RealtimeConnection, RealtimeScene, RealtimeScenes, Rest, SceneSchema, SceneSettings, Schema, Selection, SelectionHistory, Settings, SettingsSchema, globals, revision, utils, version };
